import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle, Shield, ShieldAlert, ShieldCheck, FileText, FileCheck, Users, Activity,
  Plus, Trash2, Clock, CheckCircle2, XCircle, Building2,
  BookOpen, Eye, Pencil, RefreshCcw, GraduationCap,
  Lock, Unlock, Search, Bell, ClipboardList, ClipboardCheck, Info, Share2,
  Bot, MessageSquare, Globe,
} from "lucide-react";
import { queryClient, throwIfResNotOk, extractFieldErrors } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { fErr, fieldCls, clearErr, extractServerErrors, required } from "@/lib/form-validation";
import { FerpaSourceVerifications } from "@/components/ferpa-source-verifications";

// ── Types ──────────────────────────────────────────────────────────────────────

type FerpaSummary = {
  accessRequests: { pending: number; overdue: number };
  amendmentRequests: { pending: number; unreviewed: number };
  disclosures: { last30Days: number; total: number };
  directoryPreferences: { optedOut: number; total: number };
  vendorAgreements: { active: number; pendingSecurityReview: number; expiringSoon: number };
  accessEvents: { last30Days: number };
  annualNotices: { published: number; currentAcademicYear: string; currentYearPublished: boolean };
};

type AccessRequest = {
  id: string; requestType: string; status: string;
  requesterUserId: string | null; studentId: string;
  requesterName: string; requesterRelationship: string;
  recordsRequested: string; submittedAt: string; dueAt: string;
  approvedAt: string | null; approvedBy: string | null;
  fulfilledAt: string | null; decisionNotes: string | null;
  createdBy: string; createdAt: string; updatedAt: string;
};

type AmendmentRequest = {
  id: string; studentId: string; requestedBy: string;
  recordIdentifier: string; requestReason: string;
  proposedChange: string | null; status: string; decision: string;
  reviewedBy: string | null; reviewedAt: string | null;
  decisionNotes: string | null; hearingRightNoticed: boolean;
  createdAt: string; updatedAt: string;
};

type ConsentRecord = {
  id: string;
  organizationId: string;
  studentId: string;
  dataScope: string;
  purpose: string;
  recipient: string;
  recipientContactInfo: string | null;
  consentStatus: "Pending" | "Approved" | "Revoked" | "Expired";
  consentedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  grantedByUserId: string | null;
  notes: string | null;
  createdAt: string;
};

type DisclosureRecord = {
  id: string; studentId: string; recipient: string;
  recipientContactInfo: string | null;
  disclosureType: string; ferpaExceptionCategory: string;
  purpose: string; recordsDisclosed: string;
  approvedBy: string | null; disclosureDate: string;
  notes: string | null; createdBy: string; createdAt: string;
  retentionLockedUntil: string | null;
};

type VendorAgreement = {
  id: string; vendorName: string; agreementType: string;
  contactEmail: string | null; documentUrl: string | null;
  dataShared: string; effectiveDate: string; expiryDate: string | null;
  approvedBy: string | null; securityReviewStatus: string;
  securityReviewedAt: string | null; notes: string | null;
  createdBy: string; createdAt: string; updatedAt: string;
};

type AccessEvent = {
  id: string; userId: string | null; studentId: string | null;
  recordType: string; source: string; action: string;
  outcome: string; notes: string | null; timestamp: string;
};

type PagedResult<T> = { total: number; page: number; pageSize: number; items: T[] };

type HearingNotice = {
  id: string; organizationId: string; amendmentRequestId: string;
  studentId: string; noticeMethod: string; noticeContent: string;
  sentAt: string; sentBy: string; notes: string | null; createdAt: string;
};

type DeadlineAlertItem = {
  id: string; studentId: string; requestType: string; status: string;
  submittedAt: string; dueAt: string;
  daysOverdue?: number; daysRemaining?: number;
};

type DeadlineAlerts = {
  overdue: DeadlineAlertItem[];
  approaching: DeadlineAlertItem[];
};

type AnnualNotice = {
  id: string; organizationId: string;
  noticeTitle: string; noticeContent: string; academicYear: string;
  publishedDate: string | null; publishedBy: string | null;
  distributionMethod: string | null; distributionDate: string | null;
  status: string; notes: string | null;
  createdBy: string; createdAt: string; updatedAt: string;
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function isOverdue(iso: string | null | undefined) {
  if (!iso) return false;
  return new Date(iso) < new Date();
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === "Fulfilled" || status === "Approved" || status === "Implemented" ? "bg-green-100 text-green-800" :
    status === "Denied" ? "bg-red-100 text-red-800" :
    status === "InReview" || status === "UnderReview" ? "bg-blue-100 text-blue-800" :
    "bg-yellow-100 text-yellow-800";
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>{status}</span>;
}

function SecurityBadge({ status }: { status: string }) {
  const color =
    status === "Passed" ? "bg-green-100 text-green-800" :
    status === "Failed" ? "bg-red-100 text-red-800" :
    status === "InProgress" ? "bg-blue-100 text-blue-800" :
    status === "Waived" ? "bg-gray-100 text-gray-600" :
    "bg-yellow-100 text-yellow-800";
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>{status}</span>;
}

/** Renders an inline field-level validation error below an input. */
function FieldErr({ id, msg }: { id: string; msg?: string }) {
  if (!msg) return null;
  return (
    <p id={id} role="alert" className="text-xs text-destructive mt-0.5">
      {msg}
    </p>
  );
}

/** Auth header for raw fetch calls using the localStorage JWT. */
function bearerHeader(): Record<string, string> {
  const token = localStorage.getItem("dotnetbot.jwt") ?? sessionStorage.getItem("dotnetbot.jwt.session") ?? "";
  const h: Record<string, string> = {};
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

/**
 * Converts extractFieldErrors output to a flat Record<string,string> (first message per field).
 * Returns an empty object when the error is not a ValidationError.
 */
function toFlatFieldErrors(e: unknown): Record<string, string> {
  const fe = extractFieldErrors(e);
  if (!fe) return {};
  const out: Record<string, string> = {};
  for (const [k, msgs] of Object.entries(fe)) out[k] = msgs[0] ?? "";
  return out;
}

// ── Deadline Alerts Banner (G1 — 45-day rule) ────────────────────────────────

function DeadlineAlertsBanner() {
  const { data } = useQuery<DeadlineAlerts>({
    queryKey: ["/api/organization/compliance/ferpa/deadline-alerts"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/deadline-alerts", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load alerts");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const overdueCount    = data?.overdue.length ?? 0;
  const approachingCount = data?.approaching.length ?? 0;
  if (!overdueCount && !approachingCount) return null;

  return (
    <div className="space-y-2">
      {overdueCount > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm">
          <Bell className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold text-red-800">
              {overdueCount} overdue access {overdueCount === 1 ? "request" : "requests"} (§ 99.10(b) 45-day rule)
            </span>
            <div className="text-red-700 text-xs mt-0.5">
              {data!.overdue.map(r => (
                <span key={r.id} className="mr-2">
                  Student <span className="font-mono">{r.studentId}</span> — {r.daysOverdue}d overdue
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {approachingCount > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm">
          <Clock className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold text-yellow-800">
              {approachingCount} access {approachingCount === 1 ? "request" : "requests"} due within 7 days
            </span>
            <div className="text-yellow-700 text-xs mt-0.5">
              {data!.approaching.map(r => (
                <span key={r.id} className="mr-2">
                  Student <span className="font-mono">{r.studentId}</span> — {r.daysRemaining}d remaining
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Student Disclosure Lookup (G7 — § 99.32(c)) ───────────────────────────────

function StudentDisclosureLookupSection() {
  const { toast } = useToast();
  const [studentId, setStudentId] = useState("");
  const [queryId, setQueryId]     = useState<string | null>(null);

  const { data, isFetching } = useQuery<{ total: number; studentId: string; items: DisclosureRecord[] }>({
    queryKey: ["/api/organization/compliance/ferpa/student-disclosures", queryId],
    queryFn: async () => {
      const res = await fetch(`/api/organization/compliance/ferpa/student-disclosures/${encodeURIComponent(queryId!)}`, {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Lookup failed");
      return res.json();
    },
    enabled: queryId !== null,
  });

  const handleLookup = () => {
    const sid = studentId.trim();
    if (!sid) return;
    setQueryId(sid);
    toast({ title: `Looking up disclosures for student ${sid}…` });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Search className="w-4 h-4" /> Student Disclosure Lookup
        </CardTitle>
        <CardDescription className="text-xs">
          34 CFR § 99.32(c) — right of eligible students and parents to inspect the disclosure log.
          Each lookup is automatically recorded as a FERPA access event.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="Enter student ID…"
            value={studentId}
            onChange={e => setStudentId(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleLookup()}
            className="flex-1"
          />
          <Button onClick={handleLookup} disabled={isFetching || !studentId.trim()}>
            {isFetching ? "…" : <><Search className="w-4 h-4 mr-1" /> Lookup</>}
          </Button>
        </div>
        {data && (
          <div>
            <p className="text-xs text-muted-foreground mb-2">
              {data.total} disclosure{data.total !== 1 ? "s" : ""} on record for student{" "}
              <span className="font-mono">{data.studentId}</span> · this lookup was logged as a FERPA access event.
            </p>
            {data.items.length === 0 ? (
              <p className="text-sm text-center py-2 text-muted-foreground">No disclosures found for this student.</p>
            ) : (
              <div className="divide-y text-sm border rounded-md">
                {data.items.map(r => (
                  <div key={r.id} className="px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.recipient}</span>
                      <Badge variant="outline" className="text-xs">{r.disclosureType}</Badge>
                      {r.retentionLockedUntil && new Date(r.retentionLockedUntil) > new Date() && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                          <Lock className="w-3 h-3" /> Locked until {fmtDate(r.retentionLockedUntil)}
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground text-xs mt-0.5">{r.purpose} · {fmtDate(r.disclosureDate)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Hearing Notices Panel (G2 — § 99.21) ─────────────────────────────────────

function HearingNoticesPanel({
  amendmentId, studentId, canManage, decision,
}: { amendmentId: string; studentId: string; canManage: boolean; decision?: string }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ noticeMethod: "Written", noticeContent: "", notes: "" });
  const [showForm, setShowForm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery<HearingNotice[]>({
    queryKey: ["/api/organization/compliance/ferpa/amendment-requests", amendmentId, "hearing-notices"],
    queryFn: async () => {
      const res = await fetch(
        `/api/organization/compliance/ferpa/amendment-requests/${amendmentId}/hearing-notices`,
        { headers: bearerHeader() },
      );
      if (!res.ok) throw new Error("Failed to load hearing notices");
      return res.json();
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `/api/organization/compliance/ferpa/amendment-requests/${amendmentId}/hearing-notices`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...bearerHeader() },
          body: JSON.stringify({ ...form, notes: form.notes || null }),
        },
      );
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/organization/compliance/ferpa/amendment-requests", amendmentId, "hearing-notices"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/amendment-requests"] });
      toast({ title: "Hearing right notice recorded" });
      setForm({ noticeMethod: "Written", noticeContent: "", notes: "" });
      setFieldErrors({});
      setShowForm(false);
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not save." });
        toast({ title: "Unable to record hearing notice", description: e.message, variant: "destructive" });
      }
    },
  });

  const handleCreate = () => {
    const errs: Record<string, string> = {};
    if (!form.noticeContent.trim()) errs.noticeContent = "Notice content is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  return (
    <div className="mt-2 ml-4 pl-3 border-l-2 border-orange-200 space-y-2">
      <p className="text-xs font-medium text-orange-700">§ 99.21 Hearing Right Notices</p>
      {isLoading ? (
        <Skeleton className="h-6 w-48" />
      ) : !data?.length ? (
        <p className="text-xs text-muted-foreground">No hearing notices on record.</p>
      ) : (
        <div className="space-y-1">
          {data.map(n => (
            <div key={n.id} className="text-xs bg-orange-50 rounded px-2 py-1">
              <span className="font-medium">{n.noticeMethod}</span> · {fmtDate(n.sentAt)}
              {n.notes && <span className="text-muted-foreground"> · {n.notes}</span>}
            </div>
          ))}
        </div>
      )}
      {canManage && decision !== "Denied" && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Hearing notices can only be filed after the amendment request is marked as <strong>Denied</strong> (§ 99.21).
        </p>
      )}
      {canManage && decision === "Denied" && !showForm && (
        <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => setShowForm(true)}>
          <Plus className="w-3 h-3 mr-1" /> Add Notice
        </Button>
      )}
      {canManage && showForm && (
        <div className="space-y-2 bg-orange-50 rounded p-2" role="form" aria-label="Record hearing right notice">
          {fieldErrors._ && (
            <p role="alert" className="text-xs text-destructive">{fieldErrors._}</p>
          )}
          <div>
            <Label htmlFor="ferpa-hn-noticeMethod" className="text-xs">Method</Label>
            <select id="ferpa-hn-noticeMethod" className="w-full border rounded px-2 py-1 text-xs mt-0.5"
              value={form.noticeMethod}
              onChange={e => setForm(f => ({ ...f, noticeMethod: e.target.value }))}>
              <option>Written</option>
              <option>Email</option>
              <option>Portal</option>
              <option>InPerson</option>
            </select>
          </div>
          <div>
            <Label htmlFor="ferpa-hn-noticeContent" className="text-xs">Notice Content *</Label>
            <Textarea
              id="ferpa-hn-noticeContent"
              value={form.noticeContent}
              onChange={e => {
                setForm(f => ({ ...f, noticeContent: e.target.value }));
                if (fieldErrors.noticeContent) setFieldErrors(fe => ({ ...fe, noticeContent: "" }));
              }}
              placeholder="Summarise the hearing right notice delivered to the student/parent"
              rows={2}
              className={fieldCls("text-xs", fieldErrors, "noticeContent")}
              required
              aria-invalid={!!fieldErrors.noticeContent || undefined}
              aria-describedby={fieldErrors.noticeContent ? "ferpa-hn-noticeContent-err" : undefined}
            />
            <FieldErr id="ferpa-hn-noticeContent-err" msg={fieldErrors.noticeContent} />
          </div>
          <div>
            <Label htmlFor="ferpa-hn-notes" className="text-xs">Notes (optional)</Label>
            <Input id="ferpa-hn-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. emailed to parent at address on file" className="text-xs h-7" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="h-6 text-xs" onClick={handleCreate} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Record"}
            </Button>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { setShowForm(false); setFieldErrors({}); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Summary Card ──────────────────────────────────────────────────────────────

function SummarySection({ summary }: { summary: FerpaSummary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-blue-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Access Requests</span>
          </div>
          <div className="text-2xl font-bold">{summary.accessRequests.pending}</div>
          <div className="text-xs text-muted-foreground">pending
            {summary.accessRequests.overdue > 0 && (
              <span className="ml-1 text-red-600 font-medium">· {summary.accessRequests.overdue} overdue</span>
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <Pencil className="w-4 h-4 text-orange-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Amendment Requests</span>
          </div>
          <div className="text-2xl font-bold">{summary.amendmentRequests.pending}</div>
          <div className="text-xs text-muted-foreground">pending
            {summary.amendmentRequests.unreviewed > 0 && (
              <span className="ml-1 text-orange-600 font-medium">· {summary.amendmentRequests.unreviewed} unreviewed</span>
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-purple-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Disclosures (30d)</span>
          </div>
          <div className="text-2xl font-bold">{summary.disclosures.last30Days}</div>
          <div className="text-xs text-muted-foreground">{summary.disclosures.total} total logged</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4 text-teal-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Directory Opt-Outs</span>
          </div>
          <div className="text-2xl font-bold">{summary.directoryPreferences.optedOut}</div>
          <div className="text-xs text-muted-foreground">of {summary.directoryPreferences.total} preferences</div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-indigo-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Vendor Agreements</span>
          </div>
          <div className="text-2xl font-bold">{summary.vendorAgreements.active}</div>
          <div className="text-xs text-muted-foreground">active
            {summary.vendorAgreements.pendingSecurityReview > 0 && (
              <span className="ml-1 text-yellow-600 font-medium">· {summary.vendorAgreements.pendingSecurityReview} pending review</span>
            )}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Access Events (30d)</span>
          </div>
          <div className="text-2xl font-bold">{summary.accessEvents.last30Days}</div>
          <div className="text-xs text-muted-foreground">record access log entries</div>
        </CardContent>
      </Card>
      <Card className={summary.annualNotices?.currentYearPublished ? "" : "border-orange-200 bg-orange-50"}>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 mb-1">
            <Bell className={`w-4 h-4 ${summary.annualNotices?.currentYearPublished ? "text-green-500" : "text-orange-500"}`} />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Annual Notice (§ 99.7)</span>
          </div>
          <div className={`text-2xl font-bold ${summary.annualNotices?.currentYearPublished ? "" : "text-orange-700"}`}>
            {summary.annualNotices?.currentYearPublished ? "✓" : "!"}
          </div>
          <div className="text-xs text-muted-foreground">
            {summary.annualNotices?.currentYearPublished
              ? `Published for ${summary.annualNotices.currentAcademicYear}`
              : `Not published for ${summary.annualNotices?.currentAcademicYear ?? "this year"}`}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Access Requests Section ────────────────────────────────────────────────────

function AccessRequestsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    requestType: "Inspect",
    studentId: "",
    requesterName: "",
    requesterRelationship: "Student",
    recordsRequested: "",
  });

  const { data, isLoading } = useQuery<PagedResult<AccessRequest>>({
    queryKey: ["/api/organization/compliance/ferpa/access-requests"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/access-requests?pageSize=20", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load access requests");
      return res.json();
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/access-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ ...form, submittedAt: new Date().toISOString() }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/access-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Access request created" });
      setOpen(false);
      setFieldErrors({});
      setForm({ requestType: "Inspect", studentId: "", requesterName: "", requesterRelationship: "Student", recordsRequested: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not save. Please check the fields and try again." });
        toast({ title: "Unable to create access request", description: e.message, variant: "destructive" });
      }
    },
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.studentId.trim())        errs.studentId        = "Student ID is required.";
    if (!form.requesterName.trim())    errs.requesterName    = "Requester name is required.";
    if (!form.recordsRequested.trim()) errs.recordsRequested = "Records requested description is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  const setField = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (fieldErrors[key]) setFieldErrors(fe => ({ ...fe, [key]: "" }));
  };

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/access-requests/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/access-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Access request deleted" });
    },
    onError: () => toast({ title: "Unable to delete access request", variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Student Record Access Requests
            </CardTitle>
            <CardDescription className="text-xs">34 CFR § 99.10–12 — must act within 45 days of receipt</CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> New Request
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : !data?.items.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">No access requests recorded.</p>
        ) : (
          <div className="divide-y text-sm">
            {data.items.map(r => (
              <div key={r.id} className="py-2 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.requestType}</span>
                    <StatusBadge status={r.status} />
                    {isOverdue(r.dueAt) && r.status !== "Fulfilled" && r.status !== "Denied" && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Overdue
                      </span>
                    )}
                  </div>
                  <div className="text-muted-foreground text-xs mt-0.5">
                    Student: <span className="font-mono">{r.studentId}</span> ·
                    {r.requesterName} ({r.requesterRelationship}) ·
                    Submitted {fmtDate(r.submittedAt)} · Due {fmtDate(r.dueAt)}
                  </div>
                </div>
                {canManage && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                    aria-label="Remove record request"
                    onClick={() => remove.mutate(r.id)}>
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setFieldErrors({}); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Student Record Access Request</DialogTitle>
            <DialogDescription>Log a formal FERPA records access request (§ 99.10).</DialogDescription>
          </DialogHeader>

          {/* General (non-field) error banner */}
          {fieldErrors._ && (
            <p role="alert" className="text-xs text-destructive px-1 -mt-1">{fieldErrors._}</p>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="ferpa-ar-requestType">Request Type</Label>
              <select id="ferpa-ar-requestType" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={form.requestType} onChange={setField("requestType")}>
                <option>Inspect</option>
                <option>Copy</option>
                <option>Explanation</option>
              </select>
            </div>

            <div>
              <Label htmlFor="ferpa-ar-studentId">Student ID *</Label>
              <Input
                id="ferpa-ar-studentId"
                value={form.studentId}
                onChange={setField("studentId")}
                placeholder="Student identifier"
                required
                className={fieldCls("", fieldErrors, "studentId")}
                aria-invalid={!!fieldErrors.studentId || undefined}
                aria-describedby={fieldErrors.studentId ? "ferpa-ar-studentId-err" : undefined}
              />
              <FieldErr id="ferpa-ar-studentId-err" msg={fieldErrors.studentId} />
            </div>

            <div>
              <Label htmlFor="ferpa-ar-requesterName">Requester Name *</Label>
              <Input
                id="ferpa-ar-requesterName"
                value={form.requesterName}
                onChange={setField("requesterName")}
                placeholder="Full name"
                required
                className={fieldCls("", fieldErrors, "requesterName")}
                aria-invalid={!!fieldErrors.requesterName || undefined}
                aria-describedby={fieldErrors.requesterName ? "ferpa-ar-requesterName-err" : undefined}
              />
              <FieldErr id="ferpa-ar-requesterName-err" msg={fieldErrors.requesterName} />
            </div>

            <div>
              <Label htmlFor="ferpa-ar-requesterRelationship">Requester Relationship</Label>
              <select id="ferpa-ar-requesterRelationship" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={form.requesterRelationship} onChange={setField("requesterRelationship")}>
                <option>Student</option>
                <option>Parent</option>
                <option>Guardian</option>
                <option>Authorized</option>
              </select>
            </div>

            <div>
              <Label htmlFor="ferpa-ar-recordsRequested">Records Requested *</Label>
              <Textarea
                id="ferpa-ar-recordsRequested"
                value={form.recordsRequested}
                onChange={setField("recordsRequested")}
                placeholder="Describe which records are being requested"
                rows={2}
                required
                className={fieldCls("", fieldErrors, "recordsRequested")}
                aria-invalid={!!fieldErrors.recordsRequested || undefined}
                aria-describedby={fieldErrors.recordsRequested ? "ferpa-ar-recordsRequested-err" : undefined}
              />
              <FieldErr id="ferpa-ar-recordsRequested-err" msg={fieldErrors.recordsRequested} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Amendment Requests Section ─────────────────────────────────────────────────

function AmendmentRequestsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [expandedAmendmentId, setExpandedAmendmentId] = useState<string | null>(null);
  const [form, setForm] = useState({
    studentId: "",
    recordIdentifier: "",
    requestReason: "",
    proposedChange: "",
  });

  const { data, isLoading } = useQuery<PagedResult<AmendmentRequest>>({
    queryKey: ["/api/organization/compliance/ferpa/amendment-requests"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/amendment-requests?pageSize=20", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load amendment requests");
      return res.json();
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/amendment-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify(form),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/amendment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Amendment request created" });
      setOpen(false);
      setFieldErrors({});
      setForm({ studentId: "", recordIdentifier: "", requestReason: "", proposedChange: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not save. Please check the fields and try again." });
        toast({ title: "Unable to create amendment request", description: e.message, variant: "destructive" });
      }
    },
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.studentId.trim())        errs.studentId        = "Student ID is required.";
    if (!form.recordIdentifier.trim()) errs.recordIdentifier = "Record identifier is required.";
    if (!form.requestReason.trim())    errs.requestReason    = "Reason for amendment is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  const setField = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (fieldErrors[key]) setFieldErrors(fe => ({ ...fe, [key]: "" }));
  };

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/amendment-requests/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/amendment-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Amendment request deleted" });
    },
    onError: () => toast({ title: "Unable to delete amendment request", variant: "destructive" }),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Amendment Requests
            </CardTitle>
            <CardDescription className="text-xs">34 CFR § 99.20–22 — right to amend inaccurate or misleading records</CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> New Amendment
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : !data?.items.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">No amendment requests recorded.</p>
        ) : (
          <div className="divide-y text-sm">
            {data.items.map(r => (
              <div key={r.id} className="py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate max-w-xs">{r.recordIdentifier}</span>
                      <StatusBadge status={r.status} />
                      {r.decision !== "Pending" && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.decision === "Approved" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {r.decision}
                        </span>
                      )}
                      <button
                          className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors"
                          onClick={() => setExpandedAmendmentId(expandedAmendmentId === r.id ? null : r.id)}
                        >
                          {r.hearingRightNoticed ? "✓ Hearing notices" : "§ 99.21 Hearing notices"}
                          {expandedAmendmentId === r.id ? " ▲" : " ▼"}
                        </button>
                    </div>
                    <div className="text-muted-foreground text-xs mt-0.5">
                      Student: <span className="font-mono">{r.studentId}</span> ·
                      Logged {fmtDate(r.createdAt)}
                      {r.reviewedAt && ` · Reviewed ${fmtDate(r.reviewedAt)}`}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{r.requestReason}</div>
                  </div>
                  {canManage && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                      aria-label="Remove amendment request"
                      onClick={() => remove.mutate(r.id)}>
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                  )}
                </div>
                {expandedAmendmentId === r.id && (
                  <HearingNoticesPanel
                    amendmentId={r.id}
                    studentId={r.studentId}
                    canManage={canManage}
                    decision={r.decision}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setFieldErrors({}); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Amendment Request</DialogTitle>
            <DialogDescription>File a FERPA amendment request to correct inaccurate or misleading records (§ 99.20).</DialogDescription>
          </DialogHeader>

          {fieldErrors._ && (
            <p role="alert" className="text-xs text-destructive px-1 -mt-1">{fieldErrors._}</p>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="ferpa-amend-studentId">Student ID *</Label>
              <Input
                id="ferpa-amend-studentId"
                value={form.studentId}
                onChange={setField("studentId")}
                placeholder="Student identifier"
                required
                className={fieldCls("", fieldErrors, "studentId")}
                aria-invalid={!!fieldErrors.studentId || undefined}
                aria-describedby={fieldErrors.studentId ? "ferpa-amend-studentId-err" : undefined}
              />
              <FieldErr id="ferpa-amend-studentId-err" msg={fieldErrors.studentId} />
            </div>

            <div>
              <Label htmlFor="ferpa-amend-recordIdentifier">Record Identifier *</Label>
              <Input
                id="ferpa-amend-recordIdentifier"
                value={form.recordIdentifier}
                onChange={setField("recordIdentifier")}
                placeholder="Which record(s) are believed to be inaccurate?"
                required
                className={fieldCls("", fieldErrors, "recordIdentifier")}
                aria-invalid={!!fieldErrors.recordIdentifier || undefined}
                aria-describedby={fieldErrors.recordIdentifier ? "ferpa-amend-recordIdentifier-err" : undefined}
              />
              <FieldErr id="ferpa-amend-recordIdentifier-err" msg={fieldErrors.recordIdentifier} />
            </div>

            <div>
              <Label htmlFor="ferpa-amend-requestReason">Reason for Amendment *</Label>
              <Textarea
                id="ferpa-amend-requestReason"
                value={form.requestReason}
                onChange={setField("requestReason")}
                placeholder="Why does the requester believe the record is inaccurate or misleading?"
                rows={3}
                required
                className={fieldCls("", fieldErrors, "requestReason")}
                aria-invalid={!!fieldErrors.requestReason || undefined}
                aria-describedby={fieldErrors.requestReason ? "ferpa-amend-requestReason-err" : undefined}
              />
              <FieldErr id="ferpa-amend-requestReason-err" msg={fieldErrors.requestReason} />
            </div>

            <div>
              <Label htmlFor="ferpa-amend-proposedChange">Proposed Change <span className="text-muted-foreground">(optional)</span></Label>
              <Textarea
                id="ferpa-amend-proposedChange"
                value={form.proposedChange}
                onChange={setField("proposedChange")}
                placeholder="What specific change is being requested?"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "File Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── FERPA-09 Redisclosure Controls Section (34 CFR § 99.33) ───────────────────

type RedisclosureControl = {
  id: string;
  organizationId: string;
  originalDisclosureId: string | null;
  schoolOfficialRecordId: string | null;
  exceptionRecordId: string | null;
  originalRecipient: string;
  recipientType: "SchoolOfficial" | "ExceptionRecipient" | "InternalUser" | "Vendor" | "Other";
  redisclosureAllowed: boolean;
  contractPolicyReference: string | null;
  secondaryUseRestriction: string | null;
  approvedPurpose: string;
  reviewDate: string | null;
  notes: string | null;
  createdAt: string;
};

type RedisclosureAttempt = {
  id: string;
  controlId: string | null;
  requestingParty: string;
  proposedRecipient: string;
  dataScope: string;
  proposedPurpose: string;
  outcome: "Allowed" | "Blocked";
  blockReason: string | null;
  timestamp: string;
};

const RECIPIENT_TYPES = ["SchoolOfficial", "ExceptionRecipient", "InternalUser", "Vendor", "Other"];

const RD_CTRL_EMPTY = {
  originalRecipient: "",
  recipientType: "Vendor",
  approvedPurpose: "",
  originalDisclosureId: "",
  schoolOfficialRecordId: "",
  exceptionRecordId: "",
  redisclosureAllowed: false,
  contractPolicyReference: "",
  secondaryUseRestriction: "",
  reviewDate: "",
  notes: "",
};

const RD_CHECK_EMPTY = {
  requestingParty: "",
  proposedRecipient: "",
  dataScope: "",
  proposedPurpose: "",
  isSecondaryUse: false,
  controlId: "",
};

function RedisclosureSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"controls" | "attempts" | "check">("controls");
  const [ctrlOpen, setCtrlOpen] = useState(false);
  const [ctrlForm, setCtrlForm] = useState(RD_CTRL_EMPTY);
  const [ctrlErrors, setCtrlErrors] = useState<Record<string, string>>({});
  const [checkForm, setCheckForm] = useState(RD_CHECK_EMPTY);
  const [checkResult, setCheckResult] = useState<any>(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const invalidateControls = () =>
    queryClient.invalidateQueries({
      queryKey: ["/api/organization/compliance/ferpa/redisclosure-controls"],
    });
  const invalidateAttempts = () =>
    queryClient.invalidateQueries({
      queryKey: ["/api/organization/compliance/ferpa/redisclosure-attempts"],
    });

  const { data: ctrlData, isLoading: ctrlLoading } = useQuery<PagedResult<RedisclosureControl>>({
    queryKey: ["/api/organization/compliance/ferpa/redisclosure-controls"],
    queryFn: async () => {
      const r = await fetch(
        "/api/organization/compliance/ferpa/redisclosure-controls?pageSize=50",
        { headers: bearerHeader() }
      );
      if (!r.ok) throw new Error("Failed to load redisclosure controls");
      return r.json();
    },
  });

  const { data: attData, isLoading: attLoading } = useQuery<PagedResult<RedisclosureAttempt>>({
    queryKey: ["/api/organization/compliance/ferpa/redisclosure-attempts"],
    queryFn: async () => {
      const r = await fetch(
        "/api/organization/compliance/ferpa/redisclosure-attempts?pageSize=50",
        { headers: bearerHeader() }
      );
      if (!r.ok) throw new Error("Failed to load redisclosure attempts");
      return r.json();
    },
    enabled: tab === "attempts",
  });

  const createCtrl = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        originalRecipient: ctrlForm.originalRecipient,
        recipientType: ctrlForm.recipientType,
        approvedPurpose: ctrlForm.approvedPurpose,
        redisclosureAllowed: ctrlForm.redisclosureAllowed,
        contractPolicyReference: ctrlForm.contractPolicyReference || null,
        secondaryUseRestriction: ctrlForm.secondaryUseRestriction || null,
        reviewDate: ctrlForm.reviewDate ? new Date(ctrlForm.reviewDate).toISOString() : null,
        notes: ctrlForm.notes || null,
      };
      if (ctrlForm.originalDisclosureId)   body.originalDisclosureId   = ctrlForm.originalDisclosureId;
      if (ctrlForm.schoolOfficialRecordId) body.schoolOfficialRecordId = ctrlForm.schoolOfficialRecordId;
      if (ctrlForm.exceptionRecordId)      body.exceptionRecordId      = ctrlForm.exceptionRecordId;

      const r = await fetch("/api/organization/compliance/ferpa/redisclosure-controls", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify(body),
      });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => {
      invalidateControls();
      toast({ title: "Redisclosure control created" });
      setCtrlOpen(false);
      setCtrlErrors({});
      setCtrlForm(RD_CTRL_EMPTY);
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) setCtrlErrors(errs);
      else toast({ title: "Unable to save", description: e.message, variant: "destructive" });
    },
  });

  const toggleAllowed = useMutation({
    mutationFn: async ({ id, allowed }: { id: string; allowed: boolean }) => {
      const r = await fetch(`/api/organization/compliance/ferpa/redisclosure-controls/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ redisclosureAllowed: allowed }),
      });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => { invalidateControls(); toast({ title: "Control updated" }); },
    onError: (e: any) =>
      toast({ title: e.message ?? "Update failed", variant: "destructive" }),
  });

  const deleteCtrl = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/organization/compliance/ferpa/redisclosure-controls/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message ?? "Failed to delete");
    },
    onSuccess: () => { invalidateControls(); toast({ title: "Control deleted" }); },
    onError: (e: any) =>
      toast({ title: e.message ?? "Unable to delete", variant: "destructive" }),
  });

  const handleCtrlSubmit = () => {
    const errs: Record<string, string> = {};
    if (!ctrlForm.originalRecipient.trim()) errs.originalRecipient = "Original recipient is required.";
    if (!ctrlForm.approvedPurpose.trim())   errs.approvedPurpose   = "Approved purpose is required.";
    if (Object.keys(errs).length) { setCtrlErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setCtrlErrors({});
    createCtrl.mutate();
  };

  const handleCheck = async () => {
    if (!checkForm.requestingParty.trim() || !checkForm.proposedRecipient.trim() || !checkForm.dataScope.trim()) {
      toast({ title: "Requesting party, proposed recipient, and data scope are required.", variant: "destructive" });
      return;
    }
    setCheckLoading(true);
    setCheckResult(null);
    try {
      const body: Record<string, any> = {
        requestingParty: checkForm.requestingParty,
        proposedRecipient: checkForm.proposedRecipient,
        dataScope: checkForm.dataScope,
        proposedPurpose: checkForm.proposedPurpose,
        isSecondaryUse: checkForm.isSecondaryUse,
      };
      if (checkForm.controlId) body.controlId = checkForm.controlId;

      const r = await fetch("/api/organization/compliance/ferpa/redisclosure-check", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      setCheckResult({ ...data, status: r.ok ? "ok" : "error" });
      invalidateAttempts();
    } catch (e: any) {
      setCheckResult({ status: "error", message: e.message });
    } finally {
      setCheckLoading(false);
    }
  };

  const recipientTypeBadge = (t: string) => {
    const map: Record<string, string> = {
      SchoolOfficial: "bg-blue-100 text-blue-800",
      ExceptionRecipient: "bg-purple-100 text-purple-800",
      InternalUser: "bg-gray-100 text-gray-700",
      Vendor: "bg-orange-100 text-orange-800",
      Other: "bg-gray-100 text-gray-600",
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[t] ?? "bg-gray-100 text-gray-700"}`}>
        {t}
      </span>
    );
  };

  const outcomeBadge = (outcome: string) =>
    outcome === "Allowed"
      ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Allowed</span>
      : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Blocked</span>;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4" /> Redisclosure Controls
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99.33 — education-record recipients cannot redisclose student data without consent or a separate FERPA exception. Default: redisclosure is prohibited.
            </CardDescription>
          </div>
          {canManage && tab === "controls" && (
            <Button size="sm" onClick={() => setCtrlOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> New Control
            </Button>
          )}
        </div>
        <div className="flex gap-1 pt-1 border-b">
          {(["controls", "attempts", "check"] as const).map(t => (
            <button key={t}
              onClick={() => setTab(t)}
              className={`text-xs px-3 py-1.5 font-medium border-b-2 transition-colors ${
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              {t === "controls" ? "Controls" : t === "attempts" ? "Attempt Log" : "Check"}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>

        {/* ── Controls tab ── */}
        {tab === "controls" && (
          <>
            {ctrlLoading ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
            ) : !ctrlData?.items?.length ? (
              <p className="text-sm text-muted-foreground py-3 text-center">
                No redisclosure controls. Create a control for each disclosure recipient to document whether redisclosure is permitted and under what conditions.
              </p>
            ) : (
              <div className="space-y-2">
                {ctrlData.items.map(c => (
                  <div key={c.id} className="border rounded-md p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {recipientTypeBadge(c.recipientType)}
                          {c.redisclosureAllowed
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Redisclosure Allowed</span>
                            : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Redisclosure Prohibited</span>
                          }
                          <span className="text-sm font-semibold truncate">{c.originalRecipient}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">Approved purpose: {c.approvedPurpose}</div>
                        {c.contractPolicyReference && (
                          <div className="text-xs text-muted-foreground">Contract/policy: {c.contractPolicyReference}</div>
                        )}
                        {c.secondaryUseRestriction && (
                          <div className="text-xs text-muted-foreground">Secondary use restriction: {c.secondaryUseRestriction}</div>
                        )}
                        {c.reviewDate && (
                          <div className="text-xs text-muted-foreground">
                            Review due: {new Date(c.reviewDate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      {canManage && (
                        <div className="flex gap-1 shrink-0 flex-col items-end">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                            onClick={() => toggleAllowed.mutate({ id: c.id, allowed: !c.redisclosureAllowed })}
                            disabled={toggleAllowed.isPending}>
                            {c.redisclosureAllowed ? "Prohibit" : "Allow"}
                          </Button>
                          <Button size="sm" variant="ghost"
                            className="text-destructive hover:bg-destructive/10 h-7 px-2"
                            onClick={() => { if (confirm("Delete this control?")) deleteCtrl.mutate(c.id); }}
                            disabled={deleteCtrl.isPending}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Attempt Log tab ── */}
        {tab === "attempts" && (
          <>
            {attLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : !attData?.items?.length ? (
              <p className="text-sm text-muted-foreground py-3 text-center">
                No redisclosure check attempts yet. Every Check call produces an immutable entry here.
              </p>
            ) : (
              <div className="space-y-1.5">
                {attData.items.map(a => (
                  <div key={a.id} className="border rounded-md p-2.5 flex items-start gap-2">
                    <div className="pt-0.5">{outcomeBadge(a.outcome)}</div>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="text-xs font-medium">
                        {a.requestingParty} → {a.proposedRecipient} ({a.dataScope})
                        {a.blockReason && <span className="ml-2 text-muted-foreground">({a.blockReason})</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">Purpose: {a.proposedPurpose || "—"}</div>
                      <div className="text-xs text-muted-foreground">{new Date(a.timestamp).toLocaleString()}</div>
                    </div>
                  </div>
                ))}
                {(attData?.total ?? 0) > 50 && (
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    Showing 50 of {attData!.total} entries.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Check tab ── */}
        {tab === "check" && canManage && (
          <div className="space-y-4 py-1 max-w-lg">
            <p className="text-xs text-muted-foreground">
              Validate whether a proposed redisclosure is permitted under the applicable control. Every check is logged in the Attempt Log tab.
            </p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Requesting Party <span className="text-destructive">*</span></label>
                  <Input placeholder="Who wants to redisclose?"
                    value={checkForm.requestingParty}
                    onChange={e => setCheckForm(f => ({ ...f, requestingParty: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Proposed Recipient <span className="text-destructive">*</span></label>
                  <Input placeholder="Who would receive the data?"
                    value={checkForm.proposedRecipient}
                    onChange={e => setCheckForm(f => ({ ...f, proposedRecipient: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Data Scope <span className="text-destructive">*</span></label>
                <Input placeholder="What records / data would be shared?"
                  value={checkForm.dataScope}
                  onChange={e => setCheckForm(f => ({ ...f, dataScope: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Proposed Purpose</label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[70px] resize-none"
                  placeholder="Why is the recipient requesting to share this data?"
                  value={checkForm.proposedPurpose}
                  onChange={e => setCheckForm(f => ({ ...f, proposedPurpose: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Control ID (optional)</label>
                <Input placeholder="UUID of the redisclosure control, or leave blank to look up by disclosure ID"
                  value={checkForm.controlId}
                  onChange={e => setCheckForm(f => ({ ...f, controlId: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="rd-secondary-use"
                  checked={checkForm.isSecondaryUse}
                  onChange={e => setCheckForm(f => ({ ...f, isSecondaryUse: e.target.checked }))}
                  className="h-4 w-4" />
                <label htmlFor="rd-secondary-use" className="text-sm text-orange-700">
                  This is a secondary / repurposed use of the data (automatically blocked)
                </label>
              </div>
            </div>
            <Button onClick={handleCheck} disabled={checkLoading}>
              {checkLoading ? "Checking…" : "Run Redisclosure Check"}
            </Button>
            {checkResult && (
              <div className={`mt-3 rounded-md p-3 text-sm border ${
                checkResult.outcome === "Allowed"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}>
                <div className="font-semibold mb-1">
                  {checkResult.outcome === "Allowed" ? "✓ Redisclosure Allowed" : "✗ Redisclosure Blocked"}
                </div>
                <div className="text-xs">{checkResult.message}</div>
                {checkResult.code && <div className="text-xs mt-1 font-mono opacity-70">{checkResult.code}</div>}
              </div>
            )}
          </div>
        )}
        {tab === "check" && !canManage && (
          <p className="text-sm text-muted-foreground py-3 text-center">
            You need the Compliance Manage permission to run redisclosure checks.
          </p>
        )}
      </CardContent>

      {/* ── Create Control dialog ── */}
      <Dialog open={ctrlOpen} onOpenChange={setCtrlOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Redisclosure Control</DialogTitle>
            <DialogDescription>
              34 CFR § 99.33 — documents redisclosure restrictions for an education-record recipient. Default is prohibited.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {ctrlErrors._ && <p role="alert" className="text-sm text-destructive">{ctrlErrors._}</p>}
            <div className="space-y-1">
              <label className="text-sm font-medium">Original Recipient <span className="text-destructive">*</span></label>
              <Input placeholder="Name of the party that originally received the data"
                value={ctrlForm.originalRecipient}
                onChange={e => setCtrlForm(f => ({ ...f, originalRecipient: e.target.value }))}
                className={fieldCls("", ctrlErrors, "originalRecipient")}
                aria-invalid={!!ctrlErrors.originalRecipient} />
              {ctrlErrors.originalRecipient && <p role="alert" className="text-xs text-destructive">{ctrlErrors.originalRecipient}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Recipient Type <span className="text-destructive">*</span></label>
              <select className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={ctrlForm.recipientType}
                onChange={e => setCtrlForm(f => ({ ...f, recipientType: e.target.value }))}>
                {RECIPIENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Approved Purpose <span className="text-destructive">*</span></label>
              <textarea className={`w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[70px] resize-none${ctrlErrors.approvedPurpose ? " border-red-500" : ""}`}
                placeholder="The only purpose for which this recipient may use the data"
                value={ctrlForm.approvedPurpose}
                onChange={e => setCtrlForm(f => ({ ...f, approvedPurpose: e.target.value }))}
                aria-invalid={!!ctrlErrors.approvedPurpose} />
              {ctrlErrors.approvedPurpose && <p role="alert" className="text-xs text-destructive">{ctrlErrors.approvedPurpose}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Contract / Policy Reference</label>
              <Input placeholder="e.g. DUA § 5.1, Contract clause 12"
                value={ctrlForm.contractPolicyReference}
                onChange={e => setCtrlForm(f => ({ ...f, contractPolicyReference: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Secondary Use Restriction</label>
              <textarea className="w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[60px] resize-none"
                placeholder="Describe what secondary uses are explicitly prohibited"
                value={ctrlForm.secondaryUseRestriction}
                onChange={e => setCtrlForm(f => ({ ...f, secondaryUseRestriction: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Review Date</label>
              <Input type="date" value={ctrlForm.reviewDate}
                onChange={e => setCtrlForm(f => ({ ...f, reviewDate: e.target.value }))} />
            </div>
            <div className="flex items-center gap-2 p-2 bg-yellow-50 rounded-md border border-yellow-200">
              <input type="checkbox" id="rd-ctrl-allowed"
                checked={ctrlForm.redisclosureAllowed}
                onChange={e => setCtrlForm(f => ({ ...f, redisclosureAllowed: e.target.checked }))}
                className="h-4 w-4" />
              <label htmlFor="rd-ctrl-allowed" className="text-sm">
                Redisclosure is <strong>allowed</strong> (uncheck = prohibited, which is the default and the safer choice)
              </label>
            </div>
            <div className="text-xs text-muted-foreground p-2 bg-blue-50 rounded border border-blue-100">
              Linked disclosure references (optional — paste UUIDs from existing records):
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">Original Disclosure ID</label>
              <Input placeholder="UUID (optional)"
                value={ctrlForm.originalDisclosureId}
                onChange={e => setCtrlForm(f => ({ ...f, originalDisclosureId: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">School Official Record ID</label>
              <Input placeholder="UUID (optional)"
                value={ctrlForm.schoolOfficialRecordId}
                onChange={e => setCtrlForm(f => ({ ...f, schoolOfficialRecordId: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Notes</label>
              <Input placeholder="Optional notes"
                value={ctrlForm.notes}
                onChange={e => setCtrlForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCtrlOpen(false); setCtrlErrors({}); }}>Cancel</Button>
            <Button onClick={handleCtrlSubmit} disabled={createCtrl.isPending}>
              {createCtrl.isPending ? "Saving…" : "Create Control"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── FERPA-08 Legitimate Educational Interest Section (34 CFR § 99.31(a)(1)(i)(B)) ──

type LeiDefinition = {
  id: string;
  organizationId: string;
  roleName: string;
  recordScope: string;
  educationalPurpose: string;
  businessJustification: string;
  requiresApproval: boolean;
  isActive: boolean;
  expiresAt: string | null;
  notes: string | null;
  createdAt: string;
};

type LeiAccessLog = {
  id: string;
  organizationId: string;
  userId: string | null;
  userRole: string;
  recordScope: string;
  purpose: string;
  outcome: "Allowed" | "Denied";
  denialReason: string | null;
  leiDefinitionId: string | null;
  isAdminBypass: boolean;
  accessedAt: string;
};

const LEI_RECORD_SCOPES = [
  "Transcript", "Grades", "Enrollment", "Attendance", "Assessment",
  "Financial", "Disciplinary", "Medical", "Directory", "Other",
];

const LEI_DEF_EMPTY = {
  roleName: "",
  recordScope: "Grades",
  educationalPurpose: "",
  businessJustification: "",
  requiresApproval: false,
  expiresAt: "",
  notes: "",
};

const LEI_CHECK_EMPTY = {
  userRole: "",
  recordScope: "Grades",
  purpose: "",
  adminBypass: false,
};

function LeiSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"definitions" | "logs" | "check">("definitions");
  const [defOpen, setDefOpen]       = useState(false);
  const [defForm, setDefForm]       = useState(LEI_DEF_EMPTY);
  const [defErrors, setDefErrors]   = useState<Record<string, string>>({});
  const [checkForm, setCheckForm]   = useState(LEI_CHECK_EMPTY);
  const [checkResult, setCheckResult] = useState<any>(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const invalidateDefs = () =>
    queryClient.invalidateQueries({
      queryKey: ["/api/organization/compliance/ferpa/lei-definitions"],
    });
  const invalidateLogs = () =>
    queryClient.invalidateQueries({
      queryKey: ["/api/organization/compliance/ferpa/lei-access-logs"],
    });

  const { data: defsData, isLoading: defsLoading } = useQuery<PagedResult<LeiDefinition>>({
    queryKey: ["/api/organization/compliance/ferpa/lei-definitions"],
    queryFn: async () => {
      const r = await fetch(
        "/api/organization/compliance/ferpa/lei-definitions?pageSize=50",
        { headers: bearerHeader() }
      );
      if (!r.ok) throw new Error("Failed to load LEI definitions");
      return r.json();
    },
  });

  const { data: logsData, isLoading: logsLoading } = useQuery<PagedResult<LeiAccessLog>>({
    queryKey: ["/api/organization/compliance/ferpa/lei-access-logs"],
    queryFn: async () => {
      const r = await fetch(
        "/api/organization/compliance/ferpa/lei-access-logs?pageSize=50",
        { headers: bearerHeader() }
      );
      if (!r.ok) throw new Error("Failed to load LEI access logs");
      return r.json();
    },
    enabled: tab === "logs",
  });

  const createDef = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/organization/compliance/ferpa/lei-definitions", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          ...defForm,
          expiresAt: defForm.expiresAt ? new Date(defForm.expiresAt).toISOString() : null,
          notes: defForm.notes || null,
        }),
      });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => {
      invalidateDefs();
      toast({ title: "LEI definition created" });
      setDefOpen(false);
      setDefErrors({});
      setDefForm(LEI_DEF_EMPTY);
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) setDefErrors(errs);
      else toast({ title: "Unable to save", description: e.message, variant: "destructive" });
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const r = await fetch(`/api/organization/compliance/ferpa/lei-definitions/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ isActive }),
      });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => { invalidateDefs(); toast({ title: "Definition updated" }); },
    onError: (e: any) =>
      toast({ title: e.message ?? "Failed to update", variant: "destructive" }),
  });

  const deleteDef = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/organization/compliance/ferpa/lei-definitions/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message ?? "Failed to delete");
    },
    onSuccess: () => { invalidateDefs(); toast({ title: "Definition deleted" }); },
    onError: (e: any) =>
      toast({ title: e.message ?? "Failed to delete", variant: "destructive" }),
  });

  const handleDefSubmit = () => {
    const errs: Record<string, string> = {};
    if (!defForm.roleName.trim())             errs.roleName             = "Role name is required.";
    if (!defForm.educationalPurpose.trim())   errs.educationalPurpose   = "Educational purpose is required.";
    if (!defForm.businessJustification.trim()) errs.businessJustification = "Business justification is required.";
    if (Object.keys(errs).length) { setDefErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setDefErrors({});
    createDef.mutate();
  };

  const handleLeiCheck = async () => {
    if (!checkForm.userRole.trim() || !checkForm.purpose.trim()) {
      toast({ title: "Role and purpose are required", variant: "destructive" });
      return;
    }
    setCheckLoading(true);
    setCheckResult(null);
    try {
      const r = await fetch("/api/organization/compliance/ferpa/lei-check", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          userRole:    checkForm.userRole,
          recordScope: checkForm.recordScope,
          purpose:     checkForm.purpose,
          adminBypass: checkForm.adminBypass,
        }),
      });
      const body = await r.json();
      setCheckResult({ ...body, status: r.ok ? "ok" : "error" });
      invalidateLogs();
    } catch (e: any) {
      setCheckResult({ status: "error", message: e.message });
    } finally {
      setCheckLoading(false);
    }
  };

  const outcomeBadge = (outcome: "Allowed" | "Denied", bypass?: boolean) => {
    if (bypass)
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">Bypass</span>;
    return outcome === "Allowed"
      ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Allowed</span>
      : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Denied</span>;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Legitimate Educational Interest
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99.31(a)(1)(i)(B) — document which roles have LEI for each record scope, perform access checks, and review the access-decision log.
            </CardDescription>
          </div>
          {canManage && tab === "definitions" && (
            <Button size="sm" onClick={() => setDefOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> New Definition
            </Button>
          )}
        </div>
        {/* Tab bar */}
        <div className="flex gap-1 pt-1 border-b">
          {(["definitions", "logs", "check"] as const).map(t => (
            <button key={t}
              onClick={() => setTab(t)}
              className={`text-xs px-3 py-1.5 font-medium border-b-2 transition-colors ${
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              {t === "definitions" ? "LEI Definitions" : t === "logs" ? "Access Logs" : "Access Check"}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>

        {/* ── Definitions tab ── */}
        {tab === "definitions" && (
          <>
            {defsLoading ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
            ) : !defsData?.items?.length ? (
              <p className="text-sm text-muted-foreground py-3 text-center">
                No LEI definitions. Add role-to-record-scope mappings to document which roles have a legitimate educational interest in each type of education record.
              </p>
            ) : (
              <div className="space-y-2">
                {defsData.items.map(d => {
                  const isExpired = !!d.expiresAt && new Date(d.expiresAt) < new Date();
                  return (
                    <div key={d.id} className="border rounded-md p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0 space-y-0.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            {d.isActive && !isExpired
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Active</span>
                              : isExpired
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">Expired</span>
                              : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Inactive</span>
                            }
                            {d.requiresApproval && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-yellow-50 text-yellow-700 border border-yellow-200">Requires Approval</span>
                            )}
                            <span className="text-sm font-semibold">{d.roleName}</span>
                            <span className="text-muted-foreground text-xs">→</span>
                            <span className="text-sm font-medium">{d.recordScope}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">Purpose: {d.educationalPurpose}</div>
                          <div className="text-xs text-muted-foreground">Justification: {d.businessJustification}</div>
                          {d.expiresAt && (
                            <div className={`text-xs ${isExpired ? "text-orange-600 font-medium" : "text-muted-foreground"}`}>
                              {isExpired ? "⚠ Expired" : "Expires"}: {new Date(d.expiresAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        {canManage && (
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
                              onClick={() => toggleActive.mutate({ id: d.id, isActive: !d.isActive })}
                              disabled={toggleActive.isPending}>
                              {d.isActive ? "Deactivate" : "Activate"}
                            </Button>
                            <Button size="sm" variant="ghost"
                              className="text-destructive hover:bg-destructive/10 h-7 px-2"
                              onClick={() => { if (confirm("Delete this LEI definition?")) deleteDef.mutate(d.id); }}
                              disabled={deleteDef.isPending}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ── Access Logs tab ── */}
        {tab === "logs" && (
          <>
            {logsLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : !logsData?.items?.length ? (
              <p className="text-sm text-muted-foreground py-3 text-center">
                No LEI access-check logs yet. Every Access Check call produces an immutable entry here.
              </p>
            ) : (
              <div className="space-y-1.5">
                {logsData.items.map(l => (
                  <div key={l.id} className="border rounded-md p-2.5 flex items-start gap-2">
                    <div className="pt-0.5">{outcomeBadge(l.outcome, l.isAdminBypass)}</div>
                    <div className="flex-1 min-w-0 space-y-0.5">
                      <div className="text-xs font-medium">
                        {l.userRole} → {l.recordScope}
                        {l.denialReason && <span className="ml-2 text-muted-foreground">({l.denialReason})</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">Purpose: {l.purpose || "—"}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(l.accessedAt).toLocaleString()}
                        {l.isAdminBypass && " · Admin bypass (audited)"}
                      </div>
                    </div>
                  </div>
                ))}
                {(logsData?.total ?? 0) > 50 && (
                  <p className="text-xs text-muted-foreground text-center pt-1">
                    Showing 50 of {logsData!.total} log entries.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* ── Access Check tab ── */}
        {tab === "check" && canManage && (
          <div className="space-y-4 py-1 max-w-lg">
            <p className="text-xs text-muted-foreground">
              Validate and log whether a role has a documented Legitimate Educational Interest for a specific record scope. Every check is recorded in the Access Logs tab.
            </p>
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">User Role <span className="text-destructive">*</span></label>
                <Input placeholder="e.g. Faculty, Advisor, Registrar"
                  value={checkForm.userRole}
                  onChange={e => setCheckForm(f => ({ ...f, userRole: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Record Scope <span className="text-destructive">*</span></label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                  value={checkForm.recordScope}
                  onChange={e => setCheckForm(f => ({ ...f, recordScope: e.target.value }))}>
                  {LEI_RECORD_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Educational Purpose / Justification <span className="text-destructive">*</span></label>
                <textarea
                  className="w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[80px] resize-none"
                  placeholder="Why does this role need to access this specific record for this student / at this time?"
                  value={checkForm.purpose}
                  onChange={e => setCheckForm(f => ({ ...f, purpose: e.target.value }))} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="lei-admin-bypass"
                  checked={checkForm.adminBypass}
                  onChange={e => setCheckForm(f => ({ ...f, adminBypass: e.target.checked }))}
                  className="h-4 w-4" />
                <label htmlFor="lei-admin-bypass" className="text-sm text-orange-700">
                  Admin bypass (access allowed without LEI definition — will be flagged in audit log)
                </label>
              </div>
            </div>
            <Button onClick={handleLeiCheck} disabled={checkLoading}>
              {checkLoading ? "Checking…" : "Run LEI Check"}
            </Button>
            {checkResult && (
              <div className={`mt-3 rounded-md p-3 text-sm border ${
                checkResult.outcome === "Allowed" || checkResult.status === "ok"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}>
                <div className="font-semibold mb-1">
                  {checkResult.outcome === "Allowed" ? "✓ Access Allowed" : "✗ Access Denied"}
                  {checkResult.isAdminBypass && " (Admin Bypass — Audited)"}
                </div>
                <div className="text-xs">{checkResult.message}</div>
                {checkResult.code && <div className="text-xs mt-1 font-mono opacity-70">{checkResult.code}</div>}
              </div>
            )}
          </div>
        )}
        {tab === "check" && !canManage && (
          <p className="text-sm text-muted-foreground py-3 text-center">
            You need the FERPA LEI Manage permission to run access checks.
          </p>
        )}
      </CardContent>

      {/* ── Create Definition dialog ── */}
      <Dialog open={defOpen} onOpenChange={setDefOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New LEI Definition</DialogTitle>
            <DialogDescription>
              34 CFR § 99.31(a)(1)(i)(B) — document why this role has a legitimate educational interest in the specified record type.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {defErrors._ && <p role="alert" className="text-sm text-destructive">{defErrors._}</p>}
            <div className="space-y-1">
              <label className="text-sm font-medium">Role Name <span className="text-destructive">*</span></label>
              <Input placeholder="e.g. Faculty, Advisor, Registrar, StudentHealth"
                value={defForm.roleName}
                onChange={e => setDefForm(f => ({ ...f, roleName: e.target.value }))}
                className={fieldCls("", defErrors, "roleName")}
                aria-invalid={!!defErrors.roleName} />
              {defErrors.roleName && <p role="alert" className="text-xs text-destructive">{defErrors.roleName}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Record Scope <span className="text-destructive">*</span></label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                value={defForm.recordScope}
                onChange={e => setDefForm(f => ({ ...f, recordScope: e.target.value }))}>
                {LEI_RECORD_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Educational Purpose <span className="text-destructive">*</span></label>
              <textarea
                className={`w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[70px] resize-none${defErrors.educationalPurpose ? " border-red-500" : ""}`}
                placeholder="Why does this role need access? (§ 99.31(a)(1)(i)(B) criterion)"
                value={defForm.educationalPurpose}
                onChange={e => setDefForm(f => ({ ...f, educationalPurpose: e.target.value }))}
                aria-invalid={!!defErrors.educationalPurpose} />
              {defErrors.educationalPurpose && <p role="alert" className="text-xs text-destructive">{defErrors.educationalPurpose}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Business Justification <span className="text-destructive">*</span></label>
              <textarea
                className={`w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[70px] resize-none${defErrors.businessJustification ? " border-red-500" : ""}`}
                placeholder="Operational reason this role needs this specific record type"
                value={defForm.businessJustification}
                onChange={e => setDefForm(f => ({ ...f, businessJustification: e.target.value }))}
                aria-invalid={!!defErrors.businessJustification} />
              {defErrors.businessJustification && <p role="alert" className="text-xs text-destructive">{defErrors.businessJustification}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Access Expiry Date</label>
              <Input type="date" value={defForm.expiresAt}
                onChange={e => setDefForm(f => ({ ...f, expiresAt: e.target.value }))} />
              <p className="text-xs text-muted-foreground">Leave blank for no fixed expiry (annual review recommended).</p>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="lei-requires-approval"
                checked={defForm.requiresApproval}
                onChange={e => setDefForm(f => ({ ...f, requiresApproval: e.target.checked }))}
                className="h-4 w-4" />
              <label htmlFor="lei-requires-approval" className="text-sm">
                Require explicit per-access approval for this role/scope combination
              </label>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Notes</label>
              <Input placeholder="Optional notes"
                value={defForm.notes}
                onChange={e => setDefForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDefOpen(false); setDefErrors({}); }}>Cancel</Button>
            <Button onClick={handleDefSubmit} disabled={createDef.isPending}>
              {createDef.isPending ? "Saving…" : "Create Definition"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── FERPA-07 School Official Records Section (34 CFR § 99.31(a)(1)) ───────────

type SchoolOfficialRecord = {
  id: string;
  organizationId: string;
  vendorOrOrgName: string;
  serviceFunction: string;
  educationalPurpose: string;
  authorizedDataScope: string;
  controllingInstitution: string;
  accessRestrictions: string;
  allowedOperations: string;
  accessExpiresAt: string | null;
  businessJustification: string;
  authorizedRoleOrPerson: string;
  redisclosureProhibited: boolean;
  redisclosureTerms: string | null;
  status: "Pending" | "Approved" | "Rejected" | "Revoked";
  rejectionReason: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalNotes: string | null;
  notes: string | null;
  createdAt: string;
};

const SO_EMPTY = {
  vendorOrOrgName: "",
  serviceFunction: "",
  educationalPurpose: "",
  authorizedDataScope: "",
  controllingInstitution: "",
  accessRestrictions: "",
  allowedOperations: "",
  accessExpiresAt: "",
  businessJustification: "",
  authorizedRoleOrPerson: "",
  redisclosureProhibited: true,
  redisclosureTerms: "",
  notes: "",
};

function SchoolOfficialsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState(SO_EMPTY);

  const { data, isLoading } = useQuery<PagedResult<SchoolOfficialRecord>>({
    queryKey: ["/api/organization/compliance/ferpa/school-official-records"],
    queryFn: async () => {
      const res = await fetch(
        "/api/organization/compliance/ferpa/school-official-records?pageSize=20",
        { headers: bearerHeader() }
      );
      if (!res.ok) throw new Error("Failed to load school official records");
      return res.json();
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["/api/organization/compliance/ferpa/school-official-records"],
    });

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        "/api/organization/compliance/ferpa/school-official-records",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...bearerHeader() },
          body: JSON.stringify({
            ...form,
            accessExpiresAt: form.accessExpiresAt
              ? new Date(form.accessExpiresAt).toISOString()
              : null,
            redisclosureTerms: form.redisclosureTerms || null,
            notes: form.notes || null,
          }),
        }
      );
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "School official record created" });
      setOpen(false);
      setFieldErrors({});
      setForm(SO_EMPTY);
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not create record." });
        toast({ title: "Unable to create record", description: e.message, variant: "destructive" });
      }
    },
  });

  const patch = (action: "approve" | "reject" | "revoke") =>
    useMutation({
      mutationFn: async (id: string) => {
        const res = await fetch(
          `/api/organization/compliance/ferpa/school-official-records/${id}/${action}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json", ...bearerHeader() },
            body: JSON.stringify({}),
          }
        );
        await throwIfResNotOk(res);
        return res.json();
      },
      onSuccess: () => {
        invalidate();
        toast({ title: `Record ${action}d` });
      },
      onError: (e: any) =>
        toast({ title: e.message ?? `Failed to ${action}`, variant: "destructive" }),
    });

  const approve = patch("approve");
  const reject  = patch("reject");
  const revoke  = patch("revoke");

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(
        `/api/organization/compliance/ferpa/school-official-records/${id}`,
        { method: "DELETE", headers: bearerHeader() }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to delete");
      }
    },
    onSuccess: () => { invalidate(); toast({ title: "Record deleted" }); },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.vendorOrOrgName.trim())       errs.vendorOrOrgName        = "Vendor or org name is required.";
    if (!form.serviceFunction.trim())       errs.serviceFunction        = "Service function is required.";
    if (!form.educationalPurpose.trim())    errs.educationalPurpose     = "Educational purpose is required.";
    if (!form.authorizedDataScope.trim())   errs.authorizedDataScope    = "Authorized data scope is required.";
    if (!form.controllingInstitution.trim()) errs.controllingInstitution = "Controlling institution is required.";
    if (!form.accessRestrictions.trim())    errs.accessRestrictions     = "Access restrictions are required.";
    if (!form.allowedOperations.trim())     errs.allowedOperations      = "Allowed operations are required.";
    if (!form.businessJustification.trim()) errs.businessJustification  = "Business justification is required.";
    if (!form.authorizedRoleOrPerson.trim()) errs.authorizedRoleOrPerson = "Authorized role or person is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  const statusBadge = (r: SchoolOfficialRecord) => {
    const map: Record<string, string> = {
      Approved: "bg-green-100 text-green-800",
      Pending:  "bg-yellow-100 text-yellow-800",
      Rejected: "bg-red-100 text-red-800",
      Revoked:  "bg-gray-100 text-gray-600",
    };
    const expired =
      r.status === "Approved" &&
      r.accessExpiresAt &&
      new Date(r.accessExpiresAt) < new Date();
    const label = expired ? "Expired" : r.status;
    const cls   = expired ? "bg-orange-100 text-orange-800" : (map[r.status] ?? "bg-gray-100 text-gray-700");
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
        {label}
      </span>
    );
  };

  const field = (key: keyof typeof SO_EMPTY, label: string, placeholder: string, required = true) => (
    <div className="space-y-1">
      <label className="text-sm font-medium">
        {label}{required && <span className="text-destructive"> *</span>}
      </label>
      <Input
        value={form[key] as string}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        className={fieldCls("", fieldErrors, key as string)}
        aria-invalid={!!fieldErrors[key]}
      />
      {fieldErrors[key] && <p role="alert" className="text-xs text-destructive">{fieldErrors[key]}</p>}
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> School Official Exceptions
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99.31(a)(1) — authorise contractors and third parties acting as school officials. All four criteria (institutional function, direct control, legitimate educational interest, redisclosure restriction) must be satisfied before a SchoolOfficial disclosure is permitted.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> New Authorisation
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : !data?.items?.length ? (
          <p className="text-sm text-muted-foreground py-3 text-center">
            No school official exception records. Add vendors or third-party services that access student education records on behalf of this institution.
          </p>
        ) : (
          <div className="space-y-2">
            {data.items.map(r => (
              <div key={r.id} className="border rounded-md p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusBadge(r)}
                      <span className="text-sm font-semibold truncate">{r.vendorOrOrgName}</span>
                      {r.redisclosureProhibited && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                          No Redisclosure
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Function: {r.serviceFunction} · Purpose: {r.educationalPurpose}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Data: {r.authorizedDataScope} · Role: {r.authorizedRoleOrPerson}
                    </div>
                    {r.accessExpiresAt && (
                      <div className={`text-xs ${new Date(r.accessExpiresAt) < new Date() ? "text-orange-600 font-medium" : "text-muted-foreground"}`}>
                        {new Date(r.accessExpiresAt) < new Date() ? "⚠ Expired" : "Expires"}:{" "}
                        {new Date(r.accessExpiresAt).toLocaleDateString()}
                      </div>
                    )}
                    {r.rejectionReason && (
                      <div className="text-xs text-muted-foreground">Reason: {r.rejectionReason}</div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0 flex-col items-end">
                    {canManage && r.status === "Pending" && (
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline"
                          className="text-green-700 border-green-300 hover:bg-green-50 h-7 px-2 text-xs"
                          onClick={() => approve.mutate(r.id)} disabled={approve.isPending}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline"
                          className="text-red-700 border-red-300 hover:bg-red-50 h-7 px-2 text-xs"
                          onClick={() => reject.mutate(r.id)} disabled={reject.isPending}>
                          Reject
                        </Button>
                      </div>
                    )}
                    {canManage && r.status === "Approved" && (
                      <Button size="sm" variant="outline"
                        className="text-orange-700 border-orange-300 hover:bg-orange-50 h-7 px-2 text-xs"
                        onClick={() => revoke.mutate(r.id)} disabled={revoke.isPending}>
                        Revoke
                      </Button>
                    )}
                    {canManage && (r.status === "Pending" || r.status === "Rejected") && (
                      <Button size="sm" variant="ghost"
                        className="text-destructive hover:bg-destructive/10 h-7 px-2"
                        onClick={() => { if (confirm("Delete this record?")) remove.mutate(r.id); }}
                        disabled={remove.isPending}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {data.total > 20 && (
              <p className="text-xs text-muted-foreground text-center pt-1">
                Showing 20 of {data.total} records.
              </p>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New School Official Authorisation</DialogTitle>
            <DialogDescription>
              34 CFR § 99.31(a)(1) — all four criteria are required before a SchoolOfficial-exception disclosure can be logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            {fieldErrors._ && <p role="alert" className="text-sm text-destructive">{fieldErrors._}</p>}

            <div className="border rounded-md p-3 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Institutional Function (§ 99.31(a)(1)(i)(A))</p>
              {field("vendorOrOrgName",     "Vendor / Org Name",     "Legal name of the contractor or third-party org")}
              {field("serviceFunction",     "Service Function",      "What service does the vendor perform for the institution?")}
              {field("educationalPurpose",  "Educational Purpose",   "How does this service directly support the institution's educational mission?")}
              {field("authorizedDataScope", "Authorized Data Scope", "Which student education records does the vendor need access to?")}
            </div>

            <div className="border rounded-md p-3 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Direct Control (§ 99.31(a)(1)(i)(B))</p>
              {field("controllingInstitution", "Controlling Institution", "Name of the educational institution maintaining direct control")}
              {field("accessRestrictions",     "Access Restrictions",     "Technical, contractual, and procedural access limits")}
              {field("allowedOperations",      "Allowed Operations",      "Exhaustive list of permitted operations on the records")}
              <div className="space-y-1">
                <label className="text-sm font-medium">Access Expiry / Review Date</label>
                <Input type="date" value={form.accessExpiresAt}
                  onChange={e => setForm(f => ({ ...f, accessExpiresAt: e.target.value }))} />
                <p className="text-xs text-muted-foreground">Leave blank for no fixed expiry (annual review recommended).</p>
              </div>
            </div>

            <div className="border rounded-md p-3 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Legitimate Educational Interest (§ 99.31(a)(1)(i)(B))</p>
              {field("businessJustification",  "Business Justification",   "Why does the vendor need these specific records to perform their duties?")}
              {field("authorizedRoleOrPerson", "Authorized Role / Person",  "Specific role title or named individual authorized at the vendor")}
            </div>

            <div className="border rounded-md p-3 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Redisclosure Restriction (§ 99.32(a), § 99.33)</p>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="so-redisclosure"
                  checked={form.redisclosureProhibited}
                  onChange={e => setForm(f => ({ ...f, redisclosureProhibited: e.target.checked }))}
                  className="h-4 w-4" />
                <label htmlFor="so-redisclosure" className="text-sm">
                  Vendor is contractually prohibited from re-disclosing records to further parties
                </label>
              </div>
              {field("redisclosureTerms", "Redisclosure Terms", "Verbatim clause from the contract or DUA", false)}
            </div>

            {field("notes", "Notes", "Optional additional notes", false)}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setFieldErrors({}); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Create Authorisation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── FERPA-06 Exception Records Section (34 CFR § 99.31) ───────────────────────

type ExceptionDefinition = {
  id: string;
  organizationId: string | null;
  exceptionKey: string;
  displayName: string;
  legalBasis: string;
  eligibilityCriteria: string;
  allowedPurposes: string;
  requiredEvidence: string;
  requiresApproval: boolean;
  isActive: boolean;
};

type ExceptionRecord = {
  id: string;
  organizationId: string;
  exceptionKey: string;
  studentId: string;
  reason: string;
  recipient: string;
  recipientContactInfo: string | null;
  dataScope: string;
  eligibilityJustification: string;
  status: "Pending" | "Approved" | "Rejected" | "Used";
  approvedBy: string | null;
  approvedAt: string | null;
  approvalNotes: string | null;
  linkedDisclosureId: string | null;
  notes: string | null;
  createdAt: string;
};

function ExceptionRecordsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    exceptionKey: "",
    studentId: "",
    reason: "",
    recipient: "",
    recipientContactInfo: "",
    dataScope: "",
    eligibilityJustification: "",
    notes: "",
  });

  const { data: defs } = useQuery<{ total: number; items: ExceptionDefinition[] }>({
    queryKey: ["/api/organization/compliance/ferpa/exception-definitions"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/exception-definitions", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load exception definitions");
      return res.json();
    },
  });

  const { data, isLoading } = useQuery<PagedResult<ExceptionRecord>>({
    queryKey: ["/api/organization/compliance/ferpa/exception-records"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/exception-records?pageSize=20", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load exception records");
      return res.json();
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/exception-records"] });
  };

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/exception-records", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          exceptionKey: form.exceptionKey,
          studentId: form.studentId,
          reason: form.reason,
          recipient: form.recipient,
          recipientContactInfo: form.recipientContactInfo || null,
          dataScope: form.dataScope,
          eligibilityJustification: form.eligibilityJustification,
          notes: form.notes || null,
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Exception record created" });
      setOpen(false);
      setFieldErrors({});
      setForm({ exceptionKey: "", studentId: "", reason: "", recipient: "", recipientContactInfo: "", dataScope: "", eligibilityJustification: "", notes: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not create exception record." });
        toast({ title: "Unable to create exception record", description: e.message, variant: "destructive" });
      }
    },
  });

  const approve = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/exception-records/${id}/approve`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({}),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => { invalidate(); toast({ title: "Exception record approved" }); },
    onError: (e: any) => toast({ title: e.message ?? "Failed to approve", variant: "destructive" }),
  });

  const reject = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/exception-records/${id}/reject`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({}),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => { invalidate(); toast({ title: "Exception record rejected" }); },
    onError: (e: any) => toast({ title: e.message ?? "Failed to reject", variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/exception-records/${id}`, {
        method: "DELETE",
        headers: bearerHeader(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to delete");
      }
    },
    onSuccess: () => { invalidate(); toast({ title: "Exception record deleted" }); },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.exceptionKey)               errs.exceptionKey             = "Exception type is required.";
    if (!form.studentId.trim())           errs.studentId                = "Student ID is required.";
    if (!form.reason.trim())              errs.reason                   = "Reason is required.";
    if (!form.recipient.trim())           errs.recipient                = "Recipient is required.";
    if (!form.dataScope.trim())           errs.dataScope                = "Data scope (records) is required.";
    if (!form.eligibilityJustification.trim()) errs.eligibilityJustification = "Eligibility justification is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  const selectedDef = defs?.items?.find(d => d.exceptionKey === form.exceptionKey);

  const statusBadge = (status: ExceptionRecord["status"]) => {
    const map: Record<string, string> = {
      Approved: "bg-green-100 text-green-800",
      Pending:  "bg-yellow-100 text-yellow-800",
      Rejected: "bg-red-100 text-red-800",
      Used:     "bg-blue-100 text-blue-800",
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? "bg-gray-100 text-gray-700"}`}>{status}</span>;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Exception Records
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99.31 — per-use audit trail for each FERPA disclosure exception. Required for all exception-based disclosures.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> New Exception
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : !data?.items?.length ? (
          <p className="text-sm text-muted-foreground py-3 text-center">No exception records on file. Records are auto-created when an exception-based disclosure is logged.</p>
        ) : (
          <div className="space-y-2">
            {data.items.map(r => (
              <div key={r.id} className="border rounded-md p-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusBadge(r.status)}
                      <span className="text-xs font-mono text-muted-foreground">{r.exceptionKey}</span>
                      <span className="text-sm font-medium truncate">Student: {r.studentId}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Recipient: {r.recipient} · {r.reason}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Justification: {r.eligibilityJustification}
                    </div>
                    {r.approvalNotes && (
                      <div className="text-xs text-muted-foreground">Notes: {r.approvalNotes}</div>
                    )}
                  </div>
                  {canManage && r.status === "Pending" && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="outline"
                        className="text-green-700 border-green-300 hover:bg-green-50 h-7 px-2 text-xs"
                        onClick={() => approve.mutate(r.id)} disabled={approve.isPending}>
                        Approve
                      </Button>
                      <Button size="sm" variant="outline"
                        className="text-red-700 border-red-300 hover:bg-red-50 h-7 px-2 text-xs"
                        onClick={() => reject.mutate(r.id)} disabled={reject.isPending}>
                        Reject
                      </Button>
                    </div>
                  )}
                  {canManage && (r.status === "Pending" || r.status === "Rejected") && (
                    <Button size="sm" variant="ghost"
                      className="text-destructive hover:bg-destructive/10 h-7 px-2 shrink-0"
                      onClick={() => { if (confirm("Delete this exception record?")) remove.mutate(r.id); }}
                      disabled={remove.isPending}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {data.total > 20 && (
              <p className="text-xs text-muted-foreground text-center pt-1">Showing 20 of {data.total} records.</p>
            )}
          </div>
        )}
      </CardContent>

      {/* Create exception record dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Exception Record</DialogTitle>
            <DialogDescription>
              34 CFR § 99.31 — document the specific justification for this exception-based disclosure.
              For exceptions requiring pre-approval, this record must be approved before a disclosure can be created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {fieldErrors._ && <p role="alert" className="text-sm text-destructive">{fieldErrors._}</p>}

            <div className="space-y-1">
              <label className="text-sm font-medium">Exception Type <span className="text-destructive">*</span></label>
              <select
                value={form.exceptionKey}
                onChange={e => setForm(f => ({ ...f, exceptionKey: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                aria-invalid={!!fieldErrors.exceptionKey}
              >
                <option value="">Select exception type…</option>
                {defs?.items?.filter(d => d.isActive).map(d => (
                  <option key={d.exceptionKey} value={d.exceptionKey}>
                    {d.displayName} ({d.legalBasis})
                  </option>
                ))}
              </select>
              {fieldErrors.exceptionKey && <p role="alert" className="text-xs text-destructive">{fieldErrors.exceptionKey}</p>}
              {selectedDef && (
                <div className="rounded bg-muted p-2 text-xs space-y-1">
                  <div><span className="font-medium">Criteria:</span> {selectedDef.eligibilityCriteria}</div>
                  <div><span className="font-medium">Required evidence:</span> {selectedDef.requiredEvidence}</div>
                  {selectedDef.requiresApproval && (
                    <div className="text-amber-700 font-medium">⚠ This exception requires pre-approval before disclosure.</div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Student ID <span className="text-destructive">*</span></label>
              <Input value={form.studentId} onChange={e => setForm(f => ({ ...f, studentId: e.target.value }))}
                placeholder="Student identifier" className={fieldCls("", fieldErrors, "studentId")} aria-invalid={!!fieldErrors.studentId} />
              {fieldErrors.studentId && <p role="alert" className="text-xs text-destructive">{fieldErrors.studentId}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Reason <span className="text-destructive">*</span></label>
              <Input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Academic advising session" className={fieldCls("", fieldErrors, "reason")} aria-invalid={!!fieldErrors.reason} />
              {fieldErrors.reason && <p role="alert" className="text-xs text-destructive">{fieldErrors.reason}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Recipient <span className="text-destructive">*</span></label>
              <Input value={form.recipient} onChange={e => setForm(f => ({ ...f, recipient: e.target.value }))}
                placeholder="Name or department receiving the information" className={fieldCls("", fieldErrors, "recipient")} aria-invalid={!!fieldErrors.recipient} />
              {fieldErrors.recipient && <p role="alert" className="text-xs text-destructive">{fieldErrors.recipient}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Records / Data Scope <span className="text-destructive">*</span></label>
              <Input value={form.dataScope} onChange={e => setForm(f => ({ ...f, dataScope: e.target.value }))}
                placeholder="e.g. GPA, enrollment status, transcript" className={fieldCls("", fieldErrors, "dataScope")} aria-invalid={!!fieldErrors.dataScope} />
              {fieldErrors.dataScope && <p role="alert" className="text-xs text-destructive">{fieldErrors.dataScope}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Eligibility Justification <span className="text-destructive">*</span></label>
              <Input value={form.eligibilityJustification}
                onChange={e => setForm(f => ({ ...f, eligibilityJustification: e.target.value }))}
                placeholder="Explain why this specific case meets the exception's eligibility criteria"
                className={fieldCls("", fieldErrors, "eligibilityJustification")}
                aria-invalid={!!fieldErrors.eligibilityJustification} />
              {fieldErrors.eligibilityJustification && <p role="alert" className="text-xs text-destructive">{fieldErrors.eligibilityJustification}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional additional notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setFieldErrors({}); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Create Exception Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── FERPA-05 Consent Records Section (34 CFR § 99.30) ─────────────────────────

function ConsentRecordsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    studentId: "",
    dataScope: "",
    purpose: "",
    recipient: "",
    recipientContactInfo: "",
    consentedAt: "",
    expiresAt: "",
    notes: "",
  });

  const { data, isLoading } = useQuery<PagedResult<ConsentRecord>>({
    queryKey: ["/api/organization/compliance/ferpa/consents"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/consents?pageSize=20", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load consent records");
      return res.json();
    },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/consents"] });
  };

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/consents", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          studentId: form.studentId,
          dataScope: form.dataScope,
          purpose: form.purpose,
          recipient: form.recipient,
          recipientContactInfo: form.recipientContactInfo || null,
          consentedAt: form.consentedAt ? new Date(form.consentedAt).toISOString() : null,
          expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
          notes: form.notes || null,
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Consent record created" });
      setOpen(false);
      setFieldErrors({});
      setForm({ studentId: "", dataScope: "", purpose: "", recipient: "", recipientContactInfo: "", consentedAt: "", expiresAt: "", notes: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not save consent record." });
        toast({ title: "Unable to create consent record", description: e.message, variant: "destructive" });
      }
    },
  });

  const approve = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/consents/${id}/approve`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ consentedAt: new Date().toISOString() }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Consent approved — disclosure may now proceed." });
    },
    onError: (e: any) => toast({ title: e.message ?? "Failed to approve consent", variant: "destructive" }),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/consents/${id}/revoke`, {
        method: "PATCH",
        headers: { ...bearerHeader() },
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Consent revoked — future disclosures are blocked." });
    },
    onError: (e: any) => toast({ title: e.message ?? "Failed to revoke consent", variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/consents/${id}`, {
        method: "DELETE",
        headers: bearerHeader(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Failed to delete consent record");
      }
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Consent record deleted" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.studentId.trim()) errs.studentId = "Student ID is required.";
    if (!form.dataScope.trim()) errs.dataScope = "Records to be disclosed (§ 99.30(a)(1)) is required.";
    if (!form.purpose.trim())   errs.purpose   = "Purpose (§ 99.30(a)(2)) is required.";
    if (!form.recipient.trim()) errs.recipient = "Recipient (§ 99.30(a)(3)) is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  const statusBadge = (status: ConsentRecord["consentStatus"]) => {
    const map: Record<string, string> = {
      Approved: "bg-green-100 text-green-800",
      Pending:  "bg-yellow-100 text-yellow-800",
      Revoked:  "bg-red-100 text-red-800",
      Expired:  "bg-gray-100 text-gray-600",
    };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? "bg-gray-100 text-gray-700"}`}>{status}</span>;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Consent Records
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99.30 — prior written consent required before disclosure. Must specify records, purpose, and recipient.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Record Consent
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : !data?.items?.length ? (
          <p className="text-sm text-muted-foreground py-3 text-center">No consent records on file.</p>
        ) : (
          <div className="space-y-2">
            {data.items.map(c => (
              <div key={c.id} className="border rounded-md p-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusBadge(c.consentStatus)}
                      <span className="text-sm font-medium truncate">Student: {c.studentId}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Recipient: {c.recipient} · Purpose: {c.purpose}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Records: {c.dataScope}
                      {c.consentedAt && <> · Consented: {fmtDate(c.consentedAt)}</>}
                      {c.expiresAt && <> · Expires: {fmtDate(c.expiresAt)}</>}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-1 shrink-0">
                      {c.consentStatus === "Pending" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-700 border-green-300 hover:bg-green-50 h-7 px-2 text-xs"
                          onClick={() => approve.mutate(c.id)}
                          disabled={approve.isPending}
                          aria-label="Approve consent"
                        >
                          Approve
                        </Button>
                      )}
                      {c.consentStatus === "Approved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 px-2 text-xs"
                          onClick={() => revoke.mutate(c.id)}
                          disabled={revoke.isPending}
                          aria-label="Revoke consent"
                        >
                          Revoke
                        </Button>
                      )}
                      {c.consentStatus !== "Approved" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 h-7 px-2"
                          onClick={() => { if (confirm("Delete this consent record?")) remove.mutate(c.id); }}
                          disabled={remove.isPending}
                          aria-label="Delete consent record"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {data.total > 20 && (
              <p className="text-xs text-muted-foreground text-center pt-1">Showing 20 of {data.total} records.</p>
            )}
          </div>
        )}
      </CardContent>

      {/* Create consent dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Prior Written Consent</DialogTitle>
            <DialogDescription>
              34 CFR § 99.30 — capture student/parent consent before disclosing education records.
              Consent must specify the records, purpose, and recipient.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {fieldErrors._ && <p role="alert" className="text-sm text-destructive">{fieldErrors._}</p>}

            <div className="space-y-1">
              <label className="text-sm font-medium">Student ID <span className="text-destructive">*</span></label>
              <Input value={form.studentId} onChange={e => setForm(f => ({ ...f, studentId: e.target.value }))}
                placeholder="Student identifier" className={fieldCls("", fieldErrors, "studentId")} aria-invalid={!!fieldErrors.studentId} />
              {fieldErrors.studentId && <p role="alert" className="text-xs text-destructive">{fieldErrors.studentId}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Records to be Disclosed (§ 99.30(a)(1)) <span className="text-destructive">*</span></label>
              <Input value={form.dataScope} onChange={e => setForm(f => ({ ...f, dataScope: e.target.value }))}
                placeholder="e.g. Transcript, GPA, enrollment status" className={fieldCls("", fieldErrors, "dataScope")} aria-invalid={!!fieldErrors.dataScope} />
              {fieldErrors.dataScope && <p role="alert" className="text-xs text-destructive">{fieldErrors.dataScope}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Purpose (§ 99.30(a)(2)) <span className="text-destructive">*</span></label>
              <Input value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                placeholder="e.g. Graduate school application" className={fieldCls("", fieldErrors, "purpose")} aria-invalid={!!fieldErrors.purpose} />
              {fieldErrors.purpose && <p role="alert" className="text-xs text-destructive">{fieldErrors.purpose}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Recipient (§ 99.30(a)(3)) <span className="text-destructive">*</span></label>
              <Input value={form.recipient} onChange={e => setForm(f => ({ ...f, recipient: e.target.value }))}
                placeholder="e.g. MIT Admissions Office" className={fieldCls("", fieldErrors, "recipient")} aria-invalid={!!fieldErrors.recipient} />
              {fieldErrors.recipient && <p role="alert" className="text-xs text-destructive">{fieldErrors.recipient}</p>}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Recipient Contact Info</label>
              <Input value={form.recipientContactInfo} onChange={e => setForm(f => ({ ...f, recipientContactInfo: e.target.value }))}
                placeholder="Email, address, or phone (optional)" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Consent Date</label>
                <Input type="date" value={form.consentedAt} onChange={e => setForm(f => ({ ...f, consentedAt: e.target.value }))} />
                <p className="text-xs text-muted-foreground">Leave blank to start as Pending</p>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Expiry Date</label>
                <Input type="date" value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
                <p className="text-xs text-muted-foreground">Optional</p>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Notes</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes or reference number" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setFieldErrors({}); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Record Consent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Disclosure Records Section ─────────────────────────────────────────────────

// Extended DisclosureRecord type with FERPA-10 fields
type DisclosureRecord10 = DisclosureRecord & {
  recipientOrganization?: string | null;
  requestorName?: string | null;
  authorizationBasis?: string;
  consentRecordId?: string | null;
  exceptionRecordId?: string | null;
  schoolOfficialRecordId?: string | null;
  leiAccessLogId?: string | null;
  retentionStatus?: string;
  retentionReviewDate?: string | null;
};

const AUTH_BASIS_OPTIONS = ["Consent", "Exception", "SchoolOfficial", "LegitimateInterest", "Other"];

const authBasisBadge = (basis: string) => {
  const map: Record<string, string> = {
    Consent:             "bg-green-100 text-green-800",
    Exception:           "bg-purple-100 text-purple-800",
    SchoolOfficial:      "bg-blue-100 text-blue-800",
    LegitimateInterest:  "bg-teal-100 text-teal-800",
    Other:               "bg-gray-100 text-gray-600",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[basis] ?? "bg-gray-100 text-gray-700"}`}>
      {basis}
    </span>
  );
};

const retentionBadge = (status: string) => {
  const map: Record<string, string> = {
    Active:       "bg-green-50 text-green-700",
    UnderReview:  "bg-yellow-50 text-yellow-700",
    Expired:      "bg-red-50 text-red-600",
  };
  return status !== "Active"
    ? <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${map[status] ?? ""}`}>{status}</span>
    : null;
};

function DisclosureRecordsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // FERPA-10 — filter state
  const [filters, setFilters] = useState({
    disclosureType: "",
    authorizationBasis: "",
    from: "",
    to: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const [form, setForm] = useState({
    studentId: "",
    recipient: "",
    recipientOrganization: "",
    requestorName: "",
    disclosureType: "Consent",
    ferpaExceptionCategory: "Consent",
    purpose: "",
    recordsDisclosed: "",
    eligibilityJustification: "",
    notes: "",
    consentRecordId: "",
    exceptionRecordId: "",
    schoolOfficialRecordId: "",
    leiAccessLogId: "",
    retentionReviewDate: "",
  });

  const buildQs = (f: typeof appliedFilters) => {
    const p = new URLSearchParams({ pageSize: "20" });
    if (f.disclosureType)    p.set("disclosureType",    f.disclosureType);
    if (f.authorizationBasis) p.set("authorizationBasis", f.authorizationBasis);
    if (f.from)              p.set("from",              new Date(f.from).toISOString());
    if (f.to)                p.set("to",                new Date(f.to).toISOString());
    return p.toString();
  };

  const { data, isLoading } = useQuery<PagedResult<DisclosureRecord10>>({
    queryKey: ["/api/organization/compliance/ferpa/disclosures", appliedFilters],
    queryFn: async () => {
      const res = await fetch(
        `/api/organization/compliance/ferpa/disclosures?${buildQs(appliedFilters)}`,
        { headers: bearerHeader() }
      );
      if (!res.ok) throw new Error("Failed to load disclosures");
      return res.json();
    },
  });

  const FORM_EMPTY = {
    studentId: "", recipient: "", recipientOrganization: "", requestorName: "",
    disclosureType: "Consent", ferpaExceptionCategory: "Consent",
    purpose: "", recordsDisclosed: "", eligibilityJustification: "", notes: "",
    consentRecordId: "", exceptionRecordId: "", schoolOfficialRecordId: "",
    leiAccessLogId: "", retentionReviewDate: "",
  };

  const create = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {
        studentId: form.studentId,
        recipient: form.recipient,
        recipientContactInfo: null,
        disclosureType: form.disclosureType,
        ferpaExceptionCategory: form.ferpaExceptionCategory,
        purpose: form.purpose,
        recordsDisclosed: form.recordsDisclosed,
        notes: form.notes || null,
        eligibilityJustification: form.eligibilityJustification || null,
        // FERPA-10 extended fields
        recipientOrganization: form.recipientOrganization || null,
        requestorName: form.requestorName || null,
        consentRecordId: form.consentRecordId || null,
        exceptionRecordId: form.exceptionRecordId || null,
        schoolOfficialRecordId: form.schoolOfficialRecordId || null,
        leiAccessLogId: form.leiAccessLogId || null,
        retentionReviewDate: form.retentionReviewDate
          ? new Date(form.retentionReviewDate).toISOString()
          : null,
      };
      const res = await fetch("/api/organization/compliance/ferpa/disclosures", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify(body),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosures"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Disclosure record logged" });
      setOpen(false);
      setFieldErrors({});
      setForm(FORM_EMPTY);
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not save. Please check the fields and try again." });
        toast({ title: "Unable to log disclosure record", description: e.message, variant: "destructive" });
      }
    },
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.studentId.trim())        errs.studentId        = "Student ID is required.";
    if (!form.recipient.trim())        errs.recipient        = "Recipient name or organization is required.";
    if (!form.purpose.trim())          errs.purpose          = "Purpose of disclosure is required.";
    if (!form.recordsDisclosed.trim()) errs.recordsDisclosed = "Description of records disclosed is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  const setField = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const val = e.target.value;
    setForm(f => ({
      ...f,
      [key]: val,
      // Keep ferpaExceptionCategory in sync with disclosureType
      ...(key === "disclosureType" ? { ferpaExceptionCategory: val } : {}),
    }));
    if (fieldErrors[key]) setFieldErrors(fe => ({ ...fe, [key]: "" }));
  };

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/disclosures/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? "Record is retention-locked and cannot be deleted.");
      }
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosures"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Disclosure record deleted" });
    },
    onError: (err: Error) => toast({ title: err.message, variant: "destructive" }),
  });

  const lockUntilDefault = new Date(Date.now() + 7 * 365.25 * 24 * 3600 * 1000).toISOString().split("T")[0];
  const lockRetention = useMutation({
    mutationFn: async ({ id, lockedUntil }: { id: string; lockedUntil: string }) => {
      const res = await fetch(`/api/organization/compliance/ferpa/disclosures/${id}/lock-retention`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ lockedUntil: new Date(lockedUntil).toISOString() }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosures"] });
      toast({ title: "Retention lock applied" });
    },
    onError: (e: any) => {
      const msg = toFlatFieldErrors(e).lockedUntil ?? e.message ?? "Failed to apply retention lock";
      toast({ title: msg, variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> Disclosure Records
            </CardTitle>
            <CardDescription className="text-xs">34 CFR § 99.32 — mandatory log of all third-party disclosures of education-record PII</CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Log Disclosure
            </Button>
          )}
        </div>

        {/* FERPA-10 — Filter bar */}
        <div className="flex flex-wrap gap-2 pt-2">
          <select
            className="border rounded px-2 py-1 text-xs bg-background"
            value={filters.disclosureType}
            onChange={e => setFilters(f => ({ ...f, disclosureType: e.target.value }))}>
            <option value="">All types</option>
            <option>Consent</option><option>LegitimateInterest</option>
            <option>Emergency</option><option>Subpoena</option>
            <option>Directory</option><option>TransferSchool</option>
            <option>StudyAudit</option><option>Other</option>
          </select>
          <select
            className="border rounded px-2 py-1 text-xs bg-background"
            value={filters.authorizationBasis}
            onChange={e => setFilters(f => ({ ...f, authorizationBasis: e.target.value }))}>
            <option value="">All auth bases</option>
            {AUTH_BASIS_OPTIONS.map(o => <option key={o}>{o}</option>)}
          </select>
          <input type="date" className="border rounded px-2 py-1 text-xs bg-background"
            value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
            title="From date" />
          <input type="date" className="border rounded px-2 py-1 text-xs bg-background"
            value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
            title="To date" />
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs"
            onClick={() => setAppliedFilters(filters)}>Search</Button>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => {
              const empty = { disclosureType: "", authorizationBasis: "", from: "", to: "" };
              setFilters(empty); setAppliedFilters(empty);
            }}>Clear</Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : !data?.items.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">No disclosure records match the selected filters.</p>
        ) : (
          <div className="divide-y text-sm">
            {data.items.map((r: DisclosureRecord10) => {
              const isLocked = !!r.retentionLockedUntil && new Date(r.retentionLockedUntil) > new Date();
              return (
                <div key={r.id} className="py-2.5 flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.recipient}</span>
                      {r.recipientOrganization && (
                        <span className="text-xs text-muted-foreground">({r.recipientOrganization})</span>
                      )}
                      <Badge variant="outline" className="text-xs">{r.disclosureType}</Badge>
                      {r.authorizationBasis && authBasisBadge(r.authorizationBasis)}
                      {r.retentionStatus && retentionBadge(r.retentionStatus)}
                      {isLocked && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                          <Lock className="w-3 h-3" /> Locked until {fmtDate(r.retentionLockedUntil)}
                        </span>
                      )}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      Student: <span className="font-mono">{r.studentId}</span>
                      {r.requestorName && <> · Requested by: {r.requestorName}</>}
                      {" · "}{r.purpose} · {fmtDate(r.disclosureDate)}
                    </div>
                    {/* Authorization cross-references (FERPA-10) */}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {r.consentRecordId && <span>Consent ref: <span className="font-mono">{r.consentRecordId.slice(0,8)}…</span></span>}
                      {r.exceptionRecordId && <span>Exception ref: <span className="font-mono">{r.exceptionRecordId.slice(0,8)}…</span></span>}
                      {r.schoolOfficialRecordId && <span>School official ref: <span className="font-mono">{r.schoolOfficialRecordId.slice(0,8)}…</span></span>}
                      {r.leiAccessLogId && <span>LEI log ref: <span className="font-mono">{r.leiAccessLogId.slice(0,8)}…</span></span>}
                      {r.retentionReviewDate && <span>Review due: {fmtDate(r.retentionReviewDate)}</span>}
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-1 shrink-0">
                      {!isLocked && (
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7"
                          title="Lock retention (prevents deletion)"
                          aria-label="Lock retention (prevents deletion)"
                          onClick={() => lockRetention.mutate({ id: r.id, lockedUntil: lockUntilDefault })}
                          disabled={lockRetention.isPending}
                        >
                          <Unlock className="w-3.5 h-3.5 text-blue-500" aria-hidden="true" />
                        </Button>
                      )}
                      <Button
                        variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => remove.mutate(r.id)}
                        disabled={isLocked}
                        title={isLocked ? "Retention-locked — cannot delete" : "Delete"}
                        aria-label={isLocked ? "Retention-locked — cannot delete" : "Delete retention record"}
                      >
                        <Trash2 className={`w-3.5 h-3.5 ${isLocked ? "text-muted-foreground/40" : ""}`} aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {(data?.total ?? 0) > 20 && (
              <p className="text-xs text-muted-foreground text-center pt-2">
                Showing 20 of {data!.total} records. Use filters to narrow results.
              </p>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFieldErrors({}); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log Disclosure Record</DialogTitle>
            <DialogDescription>
              34 CFR § 99.32 — record a disclosure of student education-record PII to a third party.
              All disclosures must be auditable, retained, and associated with an authorization basis.
            </DialogDescription>
          </DialogHeader>

          {fieldErrors._ && (
            <p role="alert" className="text-xs text-destructive px-1 -mt-1">{fieldErrors._}</p>
          )}

          <div className="space-y-3">
            {/* ── Core fields ── */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa-disc-studentId">Student ID *</Label>
                <Input id="ferpa-disc-studentId" value={form.studentId}
                  onChange={setField("studentId")} placeholder="Student identifier" required
                  aria-invalid={!!fieldErrors.studentId || undefined}
                  aria-describedby={fieldErrors.studentId ? "ferpa-disc-studentId-err" : undefined} />
                <FieldErr id="ferpa-disc-studentId-err" msg={fieldErrors.studentId} />
              </div>
              <div>
                <Label htmlFor="ferpa-disc-disclosureType">Disclosure Type</Label>
                <select id="ferpa-disc-disclosureType" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={form.disclosureType} onChange={setField("disclosureType")}>
                  <option>Consent</option><option>LegitimateInterest</option>
                  <option>Emergency</option><option>Subpoena</option>
                  <option>Directory</option><option>TransferSchool</option>
                  <option>StudyAudit</option><option>Other</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa-disc-recipient">Recipient Name *</Label>
                <Input id="ferpa-disc-recipient" value={form.recipient}
                  onChange={setField("recipient")} placeholder="Name of the recipient" required
                  aria-invalid={!!fieldErrors.recipient || undefined}
                  aria-describedby={fieldErrors.recipient ? "ferpa-disc-recipient-err" : undefined} />
                <FieldErr id="ferpa-disc-recipient-err" msg={fieldErrors.recipient} />
              </div>
              <div>
                <Label htmlFor="ferpa-disc-recipientOrg">Recipient Organization</Label>
                <Input id="ferpa-disc-recipientOrg" value={form.recipientOrganization}
                  onChange={setField("recipientOrganization")} placeholder="Org / institution" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa-disc-requestorName">Requestor Name</Label>
                <Input id="ferpa-disc-requestorName" value={form.requestorName}
                  onChange={setField("requestorName")} placeholder="Who submitted the request" />
              </div>
              <div>
                <Label htmlFor="ferpa-disc-retentionReviewDate">Retention Review Date</Label>
                <Input id="ferpa-disc-retentionReviewDate" type="date"
                  value={form.retentionReviewDate} onChange={setField("retentionReviewDate")} />
              </div>
            </div>

            <div>
              <Label htmlFor="ferpa-disc-purpose">Purpose *</Label>
              <Input id="ferpa-disc-purpose" value={form.purpose}
                onChange={setField("purpose")} placeholder="Reason for disclosure" required
                aria-invalid={!!fieldErrors.purpose || undefined}
                aria-describedby={fieldErrors.purpose ? "ferpa-disc-purpose-err" : undefined} />
              <FieldErr id="ferpa-disc-purpose-err" msg={fieldErrors.purpose} />
            </div>

            <div>
              <Label htmlFor="ferpa-disc-recordsDisclosed">Records Disclosed *</Label>
              <Textarea id="ferpa-disc-recordsDisclosed" value={form.recordsDisclosed}
                onChange={setField("recordsDisclosed")}
                placeholder="Describe what records were disclosed (e.g. transcript, GPA, attendance)"
                rows={2} required
                aria-invalid={!!fieldErrors.recordsDisclosed || undefined}
                aria-describedby={fieldErrors.recordsDisclosed ? "ferpa-disc-recordsDisclosed-err" : undefined} />
              <FieldErr id="ferpa-disc-recordsDisclosed-err" msg={fieldErrors.recordsDisclosed} />
            </div>

            {form.disclosureType !== "Consent" && (
              <div>
                <Label htmlFor="ferpa-disc-justification">
                  Eligibility Justification{" "}
                  <span className="text-muted-foreground font-normal text-xs">(required for exception-based disclosures)</span>
                </Label>
                <Input id="ferpa-disc-justification" value={form.eligibilityJustification}
                  onChange={setField("eligibilityJustification")}
                  placeholder="Why this case meets the exception's eligibility criteria"
                  aria-invalid={!!fieldErrors.eligibilityJustification || undefined}
                  aria-describedby={fieldErrors.eligibilityJustification ? "ferpa-disc-justification-err" : undefined} />
                <FieldErr id="ferpa-disc-justification-err" msg={fieldErrors.eligibilityJustification} />
              </div>
            )}

            {/* ── FERPA-10 — Authorization cross-references ── */}
            <details className="border rounded-md p-2">
              <summary className="cursor-pointer text-xs font-medium text-muted-foreground select-none">
                Authorization Cross-References <span className="font-normal">(optional — link to consent, exception, school official, or LEI records)</span>
              </summary>
              <div className="mt-2 space-y-2">
                <div>
                  <label className="text-xs font-medium">Consent Record ID</label>
                  <Input className="mt-0.5 h-7 text-xs" placeholder="UUID"
                    value={form.consentRecordId} onChange={setField("consentRecordId")} />
                </div>
                <div>
                  <label className="text-xs font-medium">Exception Record ID</label>
                  <Input className="mt-0.5 h-7 text-xs" placeholder="UUID"
                    value={form.exceptionRecordId} onChange={setField("exceptionRecordId")} />
                </div>
                <div>
                  <label className="text-xs font-medium">School Official Record ID</label>
                  <Input className="mt-0.5 h-7 text-xs" placeholder="UUID"
                    value={form.schoolOfficialRecordId} onChange={setField("schoolOfficialRecordId")} />
                </div>
                <div>
                  <label className="text-xs font-medium">LEI Access Log ID</label>
                  <Input className="mt-0.5 h-7 text-xs" placeholder="UUID"
                    value={form.leiAccessLogId} onChange={setField("leiAccessLogId")} />
                </div>
              </div>
            </details>

            <div>
              <Label htmlFor="ferpa-disc-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="ferpa-disc-notes" value={form.notes} onChange={setField("notes")} placeholder="Additional notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setFieldErrors({}); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Log Disclosure"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Vendor Agreements Section ──────────────────────────────────────────────────

const APPROVAL_STATUS_STYLES: Record<string, string> = {
  Approved:  "bg-green-100 text-green-800",
  Pending:   "bg-yellow-100 text-yellow-800",
  Rejected:  "bg-red-100 text-red-800",
  Suspended: "bg-orange-100 text-orange-800",
};

function ApprovalBadge({ status }: { status: string }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${APPROVAL_STATUS_STYLES[status] ?? "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

type VendorAccessLog = {
  id: string; vendorAgreementId: string; requestingUserId: string | null;
  purpose: string; dataFieldsRequested: string | null;
  outcome: string; blockReason: string | null; timestamp: string;
};

function VendorAgreementsSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"registry" | "check" | "dataflow" | "logs">("registry");
  const [open, setOpen] = useState(false);
  const [statusDialogId, setStatusDialogId] = useState<string | null>(null);
  const [statusForm, setStatusForm] = useState({ status: "Approved", reason: "" });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    vendorName: "",
    agreementType: "SchoolOfficial",
    serviceType: "Other",
    description: "",
    contactEmail: "",
    dataShared: "",
    dataCategories: [] as string[],
    dataFieldsAccessed: "",
    authorizedPurpose: "",
    dpaReference: "",
    effectiveDate: new Date().toISOString().split("T")[0],
    expiryDate: "",
    contractExpiryDate: "",
    securityReviewStatus: "NotStarted",
    securityReviewerName: "",
    dataFlowDocumented: false,
    dataFlowNotes: "",
    notes: "",
  });

  // Access-check form
  const [checkForm, setCheckForm] = useState({ vendorId: "", purpose: "", dataFields: "" });
  const [checkResult, setCheckResult] = useState<any>(null);
  const [checkLoading, setCheckLoading] = useState(false);

  const VQ = ["/api/organization/compliance/ferpa/vendor-agreements"];
  const LQ = ["/api/organization/compliance/ferpa/vendor-access-logs"];

  const { data, isLoading } = useQuery<PagedResult<VendorAgreement>>({
    queryKey: VQ,
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/vendor-agreements?pageSize=50", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load vendor registry");
      return res.json();
    },
  });

  const { data: logData } = useQuery<PagedResult<VendorAccessLog>>({
    queryKey: LQ,
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/vendor-access-logs?pageSize=30", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load access logs");
      return res.json();
    },
    enabled: tab === "logs",
  });

  const DATA_CATS = ["EducationRecords", "StudentPII", "SensitiveData", "RestrictedData"];

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/vendor-agreements", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          vendorName:           form.vendorName,
          agreementType:        form.agreementType,
          serviceType:          form.serviceType,
          description:          form.description || null,
          contactEmail:         form.contactEmail || null,
          dataShared:           form.dataShared,
          dataCategories:       form.dataCategories.length ? JSON.stringify(form.dataCategories) : null,
          dataFieldsAccessed:   form.dataFieldsAccessed ? JSON.stringify(form.dataFieldsAccessed.split(",").map(s => s.trim()).filter(Boolean)) : null,
          authorizedPurpose:    form.authorizedPurpose || null,
          dpaReference:         form.dpaReference || null,
          effectiveDate:        new Date(form.effectiveDate).toISOString(),
          expiryDate:           form.expiryDate ? new Date(form.expiryDate).toISOString() : null,
          contractExpiryDate:   form.contractExpiryDate ? new Date(form.contractExpiryDate).toISOString() : null,
          securityReviewStatus: form.securityReviewStatus,
          securityReviewerName: form.securityReviewerName || null,
          dataFlowDocumented:   form.dataFlowDocumented,
          dataFlowNotes:        form.dataFlowNotes || null,
          notes:                form.notes || null,
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VQ });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Vendor registered" });
      setOpen(false);
      setFieldErrors({});
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Correct highlighted fields.", variant: "destructive" }); }
      else { setFieldErrors({ _: e.message ?? "Unable to save" }); toast({ title: "Unable to register vendor", description: e.message, variant: "destructive" }); }
    },
  });

  const patchStatus = useMutation({
    mutationFn: async ({ id, status, reason }: { id: string; status: string; reason?: string }) => {
      const res = await fetch(`/api/organization/compliance/ferpa/vendor-agreements/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ status, reason: reason || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VQ });
      toast({ title: "Vendor status updated" });
      setStatusDialogId(null);
    },
    onError: (e: any) => toast({ title: "Unable to update status", description: e.message, variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/vendor-agreements/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VQ });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Vendor removed" });
    },
    onError: () => toast({ title: "Unable to remove vendor", variant: "destructive" }),
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.vendorName.trim())  errs.vendorName  = "Vendor name is required.";
    if (!form.dataShared.trim())  errs.dataShared  = "Data shared description is required.";
    if (form.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim()))
      errs.contactEmail = "Enter a valid email address.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  const sf = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (fieldErrors[key]) setFieldErrors(fe => ({ ...fe, [key]: "" }));
  };

  const runCheck = async () => {
    if (!checkForm.vendorId) { toast({ title: "Select a vendor to check", variant: "destructive" }); return; }
    setCheckLoading(true);
    setCheckResult(null);
    try {
      const fields = checkForm.dataFields ? checkForm.dataFields.split(",").map(s => s.trim()).filter(Boolean) : [];
      const res = await fetch("/api/organization/compliance/ferpa/vendor-access-check", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ vendorId: checkForm.vendorId, purpose: checkForm.purpose, dataFields: fields }),
      });
      const body = await res.json();
      setCheckResult(body);
      queryClient.invalidateQueries({ queryKey: LQ });
    } catch (e: any) {
      toast({ title: "Unable to complete access check", description: e.message, variant: "destructive" });
    } finally { setCheckLoading(false); }
  };

  const statusDialog = data?.items.find(v => v.id === statusDialogId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Third-Party Services
            </CardTitle>
            <CardDescription className="text-xs">34 CFR § 99.31(a)(1)(i)(B) — vendor registry, approval workflow, and access gate</CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Register Vendor
            </Button>
          )}
        </div>
        {/* Tab bar */}
        <div className="flex gap-1 pt-2 border-b">
          {(["registry", "check", "dataflow", "logs"] as const).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs rounded-t font-medium transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
            >
              {t === "registry" ? "Vendor Registry" : t === "check" ? "Access Check" : t === "dataflow" ? "Data Flows" : "Access Log"}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {/* ── TAB: Vendor Registry ─────────────────────────────────── */}
        {tab === "registry" && (
          <>
            {isLoading ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : !data?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No vendors registered. Click "Register Vendor" to add one.</p>
            ) : (
              <div className="divide-y text-sm">
                {data.items.map(v => (
                  <div key={v.id} className="py-3 flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{v.vendorName}</span>
                        <ApprovalBadge status={(v as any).approvalStatus ?? "Pending"} />
                        <Badge variant="outline" className="text-xs">{(v as any).serviceType ?? v.agreementType}</Badge>
                        <SecurityBadge status={v.securityReviewStatus} />
                        {(v as any).contractExpiryDate && isOverdue((v as any).contractExpiryDate) && (
                          <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">Contract Expired</span>
                        )}
                      </div>
                      <div className="text-muted-foreground text-xs mt-0.5 flex flex-wrap gap-x-2">
                        <span>Effective {fmtDate(v.effectiveDate)}</span>
                        {(v as any).contractExpiryDate && <span>· Contract expires {fmtDate((v as any).contractExpiryDate)}</span>}
                        {(v as any).dpaReference && <span>· DPA: {(v as any).dpaReference}</span>}
                        {v.contactEmail && <span>· {v.contactEmail}</span>}
                        {(v as any).dataFlowDocumented && <span className="text-green-600">· Data flow documented</span>}
                      </div>
                      {(v as any).dataCategories && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {JSON.parse((v as any).dataCategories ?? "[]").map((c: string) => (
                            <span key={c} className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="outline" size="sm" className="h-7 text-xs px-2"
                          onClick={() => { setStatusDialogId(v.id); setStatusForm({ status: "Approved", reason: "" }); }}>
                          Status
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          aria-label="Remove vendor"
                          onClick={() => remove.mutate(v.id)}>
                          <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB: Access Check ────────────────────────────────────── */}
        {tab === "check" && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Evaluate whether a vendor is currently approved and authorized to receive student data. Every check is logged.
            </p>
            <div className="space-y-3">
              <div>
                <Label htmlFor="ferpa13-check-vendor">Vendor</Label>
                <select id="ferpa13-check-vendor"
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={checkForm.vendorId}
                  onChange={e => setCheckForm(f => ({ ...f, vendorId: e.target.value }))}>
                  <option value="">— select vendor —</option>
                  {data?.items.map(v => (
                    <option key={v.id} value={v.id}>{v.vendorName}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="ferpa13-check-purpose">Purpose</Label>
                <Input id="ferpa13-check-purpose" placeholder="e.g. Grade sync, Enrollment reporting"
                  value={checkForm.purpose}
                  onChange={e => setCheckForm(f => ({ ...f, purpose: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="ferpa13-check-fields">Data Fields (optional, comma-separated)</Label>
                <Input id="ferpa13-check-fields" placeholder="e.g. StudentId, GPA, EnrollmentStatus"
                  value={checkForm.dataFields}
                  onChange={e => setCheckForm(f => ({ ...f, dataFields: e.target.value }))} />
              </div>
              <Button onClick={runCheck} disabled={checkLoading} className="w-full">
                {checkLoading ? "Checking…" : "Run Access Check"}
              </Button>
            </div>

            {checkResult && (
              <div className={`rounded-lg border p-4 ${checkResult.outcome === "Allowed" ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                <div className="flex items-center gap-2 font-medium text-sm mb-1">
                  {checkResult.outcome === "Allowed"
                    ? <><CheckCircle2 className="w-4 h-4 text-green-600" /> Access Allowed</>
                    : <><XCircle className="w-4 h-4 text-red-600" /> Access Blocked</>}
                </div>
                <p className="text-xs text-muted-foreground">{checkResult.message}</p>
                {checkResult.blockReason && (
                  <p className="text-xs mt-1 font-medium text-red-700">Reason: {checkResult.blockReason}</p>
                )}
                <p className="text-xs mt-1 text-muted-foreground">Log ID: <span className="font-mono">{checkResult.logId}</span></p>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Data Flows ──────────────────────────────────────── */}
        {tab === "dataflow" && (
          <>
            {!data?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No vendors registered yet.</p>
            ) : (
              <div className="space-y-3 text-sm">
                {data.items.map(v => (
                  <div key={v.id} className="rounded border p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{v.vendorName}</span>
                      <ApprovalBadge status={(v as any).approvalStatus ?? "Pending"} />
                      {(v as any).dataFlowDocumented
                        ? <span className="text-xs text-green-600 font-medium">✓ Flow documented</span>
                        : <span className="text-xs text-yellow-600 font-medium">⚠ Flow not documented</span>}
                    </div>
                    {(v as any).authorizedPurpose && (
                      <p className="text-xs text-muted-foreground mb-1">Purpose: {(v as any).authorizedPurpose}</p>
                    )}
                    {(v as any).dataFieldsAccessed && (() => {
                      try {
                        const fields: string[] = JSON.parse((v as any).dataFieldsAccessed);
                        return (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {fields.map(f => <span key={f} className="px-1.5 py-0.5 text-xs bg-slate-100 rounded font-mono">{f}</span>)}
                          </div>
                        );
                      } catch { return null; }
                    })()}
                    {(v as any).dataFlowNotes && (
                      <p className="text-xs text-muted-foreground italic">{(v as any).dataFlowNotes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB: Access Log ──────────────────────────────────────── */}
        {tab === "logs" && (
          <>
            {!logData?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">No access checks recorded yet. Use the "Access Check" tab to run a check.</p>
            ) : (
              <div className="divide-y text-sm">
                {logData.items.map(l => (
                  <div key={l.id} className="py-2 flex items-center gap-3">
                    {l.outcome === "Allowed"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{l.outcome}</span>
                        {l.blockReason && <Badge variant="outline" className="text-xs text-red-700">{l.blockReason}</Badge>}
                        <span className="text-muted-foreground text-xs">{l.purpose}</span>
                      </div>
                      <div className="text-muted-foreground text-xs mt-0.5">
                        {fmtDateTime(l.timestamp)} · <span className="font-mono">{l.vendorAgreementId.slice(0, 8)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* ── Status Change Dialog ──────────────────────────────────── */}
      <Dialog open={!!statusDialogId} onOpenChange={v => { if (!v) setStatusDialogId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Vendor Status</DialogTitle>
            <DialogDescription>{statusDialog?.vendorName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="ferpa13-status-sel">New Status</Label>
              <select id="ferpa13-status-sel" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={statusForm.status}
                onChange={e => setStatusForm(f => ({ ...f, status: e.target.value }))}>
                <option value="Approved">Approved</option>
                <option value="Pending">Pending</option>
                <option value="Suspended">Suspended</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            {(statusForm.status === "Suspended" || statusForm.status === "Rejected") && (
              <div>
                <Label htmlFor="ferpa13-status-reason">Reason</Label>
                <Textarea id="ferpa13-status-reason" rows={2}
                  placeholder="Describe the reason for this status change"
                  value={statusForm.reason}
                  onChange={e => setStatusForm(f => ({ ...f, reason: e.target.value }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogId(null)}>Cancel</Button>
            <Button
              onClick={() => patchStatus.mutate({ id: statusDialogId!, status: statusForm.status, reason: statusForm.reason })}
              disabled={patchStatus.isPending}>
              {patchStatus.isPending ? "Saving…" : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Register Vendor Dialog ────────────────────────────────── */}
      <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) setFieldErrors({}); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register Third-Party Service</DialogTitle>
            <DialogDescription>Register a vendor or service provider that handles student education records.</DialogDescription>
          </DialogHeader>

          {fieldErrors._ && <p role="alert" className="text-xs text-destructive px-1 -mt-1">{fieldErrors._}</p>}

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa13-vn">Vendor Name *</Label>
                <Input id="ferpa13-vn" value={form.vendorName} onChange={sf("vendorName")} placeholder="e.g. Canvas LMS"
                  aria-invalid={!!fieldErrors.vendorName || undefined} />
                <FieldErr id="ferpa13-vn-err" msg={fieldErrors.vendorName} />
              </div>
              <div>
                <Label htmlFor="ferpa13-st">Service Type</Label>
                <select id="ferpa13-st" className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.serviceType} onChange={sf("serviceType")}>
                  {["AIProvider","LMS","SIS","Analytics","Assessment","Integration","Other"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa13-at">Agreement Type</Label>
                <select id="ferpa13-at" className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.agreementType} onChange={sf("agreementType")}>
                  {["SchoolOfficial","StudyAudit","Accreditation","Other"].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="ferpa13-email">Contact Email</Label>
                <Input id="ferpa13-email" type="email" value={form.contactEmail} onChange={sf("contactEmail")} placeholder="compliance@vendor.com"
                  aria-invalid={!!fieldErrors.contactEmail || undefined} />
                <FieldErr id="ferpa13-email-err" msg={fieldErrors.contactEmail} />
              </div>
            </div>

            <div>
              <Label htmlFor="ferpa13-desc">Description</Label>
              <Input id="ferpa13-desc" value={form.description} onChange={sf("description")} placeholder="Brief description of this service" />
            </div>

            <div>
              <Label htmlFor="ferpa13-ds">Data Shared *</Label>
              <Textarea id="ferpa13-ds" value={form.dataShared} onChange={sf("dataShared")} rows={2}
                placeholder="Describe the student data shared with this vendor"
                aria-invalid={!!fieldErrors.dataShared || undefined} />
              <FieldErr id="ferpa13-ds-err" msg={fieldErrors.dataShared} />
            </div>

            <div>
              <Label className="text-xs font-medium">Data Categories</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {DATA_CATS.map(cat => (
                  <label key={cat} className="flex items-center gap-1 text-xs cursor-pointer">
                    <input type="checkbox" checked={form.dataCategories.includes(cat)}
                      onChange={e => setForm(f => ({
                        ...f,
                        dataCategories: e.target.checked ? [...f.dataCategories, cat] : f.dataCategories.filter(c => c !== cat)
                      }))} />
                    {cat}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="ferpa13-fields">Data Fields Accessed (comma-separated)</Label>
              <Input id="ferpa13-fields" value={form.dataFieldsAccessed} onChange={sf("dataFieldsAccessed")} placeholder="StudentId, GPA, EnrollmentStatus" />
            </div>

            <div>
              <Label htmlFor="ferpa13-purpose">Authorized Educational Purpose</Label>
              <Input id="ferpa13-purpose" value={form.authorizedPurpose} onChange={sf("authorizedPurpose")} placeholder="e.g. Course management and grade reporting" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa13-dpa">DPA / Agreement Reference</Label>
                <Input id="ferpa13-dpa" value={form.dpaReference} onChange={sf("dpaReference")} placeholder="DPA-2026-001" />
              </div>
              <div>
                <Label htmlFor="ferpa13-cexp">Contract Expiry</Label>
                <Input id="ferpa13-cexp" type="date" value={form.contractExpiryDate} onChange={sf("contractExpiryDate")} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa13-eff">Effective Date</Label>
                <Input id="ferpa13-eff" type="date" value={form.effectiveDate} onChange={sf("effectiveDate")} />
              </div>
              <div>
                <Label htmlFor="ferpa13-exp">Agreement Expiry</Label>
                <Input id="ferpa13-exp" type="date" value={form.expiryDate} onChange={sf("expiryDate")} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa13-srs">Security Review Status</Label>
                <select id="ferpa13-srs" className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.securityReviewStatus} onChange={sf("securityReviewStatus")}>
                  {["NotStarted","InProgress","Passed","Failed","Waived"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="ferpa13-srn">Security Reviewer</Label>
                <Input id="ferpa13-srn" value={form.securityReviewerName} onChange={sf("securityReviewerName")} placeholder="e.g. CISO Office" />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.dataFlowDocumented}
                  onChange={e => setForm(f => ({ ...f, dataFlowDocumented: e.target.checked }))} />
                Data flow documented
              </label>
            </div>
            {form.dataFlowDocumented && (
              <div>
                <Label htmlFor="ferpa13-dfn">Data Flow Notes</Label>
                <Textarea id="ferpa13-dfn" value={form.dataFlowNotes} onChange={sf("dataFlowNotes")} rows={2}
                  placeholder="Source system → destination, fields shared, authorization basis" />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Register"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Access Events Log ──────────────────────────────────────────────────────────

function AccessEventsSection({ canManage: _canManage }: { canManage: boolean }) {
  const { data, isLoading } = useQuery<PagedResult<AccessEvent>>({
    queryKey: ["/api/organization/compliance/ferpa/access-events"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/access-events?pageSize=30", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load access events");
      return res.json();
    },
  });

  const outcomeIcon = (outcome: string) =>
    outcome === "Success" ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> :
    outcome === "Denied"  ? <XCircle className="w-3.5 h-3.5 text-red-500" /> :
    <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4" /> Student Record Access Log
        </CardTitle>
        <CardDescription className="text-xs">Immutable audit trail of all student record access within the platform</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : !data?.items.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">No access events recorded.</p>
        ) : (
          <div className="divide-y text-sm">
            {data.items.map(e => (
              <div key={e.id} className="py-2 flex items-center gap-3">
                {outcomeIcon(e.outcome)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{e.action}</span>
                    <Badge variant="outline" className="text-xs">{e.recordType}</Badge>
                    <span className="text-muted-foreground text-xs">{e.source}</span>
                  </div>
                  <div className="text-muted-foreground text-xs mt-0.5">
                    {e.studentId && <span>Student: <span className="font-mono">{e.studentId}</span> · </span>}
                    {fmtDateTime(e.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Approved Data Sharing Governance Records ──────────────────────

type FerpaDisclosureGovernance = {
  id: string;
  organizationId: string;
  disclosureName: string;
  sharingPurpose: string | null;
  dataRecipient: string | null;
  recipientOrganization: string | null;
  recipientType: string | null;
  educationRecordCategories: string | null;
  piiCategories: string | null;
  dataFieldsShared: string | null;
  legalBasis: string | null;
  ferpaException: string | null;
  consentRequired: boolean;
  consentReference: string | null;
  approvalRequired: boolean;
  approvalStatus: string;
  approvedBy: string | null;
  approvalDate: string | null;
  approvalNotes: string | null;
  rejectionReason: string | null;
  dataSharingAgreementRef: string | null;
  contractReference: string | null;
  dataOwner: string | null;
  department: string | null;
  reviewStatus: string;
  reviewerName: string | null;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

// ─────────────────────────────────────────────────────────────────────────────
// Browser Cache and Session Storage Protection
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa35Summary = {
  totalApplications:     number;
  protectedApplications: number;
  educationRecordApps:   number;
  storageRisks:          number;
  cacheRisks:            number;
  pendingReviews:        number;
};

type FerpaBrowserStorageControl = {
  id: string;
  organizationId: string;
  applicationName: string;
  moduleName: string | null;
  environment: string | null;
  educationRecordsHandled: boolean;
  localStorageUsageDetected: boolean;
  sessionStorageUsageDetected: boolean;
  indexedDbUsageDetected: boolean;
  browserCachePersistenceDetected: boolean;
  sensitiveCookieStorageDetected: boolean;
  storageEncryptionImplemented: boolean;
  clientSideDataMinimizationEnabled: boolean;
  cacheControlHeadersConfigured: boolean;
  evidenceReference: string | null;
  owner: string | null;
  notes: string | null;
  complianceStatus: string;
  remediationNotes: string | null;
  reviewer: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
  isProtected: boolean;
};

const FERPA35_RISK_LEVELS    = ["Low", "Medium", "High", "Critical"];
const FERPA35_STATUSES       = ["Draft", "Active", "UnderReview", "Approved", "Archived"];
const FERPA35_COMPLIANCE_STATUSES = ["Pending", "Compliant", "NonCompliant", "UnderReview"];
const FERPA35_ENVIRONMENTS   = ["Development", "Staging", "Production", "All"];

const FERPA35_CODE_LABELS: Record<string, string> = {
  EDUCATION_RECORD_STORAGE_DETECTED:    "Education records found in browser storage",
  LOCAL_STORAGE_USAGE_RISK:             "localStorage usage detected",
  SESSION_STORAGE_USAGE_RISK:           "sessionStorage usage detected",
  INDEXEDDB_STORAGE_RISK:               "IndexedDB usage detected",
  BROWSER_CACHE_ENABLED:                "Browser cache persistence detected",
  SENSITIVE_COOKIE_STORAGE_RISK:        "Sensitive data in client-accessible cookies",
  CACHE_CONTROL_CONFIGURATION_REQUIRED: "Cache-Control headers not configured",
  DATA_MINIMIZATION_REQUIRED:           "Client-side data minimization not enabled",
  EVIDENCE_REFERENCE_REQUIRED:          "Evidence reference is missing",
  REVIEW_OVERDUE:                       "Review is overdue",
};

function ferpa35RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa35EnvBadge(env: string | null) {
  if (!env) return null;
  const c =
    env === "Production"  ? "bg-red-100 text-red-700" :
    env === "Staging"     ? "bg-amber-100 text-amber-700" :
    env === "Development" ? "bg-blue-100 text-blue-700" :
                            "bg-gray-100 text-gray-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{env}</span>;
}

function useAuth35() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa35AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaBrowserStorageControl | null; onClose: () => void }) {
  const { header } = useAuth35();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/browser-storage-protection";

  const blank = {
    applicationName: "", moduleName: "", environment: "Production",
    educationRecordsHandled: false,
    localStorageUsageDetected: false, sessionStorageUsageDetected: false,
    indexedDbUsageDetected: false, browserCachePersistenceDetected: false,
    sensitiveCookieStorageDetected: false,
    storageEncryptionImplemented: false, clientSideDataMinimizationEnabled: false,
    cacheControlHeadersConfigured: false,
    evidenceReference: "", owner: "", notes: "",
    complianceStatus: "Pending", riskLevel: "High", status: "Draft", nextReviewDate: "",
  };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  useState(() => {
    if (initial) setForm({
      applicationName:                  initial.applicationName,
      moduleName:                       initial.moduleName ?? "",
      environment:                      initial.environment ?? "Production",
      educationRecordsHandled:          initial.educationRecordsHandled,
      localStorageUsageDetected:        initial.localStorageUsageDetected,
      sessionStorageUsageDetected:      initial.sessionStorageUsageDetected,
      indexedDbUsageDetected:           initial.indexedDbUsageDetected,
      browserCachePersistenceDetected:  initial.browserCachePersistenceDetected,
      sensitiveCookieStorageDetected:   initial.sensitiveCookieStorageDetected,
      storageEncryptionImplemented:     initial.storageEncryptionImplemented,
      clientSideDataMinimizationEnabled:initial.clientSideDataMinimizationEnabled,
      cacheControlHeadersConfigured:    initial.cacheControlHeadersConfigured,
      evidenceReference:                initial.evidenceReference ?? "",
      owner:                            initial.owner ?? "",
      notes:                            initial.notes ?? "",
      complianceStatus:                 initial.complianceStatus,
      riskLevel:                        initial.riskLevel,
      status:                           initial.status,
      nextReviewDate:                   initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "applicationName", form.applicationName, "Application Name");
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    setErrors({});
    const body = {
      applicationName:                   form.applicationName.trim(),
      moduleName:                        form.moduleName || null,
      environment:                       form.environment || null,
      educationRecordsHandled:           form.educationRecordsHandled,
      localStorageUsageDetected:         form.localStorageUsageDetected,
      sessionStorageUsageDetected:       form.sessionStorageUsageDetected,
      indexedDbUsageDetected:            form.indexedDbUsageDetected,
      browserCachePersistenceDetected:   form.browserCachePersistenceDetected,
      sensitiveCookieStorageDetected:    form.sensitiveCookieStorageDetected,
      storageEncryptionImplemented:      form.storageEncryptionImplemented,
      clientSideDataMinimizationEnabled: form.clientSideDataMinimizationEnabled,
      cacheControlHeadersConfigured:     form.cacheControlHeadersConfigured,
      evidenceReference:                 form.evidenceReference || null,
      owner:                             form.owner || null,
      notes:                             form.notes || null,
      complianceStatus:                  form.complianceStatus,
      riskLevel:                         form.riskLevel,
      status:                            form.status,
      nextReviewDate:                    form.nextReviewDate || null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) {
      let error: unknown;
      try { error = await res.json(); } catch { /* response has no JSON error body */ }
      const serverErrs = extractServerErrors(error);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", variant: "destructive" });
      }
      return;
    }
    toast({ title: initial ? "Browser storage control updated." : "Browser storage control added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;
  const boolRow = (key: string, label: string) => (
    <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
      <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => setForm(p => ({ ...p, [key]: !p[key as keyof typeof p] }))} className="rounded" />
      {label}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit Browser Storage Control" : "Add Browser Storage Control"}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          {/* Application info */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Application Information</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium">Application Name *</label>
                <Input className={fieldCls("", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={form.applicationName} onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="Student Portal" />
                {fErr(errors, "applicationName")}</div>
              <div><label className="text-xs font-medium">Module Name</label>
                <Input value={form.moduleName} onChange={e => set("moduleName", e.target.value)} placeholder="Profile Module" /></div>
              <div><label className="text-xs font-medium">Environment</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.environment} onChange={e => set("environment", e.target.value)}>
                  {FERPA35_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.educationRecordsHandled} onChange={() => setForm(p => ({ ...p, educationRecordsHandled: !p.educationRecordsHandled }))} className="rounded" />
                  Handles Education Records
                </label>
              </div>
            </div>
          </div>

          {/* Storage detection */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Storage Detection</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {boolRow("localStorageUsageDetected",       "Local Storage Used")}
              {boolRow("sessionStorageUsageDetected",     "Session Storage Used")}
              {boolRow("indexedDbUsageDetected",          "IndexedDB Used")}
              {boolRow("browserCachePersistenceDetected", "Browser Cache Active")}
              {boolRow("sensitiveCookieStorageDetected",  "Sensitive Cookies")}
            </div>
          </div>

          {/* Protection controls */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Protection Controls</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {boolRow("storageEncryptionImplemented",      "Storage Encryption")}
              {boolRow("clientSideDataMinimizationEnabled", "Data Minimization")}
              {boolRow("cacheControlHeadersConfigured",     "Cache-Control Headers")}
            </div>
          </div>

          {/* Evidence & review */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Evidence & Review</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium">Evidence Reference</label>
                <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://browser-storage-2026.pdf" /></div>
              <div><label className="text-xs font-medium">Owner</label>
                <Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="IT Security Team" /></div>
              <div><label className="text-xs font-medium">Risk Level</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                  {FERPA35_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium">Status</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                  {FERPA35_STATUSES.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium">Compliance Status</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.complianceStatus} onChange={e => set("complianceStatus", e.target.value)}>
                  {FERPA35_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium">Next Review Date</label>
                <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
              <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
                <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} /></div>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add Control"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function Ferpa35ValidateDialog({ control, onClose }: { control: FerpaBrowserStorageControl | null; onClose: () => void }) {
  const { header } = useAuth35();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/browser-storage-protection";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string; remediationSuggestions: string[] } | null>(null);

  async function handleValidate() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
          <span className="text-gray-500">Module</span><span>{control.moduleName ?? "—"}</span>
          <span className="text-gray-500">Environment</span><span>{control.environment ?? "—"}</span>
          <span className="text-gray-500">Ed. Records</span><span className={control.educationRecordsHandled ? "text-orange-600 font-medium" : "text-gray-500"}>{control.educationRecordsHandled ? "Yes — handles records" : "No"}</span>
          {([
            ["localStorage",    control.localStorageUsageDetected],
            ["sessionStorage",  control.sessionStorageUsageDetected],
            ["IndexedDB",       control.indexedDbUsageDetected],
            ["Browser Cache",   control.browserCachePersistenceDetected],
            ["Cookies",         control.sensitiveCookieStorageDetected],
            ["Encryption",      control.storageEncryptionImplemented],
            ["Minimization",    control.clientSideDataMinimizationEnabled],
            ["Cache Headers",   control.cacheControlHeadersConfigured],
          ] as [string, boolean][]).map(([lbl, v]) => (
            <>
              <span key={`l-${lbl}`} className="text-gray-500">{lbl}</span>
              <span key={`v-${lbl}`} className={v ? "text-green-700" : "text-red-600"}>{v ? "✓" : "✗"}</span>
            </>
          ))}
        </div>
        {result && (
          <div className={`rounded p-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ Configuration is compliant." : "✗ Issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA35_CODE_LABELS[c] ?? c}</p>)}
            {result.remediationSuggestions?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-600">Remediation:</p>
                {result.remediationSuggestions.map((s, i) => <p key={i} className="text-xs text-gray-600 mt-0.5">→ {s}</p>)}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2">Validated {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Storage-test (simulation) dialog ─────────────────────────────────────────
function Ferpa35StorageTestDialog({ control, onClose }: { control: FerpaBrowserStorageControl | null; onClose: () => void }) {
  const { header } = useAuth35();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/browser-storage-protection";
  const [flags, setFlags] = useState({
    localStorageDetected:   false,
    sessionStorageDetected: false,
    indexedDbDetected:      false,
    cacheDetected:          false,
    cookiesDetected:        false,
  });
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState<{
    passed: boolean; issues: string[];
    localStorageDetected: boolean; sessionStorageDetected: boolean;
    indexedDbDetected: boolean; cacheDetected: boolean; cookiesDetected: boolean;
    encryptionEnabled: boolean; cacheHeadersConfigured: boolean; minimizationEnabled: boolean;
    testedAt: string; note: string;
  } | null>(null);

  async function handleTest() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/storage-test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ ...flags, notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to complete test", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  const toggle = (k: keyof typeof flags) => setFlags(p => ({ ...p, [k]: !p[k] }));

  const PRESETS = [
    { label: "All clear",             flags: { localStorageDetected: false, sessionStorageDetected: false, indexedDbDetected: false, cacheDetected: false, cookiesDetected: false } },
    { label: "localStorage only",     flags: { localStorageDetected: true,  sessionStorageDetected: false, indexedDbDetected: false, cacheDetected: false, cookiesDetected: false } },
    { label: "localStorage + cache",  flags: { localStorageDetected: true,  sessionStorageDetected: false, indexedDbDetected: false, cacheDetected: true,  cookiesDetected: false } },
    { label: "All storage detected",  flags: { localStorageDetected: true,  sessionStorageDetected: true,  indexedDbDetected: true,  cacheDetected: true,  cookiesDetected: true  } },
  ];

  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Storage Test — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-800">
          ℹ Simulation only — no real browser data inspected. No student data accessed.
        </div>
        {/* Presets */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {PRESETS.map(p => (
            <button key={p.label} onClick={() => setFlags(p.flags)}
              className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50">
              {p.label}
            </button>
          ))}
        </div>
        {/* Toggle flags */}
        <div className="grid grid-cols-2 gap-2 mb-3 sm:grid-cols-3">
          {([
            ["localStorageDetected",   "localStorage"],
            ["sessionStorageDetected", "sessionStorage"],
            ["indexedDbDetected",      "IndexedDB"],
            ["cacheDetected",          "Browser Cache"],
            ["cookiesDetected",        "Cookies"],
          ] as [keyof typeof flags, string][]).map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={flags[k]} onChange={() => toggle(k)} className="rounded" />
              {lbl}
            </label>
          ))}
        </div>
        <div><label className="text-xs font-medium">Notes (optional)</label>
          <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Purpose of this test" /></div>
        {result && (
          <div className={`rounded p-3 mt-3 ${result.passed ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className={`text-sm font-bold mb-2 ${result.passed ? "text-green-700" : "text-red-700"}`}>
              {result.passed ? "✓ No storage risks detected." : `✗ ${result.issues.length} risk${result.issues.length > 1 ? "s" : ""} detected:`}
            </p>
            {result.issues.map(c => <p key={c} className="text-xs text-red-600">• {FERPA35_CODE_LABELS[c] ?? c}</p>)}
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mt-2">
              <span className="text-gray-500">Encryption</span><span className={result.encryptionEnabled ? "text-green-700" : "text-red-600"}>{result.encryptionEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
              <span className="text-gray-500">Cache headers</span><span className={result.cacheHeadersConfigured ? "text-green-700" : "text-red-600"}>{result.cacheHeadersConfigured ? "✓ Configured" : "✗ Missing"}</span>
              <span className="text-gray-500">Minimization</span><span className={result.minimizationEnabled ? "text-green-700" : "text-red-600"}>{result.minimizationEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
            </div>
            <p className="text-xs text-gray-400 italic mt-2">{result.note}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleTest}>Run Storage Test</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa35ReviewDialog({ control, onClose }: { control: FerpaBrowserStorageControl | null; onClose: () => void }) {
  const { header } = useAuth35();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/browser-storage-protection";
  const [reviewer, setReviewer]         = useState(control?.reviewer ?? "");
  const [reviewNotes, setReviewNotes]   = useState("");
  const [nextReviewDate, setNext]       = useState("");
  const [errors, setErrors]             = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!control) return;
    const errs: Record<string, string> = {};
    required(errs, "reviewer", reviewer, "Reviewer");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const res = await fetch(`${BASE}/${control.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Complete Review — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer *</label><Input value={reviewer} onChange={e => { setReviewer(e.target.value); clearErr(setErrors, "reviewer"); }} className={fieldCls("", errors, "reviewer")} aria-invalid={!!errors.reviewer} />{fErr(errors, "reviewer")}</div>
          <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={nextReviewDate} onChange={e => setNext(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa35DeleteDialog({ control, onClose }: { control: FerpaBrowserStorageControl | null; onClose: () => void }) {
  const { header } = useAuth35();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/browser-storage-protection";

  async function handleDelete() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "Browser storage control removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove Browser Storage Control</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{control.applicationName}</strong>{control.moduleName ? ` (${control.moduleName})` : ""}? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa35DetailDialog({ control, canManage, onClose, onEdit, onValidate, onStorageTest, onReview, onDelete }: {
  control: FerpaBrowserStorageControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (c: FerpaBrowserStorageControl) => void;
  onValidate: (c: FerpaBrowserStorageControl) => void;
  onStorageTest: (c: FerpaBrowserStorageControl) => void;
  onReview: (c: FerpaBrowserStorageControl) => void;
  onDelete: (c: FerpaBrowserStorageControl) => void;
}) {
  if (!control) return null;
  const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Shield className="w-4 h-4 text-blue-600" />
            {control.applicationName}
            {ferpa35EnvBadge(control.environment)}
            {ferpa35RiskBadge(control.riskLevel)}
            {control.educationRecordsHandled && <span className="inline-flex px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700 font-medium">Ed. Records</span>}
            {control.isProtected && <span className="inline-flex px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-medium">✓ Protected</span>}
          </DialogTitle>
          {control.moduleName && <p className="text-xs text-gray-500 mt-0.5">{control.moduleName}</p>}
        </DialogHeader>
        {control.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Compliance Issues</p>
            {control.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA35_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isOverdue && <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3"><p className="text-xs text-orange-700">⚠ Review overdue.</p></div>}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Storage Detection</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {([
                ["localStorage",   control.localStorageUsageDetected],
                ["sessionStorage", control.sessionStorageUsageDetected],
                ["IndexedDB",      control.indexedDbUsageDetected],
                ["Browser Cache",  control.browserCachePersistenceDetected],
                ["Cookies",        control.sensitiveCookieStorageDetected],
              ] as [string, boolean][]).map(([lbl, v]) => (
                <>
                  <span key={`sl${lbl}`} className="text-gray-500">{lbl}</span>
                  <span key={`sv${lbl}`} className={v ? "text-red-600" : "text-green-700"}>{v ? "✗ Detected" : "✓ Not detected"}</span>
                </>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Protection Controls</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {([
                ["Storage Encryption", control.storageEncryptionImplemented],
                ["Data Minimization",  control.clientSideDataMinimizationEnabled],
                ["Cache Headers",      control.cacheControlHeadersConfigured],
              ] as [string, boolean][]).map(([lbl, v]) => (
                <>
                  <span key={`pl${lbl}`} className="text-gray-500">{lbl}</span>
                  <span key={`pv${lbl}`} className={v ? "text-green-700" : "text-red-600"}>{v ? "✓ Enabled" : "✗ Disabled"}</span>
                </>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence & Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Evidence</span>
              <span className={control.evidenceReference ? "text-green-700 truncate" : "text-red-500 italic font-sans"}>{control.evidenceReference ?? "Missing"}</span>
              <span className="text-gray-500">Owner</span><span>{control.owner ?? "—"}</span>
              <span className="text-gray-500">Reviewer</span><span>{control.reviewer ?? "—"}</span>
              <span className="text-gray-500">Last Review</span><span>{control.lastReviewDate ? new Date(control.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{control.nextReviewDate ? new Date(control.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(control)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(control)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onStorageTest(control)}>Storage Test</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(control)}>Review</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(control)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaBrowserStorageProtectionSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth35();
  const BASE       = "/api/organization/compliance/ferpa/browser-storage-protection";

  const [appFilter,    setAppFilter]    = useState("");
  const [modFilter,    setModFilter]    = useState("");
  const [envFilter,    setEnvFilter]    = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [riskFilter,   setRiskFilter]   = useState("");
  const [compFilter,   setCompFilter]   = useState("");

  const [showAdd,       setShowAdd]       = useState(false);
  const [editItem,      setEditItem]      = useState<FerpaBrowserStorageControl | null>(null);
  const [validateItem,  setValidateItem]  = useState<FerpaBrowserStorageControl | null>(null);
  const [storageTestItem, setStorageTestItem] = useState<FerpaBrowserStorageControl | null>(null);
  const [reviewItem,    setReviewItem]    = useState<FerpaBrowserStorageControl | null>(null);
  const [deleteItem,    setDeleteItem]    = useState<FerpaBrowserStorageControl | null>(null);
  const [detailItem,    setDetailItem]    = useState<FerpaBrowserStorageControl | null>(null);

  const summaryQ = useQuery<Ferpa35Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => { const r = await fetch(`${BASE}/summary`, { headers: header }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  });

  const issuesQ = useQuery<{ control: FerpaBrowserStorageControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => { const r = await fetch(`${BASE}/issues`, { headers: header }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (appFilter)    params.set("applicationName", appFilter);
  if (modFilter)    params.set("moduleName",       modFilter);
  if (envFilter)    params.set("environment",      envFilter);
  if (statusFilter) params.set("status",           statusFilter);
  if (riskFilter)   params.set("riskLevel",        riskFilter);
  if (compFilter)   params.set("complianceStatus", compFilter);

  const listQ = useQuery<{ total: number; items: FerpaBrowserStorageControl[] }>({
    queryKey: [BASE, appFilter, modFilter, envFilter, statusFilter, riskFilter, compFilter],
    queryFn: async () => { const r = await fetch(`${BASE}?${params}`, { headers: header }); if (!r.ok) throw new Error("Failed"); return r.json(); },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Applications",     value: s.totalApplications,     icon: <Shield className="w-4 h-4 text-blue-500" />,        highlight: false },
    { label: "Protected",              value: s.protectedApplications, icon: <ShieldCheck className="w-4 h-4 text-green-500" />,  highlight: false },
    { label: "Ed. Record Apps",        value: s.educationRecordApps,   icon: <GraduationCap className="w-4 h-4 text-orange-500" />, highlight: s.educationRecordApps > 0 },
    { label: "Storage Risks",          value: s.storageRisks,          icon: <AlertTriangle className="w-4 h-4 text-red-500" />,  highlight: s.storageRisks > 0 },
    { label: "Cache Risks",            value: s.cacheRisks,            icon: <AlertTriangle className="w-4 h-4 text-orange-500" />, highlight: s.cacheRisks > 0 },
    { label: "Pending Reviews",        value: s.pendingReviews,        icon: <Clock className="w-4 h-4 text-yellow-500" />,       highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="w-4 h-4 text-blue-600" />
              Browser Cache and Session Storage Protection
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks storage protection controls for education record applications. No actual student data stored.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Application
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-2.5 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate leading-tight">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Storage Risks ({issuesQ.data!.length} applications)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { control: FerpaBrowserStorageControl; codes: string[] }) => (
                <div key={item.control.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.control.applicationName}</span>
                  {item.control.moduleName && <span className="text-gray-500">{item.control.moduleName}</span>}
                  {ferpa35RiskBadge(item.control.riskLevel)}
                  <span className="text-red-600">{item.codes.slice(0, 2).map(c => FERPA35_CODE_LABELS[c] ?? c).join("; ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Input placeholder="Filter by app…"    value={appFilter}    onChange={e => setAppFilter(e.target.value)}    className="text-xs h-8" />
          <Input placeholder="Filter by module…" value={modFilter}    onChange={e => setModFilter(e.target.value)}    className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={envFilter} onChange={e => setEnvFilter(e.target.value)}>
            <option value="">All environments</option>
            {FERPA35_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA35_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA35_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={compFilter} onChange={e => setCompFilter(e.target.value)}>
            <option value="">All compliance</option>
            {FERPA35_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No browser storage controls registered. Add an application to start tracking protection.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(control => {
              const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
              return (
                <div key={control.id} className="border rounded-lg p-3 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setDetailItem(control)}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">{control.applicationName}</span>
                        {ferpa35EnvBadge(control.environment)}
                        {ferpa35RiskBadge(control.riskLevel)}
                        {control.educationRecordsHandled && <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-700">Ed. Records</span>}
                        {control.isProtected && <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">✓ Protected</span>}
                      </div>
                      {control.moduleName && <p className="text-xs text-gray-500 mt-0.5">{control.moduleName}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      {canManage && (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={e => { e.stopPropagation(); setEditItem(control); }}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Review" onClick={e => { e.stopPropagation(); setReviewItem(control); }}>
                            <ClipboardCheck className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Storage checks row */}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs">
                    <span className={control.localStorageUsageDetected    ? "text-red-500" : "text-green-600"}>{control.localStorageUsageDetected    ? "✗ localStorage"  : "✓ localStorage"}</span>
                    <span className={control.sessionStorageUsageDetected  ? "text-red-500" : "text-green-600"}>{control.sessionStorageUsageDetected  ? "✗ sessionStorage" : "✓ sessionStorage"}</span>
                    <span className={control.indexedDbUsageDetected       ? "text-red-500" : "text-green-600"}>{control.indexedDbUsageDetected       ? "✗ IndexedDB"      : "✓ IndexedDB"}</span>
                    <span className={control.browserCachePersistenceDetected ? "text-red-500" : "text-green-600"}>{control.browserCachePersistenceDetected ? "✗ Cache"      : "✓ Cache"}</span>
                    <span className={control.sensitiveCookieStorageDetected  ? "text-red-500" : "text-green-600"}>{control.sensitiveCookieStorageDetected  ? "✗ Cookies"    : "✓ Cookies"}</span>
                    {control.cacheControlHeadersConfigured    && <span className="text-green-600">✓ Cache-Control</span>}
                    {control.clientSideDataMinimizationEnabled && <span className="text-green-600">✓ Minimization</span>}
                    {control.evidenceReference && <span className="text-green-600">✓ Evidence</span>}
                    {isOverdue && <span className="text-orange-600">⚠ Review overdue</span>}
                  </div>
                  {control.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {control.validationCodes.slice(0, 4).map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA35_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                      {control.validationCodes.length > 4 && (
                        <span className="text-xs text-red-400">+{control.validationCodes.length - 4} more</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa35AddDialog open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa35ValidateDialog   control={validateItem}    onClose={() => setValidateItem(null)} />
      <Ferpa35StorageTestDialog control={storageTestItem} onClose={() => setStorageTestItem(null)} />
      <Ferpa35ReviewDialog     control={reviewItem}      onClose={() => setReviewItem(null)} />
      <Ferpa35DeleteDialog     control={deleteItem}      onClose={() => setDeleteItem(null)} />
      <Ferpa35DetailDialog
        control={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={c => { setDetailItem(null); setEditItem(c); }}
        onValidate={c => { setDetailItem(null); setValidateItem(c); }}
        onStorageTest={c => { setDetailItem(null); setStorageTestItem(c); }}
        onReview={c => { setDetailItem(null); setReviewItem(c); }}
        onDelete={c => { setDetailItem(null); setDeleteItem(c); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Prevent Education Records Exposure Through URLs
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa34Summary = {
  totalRoutes: number;
  protectedRoutes: number;
  exposureRisks: number;
  pendingReviews: number;
};

type FerpaUrlDataExposureControl = {
  id: string;
  organizationId: string;
  applicationName: string;
  routePattern: string | null;
  queryParameterPolicy: string | null;
  sensitiveDataInUrlBlocked: boolean;
  identifierMaskingEnabled: boolean;
  tokenBasedNavigationEnabled: boolean;
  loggingUrlSanitizationEnabled: boolean;
  routeReviewCompleted: boolean;
  evidenceReference: string | null;
  owner: string | null;
  notes: string | null;
  complianceStatus: string;
  remediationNotes: string | null;
  reviewer: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
  isProtected: boolean;
};

const FERPA34_QUERY_POLICIES = ["block_sensitive", "token_only", "custom", "allow_all"];
const FERPA34_RISK_LEVELS    = ["Low", "Medium", "High", "Critical"];
const FERPA34_STATUSES       = ["Draft", "Active", "UnderReview", "Approved", "Archived"];
const FERPA34_COMPLIANCE_STATUSES = ["Pending", "Compliant", "NonCompliant", "UnderReview"];

const FERPA34_CODE_LABELS: Record<string, string> = {
  SENSITIVE_DATA_URL_ALLOWED:        "Sensitive data is not blocked in URLs",
  QUERY_PARAMETER_EXPOSURE_RISK:     "Unsafe query parameter policy (allow_all or not set)",
  IDENTIFIER_MASKING_MISSING:        "Identifier masking is not enabled",
  TOKEN_NAVIGATION_MISSING:          "Token-based navigation is not enabled",
  URL_LOG_SANITIZATION_REQUIRED:     "URL logging sanitization is not enabled",
  EVIDENCE_REFERENCE_REQUIRED:       "Evidence reference is missing",
  REVIEW_OVERDUE:                    "Route review is overdue",
};

function ferpa34RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa34PolicyBadge(policy: string | null) {
  if (!policy) return <span className="inline-flex px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">No Policy</span>;
  const c =
    policy === "block_sensitive" ? "bg-green-100 text-green-700" :
    policy === "token_only"      ? "bg-blue-100 text-blue-700" :
    policy === "custom"          ? "bg-amber-100 text-amber-700" :
                                   "bg-red-100 text-red-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{policy}</span>;
}

function useAuth34() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa34AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaUrlDataExposureControl | null; onClose: () => void }) {
  const { header } = useAuth34();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/url-exposure";

  const blankForm = {
    applicationName: "", routePattern: "", queryParameterPolicy: "block_sensitive",
    sensitiveDataInUrlBlocked: false, identifierMaskingEnabled: false,
    tokenBasedNavigationEnabled: false, loggingUrlSanitizationEnabled: false, routeReviewCompleted: false,
    evidenceReference: "", owner: "", notes: "",
    complianceStatus: "Pending", riskLevel: "High", status: "Draft", nextReviewDate: "",
  };

  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  useState(() => {
    if (initial) setForm({
      applicationName:              initial.applicationName,
      routePattern:                 initial.routePattern ?? "",
      queryParameterPolicy:         initial.queryParameterPolicy ?? "block_sensitive",
      sensitiveDataInUrlBlocked:    initial.sensitiveDataInUrlBlocked,
      identifierMaskingEnabled:     initial.identifierMaskingEnabled,
      tokenBasedNavigationEnabled:  initial.tokenBasedNavigationEnabled,
      loggingUrlSanitizationEnabled:initial.loggingUrlSanitizationEnabled,
      routeReviewCompleted:         initial.routeReviewCompleted,
      evidenceReference:            initial.evidenceReference ?? "",
      owner:                        initial.owner ?? "",
      notes:                        initial.notes ?? "",
      complianceStatus:             initial.complianceStatus,
      riskLevel:                    initial.riskLevel,
      status:                       initial.status,
      nextReviewDate:               initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "applicationName", form.applicationName, "Application Name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const body = {
      applicationName:              form.applicationName.trim(),
      routePattern:                 form.routePattern || null,
      queryParameterPolicy:         form.queryParameterPolicy || null,
      sensitiveDataInUrlBlocked:    form.sensitiveDataInUrlBlocked,
      identifierMaskingEnabled:     form.identifierMaskingEnabled,
      tokenBasedNavigationEnabled:  form.tokenBasedNavigationEnabled,
      loggingUrlSanitizationEnabled:form.loggingUrlSanitizationEnabled,
      routeReviewCompleted:         form.routeReviewCompleted,
      evidenceReference:            form.evidenceReference || null,
      owner:                        form.owner || null,
      notes:                        form.notes || null,
      complianceStatus:             form.complianceStatus,
      riskLevel:                    form.riskLevel,
      status:                       form.status,
      nextReviewDate:               form.nextReviewDate || null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Unable to save", variant: "destructive" }); return; }
    toast({ title: initial ? "Route control updated." : "Route control added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit Route Control" : "Add Route Control"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Application Name *</label>
              <Input className={fieldCls("", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={form.applicationName} onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="Student Portal" />{fErr(errors, "applicationName")}</div>
            <div><label className="text-xs font-medium">Route Pattern</label>
              <Input value={form.routePattern} onChange={e => set("routePattern", e.target.value)} placeholder="/student/profile/token/{token}" /></div>
            <div><label className="text-xs font-medium">Query Parameter Policy</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.queryParameterPolicy} onChange={e => set("queryParameterPolicy", e.target.value)}>
                {FERPA34_QUERY_POLICIES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Risk Level</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                {FERPA34_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>
          {/* Security controls */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">URL Security Controls</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {[
                ["sensitiveDataInUrlBlocked",     "Sensitive Data Blocked"],
                ["identifierMaskingEnabled",      "Identifier Masking"],
                ["tokenBasedNavigationEnabled",   "Token-Based Navigation"],
                ["loggingUrlSanitizationEnabled", "Log URL Sanitization"],
                ["routeReviewCompleted",          "Route Review Completed"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => setForm(p => ({ ...p, [key]: !p[key as keyof typeof p] }))} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Evidence Reference</label>
              <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://url-security-config-2026.pdf" /></div>
            <div><label className="text-xs font-medium">Owner</label>
              <Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="IT Security" /></div>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA34_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Compliance Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.complianceStatus} onChange={e => set("complianceStatus", e.target.value)}>
                {FERPA34_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Next Review Date</label>
              <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional notes" /></div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add Route Control"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function Ferpa34ValidateDialog({ control, onClose }: { control: FerpaUrlDataExposureControl | null; onClose: () => void }) {
  const { header } = useAuth34();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/url-exposure";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string } | null>(null);

  async function handleValidate() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate Route — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
          <span className="text-gray-500">Route Pattern</span><span className="font-mono truncate">{control.routePattern ?? "—"}</span>
          <span className="text-gray-500">Query Policy</span><span>{control.queryParameterPolicy ?? "Not set"}</span>
          <span className="text-gray-500">Data Blocked</span><span className={control.sensitiveDataInUrlBlocked ? "text-green-700" : "text-red-600"}>{control.sensitiveDataInUrlBlocked ? "✓ Yes" : "✗ No"}</span>
          <span className="text-gray-500">Masking</span><span className={control.identifierMaskingEnabled ? "text-green-700" : "text-red-600"}>{control.identifierMaskingEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
          <span className="text-gray-500">Token Nav</span><span className={control.tokenBasedNavigationEnabled ? "text-green-700" : "text-red-600"}>{control.tokenBasedNavigationEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
          <span className="text-gray-500">Log Sanitization</span><span className={control.loggingUrlSanitizationEnabled ? "text-green-700" : "text-red-600"}>{control.loggingUrlSanitizationEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
          <span className="text-gray-500">Evidence</span><span className={control.evidenceReference ? "text-green-700" : "text-red-600"}>{control.evidenceReference ? "Present" : "Missing"}</span>
        </div>
        {result && (
          <div className={`rounded p-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ Route control is compliant." : "✗ Issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA34_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">Validated {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Route-test (URL simulation) dialog ────────────────────────────────────────
function Ferpa34RouteTestDialog({ control, onClose }: { control: FerpaUrlDataExposureControl | null; onClose: () => void }) {
  const { header } = useAuth34();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/url-exposure";
  const [testUrl, setTestUrl] = useState("");
  const [notes,   setNotes]   = useState("");
  const EXAMPLES = [
    "/student/profile?id=12345",
    "/student/profile?studentId=99876",
    "/records?ssn=123-45-6789",
    "/student/profile/token/abc123",
    "/student/profile/token/xyz789secure",
  ];
  const [result, setResult] = useState<{
    passed: boolean; result: string; reason: string; category: string;
    configuredPolicy: string | null; tokenNavEnabled: boolean; maskingEnabled: boolean;
    testedAt: string; note: string;
  } | null>(null);

  async function handleTest() {
    if (!control || !testUrl.trim()) { toast({ title: "Enter a URL to test.", variant: "destructive" }); return; }
    const res = await fetch(`${BASE}/${control.id}/route-test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ testUrl: testUrl.trim(), notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to complete test", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Simulate URL Test — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-800">
          ℹ Simulation only — no real routing, user sessions, or student data accessed.
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Test URL *</label>
            <Input value={testUrl} onChange={e => setTestUrl(e.target.value)} placeholder="/student/profile?id=12345" className="font-mono text-sm" />
            <div className="flex flex-wrap gap-1 mt-1.5">
              <span className="text-xs text-gray-400 mr-1">Examples:</span>
              {EXAMPLES.map(ex => (
                <button key={ex} onClick={() => setTestUrl(ex)}
                  className="text-xs px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 font-mono truncate max-w-[180px]">
                  {ex}
                </button>
              ))}
            </div>
          </div>
          <div><label className="text-xs font-medium">Notes (optional)</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Purpose of this test" /></div>
        </div>
        {result && (
          <div className={`rounded p-3 mt-3 ${result.passed ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-sm font-bold ${result.passed ? "text-green-700" : "text-red-700"}`}>
                {result.passed ? "✓ Pass" : "✗ Fail"}
              </span>
              <span className="text-xs px-1.5 py-0.5 rounded bg-white border font-mono">{result.category}</span>
            </div>
            <p className="text-xs text-gray-700 mb-2">{result.reason}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
              <span className="text-gray-500">Query policy</span><span>{result.configuredPolicy ?? "Not set"}</span>
              <span className="text-gray-500">Token nav</span><span className={result.tokenNavEnabled ? "text-green-700" : "text-red-600"}>{result.tokenNavEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
              <span className="text-gray-500">Masking</span><span className={result.maskingEnabled ? "text-green-700" : "text-red-600"}>{result.maskingEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
            </div>
            <p className="text-xs text-gray-400 italic mt-2">{result.note}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleTest}>Run Test</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa34ReviewDialog({ control, onClose }: { control: FerpaUrlDataExposureControl | null; onClose: () => void }) {
  const { header } = useAuth34();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/url-exposure";
  const [reviewer,       setReviewer]       = useState(control?.reviewer ?? "");
  const [reviewNotes,    setReviewNotes]    = useState("");
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [errors,         setErrors]         = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!control) return;
    const errs: Record<string, string> = {};
    required(errs, "reviewer", reviewer, "Reviewer name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const res = await fetch(`${BASE}/${control.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Complete Review — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer Name *</label>
            <Input value={reviewer} onChange={e => { setReviewer(e.target.value); clearErr(setErrors, "reviewer"); }} className={fieldCls("", errors, "reviewer")} aria-invalid={!!errors.reviewer} placeholder="Compliance Officer" />{fErr(errors, "reviewer")}</div>
          <div><label className="text-xs font-medium">Next Review Date</label>
            <Input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional review notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa34DeleteDialog({ control, onClose }: { control: FerpaUrlDataExposureControl | null; onClose: () => void }) {
  const { header } = useAuth34();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/url-exposure";

  async function handleDelete() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "Route control removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove Route Control</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{control.applicationName}</strong>{control.routePattern ? <> (<code className="text-xs">{control.routePattern}</code>)</> : ""}? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa34DetailDialog({ control, canManage, onClose, onEdit, onValidate, onRouteTest, onReview, onDelete }: {
  control: FerpaUrlDataExposureControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (c: FerpaUrlDataExposureControl) => void;
  onValidate: (c: FerpaUrlDataExposureControl) => void;
  onRouteTest: (c: FerpaUrlDataExposureControl) => void;
  onReview: (c: FerpaUrlDataExposureControl) => void;
  onDelete: (c: FerpaUrlDataExposureControl) => void;
}) {
  if (!control) return null;
  const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Globe className="w-4 h-4 text-blue-600" />
            {control.applicationName}
            {ferpa34PolicyBadge(control.queryParameterPolicy)}
            {ferpa34RiskBadge(control.riskLevel)}
            {control.isProtected && <span className="inline-flex px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-medium">✓ Protected</span>}
          </DialogTitle>
          {control.routePattern && <p className="text-xs text-gray-500 font-mono mt-1">{control.routePattern}</p>}
        </DialogHeader>
        {control.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Compliance Issues</p>
            {control.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA34_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isOverdue && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Review overdue — was due {new Date(control.nextReviewDate!).toLocaleDateString()}.</p>
          </div>
        )}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">URL Security Controls</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {[
                ["Sensitive Data Blocked", control.sensitiveDataInUrlBlocked],
                ["Identifier Masking",     control.identifierMaskingEnabled],
                ["Token Navigation",       control.tokenBasedNavigationEnabled],
                ["Log Sanitization",       control.loggingUrlSanitizationEnabled],
                ["Route Review Done",      control.routeReviewCompleted],
              ].map(([label, val]) => (
                <>
                  <span key={`l-${label}`} className="text-gray-500">{label as string}</span>
                  <span key={`v-${label}`} className={val ? "text-green-700" : "text-red-600"}>{val ? "✓ Yes" : "✗ No"}</span>
                </>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence & Ownership</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Evidence Ref</span>
              <span className={control.evidenceReference ? "text-green-700 font-mono truncate" : "text-red-500 italic font-sans"}>{control.evidenceReference ?? "Missing"}</span>
              <span className="text-gray-500">Owner</span><span>{control.owner ?? "—"}</span>
              <span className="text-gray-500">Compliance</span><span>{control.complianceStatus}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Reviewer</span><span>{control.reviewer ?? "—"}</span>
              <span className="text-gray-500">Review Status</span><span>{control.reviewStatus}</span>
              <span className="text-gray-500">Last Review</span><span>{control.lastReviewDate ? new Date(control.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{control.nextReviewDate ? new Date(control.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(control)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(control)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onRouteTest(control)}>Route Test</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(control)}>Review</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(control)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaUrlProtectionSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth34();
  const BASE       = "/api/organization/compliance/ferpa/url-exposure";

  const [appFilter,    setAppFilter]    = useState("");
  const [routeFilter,  setRouteFilter]  = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [riskFilter,   setRiskFilter]   = useState("");

  const [showAdd,      setShowAdd]      = useState(false);
  const [editItem,     setEditItem]     = useState<FerpaUrlDataExposureControl | null>(null);
  const [validateItem, setValidateItem] = useState<FerpaUrlDataExposureControl | null>(null);
  const [routeTestItem,setRouteTestItem]= useState<FerpaUrlDataExposureControl | null>(null);
  const [reviewItem,   setReviewItem]   = useState<FerpaUrlDataExposureControl | null>(null);
  const [deleteItem,   setDeleteItem]   = useState<FerpaUrlDataExposureControl | null>(null);
  const [detailItem,   setDetailItem]   = useState<FerpaUrlDataExposureControl | null>(null);

  const summaryQ = useQuery<Ferpa34Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ control: FerpaUrlDataExposureControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (appFilter)    params.set("applicationName", appFilter);
  if (routeFilter)  params.set("routePattern",    routeFilter);
  if (statusFilter) params.set("status",          statusFilter);
  if (riskFilter)   params.set("riskLevel",       riskFilter);

  const listQ = useQuery<{ total: number; items: FerpaUrlDataExposureControl[] }>({
    queryKey: [BASE, appFilter, routeFilter, statusFilter, riskFilter],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Routes",     value: s.totalRoutes,     icon: <Globe className="w-4 h-4 text-blue-500" />,         highlight: false },
    { label: "Protected Routes", value: s.protectedRoutes, icon: <Shield className="w-4 h-4 text-green-500" />,       highlight: false },
    { label: "Exposure Risks",   value: s.exposureRisks,   icon: <AlertTriangle className="w-4 h-4 text-red-500" />,  highlight: s.exposureRisks > 0 },
    { label: "Pending Reviews",  value: s.pendingReviews,  icon: <Clock className="w-4 h-4 text-orange-500" />,       highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe className="w-4 h-4 text-blue-600" />
              Prevent Education Records Exposure Through URLs
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks URL security controls preventing education record exposure. No actual student information stored.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Route
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Exposure Risks ({issuesQ.data!.length} routes)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { control: FerpaUrlDataExposureControl; codes: string[] }) => (
                <div key={item.control.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.control.applicationName}</span>
                  {item.control.routePattern && <code className="text-gray-500 text-xs truncate max-w-[160px]">{item.control.routePattern}</code>}
                  {ferpa34RiskBadge(item.control.riskLevel)}
                  <span className="text-red-600">{item.codes.map(c => FERPA34_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Input placeholder="Filter by app…"   value={appFilter}    onChange={e => setAppFilter(e.target.value)}    className="text-xs h-8" />
          <Input placeholder="Filter by route…" value={routeFilter}  onChange={e => setRouteFilter(e.target.value)}  className="text-xs h-8 font-mono" />
          <select className="border rounded px-2 text-xs h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA34_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA34_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No route controls registered. Add a route to start tracking URL exposure prevention.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(control => {
              const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
              return (
                <div
                  key={control.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(control)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-800">{control.applicationName}</span>
                        {ferpa34PolicyBadge(control.queryParameterPolicy)}
                        {ferpa34RiskBadge(control.riskLevel)}
                        {control.isProtected && <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">✓ Protected</span>}
                      </div>
                      {control.routePattern && <code className="text-xs text-gray-500 mt-0.5 block">{control.routePattern}</code>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs">
                    <span className={control.sensitiveDataInUrlBlocked ? "text-green-600" : "text-red-500"}>{control.sensitiveDataInUrlBlocked ? "✓ Data blocked" : "✗ Data in URL"}</span>
                    <span className={control.identifierMaskingEnabled ? "text-green-600" : "text-red-500"}>{control.identifierMaskingEnabled ? "✓ Masking" : "✗ No masking"}</span>
                    <span className={control.tokenBasedNavigationEnabled ? "text-green-600" : "text-red-500"}>{control.tokenBasedNavigationEnabled ? "✓ Token nav" : "✗ No token nav"}</span>
                    <span className={control.loggingUrlSanitizationEnabled ? "text-green-600" : "text-orange-500"}>{control.loggingUrlSanitizationEnabled ? "✓ Log sanitized" : "✗ Logs unsanitized"}</span>
                    {control.evidenceReference && <span className="text-green-600">✓ Evidence</span>}
                    {isOverdue && <span className="text-orange-600">⚠ Review overdue</span>}
                  </div>
                  {control.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {control.validationCodes.map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA34_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa34AddDialog    open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa34ValidateDialog  control={validateItem}  onClose={() => setValidateItem(null)} />
      <Ferpa34RouteTestDialog control={routeTestItem} onClose={() => setRouteTestItem(null)} />
      <Ferpa34ReviewDialog    control={reviewItem}    onClose={() => setReviewItem(null)} />
      <Ferpa34DeleteDialog    control={deleteItem}    onClose={() => setDeleteItem(null)} />
      <Ferpa34DetailDialog
        control={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={c => { setDetailItem(null); setEditItem(c); }}
        onValidate={c => { setDetailItem(null); setValidateItem(c); }}
        onRouteTest={c => { setDetailItem(null); setRouteTestItem(c); }}
        onReview={c => { setDetailItem(null); setReviewItem(c); }}
        onDelete={c => { setDetailItem(null); setDeleteItem(c); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Role-Sensitive UI Rendering
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa33Summary = {
  totalControls: number;
  uiRestrictionsEnabled: number;
  backendAuthorizationEnabled: number;
  sensitiveDataHidden: number;
  nonCompliant: number;
  pendingReviews: number;
};

type FerpaRoleBasedUiControl = {
  id: string;
  organizationId: string;
  applicationName: string;
  moduleName: string | null;
  userRole: string | null;
  allowedFeatures: string | null;
  restrictedFeatures: string | null;
  permissionModel: string | null;
  uiRestrictionEnabled: boolean;
  backendAuthorizationEnabled: boolean;
  sensitiveDataHidden: boolean;
  evidenceReference: string | null;
  owner: string | null;
  notes: string | null;
  complianceStatus: string;
  remediationNotes: string | null;
  lastRemediationDate: string | null;
  reviewer: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

const FERPA33_PERMISSION_MODELS = ["RBAC", "ABAC", "Custom", "None"];
const FERPA33_RISK_LEVELS        = ["Low", "Medium", "High", "Critical"];
const FERPA33_STATUSES           = ["Draft", "Active", "UnderReview", "Approved", "Archived"];
const FERPA33_COMPLIANCE_STATUSES= ["Pending", "Compliant", "NonCompliant", "UnderReview"];

const FERPA33_CODE_LABELS: Record<string, string> = {
  ROLE_PERMISSION_MAPPING_MISSING:  "Role has no permission mapping configured",
  UI_RESTRICTION_DISABLED:          "UI restriction is not enabled",
  BACKEND_AUTHORIZATION_MISSING:    "Backend authorization is not enabled",
  SENSITIVE_DATA_VISIBILITY_RISK:   "Sensitive data is not hidden from this role",
  EVIDENCE_REFERENCE_REQUIRED:      "Evidence reference is missing",
  REVIEW_OVERDUE:                   "Review is overdue",
};

function ferpa33RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa33ModelBadge(model: string | null) {
  if (!model) return null;
  const c =
    model === "RBAC" ? "bg-blue-100 text-blue-700" :
    model === "ABAC" ? "bg-purple-100 text-purple-700" :
    model === "None" ? "bg-gray-100 text-gray-500" :
                       "bg-amber-100 text-amber-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{model}</span>;
}

function useAuth33() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa33AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaRoleBasedUiControl | null; onClose: () => void }) {
  const { header } = useAuth33();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/role-based-ui";

  const blankForm = {
    applicationName: "", moduleName: "", userRole: "",
    allowedFeatures: "", restrictedFeatures: "", permissionModel: "RBAC",
    uiRestrictionEnabled: false, backendAuthorizationEnabled: false, sensitiveDataHidden: false,
    evidenceReference: "", owner: "", notes: "",
    complianceStatus: "Pending", riskLevel: "High", status: "Draft", nextReviewDate: "",
  };

  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  useState(() => {
    if (initial) setForm({
      applicationName:            initial.applicationName,
      moduleName:                 initial.moduleName ?? "",
      userRole:                   initial.userRole ?? "",
      allowedFeatures:            initial.allowedFeatures ?? "",
      restrictedFeatures:         initial.restrictedFeatures ?? "",
      permissionModel:            initial.permissionModel ?? "RBAC",
      uiRestrictionEnabled:       initial.uiRestrictionEnabled,
      backendAuthorizationEnabled:initial.backendAuthorizationEnabled,
      sensitiveDataHidden:        initial.sensitiveDataHidden,
      evidenceReference:          initial.evidenceReference ?? "",
      owner:                      initial.owner ?? "",
      notes:                      initial.notes ?? "",
      complianceStatus:           initial.complianceStatus,
      riskLevel:                  initial.riskLevel,
      status:                     initial.status,
      nextReviewDate:             initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "applicationName", form.applicationName, "Application Name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const body = {
      applicationName:            form.applicationName.trim(),
      moduleName:                 form.moduleName || null,
      userRole:                   form.userRole || null,
      allowedFeatures:            form.allowedFeatures || null,
      restrictedFeatures:         form.restrictedFeatures || null,
      permissionModel:            form.permissionModel || null,
      uiRestrictionEnabled:       form.uiRestrictionEnabled,
      backendAuthorizationEnabled:form.backendAuthorizationEnabled,
      sensitiveDataHidden:        form.sensitiveDataHidden,
      evidenceReference:          form.evidenceReference || null,
      owner:                      form.owner || null,
      notes:                      form.notes || null,
      complianceStatus:           form.complianceStatus,
      riskLevel:                  form.riskLevel,
      status:                     form.status,
      nextReviewDate:             form.nextReviewDate || null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Unable to save", variant: "destructive" }); return; }
    toast({ title: initial ? "UI control updated." : "UI control added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit UI Role Control" : "Add UI Role Control"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Application Name *</label>
              <Input className={fieldCls("", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={form.applicationName} onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="Student Portal" />{fErr(errors, "applicationName")}</div>
            <div><label className="text-xs font-medium">Module Name</label>
              <Input value={form.moduleName} onChange={e => set("moduleName", e.target.value)} placeholder="Grade Management" /></div>
            <div><label className="text-xs font-medium">User Role</label>
              <Input value={form.userRole} onChange={e => set("userRole", e.target.value)} placeholder="Registrar" /></div>
            <div><label className="text-xs font-medium">Permission Model</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.permissionModel} onChange={e => set("permissionModel", e.target.value)}>
                {FERPA33_PERMISSION_MODELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>
          {/* Feature lists */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Allowed Features</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.allowedFeatures} onChange={e => set("allowedFeatures", e.target.value)} placeholder="ViewGrades, EditGrades" /></div>
            <div><label className="text-xs font-medium">Restricted Features</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.restrictedFeatures} onChange={e => set("restrictedFeatures", e.target.value)} placeholder="DeleteRecords, ExportBulk" /></div>
          </div>
          {/* Enforcement toggles */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Enforcement Controls</p>
            <div className="flex gap-6 flex-wrap">
              {[
                ["uiRestrictionEnabled",        "UI Restriction Enabled"],
                ["backendAuthorizationEnabled",  "Backend Authorization Enabled"],
                ["sensitiveDataHidden",          "Sensitive Data Hidden"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => setForm(p => ({ ...p, [key]: !p[key as keyof typeof p] }))} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          {/* Ownership & compliance */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Evidence Reference</label>
              <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://rbac-config-2026.pdf" /></div>
            <div><label className="text-xs font-medium">Owner</label>
              <Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="IT Security" /></div>
            <div><label className="text-xs font-medium">Risk Level</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                {FERPA33_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA33_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Compliance Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.complianceStatus} onChange={e => set("complianceStatus", e.target.value)}>
                {FERPA33_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Next Review Date</label>
              <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional notes" /></div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add Control"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function Ferpa33ValidateDialog({ control, onClose }: { control: FerpaRoleBasedUiControl | null; onClose: () => void }) {
  const { header } = useAuth33();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/role-based-ui";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string } | null>(null);

  async function handleValidate() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate Permissions — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
          <span className="text-gray-500">Role / Model</span><span>{control.userRole ?? "—"} / {control.permissionModel ?? "—"}</span>
          <span className="text-gray-500">UI Restriction</span>
            <span className={control.uiRestrictionEnabled ? "text-green-700" : "text-red-600"}>{control.uiRestrictionEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
          <span className="text-gray-500">Backend Auth</span>
            <span className={control.backendAuthorizationEnabled ? "text-green-700" : "text-red-600"}>{control.backendAuthorizationEnabled ? "✓ Enabled" : "✗ Disabled"}</span>
          <span className="text-gray-500">Sensitive Data Hidden</span>
            <span className={control.sensitiveDataHidden ? "text-green-700" : "text-red-600"}>{control.sensitiveDataHidden ? "✓ Yes" : "✗ No"}</span>
          <span className="text-gray-500">Evidence</span>
            <span className={control.evidenceReference ? "text-green-700" : "text-red-600"}>{control.evidenceReference ? "Present" : "Missing"}</span>
        </div>
        {result && (
          <div className={`rounded p-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ Role UI control is compliant." : "✗ Issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA33_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">Validated at {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Permission test dialog ────────────────────────────────────────────────────
function Ferpa33PermissionTestDialog({ control, onClose }: { control: FerpaRoleBasedUiControl | null; onClose: () => void }) {
  const { header } = useAuth33();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/role-based-ui";
  const [testRole, setTestRole] = useState(control?.userRole ?? "");
  const [notes,    setNotes]    = useState("");
  const [result, setResult] = useState<{
    simulationResult: string; testedRole: string;
    uiRestrictionsActive: boolean; backendAuthorizationActive: boolean; sensitiveDataHidden: boolean;
    roleMappingConfigured: boolean; permissionModel: string | null;
    allowedFeatures: string | null; restrictedFeatures: string | null;
    openIssues: string[]; testedAt: string; note: string;
  } | null>(null);

  async function handleTest() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/permission-test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ testRole: testRole || null, notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to complete test", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>UI Access Simulation — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-800">
          ℹ Simulation only — no real user permission changes or student data accessed.
        </div>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Test Role (leave blank to use configured role)</label>
            <Input value={testRole} onChange={e => setTestRole(e.target.value)} placeholder={control.userRole ?? "Enter role to simulate"} /></div>
          <div><label className="text-xs font-medium">Test Notes (optional)</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Purpose of this simulation" /></div>
        </div>
        {result && (
          <div className={`rounded p-3 mt-3 text-sm ${result.simulationResult === "SimulatedSuccess" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.simulationResult === "SimulatedSuccess" ? `✓ Role "${result.testedRole}" passes all UI access controls.` : `✗ Issues found for role "${result.testedRole}"`}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs my-1">
              <span className="text-gray-500">UI restrictions</span><span className={result.uiRestrictionsActive ? "text-green-700" : "text-red-600"}>{result.uiRestrictionsActive ? "✓ Active" : "✗ Disabled"}</span>
              <span className="text-gray-500">Backend auth</span><span className={result.backendAuthorizationActive ? "text-green-700" : "text-red-600"}>{result.backendAuthorizationActive ? "✓ Active" : "✗ Disabled"}</span>
              <span className="text-gray-500">Sensitive data</span><span className={result.sensitiveDataHidden ? "text-green-700" : "text-red-600"}>{result.sensitiveDataHidden ? "✓ Hidden" : "✗ Exposed"}</span>
              <span className="text-gray-500">Role mapping</span><span className={result.roleMappingConfigured ? "text-green-700" : "text-red-600"}>{result.roleMappingConfigured ? `✓ ${result.permissionModel}` : "✗ Not configured"}</span>
            </div>
            {result.allowedFeatures && <p className="text-xs text-gray-600 mt-1"><strong>Allowed:</strong> {result.allowedFeatures}</p>}
            {result.restrictedFeatures && <p className="text-xs text-gray-600"><strong>Restricted:</strong> {result.restrictedFeatures}</p>}
            {result.openIssues.length > 0 && result.openIssues.map(c => <p key={c} className="text-xs text-red-700 mt-0.5">• {FERPA33_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-400 italic mt-1">{result.note}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleTest}>Run Simulation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa33ReviewDialog({ control, onClose }: { control: FerpaRoleBasedUiControl | null; onClose: () => void }) {
  const { header } = useAuth33();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/role-based-ui";
  const [reviewer,       setReviewer]       = useState(control?.reviewer ?? "");
  const [reviewNotes,    setReviewNotes]    = useState("");
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [errors,         setErrors]         = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!control) return;
    const errs: Record<string, string> = {};
    required(errs, "reviewer", reviewer, "Reviewer name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const res = await fetch(`${BASE}/${control.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Complete Review — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer Name *</label>
            <Input value={reviewer} onChange={e => { setReviewer(e.target.value); clearErr(setErrors, "reviewer"); }} className={fieldCls("", errors, "reviewer")} aria-invalid={!!errors.reviewer} placeholder="Compliance Officer" />{fErr(errors, "reviewer")}</div>
          <div><label className="text-xs font-medium">Next Review Date</label>
            <Input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional review notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa33DeleteDialog({ control, onClose }: { control: FerpaRoleBasedUiControl | null; onClose: () => void }) {
  const { header } = useAuth33();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/role-based-ui";

  async function handleDelete() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "UI control removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove UI Role Control</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{control.applicationName}</strong>{control.userRole ? ` (${control.userRole})` : ""}? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa33DetailDialog({ control, canManage, onClose, onEdit, onValidate, onPermTest, onReview, onDelete }: {
  control: FerpaRoleBasedUiControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (c: FerpaRoleBasedUiControl) => void;
  onValidate: (c: FerpaRoleBasedUiControl) => void;
  onPermTest: (c: FerpaRoleBasedUiControl) => void;
  onReview: (c: FerpaRoleBasedUiControl) => void;
  onDelete: (c: FerpaRoleBasedUiControl) => void;
}) {
  if (!control) return null;
  const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <ShieldCheck className="w-4 h-4 text-blue-600" />
            {control.applicationName}
            {control.moduleName && <span className="text-sm text-gray-500 font-normal">/ {control.moduleName}</span>}
            {ferpa33ModelBadge(control.permissionModel)}
            {ferpa33RiskBadge(control.riskLevel)}
          </DialogTitle>
        </DialogHeader>
        {control.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Compliance Issues</p>
            {control.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA33_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isOverdue && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Review overdue — was due {new Date(control.nextReviewDate!).toLocaleDateString()}.</p>
          </div>
        )}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Role & Permissions</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">User Role</span><span className={control.userRole ? "text-gray-800" : "text-red-500 italic"}>{control.userRole ?? "Not configured"}</span>
              <span className="text-gray-500">Permission Model</span><span className={control.permissionModel ? "text-gray-800" : "text-red-500 italic"}>{control.permissionModel ?? "Not configured"}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Enforcement Controls</p>
            <div className="flex gap-4 flex-wrap text-xs">
              <span className={`flex items-center gap-1 ${control.uiRestrictionEnabled ? "text-green-700" : "text-red-600"}`}>
                {control.uiRestrictionEnabled ? "✓" : "✗"} UI Restriction {control.uiRestrictionEnabled ? "Enabled" : "Disabled"}
              </span>
              <span className={`flex items-center gap-1 ${control.backendAuthorizationEnabled ? "text-green-700" : "text-red-600"}`}>
                {control.backendAuthorizationEnabled ? "✓" : "✗"} Backend Auth {control.backendAuthorizationEnabled ? "Enabled" : "Disabled"}
              </span>
              <span className={`flex items-center gap-1 ${control.sensitiveDataHidden ? "text-green-700" : "text-red-600"}`}>
                {control.sensitiveDataHidden ? "✓" : "✗"} Sensitive Data {control.sensitiveDataHidden ? "Hidden" : "Exposed"}
              </span>
            </div>
          </div>
          {(control.allowedFeatures || control.restrictedFeatures) && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Feature Access</p>
              {control.allowedFeatures && <p className="text-xs"><span className="text-gray-500">Allowed: </span><span className="text-green-700">{control.allowedFeatures}</span></p>}
              {control.restrictedFeatures && <p className="text-xs mt-0.5"><span className="text-gray-500">Restricted: </span><span className="text-red-700">{control.restrictedFeatures}</span></p>}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence & Ownership</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Evidence Ref</span><span className={control.evidenceReference ? "text-green-700 font-mono truncate" : "text-red-500 italic font-sans"}>{control.evidenceReference ?? "Missing"}</span>
              <span className="text-gray-500">Owner</span><span>{control.owner ?? "—"}</span>
              <span className="text-gray-500">Compliance Status</span><span>{control.complianceStatus}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Reviewer</span><span>{control.reviewer ?? "—"}</span>
              <span className="text-gray-500">Review Status</span><span>{control.reviewStatus}</span>
              <span className="text-gray-500">Last Review</span><span>{control.lastReviewDate ? new Date(control.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{control.nextReviewDate ? new Date(control.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(control)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(control)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onPermTest(control)}>Perm Test</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(control)}>Review</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(control)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaRoleBasedUiSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth33();
  const BASE       = "/api/organization/compliance/ferpa/role-based-ui";

  const [appFilter,        setAppFilter]        = useState("");
  const [moduleFilter,     setModuleFilter]      = useState("");
  const [roleFilter,       setRoleFilter]        = useState("");
  const [statusFilter,     setStatusFilter]      = useState("");
  const [riskFilter,       setRiskFilter]        = useState("");

  const [showAdd,      setShowAdd]      = useState(false);
  const [editItem,     setEditItem]     = useState<FerpaRoleBasedUiControl | null>(null);
  const [validateItem, setValidateItem] = useState<FerpaRoleBasedUiControl | null>(null);
  const [permTestItem, setPermTestItem] = useState<FerpaRoleBasedUiControl | null>(null);
  const [reviewItem,   setReviewItem]   = useState<FerpaRoleBasedUiControl | null>(null);
  const [deleteItem,   setDeleteItem]   = useState<FerpaRoleBasedUiControl | null>(null);
  const [detailItem,   setDetailItem]   = useState<FerpaRoleBasedUiControl | null>(null);

  const summaryQ = useQuery<Ferpa33Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ control: FerpaRoleBasedUiControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (appFilter)    params.set("applicationName", appFilter);
  if (moduleFilter) params.set("moduleName",      moduleFilter);
  if (roleFilter)   params.set("userRole",        roleFilter);
  if (statusFilter) params.set("status",          statusFilter);
  if (riskFilter)   params.set("riskLevel",       riskFilter);

  const listQ = useQuery<{ total: number; items: FerpaRoleBasedUiControl[] }>({
    queryKey: [BASE, appFilter, moduleFilter, roleFilter, statusFilter, riskFilter],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Controls",              value: s.totalControls,              icon: <ShieldCheck className="w-4 h-4 text-blue-500" />,      highlight: false },
    { label: "UI Restrictions Enabled",     value: s.uiRestrictionsEnabled,      icon: <Shield className="w-4 h-4 text-green-500" />,          highlight: false },
    { label: "Backend Auth Enabled",        value: s.backendAuthorizationEnabled, icon: <Lock className="w-4 h-4 text-green-500" />,           highlight: false },
    { label: "Sensitive Data Hidden",       value: s.sensitiveDataHidden,        icon: <Eye className="w-4 h-4 text-blue-500" />,              highlight: false },
    { label: "Non-Compliant",               value: s.nonCompliant,               icon: <AlertTriangle className="w-4 h-4 text-red-500" />,     highlight: s.nonCompliant > 0 },
    { label: "Pending Reviews",             value: s.pendingReviews,             icon: <Clock className="w-4 h-4 text-orange-500" />,          highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              Role-Sensitive UI Rendering
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks UI permission enforcement and role-based feature visibility. Configuration metadata only — no student data stored.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Control
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Non-Compliant UI Controls ({issuesQ.data!.length})
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { control: FerpaRoleBasedUiControl; codes: string[] }) => (
                <div key={item.control.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.control.applicationName}{item.control.userRole ? ` (${item.control.userRole})` : ""}</span>
                  {ferpa33RiskBadge(item.control.riskLevel)}
                  <span className="text-red-600">{item.codes.map(c => FERPA33_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <Input placeholder="Filter by app…"    value={appFilter}    onChange={e => setAppFilter(e.target.value)}    className="text-xs h-8" />
          <Input placeholder="Filter by module…" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} className="text-xs h-8" />
          <Input placeholder="Filter by role…"   value={roleFilter}   onChange={e => setRoleFilter(e.target.value)}   className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA33_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA33_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No UI role controls registered. Add a control to start tracking role-based UI permission enforcement.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(control => {
              const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
              return (
                <div
                  key={control.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(control)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="text-sm font-medium text-gray-800">{control.applicationName}</span>
                      {control.moduleName && <span className="text-xs text-gray-500">/ {control.moduleName}</span>}
                      {ferpa33ModelBadge(control.permissionModel)}
                      {ferpa33RiskBadge(control.riskLevel)}
                    </div>
                    {control.userRole && <span className="text-xs text-gray-500 shrink-0 font-medium">{control.userRole}</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs">
                    <span className={control.uiRestrictionEnabled ? "text-green-600" : "text-red-500"}>
                      {control.uiRestrictionEnabled ? "✓ UI restricted" : "✗ UI open"}
                    </span>
                    <span className={control.backendAuthorizationEnabled ? "text-green-600" : "text-red-500"}>
                      {control.backendAuthorizationEnabled ? "✓ Backend auth" : "✗ No backend auth"}
                    </span>
                    <span className={control.sensitiveDataHidden ? "text-green-600" : "text-red-500"}>
                      {control.sensitiveDataHidden ? "✓ Data hidden" : "✗ Data exposed"}
                    </span>
                    {control.evidenceReference && <span className="text-green-600">✓ Evidence</span>}
                    {isOverdue && <span className="text-orange-600">⚠ Review overdue</span>}
                  </div>
                  {control.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {control.validationCodes.map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA33_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa33AddDialog       open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa33ValidateDialog  control={validateItem} onClose={() => setValidateItem(null)} />
      <Ferpa33PermissionTestDialog control={permTestItem} onClose={() => setPermTestItem(null)} />
      <Ferpa33ReviewDialog    control={reviewItem}   onClose={() => setReviewItem(null)} />
      <Ferpa33DeleteDialog    control={deleteItem}   onClose={() => setDeleteItem(null)} />
      <Ferpa33DetailDialog
        control={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={c => { setDetailItem(null); setEditItem(c); }}
        onValidate={c => { setDetailItem(null); setValidateItem(c); }}
        onPermTest={c => { setDetailItem(null); setPermTestItem(c); }}
        onReview={c => { setDetailItem(null); setReviewItem(c); }}
        onDelete={c => { setDetailItem(null); setDeleteItem(c); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FERPA-32 — Privacy Notice Display and User Acknowledgement
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa32Summary = {
  totalNotices: number;
  activeNotices: number;
  acknowledgementTracked: number;
  missingConfiguration: number;
  expiredNotices: number;
  pendingReviews: number;
};

type FerpaPrivacyNoticeControl = {
  id: string;
  organizationId: string;
  noticeName: string;
  noticeType: string | null;
  audienceType: string | null;
  displayLocation: string | null;
  displayRequired: boolean;
  acknowledgementRequired: boolean;
  userAcknowledgementTracked: boolean;
  version: string | null;
  effectiveDate: string | null;
  expiryDate: string | null;
  evidenceReference: string | null;
  owner: string | null;
  notes: string | null;
  complianceStatus: string;
  remediationNotes: string | null;
  lastRemediationDate: string | null;
  reviewer: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
  isExpired: boolean;
};

const FERPA32_NOTICE_TYPES   = ["FERPA","HIPAA","General Privacy"];
const FERPA32_AUDIENCE_TYPES = ["Student","Faculty","Admin","Parent"];
const FERPA32_RISK_LEVELS    = ["Low","Medium","High","Critical"];
const FERPA32_STATUSES       = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA32_COMPLIANCE_STATUSES = ["Pending","Compliant","NonCompliant","UnderReview"];

const FERPA32_CODE_LABELS: Record<string, string> = {
  PRIVACY_NOTICE_MISSING:        "Notice display location not configured",
  ACKNOWLEDGEMENT_REQUIRED:      "Acknowledgement required but not tracked",
  NOTICE_VERSION_MISSING:        "Notice version not set",
  EFFECTIVE_DATE_MISSING:        "Effective date missing",
  EVIDENCE_REFERENCE_REQUIRED:   "Evidence reference missing",
  REVIEW_OVERDUE:                "Review is overdue",
};

function ferpa32RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa32TypeBadge(type: string | null) {
  if (!type) return null;
  const c =
    type === "FERPA"           ? "bg-blue-100 text-blue-700" :
    type === "HIPAA"           ? "bg-purple-100 text-purple-700" :
                                 "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{type}</span>;
}

function useAuth32() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa32AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaPrivacyNoticeControl | null; onClose: () => void }) {
  const { header } = useAuth32();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/privacy-notices";

  const blankForm = {
    noticeName: "", noticeType: "FERPA", audienceType: "Student",
    displayLocation: "", displayRequired: false,
    acknowledgementRequired: false, userAcknowledgementTracked: false,
    version: "", effectiveDate: "", expiryDate: "",
    evidenceReference: "", owner: "", notes: "",
    complianceStatus: "Pending", riskLevel: "Medium", status: "Draft", nextReviewDate: "",
  };

  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  useState(() => {
    if (initial) setForm({
      noticeName:                  initial.noticeName,
      noticeType:                  initial.noticeType ?? "FERPA",
      audienceType:                initial.audienceType ?? "Student",
      displayLocation:             initial.displayLocation ?? "",
      displayRequired:             initial.displayRequired,
      acknowledgementRequired:     initial.acknowledgementRequired,
      userAcknowledgementTracked:  initial.userAcknowledgementTracked,
      version:                     initial.version ?? "",
      effectiveDate:               initial.effectiveDate ? initial.effectiveDate.split("T")[0] : "",
      expiryDate:                  initial.expiryDate ? initial.expiryDate.split("T")[0] : "",
      evidenceReference:           initial.evidenceReference ?? "",
      owner:                       initial.owner ?? "",
      notes:                       initial.notes ?? "",
      complianceStatus:            initial.complianceStatus,
      riskLevel:                   initial.riskLevel,
      status:                      initial.status,
      nextReviewDate:              initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "noticeName", form.noticeName, "Notice Name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const body = {
      noticeName:                  form.noticeName.trim(),
      noticeType:                  form.noticeType || null,
      audienceType:                form.audienceType || null,
      displayLocation:             form.displayLocation || null,
      displayRequired:             form.displayRequired,
      acknowledgementRequired:     form.acknowledgementRequired,
      userAcknowledgementTracked:  form.userAcknowledgementTracked,
      version:                     form.version || null,
      effectiveDate:               form.effectiveDate || null,
      expiryDate:                  form.expiryDate || null,
      evidenceReference:           form.evidenceReference || null,
      owner:                       form.owner || null,
      notes:                       form.notes || null,
      complianceStatus:            form.complianceStatus,
      riskLevel:                   form.riskLevel,
      status:                      form.status,
      nextReviewDate:              form.nextReviewDate || null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Unable to save", variant: "destructive" }); return; }
    toast({ title: initial ? "Privacy notice updated." : "Privacy notice added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit Privacy Notice" : "Add Privacy Notice"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium">Notice Name *</label>
              <Input className={fieldCls("", errors, "noticeName")} aria-invalid={!!errors.noticeName} value={form.noticeName} onChange={e => { set("noticeName", e.target.value); clearErr(setErrors, "noticeName"); }} placeholder="FERPA Annual Privacy Notice" />{fErr(errors, "noticeName")}</div>
            <div><label className="text-xs font-medium">Notice Type</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.noticeType} onChange={e => set("noticeType", e.target.value)}>
                {FERPA32_NOTICE_TYPES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Audience Type</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.audienceType} onChange={e => set("audienceType", e.target.value)}>
                {FERPA32_AUDIENCE_TYPES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="text-xs font-medium">Display Location</label>
              <Input value={form.displayLocation} onChange={e => set("displayLocation", e.target.value)} placeholder="Student Portal Login Page" /></div>
          </div>
          {/* Toggles */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Display & Acknowledgement</p>
            <div className="flex gap-6 flex-wrap">
              {[
                ["displayRequired",             "Display Required"],
                ["acknowledgementRequired",     "Acknowledgement Required"],
                ["userAcknowledgementTracked",  "Acknowledgement Tracked"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => setForm(p => ({ ...p, [key]: !p[key as keyof typeof p] }))} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          {/* Versioning & dates */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Version</label>
              <Input value={form.version} onChange={e => set("version", e.target.value)} placeholder="2026.1" /></div>
            <div><label className="text-xs font-medium">Effective Date</label>
              <Input type="date" value={form.effectiveDate} onChange={e => set("effectiveDate", e.target.value)} /></div>
            <div><label className="text-xs font-medium">Expiry Date</label>
              <Input type="date" value={form.expiryDate} onChange={e => set("expiryDate", e.target.value)} /></div>
            <div><label className="text-xs font-medium">Evidence Reference</label>
              <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://notice-2026.pdf" /></div>
          </div>
          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Owner</label>
              <Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="Registrar Office" /></div>
            <div><label className="text-xs font-medium">Compliance Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.complianceStatus} onChange={e => set("complianceStatus", e.target.value)}>
                {FERPA32_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Risk Level</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                {FERPA32_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA32_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Next Review Date</label>
              <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional notes" /></div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add Notice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function Ferpa32ValidateDialog({ notice, onClose }: { notice: FerpaPrivacyNoticeControl | null; onClose: () => void }) {
  const { header } = useAuth32();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/privacy-notices";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string } | null>(null);

  async function handleValidate() {
    if (!notice) return;
    const res = await fetch(`${BASE}/${notice.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!notice) return null;
  return (
    <Dialog open={!!notice} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate Notice — {notice.noticeName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
          <span className="text-gray-500">Type / Audience</span><span>{notice.noticeType ?? "—"} / {notice.audienceType ?? "—"}</span>
          <span className="text-gray-500">Display Required</span><span className={notice.displayRequired ? "text-green-700" : "text-gray-400"}>{notice.displayRequired ? "✓ Yes" : "—"}</span>
          <span className="text-gray-500">Display Location</span><span className={notice.displayLocation ? "text-green-700" : "text-red-600"}>{notice.displayLocation ?? "Not configured"}</span>
          <span className="text-gray-500">Ack Required</span><span className={notice.acknowledgementRequired ? "text-green-700" : "text-gray-400"}>{notice.acknowledgementRequired ? "✓ Yes" : "—"}</span>
          <span className="text-gray-500">Ack Tracked</span><span className={notice.userAcknowledgementTracked ? "text-green-700" : "text-red-600"}>{notice.userAcknowledgementTracked ? "✓ Yes" : "✗ No"}</span>
          <span className="text-gray-500">Version</span><span className={notice.version ? "text-green-700" : "text-red-600"}>{notice.version ?? "Missing"}</span>
          <span className="text-gray-500">Effective Date</span><span className={notice.effectiveDate ? "text-green-700" : "text-red-600"}>{notice.effectiveDate ? new Date(notice.effectiveDate).toLocaleDateString() : "Missing"}</span>
          <span className="text-gray-500">Evidence</span><span className={notice.evidenceReference ? "text-green-700" : "text-red-600"}>{notice.evidenceReference ? "Present" : "Missing"}</span>
        </div>
        {result && (
          <div className={`rounded p-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ Privacy notice is compliant." : "✗ Issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA32_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">Validated at {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Acknowledge-test dialog ───────────────────────────────────────────────────
function Ferpa32AckTestDialog({ notice, onClose }: { notice: FerpaPrivacyNoticeControl | null; onClose: () => void }) {
  const { header } = useAuth32();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/privacy-notices";
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState<{
    simulationResult: string; noticeDisplayed: boolean; acknowledgementTracked: boolean;
    openIssues: string[]; testedAt: string; note: string;
  } | null>(null);

  async function handleTest() {
    if (!notice) return;
    const res = await fetch(`${BASE}/${notice.id}/acknowledge-test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to complete test", variant: "destructive" }); return; }
    setResult(await res.json());
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!notice) return null;
  return (
    <Dialog open={!!notice} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Acknowledge Test — {notice.noticeName}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-800">
          ℹ Configuration test only — no real student records or user sessions are accessed.
        </div>
        <div><label className="text-xs font-medium">Test Notes (optional)</label>
          <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Purpose of this test" /></div>
        {result && (
          <div className={`rounded p-3 mt-3 text-sm ${result.simulationResult === "SimulatedSuccess" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.simulationResult === "SimulatedSuccess" ? "✓ Acknowledgement configuration is functional." : "✗ Configuration issues:"}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs mb-1">
              <span className="text-gray-500">Notice displayed</span><span className={result.noticeDisplayed ? "text-green-700" : "text-red-600"}>{result.noticeDisplayed ? "✓ Yes" : "✗ No"}</span>
              <span className="text-gray-500">Acknowledgement tracked</span><span className={result.acknowledgementTracked ? "text-green-700" : "text-red-600"}>{result.acknowledgementTracked ? "✓ Yes" : "✗ No"}</span>
            </div>
            {result.openIssues.map(c => <p key={c} className="text-xs text-red-700">• {FERPA32_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-400 italic mt-1">{result.note}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleTest}>Run Test</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa32ReviewDialog({ notice, onClose }: { notice: FerpaPrivacyNoticeControl | null; onClose: () => void }) {
  const { header } = useAuth32();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/privacy-notices";
  const [reviewer,       setReviewer]       = useState(notice?.reviewer ?? "");
  const [reviewNotes,    setReviewNotes]    = useState("");
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [errors,         setErrors]         = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!notice) return;
    const errs: Record<string, string> = {};
    required(errs, "reviewer", reviewer, "Reviewer name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const res = await fetch(`${BASE}/${notice.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!notice) return null;
  return (
    <Dialog open={!!notice} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Complete Review — {notice.noticeName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer Name *</label>
            <Input value={reviewer} onChange={e => { setReviewer(e.target.value); clearErr(setErrors, "reviewer"); }} className={fieldCls("", errors, "reviewer")} aria-invalid={!!errors.reviewer} placeholder="Compliance Officer" />{fErr(errors, "reviewer")}</div>
          <div><label className="text-xs font-medium">Next Review Date</label>
            <Input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional review notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa32DeleteDialog({ notice, onClose }: { notice: FerpaPrivacyNoticeControl | null; onClose: () => void }) {
  const { header } = useAuth32();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/privacy-notices";

  async function handleDelete() {
    if (!notice) return;
    const res = await fetch(`${BASE}/${notice.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "Privacy notice removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!notice) return null;
  return (
    <Dialog open={!!notice} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove Privacy Notice</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{notice.noticeName}</strong>? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa32DetailDialog({ notice, canManage, onClose, onEdit, onValidate, onAckTest, onReview, onDelete }: {
  notice: FerpaPrivacyNoticeControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (n: FerpaPrivacyNoticeControl) => void;
  onValidate: (n: FerpaPrivacyNoticeControl) => void;
  onAckTest: (n: FerpaPrivacyNoticeControl) => void;
  onReview: (n: FerpaPrivacyNoticeControl) => void;
  onDelete: (n: FerpaPrivacyNoticeControl) => void;
}) {
  if (!notice) return null;
  const isOverdue = notice.nextReviewDate && new Date(notice.nextReviewDate) < new Date() && notice.reviewStatus !== "Reviewed";
  return (
    <Dialog open={!!notice} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <FileText className="w-4 h-4 text-blue-600" />
            {notice.noticeName}
            {ferpa32TypeBadge(notice.noticeType)}
            {notice.isExpired && <span className="inline-flex px-2 py-0.5 rounded text-xs bg-red-100 text-red-700 font-medium">Expired</span>}
          </DialogTitle>
        </DialogHeader>
        {notice.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Compliance Issues</p>
            {notice.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA32_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isOverdue && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Review overdue — was due {new Date(notice.nextReviewDate!).toLocaleDateString()}.</p>
          </div>
        )}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notice Details</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Audience</span><span>{notice.audienceType ?? "—"}</span>
              <span className="text-gray-500">Display Location</span><span className={notice.displayLocation ? "text-green-700" : "text-red-500 italic"}>{notice.displayLocation ?? "Not configured"}</span>
              <span className="text-gray-500">Display Required</span><span className={notice.displayRequired ? "text-green-700" : "text-gray-400"}>{notice.displayRequired ? "✓ Yes" : "—"}</span>
              <span className="text-gray-500">Version</span><span className={notice.version ? "text-green-700" : "text-red-500 italic"}>{notice.version ?? "Missing"}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Acknowledgement</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Required</span><span className={notice.acknowledgementRequired ? "text-green-700" : "text-gray-400"}>{notice.acknowledgementRequired ? "✓ Yes" : "—"}</span>
              <span className="text-gray-500">Tracked</span><span className={notice.userAcknowledgementTracked ? "text-green-700" : "text-red-600"}>{notice.userAcknowledgementTracked ? "✓ Yes" : "✗ No"}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Effective Period</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Effective</span><span className={notice.effectiveDate ? "text-green-700" : "text-red-500 italic"}>{notice.effectiveDate ? new Date(notice.effectiveDate).toLocaleDateString() : "Missing"}</span>
              <span className="text-gray-500">Expires</span><span className={notice.isExpired ? "text-red-600 font-medium" : "text-gray-700"}>{notice.expiryDate ? new Date(notice.expiryDate).toLocaleDateString() : "—"}{notice.isExpired ? " (Expired)" : ""}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence & Compliance</p>
            <div className="grid grid-cols-1 gap-y-1 text-xs">
              <div className="flex gap-2"><span className="text-gray-500 w-36">Evidence Ref</span><span className={notice.evidenceReference ? "text-green-700 font-mono truncate" : "text-red-500 italic font-sans"}>{notice.evidenceReference ?? "Missing"}</span></div>
              <div className="flex gap-2"><span className="text-gray-500 w-36">Compliance Status</span><span>{notice.complianceStatus}</span></div>
              <div className="flex gap-2"><span className="text-gray-500 w-36">Owner</span><span>{notice.owner ?? "—"}</span></div>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Reviewer</span><span>{notice.reviewer ?? "—"}</span>
              <span className="text-gray-500">Review Status</span><span>{notice.reviewStatus}</span>
              <span className="text-gray-500">Last Review</span><span>{notice.lastReviewDate ? new Date(notice.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{notice.nextReviewDate ? new Date(notice.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(notice)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(notice)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onAckTest(notice)}>Ack Test</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(notice)}>Review</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(notice)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaPrivacyNoticeSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth32();
  const BASE       = "/api/organization/compliance/ferpa/privacy-notices";

  const [nameFilter,        setNameFilter]        = useState("");
  const [typeFilter,        setTypeFilter]         = useState("");
  const [audienceFilter,    setAudienceFilter]     = useState("");
  const [statusFilter,      setStatusFilter]       = useState("");
  const [riskFilter,        setRiskFilter]         = useState("");
  const [complianceFilter,  setComplianceFilter]   = useState("");

  const [showAdd,      setShowAdd]      = useState(false);
  const [editItem,     setEditItem]     = useState<FerpaPrivacyNoticeControl | null>(null);
  const [validateItem, setValidateItem] = useState<FerpaPrivacyNoticeControl | null>(null);
  const [ackTestItem,  setAckTestItem]  = useState<FerpaPrivacyNoticeControl | null>(null);
  const [reviewItem,   setReviewItem]   = useState<FerpaPrivacyNoticeControl | null>(null);
  const [deleteItem,   setDeleteItem]   = useState<FerpaPrivacyNoticeControl | null>(null);
  const [detailItem,   setDetailItem]   = useState<FerpaPrivacyNoticeControl | null>(null);

  const summaryQ = useQuery<Ferpa32Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ notice: FerpaPrivacyNoticeControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (nameFilter)       params.set("noticeName",       nameFilter);
  if (typeFilter)       params.set("noticeType",       typeFilter);
  if (audienceFilter)   params.set("audienceType",     audienceFilter);
  if (statusFilter)     params.set("status",           statusFilter);
  if (riskFilter)       params.set("riskLevel",        riskFilter);
  if (complianceFilter) params.set("complianceStatus", complianceFilter);

  const listQ = useQuery<{ total: number; items: FerpaPrivacyNoticeControl[] }>({
    queryKey: [BASE, nameFilter, typeFilter, audienceFilter, statusFilter, riskFilter, complianceFilter],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Notices",           value: s.totalNotices,           icon: <FileText className="w-4 h-4 text-blue-500" />,        highlight: false },
    { label: "Active Notices",          value: s.activeNotices,          icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,   highlight: false },
    { label: "Acknowledgement Tracked", value: s.acknowledgementTracked, icon: <Shield className="w-4 h-4 text-blue-500" />,          highlight: false },
    { label: "Missing Configuration",   value: s.missingConfiguration,   icon: <AlertTriangle className="w-4 h-4 text-red-500" />,    highlight: s.missingConfiguration > 0 },
    { label: "Expired Notices",         value: s.expiredNotices,         icon: <XCircle className="w-4 h-4 text-red-500" />,          highlight: s.expiredNotices > 0 },
    { label: "Pending Reviews",         value: s.pendingReviews,         icon: <Clock className="w-4 h-4 text-orange-500" />,         highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4 text-blue-600" />
              Privacy Notice Display & Acknowledgement
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks privacy notices displayed to users and acknowledgement evidence for FERPA compliance.
              No actual student records or sensitive user content is stored here.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Notice
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Privacy Notice Issues ({issuesQ.data!.length} notices)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { notice: FerpaPrivacyNoticeControl; codes: string[] }) => (
                <div key={item.notice.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.notice.noticeName}</span>
                  {ferpa32RiskBadge(item.notice.riskLevel)}
                  <span className="text-red-600">{item.codes.map(c => FERPA32_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Input placeholder="Filter by name…" value={nameFilter} onChange={e => setNameFilter(e.target.value)} className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {FERPA32_NOTICE_TYPES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={audienceFilter} onChange={e => setAudienceFilter(e.target.value)}>
            <option value="">All audiences</option>
            {FERPA32_AUDIENCE_TYPES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA32_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA32_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={complianceFilter} onChange={e => setComplianceFilter(e.target.value)}>
            <option value="">All compliance</option>
            {FERPA32_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No privacy notices registered. Add a notice to start tracking privacy notice display and acknowledgement.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(notice => {
              const isOverdue = notice.nextReviewDate && new Date(notice.nextReviewDate) < new Date() && notice.reviewStatus !== "Reviewed";
              return (
                <div
                  key={notice.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(notice)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="text-sm font-medium text-gray-800 truncate">{notice.noticeName}</span>
                      {ferpa32TypeBadge(notice.noticeType)}
                      {ferpa32RiskBadge(notice.riskLevel)}
                      {notice.isExpired && <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">Expired</span>}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{notice.audienceType ?? "—"}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    {notice.displayLocation && <span className="text-xs text-gray-500 truncate max-w-[180px]">{notice.displayLocation}</span>}
                    {notice.version && <span className="text-xs text-blue-600">v{notice.version}</span>}
                    <span className={`text-xs ${notice.userAcknowledgementTracked ? "text-green-600" : notice.acknowledgementRequired ? "text-red-500" : "text-gray-400"}`}>
                      {notice.userAcknowledgementTracked ? "✓ Ack tracked" : notice.acknowledgementRequired ? "✗ Ack missing" : "○ Ack optional"}
                    </span>
                    {notice.evidenceReference && <span className="text-xs text-green-600">✓ Evidence</span>}
                    {isOverdue && <span className="text-xs text-orange-600">⚠ Review overdue</span>}
                    {notice.effectiveDate && <span className="text-xs text-gray-500">Eff: {new Date(notice.effectiveDate).toLocaleDateString()}</span>}
                  </div>
                  {notice.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {notice.validationCodes.map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA32_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa32AddDialog     open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa32ValidateDialog notice={validateItem} onClose={() => setValidateItem(null)} />
      <Ferpa32AckTestDialog  notice={ackTestItem}  onClose={() => setAckTestItem(null)} />
      <Ferpa32ReviewDialog   notice={reviewItem}   onClose={() => setReviewItem(null)} />
      <Ferpa32DeleteDialog   notice={deleteItem}   onClose={() => setDeleteItem(null)} />
      <Ferpa32DetailDialog
        notice={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={n => { setDetailItem(null); setEditItem(n); }}
        onValidate={n => { setDetailItem(null); setValidateItem(n); }}
        onAckTest={n => { setDetailItem(null); setAckTestItem(n); }}
        onReview={n => { setDetailItem(null); setReviewItem(n); }}
        onDelete={n => { setDetailItem(null); setDeleteItem(n); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FERPA-31 — Automatic Logout (Session Timeout Controls)
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa31Summary = {
  totalSessionPolicies: number;
  timeoutEnabled: number;
  compliantPolicies: number;
  missingTimeoutConfig: number;
  privilegedSessionRisks: number;
  pendingReviews: number;
};

type FerpaSessionTimeoutControl = {
  id: string;
  organizationId: string;
  applicationName: string;
  sessionPolicyName: string | null;
  environment: string | null;
  sessionTimeoutEnabled: boolean;
  idleTimeoutMinutes: number | null;
  absoluteTimeoutMinutes: number | null;
  warningNotificationEnabled: boolean;
  warningTimeBeforeLogoutMinutes: number | null;
  logoutActionType: string | null;
  privilegedUserTimeoutPolicy: string | null;
  adminSessionTimeoutPolicy: string | null;
  rememberMeEnabled: boolean;
  evidenceReference: string | null;
  complianceStatus: string;
  remediationNotes: string | null;
  lastRemediationDate: string | null;
  owner: string | null;
  reviewer: string | null;
  notes: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

const FERPA31_LOGOUT_ACTIONS    = ["ForceLogout", "SessionLock", "ReauthenticationRequired"];
const FERPA31_LOGOUT_LABELS: Record<string, string> = {
  ForceLogout:              "Force Logout",
  SessionLock:              "Session Lock",
  ReauthenticationRequired: "Re-authentication Required",
};
const FERPA31_ENVIRONMENTS      = ["Production", "Staging", "Development", "All"];
const FERPA31_RISK_LEVELS       = ["Low","Medium","High","Critical"];
const FERPA31_STATUSES          = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA31_COMPLIANCE_STATUSES = ["Pending","Compliant","NonCompliant","UnderReview"];

const FERPA31_CODE_LABELS: Record<string, string> = {
  SESSION_TIMEOUT_DISABLED:        "Session timeout is disabled",
  IDLE_TIMEOUT_NOT_CONFIGURED:     "Idle timeout not configured",
  ABSOLUTE_TIMEOUT_NOT_CONFIGURED: "Absolute session timeout not configured",
  PRIVILEGED_TIMEOUT_REQUIRED:     "Privileged/admin session timeout policy missing",
  WARNING_NOTIFICATION_MISSING:    "Warning notification not enabled",
  REMEMBER_ME_RISK:                "Remember-me is enabled (session persistence risk)",
  SECURITY_EVIDENCE_REQUIRED:      "Evidence reference missing",
  REVIEW_OVERDUE:                  "Review is overdue",
};

function ferpa31RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa31StatusBadge(status: string) {
  const c =
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function useAuth31() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa31AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaSessionTimeoutControl | null; onClose: () => void }) {
  const { header } = useAuth31();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/session-timeout-controls";

  const [form, setForm] = useState({
    applicationName: "", sessionPolicyName: "", environment: "Production",
    sessionTimeoutEnabled: false,
    idleTimeoutMinutes: "" as string,
    absoluteTimeoutMinutes: "" as string,
    warningNotificationEnabled: false,
    warningTimeBeforeLogoutMinutes: "" as string,
    logoutActionType: "ForceLogout",
    privilegedUserTimeoutPolicy: "",
    adminSessionTimeoutPolicy: "",
    rememberMeEnabled: false,
    evidenceReference: "",
    complianceStatus: "Pending",
    owner: "", reviewer: "", notes: "",
    riskLevel: "High", status: "Draft", nextReviewDate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  useState(() => {
    if (initial) setForm({
      applicationName: initial.applicationName,
      sessionPolicyName: initial.sessionPolicyName ?? "",
      environment: initial.environment ?? "Production",
      sessionTimeoutEnabled: initial.sessionTimeoutEnabled,
      idleTimeoutMinutes: initial.idleTimeoutMinutes?.toString() ?? "",
      absoluteTimeoutMinutes: initial.absoluteTimeoutMinutes?.toString() ?? "",
      warningNotificationEnabled: initial.warningNotificationEnabled,
      warningTimeBeforeLogoutMinutes: initial.warningTimeBeforeLogoutMinutes?.toString() ?? "",
      logoutActionType: initial.logoutActionType ?? "ForceLogout",
      privilegedUserTimeoutPolicy: initial.privilegedUserTimeoutPolicy ?? "",
      adminSessionTimeoutPolicy: initial.adminSessionTimeoutPolicy ?? "",
      rememberMeEnabled: initial.rememberMeEnabled,
      evidenceReference: initial.evidenceReference ?? "",
      complianceStatus: initial.complianceStatus,
      owner: initial.owner ?? "",
      reviewer: initial.reviewer ?? "",
      notes: initial.notes ?? "",
      riskLevel: initial.riskLevel,
      status: initial.status,
      nextReviewDate: initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "applicationName", form.applicationName, "Application Name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const body = {
      applicationName: form.applicationName.trim(),
      sessionPolicyName: form.sessionPolicyName || null,
      environment: form.environment || null,
      sessionTimeoutEnabled: form.sessionTimeoutEnabled,
      idleTimeoutMinutes: form.idleTimeoutMinutes ? parseInt(form.idleTimeoutMinutes) : null,
      absoluteTimeoutMinutes: form.absoluteTimeoutMinutes ? parseInt(form.absoluteTimeoutMinutes) : null,
      warningNotificationEnabled: form.warningNotificationEnabled,
      warningTimeBeforeLogoutMinutes: form.warningTimeBeforeLogoutMinutes ? parseInt(form.warningTimeBeforeLogoutMinutes) : null,
      logoutActionType: form.logoutActionType || null,
      privilegedUserTimeoutPolicy: form.privilegedUserTimeoutPolicy || null,
      adminSessionTimeoutPolicy: form.adminSessionTimeoutPolicy || null,
      rememberMeEnabled: form.rememberMeEnabled,
      evidenceReference: form.evidenceReference || null,
      complianceStatus: form.complianceStatus,
      owner: form.owner || null,
      reviewer: form.reviewer || null,
      notes: form.notes || null,
      riskLevel: form.riskLevel,
      status: form.status,
      nextReviewDate: form.nextReviewDate || null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Unable to save", variant: "destructive" }); return; }
    toast({ title: initial ? "Session policy updated." : "Session policy added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit Session Policy" : "Add Session Timeout Policy"}</DialogTitle></DialogHeader>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
          ⚠ Store configuration metadata only. Never enter session tokens, authentication cookies, access tokens, or user credentials here.
        </p>
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium">Application / System Name *</label>
              <Input className={fieldCls("", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={form.applicationName} onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="Student Information System" />{fErr(errors, "applicationName")}</div>
            <div><label className="text-xs font-medium">Session Policy Name</label>
              <Input value={form.sessionPolicyName} onChange={e => set("sessionPolicyName", e.target.value)} placeholder="Standard Idle Timeout Policy" /></div>
            <div><label className="text-xs font-medium">Environment</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.environment} onChange={e => set("environment", e.target.value)}>
                {FERPA31_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>
          {/* Timeout config */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Session Timeout Configuration</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 flex gap-6">
                {[
                  ["sessionTimeoutEnabled",     "Session Timeout Enabled"],
                  ["warningNotificationEnabled", "Warning Notification Enabled"],
                  ["rememberMeEnabled",          "Remember Me Enabled"],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => setForm(p => ({ ...p, [key]: !p[key as keyof typeof p] }))} className="rounded" />
                    {label}
                  </label>
                ))}
              </div>
              <div><label className="text-xs font-medium">Idle Timeout (minutes)</label>
                <Input type="number" min={1} value={form.idleTimeoutMinutes} onChange={e => set("idleTimeoutMinutes", e.target.value)} placeholder="30" /></div>
              <div><label className="text-xs font-medium">Absolute Timeout (minutes)</label>
                <Input type="number" min={1} value={form.absoluteTimeoutMinutes} onChange={e => set("absoluteTimeoutMinutes", e.target.value)} placeholder="480" /></div>
              <div><label className="text-xs font-medium">Warning Time Before Logout (minutes)</label>
                <Input type="number" min={1} value={form.warningTimeBeforeLogoutMinutes} onChange={e => set("warningTimeBeforeLogoutMinutes", e.target.value)} placeholder="5" /></div>
              <div><label className="text-xs font-medium">Logout Action Type</label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.logoutActionType} onChange={e => set("logoutActionType", e.target.value)}>
                  {FERPA31_LOGOUT_ACTIONS.map(v => <option key={v} value={v}>{FERPA31_LOGOUT_LABELS[v]}</option>)}
                </select>
              </div>
            </div>
          </div>
          {/* Privileged / admin */}
          <div className="grid grid-cols-1 gap-3">
            <div><label className="text-xs font-medium">Privileged User Timeout Policy</label>
              <Input value={form.privilegedUserTimeoutPolicy} onChange={e => set("privilegedUserTimeoutPolicy", e.target.value)} placeholder="15 min idle, 2 hr absolute" /></div>
            <div><label className="text-xs font-medium">Admin Session Timeout Policy</label>
              <Input value={form.adminSessionTimeoutPolicy} onChange={e => set("adminSessionTimeoutPolicy", e.target.value)} placeholder="10 min idle, 1 hr absolute" /></div>
            <div><label className="text-xs font-medium">Evidence Reference</label>
              <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://session-timeout-policy-2026.pdf" /></div>
          </div>
          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Owner</label><Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="IT Security" /></div>
            <div><label className="text-xs font-medium">Reviewer</label><Input value={form.reviewer} onChange={e => set("reviewer", e.target.value)} placeholder="CISO" /></div>
            <div><label className="text-xs font-medium">Compliance Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.complianceStatus} onChange={e => set("complianceStatus", e.target.value)}>
                {FERPA31_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Risk Level</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                {FERPA31_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA31_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional notes" /></div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add Session Policy"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function Ferpa31ValidateDialog({ policy, onClose }: { policy: FerpaSessionTimeoutControl | null; onClose: () => void }) {
  const { header } = useAuth31();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/session-timeout-controls";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string } | null>(null);

  async function handleValidate() {
    if (!policy) return;
    const res = await fetch(`${BASE}/${policy.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!policy) return null;
  return (
    <Dialog open={!!policy} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate Session Policy — {policy.applicationName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
          <span className="text-gray-500">Timeout Enabled</span><span className={policy.sessionTimeoutEnabled ? "text-green-700 font-medium" : "text-red-600"}>{policy.sessionTimeoutEnabled ? "✓ Yes" : "✗ No"}</span>
          <span className="text-gray-500">Idle Timeout</span><span className={policy.idleTimeoutMinutes ? "text-green-700" : "text-red-600"}>{policy.idleTimeoutMinutes ? `${policy.idleTimeoutMinutes} min` : "Not configured"}</span>
          <span className="text-gray-500">Absolute Timeout</span><span className={policy.absoluteTimeoutMinutes ? "text-green-700" : "text-red-600"}>{policy.absoluteTimeoutMinutes ? `${policy.absoluteTimeoutMinutes} min` : "Not configured"}</span>
          <span className="text-gray-500">Warning</span><span className={policy.warningNotificationEnabled ? "text-green-700" : "text-gray-400"}>{policy.warningNotificationEnabled ? `✓ ${policy.warningTimeBeforeLogoutMinutes ?? "?"} min before` : "—"}</span>
          <span className="text-gray-500">Logout Action</span><span>{FERPA31_LOGOUT_LABELS[policy.logoutActionType ?? ""] ?? policy.logoutActionType ?? "—"}</span>
          <span className="text-gray-500">Privileged Policy</span><span className={policy.privilegedUserTimeoutPolicy ? "text-green-700" : "text-red-600"}>{policy.privilegedUserTimeoutPolicy ? "✓ Defined" : "Missing"}</span>
          <span className="text-gray-500">Remember Me</span><span className={policy.rememberMeEnabled ? "text-orange-600" : "text-green-700"}>{policy.rememberMeEnabled ? "⚠ Enabled" : "✓ Disabled"}</span>
          <span className="text-gray-500">Evidence</span><span className={policy.evidenceReference ? "text-green-700" : "text-red-600"}>{policy.evidenceReference ? "Present" : "Missing"}</span>
        </div>
        {result && (
          <div className={`rounded p-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ Session policy is compliant." : "✗ Issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA31_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">Validated at {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Simulate dialog ───────────────────────────────────────────────────────────
type Ferpa31SimStep = { step: number; event_: string; detail: string };
type Ferpa31SimResult = { simulationResult: string; isCompliant: boolean; openIssues: string[]; simulatedWorkflow: Ferpa31SimStep[]; simulatedAt: string; note: string };

function Ferpa31SimulateDialog({ policy, onClose }: { policy: FerpaSessionTimeoutControl | null; onClose: () => void }) {
  const { header } = useAuth31();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/session-timeout-controls";
  const [notes,  setNotes]  = useState("");
  const [result, setResult] = useState<Ferpa31SimResult | null>(null);

  async function handleSimulate() {
    if (!policy) return;
    const res = await fetch(`${BASE}/${policy.id}/simulate`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to run simulation", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!policy) return null;
  return (
    <Dialog open={!!policy} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Simulate Timeout Flow — {policy.applicationName}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-800">
          ℹ This is a configuration simulation only. No real user sessions will be terminated, locked, or modified.
        </div>
        <div><label className="text-xs font-medium">Test Notes (optional)</label>
          <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Purpose of this simulation run" /></div>
        {result && (
          <div className="mt-3 space-y-2">
            <div className={`rounded p-2 text-sm font-medium ${result.simulationResult === "SimulatedSuccess" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
              {result.simulationResult === "SimulatedSuccess" ? "✓ Simulation passed — no open issues." : "✗ Simulation flagged issues:"}
              {result.openIssues.map(c => <p key={c} className="text-xs font-normal mt-0.5">• {FERPA31_CODE_LABELS[c] ?? c}</p>)}
            </div>
            {result.simulatedWorkflow.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Simulated Workflow Steps</p>
                <ol className="space-y-1">
                  {result.simulatedWorkflow.map(s => (
                    <li key={s.step} className="flex gap-2 text-xs">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 font-bold shrink-0">{s.step}</span>
                      <div><span className="font-medium">{s.event_}</span> — {s.detail}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <p className="text-xs text-gray-400 italic">{result.note}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleSimulate}>Run Simulation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa31ReviewDialog({ policy, onClose }: { policy: FerpaSessionTimeoutControl | null; onClose: () => void }) {
  const { header } = useAuth31();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/session-timeout-controls";
  const [reviewer,      setReviewer]      = useState(policy?.reviewer ?? "");
  const [reviewNotes,   setReviewNotes]   = useState("");
  const [nextReviewDate,setNextReviewDate]= useState("");
  const [errors,        setErrors]        = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!policy) return;
    const errs: Record<string, string> = {};
    required(errs, "reviewer", reviewer, "Reviewer name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const res = await fetch(`${BASE}/${policy.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!policy) return null;
  return (
    <Dialog open={!!policy} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Complete Review — {policy.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer Name *</label><Input value={reviewer} onChange={e => { setReviewer(e.target.value); clearErr(setErrors, "reviewer"); }} className={fieldCls("", errors, "reviewer")} aria-invalid={!!errors.reviewer} placeholder="CISO" />{fErr(errors, "reviewer")}</div>
          <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional review notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Remediation dialog ────────────────────────────────────────────────────────
function Ferpa31RemediationDialog({ policy, onClose }: { policy: FerpaSessionTimeoutControl | null; onClose: () => void }) {
  const { header } = useAuth31();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/session-timeout-controls";
  const [notes, setNotes]   = useState(policy?.remediationNotes ?? "");
  const [date,  setDate]    = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!policy) return;
    const errs: Record<string, string> = {};
    required(errs, "notes", notes, "Remediation notes");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const res = await fetch(`${BASE}/${policy.id}/remediation`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ remediationNotes: notes, remediationDate: date || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save remediation", variant: "destructive" }); return; }
    toast({ title: "Remediation recorded." });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!policy) return null;
  return (
    <Dialog open={!!policy} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Remediation — {policy.applicationName}</DialogTitle></DialogHeader>
        {policy.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-2 mb-2 text-xs text-red-700">
            <p className="font-medium mb-1">Open issues:</p>
            {policy.validationCodes.map(c => <p key={c}>• {FERPA31_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Remediation Notes *</label>
            <textarea className={`w-full border rounded px-2 py-1.5 text-sm ${errors.notes ? "border-red-500" : ""}`} rows={3} value={notes} onChange={e => { setNotes(e.target.value); clearErr(setErrors, "notes"); }} placeholder="Describe remediation steps taken or planned" aria-invalid={!!errors.notes} />{fErr(errors, "notes")}</div>
          <div><label className="text-xs font-medium">Remediation Date</label><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Save Remediation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa31DeleteDialog({ policy, onClose }: { policy: FerpaSessionTimeoutControl | null; onClose: () => void }) {
  const { header } = useAuth31();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/session-timeout-controls";

  async function handleDelete() {
    if (!policy) return;
    const res = await fetch(`${BASE}/${policy.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "Session policy removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!policy) return null;
  return (
    <Dialog open={!!policy} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove Session Policy</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{policy.applicationName}</strong>? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa31DetailDialog({ policy, canManage, onClose, onEdit, onValidate, onSimulate, onReview, onRemediate, onDelete }: {
  policy: FerpaSessionTimeoutControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (p: FerpaSessionTimeoutControl) => void;
  onValidate: (p: FerpaSessionTimeoutControl) => void;
  onSimulate: (p: FerpaSessionTimeoutControl) => void;
  onReview: (p: FerpaSessionTimeoutControl) => void;
  onRemediate: (p: FerpaSessionTimeoutControl) => void;
  onDelete: (p: FerpaSessionTimeoutControl) => void;
}) {
  if (!policy) return null;
  const isOverdue = policy.nextReviewDate && new Date(policy.nextReviewDate) < new Date() && policy.reviewStatus !== "Reviewed";
  return (
    <Dialog open={!!policy} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            {policy.applicationName}
            {policy.sessionPolicyName && <span className="text-sm text-gray-400 font-normal">— {policy.sessionPolicyName}</span>}
          </DialogTitle>
        </DialogHeader>
        {policy.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Compliance Issues</p>
            {policy.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA31_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isOverdue && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Review overdue — was due {new Date(policy.nextReviewDate!).toLocaleDateString()}.</p>
          </div>
        )}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Session Timeout Configuration</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Environment</span><span>{policy.environment ?? "—"}</span>
              <span className="text-gray-500">Timeout Enabled</span><span className={policy.sessionTimeoutEnabled ? "text-green-700 font-medium" : "text-red-600"}>{policy.sessionTimeoutEnabled ? "✓ Yes" : "✗ No"}</span>
              <span className="text-gray-500">Idle Timeout</span><span className={policy.idleTimeoutMinutes ? "text-green-700" : "text-red-600"}>{policy.idleTimeoutMinutes ? `${policy.idleTimeoutMinutes} minutes` : "Not configured"}</span>
              <span className="text-gray-500">Absolute Timeout</span><span className={policy.absoluteTimeoutMinutes ? "text-green-700" : "text-red-600"}>{policy.absoluteTimeoutMinutes ? `${policy.absoluteTimeoutMinutes} minutes` : "Not configured"}</span>
              <span className="text-gray-500">Warning</span><span className={policy.warningNotificationEnabled ? "text-green-700" : "text-gray-400"}>{policy.warningNotificationEnabled ? `✓ ${policy.warningTimeBeforeLogoutMinutes ?? "?"} min before logout` : "—"}</span>
              <span className="text-gray-500">Logout Action</span><span>{FERPA31_LOGOUT_LABELS[policy.logoutActionType ?? ""] ?? policy.logoutActionType ?? "—"}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Privileged &amp; Admin Session Policy</p>
            <div className="grid grid-cols-1 gap-y-1 text-xs">
              <div className="flex gap-2"><span className="text-gray-500 w-40">Privileged User Policy</span><span className={policy.privilegedUserTimeoutPolicy ? "text-green-700" : "text-red-500 italic"}>{policy.privilegedUserTimeoutPolicy ?? "Not defined"}</span></div>
              <div className="flex gap-2"><span className="text-gray-500 w-40">Admin Session Policy</span><span>{policy.adminSessionTimeoutPolicy ?? "—"}</span></div>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Remember-Me &amp; Evidence</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Remember Me</span><span className={policy.rememberMeEnabled ? "text-orange-600 font-medium" : "text-green-700"}>{policy.rememberMeEnabled ? "⚠ Enabled (risk)" : "✓ Disabled"}</span>
              <span className="text-gray-500">Evidence Ref</span><span className={policy.evidenceReference ? "text-green-700 font-mono" : "text-red-500 italic"}>{policy.evidenceReference ?? "Missing"}</span>
              <span className="text-gray-500">Compliance Status</span><span>{policy.complianceStatus}</span>
            </div>
          </div>
          {policy.remediationNotes && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Remediation</p>
              <p className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">{policy.remediationNotes}</p>
              {policy.lastRemediationDate && <p className="text-xs text-gray-400 mt-1">Last remediated: {new Date(policy.lastRemediationDate).toLocaleDateString()}</p>}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Ownership &amp; Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Owner</span><span>{policy.owner ?? "—"}</span>
              <span className="text-gray-500">Reviewer</span><span>{policy.reviewer ?? "—"}</span>
              <span className="text-gray-500">Review Status</span><span>{policy.reviewStatus}</span>
              <span className="text-gray-500">Last Review</span><span>{policy.lastReviewDate ? new Date(policy.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{policy.nextReviewDate ? new Date(policy.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(policy)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(policy)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onSimulate(policy)}>Simulate</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(policy)}>Review</Button>
            <Button variant="outline" size="sm" onClick={() => onRemediate(policy)}>Remediation</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(policy)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaSessionTimeoutSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth31();
  const BASE       = "/api/organization/compliance/ferpa/session-timeout-controls";

  const [appFilter,         setAppFilter]         = useState("");
  const [envFilter,         setEnvFilter]         = useState("");
  const [timeoutFilter,     setTimeoutFilter]     = useState("");
  const [actionFilter,      setActionFilter]       = useState("");
  const [riskFilter,        setRiskFilter]         = useState("");
  const [complianceFilter,  setComplianceFilter]   = useState("");

  const [showAdd,        setShowAdd]        = useState(false);
  const [editItem,       setEditItem]       = useState<FerpaSessionTimeoutControl | null>(null);
  const [validateItem,   setValidateItem]   = useState<FerpaSessionTimeoutControl | null>(null);
  const [simulateItem,   setSimulateItem]   = useState<FerpaSessionTimeoutControl | null>(null);
  const [reviewItem,     setReviewItem]     = useState<FerpaSessionTimeoutControl | null>(null);
  const [remediateItem,  setRemediateItem]  = useState<FerpaSessionTimeoutControl | null>(null);
  const [deleteItem,     setDeleteItem]     = useState<FerpaSessionTimeoutControl | null>(null);
  const [detailItem,     setDetailItem]     = useState<FerpaSessionTimeoutControl | null>(null);

  const summaryQ = useQuery<Ferpa31Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ policy: FerpaSessionTimeoutControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (appFilter)        params.set("application",    appFilter);
  if (envFilter)        params.set("environment",    envFilter);
  if (timeoutFilter)    params.set("timeoutEnabled", timeoutFilter);
  if (actionFilter)     params.set("logoutAction",   actionFilter);
  if (riskFilter)       params.set("riskLevel",      riskFilter);
  if (complianceFilter) params.set("complianceStatus", complianceFilter);

  const listQ = useQuery<{ total: number; items: FerpaSessionTimeoutControl[] }>({
    queryKey: [BASE, appFilter, envFilter, timeoutFilter, actionFilter, riskFilter, complianceFilter],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Session Policies",    value: s.totalSessionPolicies,    icon: <Clock className="w-4 h-4 text-blue-500" />,        highlight: false },
    { label: "Timeout Enabled",           value: s.timeoutEnabled,          icon: <CheckCircle2 className="w-4 h-4 text-green-500" />, highlight: false },
    { label: "Compliant Policies",        value: s.compliantPolicies,       icon: <Shield className="w-4 h-4 text-green-500" />,       highlight: false },
    { label: "Missing Timeout Config",    value: s.missingTimeoutConfig,    icon: <AlertTriangle className="w-4 h-4 text-red-500" />,  highlight: s.missingTimeoutConfig > 0 },
    { label: "Privileged Session Risks",  value: s.privilegedSessionRisks,  icon: <XCircle className="w-4 h-4 text-red-500" />,        highlight: s.privilegedSessionRisks > 0 },
    { label: "Pending Reviews",           value: s.pendingReviews,          icon: <Clock className="w-4 h-4 text-orange-500" />,       highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="w-4 h-4 text-blue-600" />
              Automatic Logout (Session Timeout)
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks session timeout policy configuration metadata for FERPA compliance evidence.
              No session tokens, authentication cookies, access tokens, or user credentials are stored.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Policy
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Session Timeout Issues ({issuesQ.data!.length} policies)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { policy: FerpaSessionTimeoutControl; codes: string[] }) => (
                <div key={item.policy.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.policy.applicationName}</span>
                  {ferpa31RiskBadge(item.policy.riskLevel)}
                  <span className="text-red-600">{item.codes.map(c => FERPA31_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Input placeholder="Filter by app…" value={appFilter} onChange={e => setAppFilter(e.target.value)} className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={envFilter} onChange={e => setEnvFilter(e.target.value)}>
            <option value="">All environments</option>
            {FERPA31_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={timeoutFilter} onChange={e => setTimeoutFilter(e.target.value)}>
            <option value="">Timeout — all</option>
            <option value="true">Enabled</option>
            <option value="false">Disabled</option>
          </select>
          <select className="border rounded px-2 text-xs h-8" value={actionFilter} onChange={e => setActionFilter(e.target.value)}>
            <option value="">All logout actions</option>
            {FERPA31_LOGOUT_ACTIONS.map(v => <option key={v} value={v}>{FERPA31_LOGOUT_LABELS[v]}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA31_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={complianceFilter} onChange={e => setComplianceFilter(e.target.value)}>
            <option value="">All compliance</option>
            {FERPA31_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No session timeout policies registered. Add a policy to start tracking automatic logout configurations.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(policy => {
              const isOverdue = policy.nextReviewDate && new Date(policy.nextReviewDate) < new Date() && policy.reviewStatus !== "Reviewed";
              return (
                <div
                  key={policy.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(policy)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="text-sm font-medium text-gray-800 truncate">{policy.applicationName}</span>
                      {ferpa31StatusBadge(policy.status)}
                      {ferpa31RiskBadge(policy.riskLevel)}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{policy.environment ?? "—"}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className={`text-xs font-medium ${policy.sessionTimeoutEnabled ? "text-green-600" : "text-red-600"}`}>{policy.sessionTimeoutEnabled ? "✓ Timeout" : "✗ Timeout"}</span>
                    {policy.idleTimeoutMinutes && <span className="text-xs text-gray-600">Idle: {policy.idleTimeoutMinutes}min</span>}
                    {policy.absoluteTimeoutMinutes && <span className="text-xs text-gray-600">Abs: {policy.absoluteTimeoutMinutes}min</span>}
                    {policy.logoutActionType && <span className="text-xs text-blue-600">{FERPA31_LOGOUT_LABELS[policy.logoutActionType] ?? policy.logoutActionType}</span>}
                    {policy.warningNotificationEnabled && <span className="text-xs text-green-600">✓ Warning</span>}
                    {policy.privilegedUserTimeoutPolicy && <span className="text-xs text-green-600">✓ Privileged policy</span>}
                    {policy.rememberMeEnabled && <span className="text-xs text-orange-600">⚠ Remember-me on</span>}
                    {policy.evidenceReference && <span className="text-xs text-green-600">✓ Evidence</span>}
                    {policy.remediationNotes && <span className="text-xs text-yellow-600">⚑ Remediation on file</span>}
                    {isOverdue && <span className="text-xs text-orange-600">⚠ Review overdue</span>}
                  </div>
                  {policy.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {policy.validationCodes.map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA31_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa31AddDialog        open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa31ValidateDialog   policy={validateItem}  onClose={() => setValidateItem(null)} />
      <Ferpa31SimulateDialog   policy={simulateItem}  onClose={() => setSimulateItem(null)} />
      <Ferpa31ReviewDialog     policy={reviewItem}    onClose={() => setReviewItem(null)} />
      <Ferpa31RemediationDialog policy={remediateItem} onClose={() => setRemediateItem(null)} />
      <Ferpa31DeleteDialog     policy={deleteItem}    onClose={() => setDeleteItem(null)} />
      <Ferpa31DetailDialog
        policy={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={p => { setDetailItem(null); setEditItem(p); }}
        onValidate={p => { setDetailItem(null); setValidateItem(p); }}
        onSimulate={p => { setDetailItem(null); setSimulateItem(p); }}
        onReview={p => { setDetailItem(null); setReviewItem(p); }}
        onRemediate={p => { setDetailItem(null); setRemediateItem(p); }}
        onDelete={p => { setDetailItem(null); setDeleteItem(p); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MFA Support Requirement
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa30Summary = {
  totalMfaControls: number;
  mfaEnabled: number;
  mfaEnforced: number;
  privilegedMfaMissing: number;
  nonCompliant: number;
  pendingReviews: number;
};

type FerpaMfaControl = {
  id: string;
  organizationId: string;
  applicationName: string;
  authenticationScope: string | null;
  mfaEnabled: boolean;
  mfaEnforced: boolean;
  supportedMfaMethods: string | null;
  userGroupsRequiringMfa: string | null;
  privilegedAccountMfaRequired: boolean;
  backupMethodConfigured: boolean;
  recoveryProcessConfigured: boolean;
  evidenceReference: string | null;
  complianceStatus: string;
  remediationNotes: string | null;
  lastRemediationDate: string | null;
  owner: string | null;
  reviewer: string | null;
  notes: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

const FERPA30_MFA_METHODS = [
  "AuthenticatorApp", "SMS", "EmailOtp", "HardwareToken", "SecurityKey",
];
const FERPA30_MFA_METHOD_LABELS: Record<string, string> = {
  AuthenticatorApp: "Authenticator App",
  SMS:              "SMS",
  EmailOtp:         "Email OTP",
  HardwareToken:    "Hardware Token",
  SecurityKey:      "Security Key / WebAuthn",
};
const FERPA30_RISK_LEVELS       = ["Low","Medium","High","Critical"];
const FERPA30_STATUSES          = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA30_COMPLIANCE_STATUSES = ["Pending","Compliant","NonCompliant","UnderReview"];

const FERPA30_CODE_LABELS: Record<string, string> = {
  MFA_DISABLED:                  "MFA is disabled",
  MFA_NOT_ENFORCED:              "MFA is not enforced",
  MFA_METHOD_NOT_DEFINED:        "No MFA method defined",
  PRIVILEGED_ACCOUNT_MFA_REQUIRED: "Privileged/admin accounts lack MFA",
  RECOVERY_PROCESS_MISSING:      "Recovery process not configured (MFA is on)",
  BACKUP_METHOD_MISSING:         "Backup authentication method missing (MFA is on)",
  SECURITY_EVIDENCE_MISSING:     "Configuration evidence reference missing",
  REVIEW_OVERDUE:                "Review is overdue",
};

function ferpa30RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa30StatusBadge(status: string) {
  const c =
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function parseMfaMethods(csv: string | null): string[] {
  if (!csv) return [];
  return csv.split(",").map(s => s.trim()).filter(Boolean);
}

function useAuth30() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa30AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaMfaControl | null; onClose: () => void }) {
  const { header } = useAuth30();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/mfa-controls";

  const [form, setForm] = useState({
    applicationName: "", authenticationScope: "",
    mfaEnabled: false, mfaEnforced: false,
    selectedMethods: [] as string[],
    userGroupsRequiringMfa: "",
    privilegedAccountMfaRequired: false,
    backupMethodConfigured: false,
    recoveryProcessConfigured: false,
    evidenceReference: "",
    complianceStatus: "Pending",
    owner: "", reviewer: "", notes: "",
    riskLevel: "High", status: "Draft", nextReviewDate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));
  const toggleMethod = (m: string) => setForm(p => ({
    ...p,
    selectedMethods: p.selectedMethods.includes(m)
      ? p.selectedMethods.filter(x => x !== m)
      : [...p.selectedMethods, m],
  }));

  useState(() => {
    if (initial) setForm({
      applicationName: initial.applicationName,
      authenticationScope: initial.authenticationScope ?? "",
      mfaEnabled: initial.mfaEnabled,
      mfaEnforced: initial.mfaEnforced,
      selectedMethods: parseMfaMethods(initial.supportedMfaMethods),
      userGroupsRequiringMfa: initial.userGroupsRequiringMfa ?? "",
      privilegedAccountMfaRequired: initial.privilegedAccountMfaRequired,
      backupMethodConfigured: initial.backupMethodConfigured,
      recoveryProcessConfigured: initial.recoveryProcessConfigured,
      evidenceReference: initial.evidenceReference ?? "",
      complianceStatus: initial.complianceStatus,
      owner: initial.owner ?? "",
      reviewer: initial.reviewer ?? "",
      notes: initial.notes ?? "",
      riskLevel: initial.riskLevel,
      status: initial.status,
      nextReviewDate: initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "applicationName", form.applicationName, "Application Name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const body = {
      applicationName: form.applicationName.trim(),
      authenticationScope: form.authenticationScope || null,
      mfaEnabled: form.mfaEnabled,
      mfaEnforced: form.mfaEnforced,
      supportedMfaMethods: form.selectedMethods.length > 0 ? form.selectedMethods.join(",") : null,
      userGroupsRequiringMfa: form.userGroupsRequiringMfa || null,
      privilegedAccountMfaRequired: form.privilegedAccountMfaRequired,
      backupMethodConfigured: form.backupMethodConfigured,
      recoveryProcessConfigured: form.recoveryProcessConfigured,
      evidenceReference: form.evidenceReference || null,
      complianceStatus: form.complianceStatus,
      owner: form.owner || null,
      reviewer: form.reviewer || null,
      notes: form.notes || null,
      riskLevel: form.riskLevel,
      status: form.status,
      nextReviewDate: form.nextReviewDate || null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Unable to save", variant: "destructive" }); return; }
    toast({ title: initial ? "MFA control updated." : "MFA control added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit MFA Control" : "Add MFA Control"}</DialogTitle></DialogHeader>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
          ⚠ Store configuration metadata only. Never enter MFA secrets, OTP codes, recovery codes, or private keys here.
        </p>
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium">Application / System Name *</label>
              <Input className={fieldCls("", errors, "applicationName")} aria-invalid={!!errors.applicationName} value={form.applicationName} onChange={e => { set("applicationName", e.target.value); clearErr(setErrors, "applicationName"); }} placeholder="Student Portal" />{fErr(errors, "applicationName")}</div>
            <div className="col-span-2"><label className="text-xs font-medium">Authentication Scope</label>
              <Input value={form.authenticationScope} onChange={e => set("authenticationScope", e.target.value)} placeholder="All users / Staff only / Admin accounts" /></div>
          </div>
          {/* MFA toggles */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">MFA Configuration</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["mfaEnabled",                 "MFA Enabled"],
                ["mfaEnforced",                "MFA Enforced"],
                ["privilegedAccountMfaRequired","Privileged Account MFA Required"],
                ["backupMethodConfigured",     "Backup Method Configured"],
                ["recoveryProcessConfigured",  "Recovery Process Configured"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => setForm(p => ({ ...p, [key]: !p[key as keyof typeof p] }))} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          {/* Methods */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Supported MFA Methods</p>
            <div className="grid grid-cols-2 gap-2">
              {FERPA30_MFA_METHODS.map(m => (
                <label key={m} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={form.selectedMethods.includes(m)} onChange={() => toggleMethod(m)} className="rounded" />
                  {FERPA30_MFA_METHOD_LABELS[m]}
                </label>
              ))}
            </div>
          </div>
          {/* Groups & evidence */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium">User Groups / Roles Requiring MFA</label>
              <Input value={form.userGroupsRequiringMfa} onChange={e => set("userGroupsRequiringMfa", e.target.value)} placeholder="Students, Faculty, Staff, Admins" /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Evidence Reference</label>
              <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://mfa-config-2026.pdf" /></div>
          </div>
          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Owner</label><Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="IT Security Team" /></div>
            <div><label className="text-xs font-medium">Reviewer</label><Input value={form.reviewer} onChange={e => set("reviewer", e.target.value)} placeholder="CISO" /></div>
            <div><label className="text-xs font-medium">Compliance Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.complianceStatus} onChange={e => set("complianceStatus", e.target.value)}>
                {FERPA30_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Risk Level</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                {FERPA30_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA30_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional notes" /></div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add MFA Control"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function Ferpa30ValidateDialog({ control, onClose }: { control: FerpaMfaControl | null; onClose: () => void }) {
  const { header } = useAuth30();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/mfa-controls";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string } | null>(null);

  async function handleValidate() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  const methods = parseMfaMethods(control.supportedMfaMethods);
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate MFA Configuration — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-3">
          <span className="text-gray-500">MFA Enabled</span><span className={control.mfaEnabled ? "text-green-700 font-medium" : "text-red-600"}>{control.mfaEnabled ? "✓ Yes" : "✗ No"}</span>
          <span className="text-gray-500">MFA Enforced</span><span className={control.mfaEnforced ? "text-green-700" : "text-red-600"}>{control.mfaEnforced ? "✓ Yes" : "✗ No"}</span>
          <span className="text-gray-500">Methods</span><span>{methods.length > 0 ? methods.map(m => FERPA30_MFA_METHOD_LABELS[m] ?? m).join(", ") : <em className="text-red-500">None defined</em>}</span>
          <span className="text-gray-500">Privileged MFA</span><span className={control.privilegedAccountMfaRequired ? "text-green-700" : "text-red-600"}>{control.privilegedAccountMfaRequired ? "✓ Required" : "✗ Not required"}</span>
          <span className="text-gray-500">Recovery Process</span><span className={control.recoveryProcessConfigured ? "text-green-700" : "text-gray-400"}>{control.recoveryProcessConfigured ? "✓ Configured" : "—"}</span>
          <span className="text-gray-500">Backup Method</span><span className={control.backupMethodConfigured ? "text-green-700" : "text-gray-400"}>{control.backupMethodConfigured ? "✓ Configured" : "—"}</span>
          <span className="text-gray-500">Evidence</span><span className={control.evidenceReference ? "text-green-700" : "text-red-600"}>{control.evidenceReference ? "Present" : "Missing"}</span>
        </div>
        {result && (
          <div className={`rounded p-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ MFA configuration is compliant." : "✗ Configuration issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA30_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">Validated at {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Test MFA Flow dialog ──────────────────────────────────────────────────────
function Ferpa30TestDialog({ control, onClose }: { control: FerpaMfaControl | null; onClose: () => void }) {
  const { header } = useAuth30();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/mfa-controls";
  const [notes,  setNotes]  = useState("");
  const [result, setResult] = useState<{ simulationResult: string; isCompliant: boolean; openIssues: string[]; testedAt: string; note: string } | null>(null);

  async function handleTest() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to complete test", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Test MFA Flow — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-800">
          ℹ This is a configuration simulation only. No real OTP codes are generated and no MFA challenge is issued.
        </div>
        <div><label className="text-xs font-medium">Test Notes (optional)</label>
          <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Purpose of this test run" /></div>
        {result && (
          <div className={`rounded p-3 mt-3 text-sm ${result.simulationResult === "SimulatedSuccess" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">
              {result.simulationResult === "SimulatedSuccess" ? "✓ Simulation passed — no open issues." : "✗ Simulation flagged issues:"}
            </p>
            {result.openIssues.map(c => <p key={c} className="text-xs text-red-700">• {FERPA30_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">{result.note}</p>
            <p className="text-xs text-gray-500">Tested at {new Date(result.testedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleTest}>Run Simulation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa30ReviewDialog({ control, onClose }: { control: FerpaMfaControl | null; onClose: () => void }) {
  const { header } = useAuth30();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/mfa-controls";
  const [reviewer,      setReviewer]      = useState(control?.reviewer ?? "");
  const [reviewNotes,   setReviewNotes]   = useState("");
  const [nextReviewDate,setNextReviewDate]= useState("");
  const [errors,        setErrors]        = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!control) return;
    const errs: Record<string, string> = {};
    if (!reviewer.trim()) errs.reviewer = "Reviewer name is required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    const res = await fetch(`${BASE}/${control.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review.", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Complete Review — {control.applicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Reviewer Name *</label>
            <Input value={reviewer} onChange={e => { setReviewer(e.target.value); clearErr(setErrors, "reviewer"); }} className={fieldCls("", errors, "reviewer")} aria-invalid={!!errors.reviewer} placeholder="CISO" />
            {fErr(errors, "reviewer")}
          </div>
          <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional review notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Remediation dialog ────────────────────────────────────────────────────────
function Ferpa30RemediationDialog({ control, onClose }: { control: FerpaMfaControl | null; onClose: () => void }) {
  const { header } = useAuth30();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/mfa-controls";
  const [notes,  setNotes]  = useState(control?.remediationNotes ?? "");
  const [date,   setDate]   = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!control) return;
    const errs: Record<string, string> = {};
    if (!notes.trim()) errs.notes = "Remediation notes are required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    const res = await fetch(`${BASE}/${control.id}/remediation`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ remediationNotes: notes, remediationDate: date || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save remediation.", variant: "destructive" }); return; }
    toast({ title: "Remediation recorded." });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Remediation — {control.applicationName}</DialogTitle></DialogHeader>
        {control.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-2 mb-2 text-xs text-red-700">
            <p className="font-medium mb-1">Open issues to remediate:</p>
            {control.validationCodes.map(c => <p key={c}>• {FERPA30_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Remediation Notes *</label>
            <textarea
              className={`w-full border rounded px-2 py-1.5 text-sm ${errors.notes ? "border-red-500" : ""}`}
              rows={3}
              value={notes}
              onChange={e => { setNotes(e.target.value); clearErr(setErrors, "notes"); }}
              placeholder="Describe remediation steps taken or planned"
              aria-invalid={!!errors.notes}
            />
            {fErr(errors, "notes")}
          </div>
          <div><label className="text-xs font-medium">Remediation Date</label><Input type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Save Remediation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa30DeleteDialog({ control, onClose }: { control: FerpaMfaControl | null; onClose: () => void }) {
  const { header } = useAuth30();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/mfa-controls";

  async function handleDelete() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "MFA control removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove MFA Control</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{control.applicationName}</strong>? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa30DetailDialog({ control, canManage, onClose, onEdit, onValidate, onTest, onReview, onRemediate, onDelete }: {
  control: FerpaMfaControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (c: FerpaMfaControl) => void;
  onValidate: (c: FerpaMfaControl) => void;
  onTest: (c: FerpaMfaControl) => void;
  onReview: (c: FerpaMfaControl) => void;
  onRemediate: (c: FerpaMfaControl) => void;
  onDelete: (c: FerpaMfaControl) => void;
}) {
  if (!control) return null;
  const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
  const methods   = parseMfaMethods(control.supportedMfaMethods);
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-blue-600" />
            {control.applicationName}
          </DialogTitle>
        </DialogHeader>
        {control.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Compliance Issues</p>
            {control.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA30_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isOverdue && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Review overdue — was due {new Date(control.nextReviewDate!).toLocaleDateString()}.</p>
          </div>
        )}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">MFA Configuration</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Scope</span><span>{control.authenticationScope ?? "—"}</span>
              <span className="text-gray-500">MFA Enabled</span><span className={control.mfaEnabled ? "text-green-700 font-medium" : "text-red-600"}>{control.mfaEnabled ? "✓ Yes" : "✗ No"}</span>
              <span className="text-gray-500">MFA Enforced</span><span className={control.mfaEnforced ? "text-green-700" : "text-red-600"}>{control.mfaEnforced ? "✓ Yes" : "✗ No"}</span>
              <span className="text-gray-500">Privileged MFA</span><span className={control.privilegedAccountMfaRequired ? "text-green-700" : "text-red-600"}>{control.privilegedAccountMfaRequired ? "✓ Required" : "✗ Not required"}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Supported Authentication Methods</p>
            {methods.length === 0
              ? <p className="text-xs text-red-500 italic">No methods defined</p>
              : <div className="flex flex-wrap gap-1.5">{methods.map(m => <span key={m} className="inline-flex px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">{FERPA30_MFA_METHOD_LABELS[m] ?? m}</span>)}</div>}
            {control.userGroupsRequiringMfa && <p className="text-xs text-gray-600 mt-1">Groups: {control.userGroupsRequiringMfa}</p>}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recovery &amp; Backup</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Recovery Process</span><span className={control.recoveryProcessConfigured ? "text-green-700" : "text-gray-400"}>{control.recoveryProcessConfigured ? "✓ Configured" : "—"}</span>
              <span className="text-gray-500">Backup Method</span><span className={control.backupMethodConfigured ? "text-green-700" : "text-gray-400"}>{control.backupMethodConfigured ? "✓ Configured" : "—"}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence &amp; Compliance</p>
            <div className="grid grid-cols-1 gap-y-1 text-xs">
              <div className="flex gap-2"><span className="text-gray-500 w-36">Evidence Ref</span><span className="font-mono truncate">{control.evidenceReference ?? <em className="text-red-500 font-sans">Missing</em>}</span></div>
              <div className="flex gap-2"><span className="text-gray-500 w-36">Compliance Status</span><span>{control.complianceStatus}</span></div>
            </div>
          </div>
          {control.remediationNotes && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Remediation</p>
              <p className="text-xs text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">{control.remediationNotes}</p>
              {control.lastRemediationDate && <p className="text-xs text-gray-400 mt-1">Last remediated: {new Date(control.lastRemediationDate).toLocaleDateString()}</p>}
            </div>
          )}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Ownership &amp; Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Owner</span><span>{control.owner ?? "—"}</span>
              <span className="text-gray-500">Reviewer</span><span>{control.reviewer ?? "—"}</span>
              <span className="text-gray-500">Review Status</span><span>{control.reviewStatus}</span>
              <span className="text-gray-500">Last Review</span><span>{control.lastReviewDate ? new Date(control.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{control.nextReviewDate ? new Date(control.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(control)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(control)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onTest(control)}>Test Flow</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(control)}>Review</Button>
            <Button variant="outline" size="sm" onClick={() => onRemediate(control)}>Remediation</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(control)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaMfaControlSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth30();
  const BASE       = "/api/organization/compliance/ferpa/mfa-controls";

  const [appFilter,         setAppFilter]         = useState("");
  const [mfaStatusFilter,   setMfaStatusFilter]   = useState("");
  const [enforcedFilter,    setEnforcedFilter]     = useState("");
  const [methodFilter,      setMethodFilter]       = useState("");
  const [riskFilter,        setRiskFilter]         = useState("");
  const [complianceFilter,  setComplianceFilter]   = useState("");

  const [showAdd,        setShowAdd]        = useState(false);
  const [editItem,       setEditItem]       = useState<FerpaMfaControl | null>(null);
  const [validateItem,   setValidateItem]   = useState<FerpaMfaControl | null>(null);
  const [testItem,       setTestItem]       = useState<FerpaMfaControl | null>(null);
  const [reviewItem,     setReviewItem]     = useState<FerpaMfaControl | null>(null);
  const [remediateItem,  setRemediateItem]  = useState<FerpaMfaControl | null>(null);
  const [deleteItem,     setDeleteItem]     = useState<FerpaMfaControl | null>(null);
  const [detailItem,     setDetailItem]     = useState<FerpaMfaControl | null>(null);

  const summaryQ = useQuery<Ferpa30Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ control: FerpaMfaControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (appFilter)        params.set("application",      appFilter);
  if (mfaStatusFilter)  params.set("mfaStatus",        mfaStatusFilter);
  if (enforcedFilter)   params.set("enforcementStatus", enforcedFilter);
  if (methodFilter)     params.set("authMethod",       methodFilter);
  if (riskFilter)       params.set("riskLevel",        riskFilter);
  if (complianceFilter) params.set("complianceStatus", complianceFilter);

  const listQ = useQuery<{ total: number; items: FerpaMfaControl[] }>({
    queryKey: [BASE, appFilter, mfaStatusFilter, enforcedFilter, methodFilter, riskFilter, complianceFilter],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total MFA Controls",    value: s.totalMfaControls,    icon: <Lock className="w-4 h-4 text-blue-500" />,          highlight: false },
    { label: "MFA Enabled",           value: s.mfaEnabled,          icon: <CheckCircle2 className="w-4 h-4 text-green-500" />, highlight: false },
    { label: "MFA Enforced",          value: s.mfaEnforced,         icon: <Shield className="w-4 h-4 text-blue-500" />,        highlight: false },
    { label: "Privileged MFA Missing",value: s.privilegedMfaMissing,icon: <AlertTriangle className="w-4 h-4 text-red-500" />,  highlight: s.privilegedMfaMissing > 0 },
    { label: "Non-Compliant",         value: s.nonCompliant,        icon: <XCircle className="w-4 h-4 text-red-500" />,        highlight: s.nonCompliant > 0 },
    { label: "Pending Reviews",       value: s.pendingReviews,      icon: <Clock className="w-4 h-4 text-orange-500" />,       highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock className="w-4 h-4 text-blue-600" />
              MFA Support Requirement
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks MFA authentication control configuration metadata for FERPA compliance evidence.
              No secrets, OTP codes, recovery codes, or private keys are stored.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add MFA Control
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> MFA Issues ({issuesQ.data!.length} controls)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { control: FerpaMfaControl; codes: string[] }) => (
                <div key={item.control.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.control.applicationName}</span>
                  {ferpa30RiskBadge(item.control.riskLevel)}
                  <span className="text-red-600">{item.codes.map(c => FERPA30_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Input placeholder="Filter by app…" value={appFilter} onChange={e => setAppFilter(e.target.value)} className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={mfaStatusFilter} onChange={e => setMfaStatusFilter(e.target.value)}>
            <option value="">MFA — all</option>
            <option value="true">MFA enabled</option>
            <option value="false">MFA disabled</option>
          </select>
          <select className="border rounded px-2 text-xs h-8" value={enforcedFilter} onChange={e => setEnforcedFilter(e.target.value)}>
            <option value="">Enforcement — all</option>
            <option value="true">Enforced</option>
            <option value="false">Not enforced</option>
          </select>
          <select className="border rounded px-2 text-xs h-8" value={methodFilter} onChange={e => setMethodFilter(e.target.value)}>
            <option value="">All methods</option>
            {FERPA30_MFA_METHODS.map(m => <option key={m} value={m}>{FERPA30_MFA_METHOD_LABELS[m]}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA30_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={complianceFilter} onChange={e => setComplianceFilter(e.target.value)}>
            <option value="">All compliance</option>
            {FERPA30_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No MFA controls registered. Add a control to start tracking MFA authentication configurations.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(ctrl => {
              const isOverdue = ctrl.nextReviewDate && new Date(ctrl.nextReviewDate) < new Date() && ctrl.reviewStatus !== "Reviewed";
              const methods   = parseMfaMethods(ctrl.supportedMfaMethods);
              return (
                <div
                  key={ctrl.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(ctrl)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="text-sm font-medium text-gray-800 truncate">{ctrl.applicationName}</span>
                      {ferpa30StatusBadge(ctrl.status)}
                      {ferpa30RiskBadge(ctrl.riskLevel)}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{ctrl.authenticationScope ?? "—"}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className={`text-xs font-medium ${ctrl.mfaEnabled ? "text-green-600" : "text-red-600"}`}>{ctrl.mfaEnabled ? "✓ MFA" : "✗ MFA"}</span>
                    <span className={`text-xs ${ctrl.mfaEnforced ? "text-green-600" : "text-orange-500"}`}>{ctrl.mfaEnforced ? "✓ Enforced" : "○ Not enforced"}</span>
                    <span className={`text-xs ${ctrl.privilegedAccountMfaRequired ? "text-green-600" : "text-red-500"}`}>{ctrl.privilegedAccountMfaRequired ? "✓ Privileged" : "✗ Privileged"}</span>
                    {methods.length > 0 && <span className="text-xs text-blue-600">{methods.length} method{methods.length !== 1 ? "s" : ""}</span>}
                    {ctrl.evidenceReference && <span className="text-xs text-green-600">✓ Evidence</span>}
                    {ctrl.remediationNotes && <span className="text-xs text-yellow-600">⚑ Remediation on file</span>}
                    {isOverdue && <span className="text-xs text-orange-600">⚠ Review overdue</span>}
                  </div>
                  {ctrl.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {ctrl.validationCodes.map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA30_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa30AddDialog       open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa30ValidateDialog  control={validateItem}  onClose={() => setValidateItem(null)} />
      <Ferpa30TestDialog      control={testItem}      onClose={() => setTestItem(null)} />
      <Ferpa30ReviewDialog    control={reviewItem}    onClose={() => setReviewItem(null)} />
      <Ferpa30RemediationDialog control={remediateItem} onClose={() => setRemediateItem(null)} />
      <Ferpa30DeleteDialog    control={deleteItem}    onClose={() => setDeleteItem(null)} />
      <Ferpa30DetailDialog
        control={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={c => { setDetailItem(null); setEditItem(c); }}
        onValidate={c => { setDetailItem(null); setValidateItem(c); }}
        onTest={c => { setDetailItem(null); setTestItem(c); }}
        onReview={c => { setDetailItem(null); setReviewItem(c); }}
        onRemediate={c => { setDetailItem(null); setRemediateItem(c); }}
        onDelete={c => { setDetailItem(null); setDeleteItem(c); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SSO Integration Requirement Support
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa29Summary = {
  totalIntegrations: number;
  activeSso: number;
  mfaEnabled: number;
  nonCompliant: number;
  missingMappings: number;
  pendingReviews: number;
};

type FerpaSsoAuthControl = {
  id: string;
  organizationId: string;
  integrationName: string;
  identityProviderName: string | null;
  providerType: string | null;
  authenticationProtocol: string | null;
  environment: string;
  ssoEnabled: boolean;
  mfaRequired: boolean;
  mfaConfigured: boolean;
  attributeMappingConfigured: boolean;
  roleMappingConfigured: boolean;
  certificateReference: string | null;
  configurationEvidenceReference: string | null;
  complianceStatus: string;
  owner: string | null;
  reviewer: string | null;
  notes: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

const FERPA29_PROVIDER_TYPES = ["Entra ID","Okta","Shibboleth","CAS","Other"];
const FERPA29_PROTOCOLS      = ["SAML","OIDC","OAuth2","CAS Protocol"];
const FERPA29_ENVIRONMENTS   = ["Production","Staging","Development","Testing"];
const FERPA29_RISK_LEVELS    = ["Low","Medium","High","Critical"];
const FERPA29_STATUSES       = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA29_COMPLIANCE_STATUSES = ["Pending","Compliant","NonCompliant","UnderReview"];

const FERPA29_CODE_LABELS: Record<string, string> = {
  SSO_NOT_ENABLED:               "SSO not enabled",
  IDENTITY_PROVIDER_MISSING:     "Identity provider not specified",
  AUTHENTICATION_PROTOCOL_MISSING:"Authentication protocol missing",
  ATTRIBUTE_MAPPING_MISSING:     "Attribute mapping not configured",
  ROLE_MAPPING_MISSING:          "Role mapping not configured",
  MFA_CONFIGURATION_REQUIRED:    "MFA required but not configured",
  SECURITY_EVIDENCE_MISSING:     "Configuration evidence reference missing",
  REVIEW_OVERDUE:                "Review is overdue",
};

function ferpa29RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa29StatusBadge(status: string) {
  const c =
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function ferpa29ComplianceBadge(status: string) {
  const c =
    status === "Compliant"   ? "bg-green-100 text-green-700" :
    status === "NonCompliant"? "bg-red-100 text-red-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
                               "bg-gray-100 text-gray-500";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function useAuth29() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa29AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaSsoAuthControl | null; onClose: () => void }) {
  const { header } = useAuth29();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/sso-auth";

  const [form, setForm] = useState({
    integrationName: "", identityProviderName: "", providerType: "Entra ID",
    authenticationProtocol: "SAML", environment: "Production",
    ssoEnabled: false, mfaRequired: false, mfaConfigured: false,
    attributeMappingConfigured: false, roleMappingConfigured: false,
    certificateReference: "", configurationEvidenceReference: "",
    complianceStatus: "Pending", owner: "", reviewer: "", notes: "",
    riskLevel: "High", status: "Draft", nextReviewDate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  useState(() => {
    if (initial) setForm({
      integrationName: initial.integrationName,
      identityProviderName: initial.identityProviderName ?? "",
      providerType: initial.providerType ?? "Entra ID",
      authenticationProtocol: initial.authenticationProtocol ?? "SAML",
      environment: initial.environment,
      ssoEnabled: initial.ssoEnabled,
      mfaRequired: initial.mfaRequired,
      mfaConfigured: initial.mfaConfigured,
      attributeMappingConfigured: initial.attributeMappingConfigured,
      roleMappingConfigured: initial.roleMappingConfigured,
      certificateReference: initial.certificateReference ?? "",
      configurationEvidenceReference: initial.configurationEvidenceReference ?? "",
      complianceStatus: initial.complianceStatus,
      owner: initial.owner ?? "",
      reviewer: initial.reviewer ?? "",
      notes: initial.notes ?? "",
      riskLevel: initial.riskLevel,
      status: initial.status,
      nextReviewDate: initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "integrationName", form.integrationName, "Integration Name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const body = {
      ...form,
      identityProviderName: form.identityProviderName || null,
      certificateReference: form.certificateReference || null,
      configurationEvidenceReference: form.configurationEvidenceReference || null,
      owner: form.owner || null,
      reviewer: form.reviewer || null,
      notes: form.notes || null,
      nextReviewDate: form.nextReviewDate || null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Unable to save", variant: "destructive" }); return; }
    toast({ title: initial ? "SSO config updated." : "SSO config added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit SSO Configuration" : "Add SSO Configuration"}</DialogTitle></DialogHeader>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
          ⚠ Store configuration metadata and reference identifiers only. Do NOT paste private keys or secrets here.
        </p>
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium">Integration Name *</label>
              <Input className={fieldCls("", errors, "integrationName")} aria-invalid={!!errors.integrationName} value={form.integrationName} onChange={e => { set("integrationName", e.target.value); clearErr(setErrors, "integrationName"); }} placeholder="University Entra ID SSO" />{fErr(errors, "integrationName")}</div>
            <div><label className="text-xs font-medium">Identity Provider Name / Issuer</label>
              <Input value={form.identityProviderName} onChange={e => set("identityProviderName", e.target.value)} placeholder="university.edu" /></div>
            <div><label className="text-xs font-medium">Provider Type</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.providerType} onChange={e => set("providerType", e.target.value)}>
                {FERPA29_PROVIDER_TYPES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Authentication Protocol</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.authenticationProtocol} onChange={e => set("authenticationProtocol", e.target.value)}>
                {FERPA29_PROTOCOLS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Environment</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.environment} onChange={e => set("environment", e.target.value)}>
                {FERPA29_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>
          {/* Toggles */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Configuration Status</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["ssoEnabled",               "SSO Enabled"],
                ["mfaRequired",              "MFA Required"],
                ["mfaConfigured",            "MFA Configured"],
                ["attributeMappingConfigured","Attribute Mapping Configured"],
                ["roleMappingConfigured",    "Role Mapping Configured"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => setForm(p => ({ ...p, [key]: !p[key as keyof typeof p] }))} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          {/* Evidence */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Certificate Reference (thumbprint / ID only)</label>
              <Input value={form.certificateReference} onChange={e => set("certificateReference", e.target.value)} placeholder="cert-thumbprint-abc123" /></div>
            <div><label className="text-xs font-medium">Configuration Evidence Reference</label>
              <Input value={form.configurationEvidenceReference} onChange={e => set("configurationEvidenceReference", e.target.value)} placeholder="evidence://sso-config-2026.pdf" /></div>
          </div>
          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Owner</label><Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="IT Identity Team" /></div>
            <div><label className="text-xs font-medium">Reviewer</label><Input value={form.reviewer} onChange={e => set("reviewer", e.target.value)} placeholder="CISO" /></div>
            <div><label className="text-xs font-medium">Compliance Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.complianceStatus} onChange={e => set("complianceStatus", e.target.value)}>
                {FERPA29_COMPLIANCE_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Risk Level</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                {FERPA29_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA29_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional notes" /></div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add SSO Config"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function Ferpa29ValidateDialog({ control, onClose }: { control: FerpaSsoAuthControl | null; onClose: () => void }) {
  const { header } = useAuth29();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/sso-auth";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string } | null>(null);

  async function handleValidate() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate SSO Configuration — {control.integrationName}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <span className="text-gray-500">Provider</span><span>{control.identityProviderName ?? "—"} ({control.providerType ?? "—"})</span>
            <span className="text-gray-500">Protocol</span><span>{control.authenticationProtocol ?? "—"}</span>
            <span className="text-gray-500">Environment</span><span>{control.environment}</span>
            <span className="text-gray-500">SSO</span><span className={control.ssoEnabled ? "text-green-700" : "text-red-600"}>{control.ssoEnabled ? "Enabled" : "Disabled"}</span>
            <span className="text-gray-500">MFA</span><span className={control.mfaConfigured ? "text-green-700" : "text-gray-500"}>{control.mfaConfigured ? "Configured" : "Not configured"}</span>
            <span className="text-gray-500">Attribute Mapping</span><span className={control.attributeMappingConfigured ? "text-green-700" : "text-red-600"}>{control.attributeMappingConfigured ? "✓" : "✗"}</span>
            <span className="text-gray-500">Role Mapping</span><span className={control.roleMappingConfigured ? "text-green-700" : "text-red-600"}>{control.roleMappingConfigured ? "✓" : "✗"}</span>
            <span className="text-gray-500">Evidence</span><span className={control.configurationEvidenceReference ? "text-green-700" : "text-red-600"}>{control.configurationEvidenceReference ? "Present" : "Missing"}</span>
          </div>
        </div>
        {result && (
          <div className={`rounded p-3 mt-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ Configuration is compliant." : "✗ Configuration issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA29_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">Validated at {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Test Auth Workflow dialog ─────────────────────────────────────────────────
function Ferpa29TestDialog({ control, onClose }: { control: FerpaSsoAuthControl | null; onClose: () => void }) {
  const { header } = useAuth29();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/sso-auth";
  const [notes,  setNotes]  = useState("");
  const [result, setResult] = useState<{ simulationResult: string; isCompliant: boolean; openIssues: string[]; testedAt: string; note: string } | null>(null);

  async function handleTest() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to complete test", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Test Authentication Workflow — {control.integrationName}</DialogTitle></DialogHeader>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3 text-xs text-blue-800">
          ℹ This runs a configuration simulation only. No actual IdP login or authentication request is performed.
        </div>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Test Notes (optional)</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Purpose of this test run" /></div>
        </div>
        {result && (
          <div className={`rounded p-3 mt-3 text-sm ${result.simulationResult === "SimulatedSuccess" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">
              {result.simulationResult === "SimulatedSuccess" ? "✓ Simulation passed — no open issues." : "✗ Simulation flagged open issues:"}
            </p>
            {result.openIssues.map(c => <p key={c} className="text-xs text-red-700">• {FERPA29_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">{result.note}</p>
            <p className="text-xs text-gray-500">Tested at {new Date(result.testedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleTest}>Run Simulation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa29ReviewDialog({ control, onClose }: { control: FerpaSsoAuthControl | null; onClose: () => void }) {
  const { header } = useAuth29();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/sso-auth";
  const [reviewer,      setReviewer]      = useState(control?.reviewer ?? "");
  const [reviewNotes,   setReviewNotes]   = useState("");
  const [nextReviewDate,setNextReviewDate]= useState("");
  const [errors,        setErrors]        = useState<Record<string, string>>({});

  async function handleSubmit() {
    if (!control) return;
    const errs: Record<string, string> = {};
    if (!reviewer.trim()) errs.reviewer = "Reviewer name is required.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      return;
    }
    const res = await fetch(`${BASE}/${control.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review.", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Review SSO Configuration — {control.integrationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Reviewer Name *</label>
            <Input value={reviewer} onChange={e => { setReviewer(e.target.value); clearErr(setErrors, "reviewer"); }} className={fieldCls("", errors, "reviewer")} aria-invalid={!!errors.reviewer} placeholder="CISO" />
            {fErr(errors, "reviewer")}
          </div>
          <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional review notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa29DeleteDialog({ control, onClose }: { control: FerpaSsoAuthControl | null; onClose: () => void }) {
  const { header } = useAuth29();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/sso-auth";

  async function handleDelete() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "SSO config removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove SSO Configuration</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{control.integrationName}</strong>? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa29DetailDialog({ control, canManage, onClose, onEdit, onValidate, onTest, onReview, onDelete }: {
  control: FerpaSsoAuthControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (c: FerpaSsoAuthControl) => void;
  onValidate: (c: FerpaSsoAuthControl) => void;
  onTest: (c: FerpaSsoAuthControl) => void;
  onReview: (c: FerpaSsoAuthControl) => void;
  onDelete: (c: FerpaSsoAuthControl) => void;
}) {
  if (!control) return null;
  const isOverdue = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-600" />
            {control.integrationName}
          </DialogTitle>
        </DialogHeader>
        {control.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Compliance Issues</p>
            {control.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA29_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isOverdue && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Review overdue — was due {new Date(control.nextReviewDate!).toLocaleDateString()}.</p>
          </div>
        )}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Identity Provider</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Provider Name</span><span>{control.identityProviderName ?? "—"}</span>
              <span className="text-gray-500">Provider Type</span><span>{control.providerType ?? "—"}</span>
              <span className="text-gray-500">Protocol</span><span>{control.authenticationProtocol ?? "—"}</span>
              <span className="text-gray-500">Environment</span><span>{control.environment}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Authentication Configuration</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">SSO Enabled</span><span className={control.ssoEnabled ? "text-green-700 font-medium" : "text-red-600"}>{control.ssoEnabled ? "✓ Yes" : "✗ No"}</span>
              <span className="text-gray-500">MFA Required</span><span>{control.mfaRequired ? "Yes" : "No"}</span>
              <span className="text-gray-500">MFA Configured</span><span className={control.mfaConfigured ? "text-green-700" : "text-gray-500"}>{control.mfaConfigured ? "✓ Yes" : "—"}</span>
              <span className="text-gray-500">Attribute Mapping</span><span className={control.attributeMappingConfigured ? "text-green-700" : "text-red-600"}>{control.attributeMappingConfigured ? "✓ Configured" : "✗ Missing"}</span>
              <span className="text-gray-500">Role Mapping</span><span className={control.roleMappingConfigured ? "text-green-700" : "text-red-600"}>{control.roleMappingConfigured ? "✓ Configured" : "✗ Missing"}</span>
              <span className="text-gray-500">Compliance Status</span><span>{ferpa29ComplianceBadge(control.complianceStatus)}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidence References</p>
            <div className="grid grid-cols-1 gap-y-1 text-xs">
              <div className="flex gap-2"><span className="text-gray-500 w-40">Certificate Ref</span><span className="font-mono text-gray-700 truncate">{control.certificateReference ?? <em className="text-gray-400 font-sans">Not provided</em>}</span></div>
              <div className="flex gap-2"><span className="text-gray-500 w-40">Config Evidence</span><span className="font-mono text-gray-700 truncate">{control.configurationEvidenceReference ?? <em className="text-red-500 font-sans">Missing</em>}</span></div>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Ownership &amp; Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Owner</span><span>{control.owner ?? "—"}</span>
              <span className="text-gray-500">Reviewer</span><span>{control.reviewer ?? "—"}</span>
              <span className="text-gray-500">Review Status</span><span>{control.reviewStatus}</span>
              <span className="text-gray-500">Last Review</span><span>{control.lastReviewDate ? new Date(control.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{control.nextReviewDate ? new Date(control.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
            {control.reviewNotes && <p className="text-xs text-gray-600 bg-gray-50 rounded p-2 mt-1">{control.reviewNotes}</p>}
            {control.notes && <p className="text-xs text-gray-600 bg-gray-50 rounded p-2 mt-1"><strong>Notes:</strong> {control.notes}</p>}
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(control)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(control)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onTest(control)}>Test Workflow</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(control)}>Review</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(control)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaSsoAuthSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth29();
  const BASE       = "/api/organization/compliance/ferpa/sso-auth";

  const [providerFilter,  setProviderFilter]  = useState("");
  const [protocolFilter,  setProtocolFilter]  = useState("");
  const [envFilter,       setEnvFilter]       = useState("");
  const [statusFilter,    setStatusFilter]    = useState("");
  const [mfaFilter,       setMfaFilter]       = useState("");
  const [riskFilter,      setRiskFilter]      = useState("");

  const [showAdd,      setShowAdd]      = useState(false);
  const [editItem,     setEditItem]     = useState<FerpaSsoAuthControl | null>(null);
  const [validateItem, setValidateItem] = useState<FerpaSsoAuthControl | null>(null);
  const [testItem,     setTestItem]     = useState<FerpaSsoAuthControl | null>(null);
  const [reviewItem,   setReviewItem]   = useState<FerpaSsoAuthControl | null>(null);
  const [deleteItem,   setDeleteItem]   = useState<FerpaSsoAuthControl | null>(null);
  const [detailItem,   setDetailItem]   = useState<FerpaSsoAuthControl | null>(null);

  const summaryQ = useQuery<Ferpa29Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ control: FerpaSsoAuthControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (providerFilter) params.set("provider",    providerFilter);
  if (protocolFilter) params.set("protocol",    protocolFilter);
  if (envFilter)      params.set("environment", envFilter);
  if (statusFilter)   params.set("status",      statusFilter);
  if (mfaFilter)      params.set("mfaEnabled",  mfaFilter);
  if (riskFilter)     params.set("riskLevel",   riskFilter);

  const listQ = useQuery<{ total: number; items: FerpaSsoAuthControl[] }>({
    queryKey: [BASE, providerFilter, protocolFilter, envFilter, statusFilter, mfaFilter, riskFilter],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Integrations", value: s.totalIntegrations, icon: <Shield className="w-4 h-4 text-blue-500" />,        highlight: false },
    { label: "Active SSO",         value: s.activeSso,         icon: <CheckCircle2 className="w-4 h-4 text-green-500" />, highlight: false },
    { label: "MFA Enabled",        value: s.mfaEnabled,        icon: <Lock className="w-4 h-4 text-blue-500" />,          highlight: false },
    { label: "Non-Compliant",      value: s.nonCompliant,      icon: <XCircle className="w-4 h-4 text-red-500" />,        highlight: s.nonCompliant > 0 },
    { label: "Missing Mappings",   value: s.missingMappings,   icon: <AlertTriangle className="w-4 h-4 text-orange-500" />,highlight: s.missingMappings > 0 },
    { label: "Pending Reviews",    value: s.pendingReviews,    icon: <Clock className="w-4 h-4 text-orange-500" />,       highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Shield className="w-4 h-4 text-blue-600" />
              SSO Integration Requirement Support
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks university identity provider SSO authentication configurations and compliance evidence.
              Certificate/key values are never stored — only reference identifiers.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add SSO Config
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Configuration Issues ({issuesQ.data!.length} integrations)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { control: FerpaSsoAuthControl; codes: string[] }) => (
                <div key={item.control.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.control.integrationName}</span>
                  <span className="text-gray-500 shrink-0">({item.control.providerType ?? "—"})</span>
                  <span className="text-red-600">{item.codes.map(c => FERPA29_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Input placeholder="Filter by provider…" value={providerFilter} onChange={e => setProviderFilter(e.target.value)} className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={protocolFilter} onChange={e => setProtocolFilter(e.target.value)}>
            <option value="">All protocols</option>
            {FERPA29_PROTOCOLS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={envFilter} onChange={e => setEnvFilter(e.target.value)}>
            <option value="">All environments</option>
            {FERPA29_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA29_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={mfaFilter} onChange={e => setMfaFilter(e.target.value)}>
            <option value="">MFA — all</option>
            <option value="true">MFA enabled</option>
            <option value="false">MFA disabled</option>
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA29_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No SSO configurations registered. Add a configuration to start tracking IdP integrations.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(ctrl => {
              const isOverdue = ctrl.nextReviewDate && new Date(ctrl.nextReviewDate) < new Date() && ctrl.reviewStatus !== "Reviewed";
              return (
                <div
                  key={ctrl.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(ctrl)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <span className="text-sm font-medium text-gray-800 truncate">{ctrl.integrationName}</span>
                      {ferpa29StatusBadge(ctrl.status)}
                      {ferpa29RiskBadge(ctrl.riskLevel)}
                      {ferpa29ComplianceBadge(ctrl.complianceStatus)}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{ctrl.providerType ?? "—"} · {ctrl.authenticationProtocol ?? "—"} · {ctrl.environment}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className={`text-xs ${ctrl.ssoEnabled ? "text-green-600" : "text-red-600"}`}>{ctrl.ssoEnabled ? "✓ SSO" : "✗ SSO"}</span>
                    <span className={`text-xs ${ctrl.mfaConfigured ? "text-green-600" : "text-gray-400"}`}>{ctrl.mfaConfigured ? "✓ MFA" : "○ MFA"}</span>
                    <span className={`text-xs ${ctrl.attributeMappingConfigured ? "text-green-600" : "text-red-500"}`}>{ctrl.attributeMappingConfigured ? "✓ Attrs" : "✗ Attrs"}</span>
                    <span className={`text-xs ${ctrl.roleMappingConfigured ? "text-green-600" : "text-red-500"}`}>{ctrl.roleMappingConfigured ? "✓ Roles" : "✗ Roles"}</span>
                    {ctrl.configurationEvidenceReference && <span className="text-xs text-green-600">✓ Evidence</span>}
                    {isOverdue && <span className="text-xs text-orange-600">⚠ Review overdue</span>}
                    {ctrl.identityProviderName && <span className="text-xs text-gray-500">{ctrl.identityProviderName}</span>}
                  </div>
                  {ctrl.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {ctrl.validationCodes.map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA29_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa29AddDialog    open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa29ValidateDialog control={validateItem} onClose={() => setValidateItem(null)} />
      <Ferpa29TestDialog     control={testItem}     onClose={() => setTestItem(null)} />
      <Ferpa29ReviewDialog   control={reviewItem}   onClose={() => setReviewItem(null)} />
      <Ferpa29DeleteDialog   control={deleteItem}   onClose={() => setDeleteItem(null)} />
      <Ferpa29DetailDialog
        control={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={c => { setDetailItem(null); setEditItem(c); }}
        onValidate={c => { setDetailItem(null); setValidateItem(c); }}
        onTest={c => { setDetailItem(null); setTestItem(c); }}
        onReview={c => { setDetailItem(null); setReviewItem(c); }}
        onDelete={c => { setDetailItem(null); setDeleteItem(c); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FERPA-28 — Prevent Cross-User Student Conversation Data Leakage
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa28Summary = {
  totalAiSystems: number;
  compliant: number;
  nonCompliant: number;
  isolationTestsDone: number;
  leakageRisks: number;
  pendingReviews: number;
};

type FerpaAiIsolationControl = {
  id: string;
  organizationId: string;
  aiApplicationName: string;
  modelProvider: string | null;
  environment: string;
  userIsolationEnabled: boolean;
  conversationIsolationEnabled: boolean;
  studentContextIsolationEnabled: boolean;
  tenantIsolationEnabled: boolean;
  retrievalFilteringEnabled: boolean;
  authorizationCheckEnabled: boolean;
  dataLeakageTestingStatus: string;
  lastSecurityTestDate: string | null;
  testEvidenceReference: string | null;
  testNotes: string | null;
  owner: string | null;
  reviewer: string | null;
  reviewStatus: string;
  reviewNotes: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  riskLevel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

const FERPA28_PROVIDERS    = ["OpenAI","Anthropic","Azure OpenAI","Google","Amazon Bedrock","Ollama","Other"];
const FERPA28_ENVIRONMENTS = ["Production","Staging","Development","Testing"];
const FERPA28_RISK_LEVELS  = ["Low","Medium","High","Critical"];
const FERPA28_STATUSES     = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA28_TEST_STATUSES= ["NotTested","InProgress","Passed","Failed"];

const FERPA28_CODE_LABELS: Record<string, string> = {
  USER_ISOLATION_DISABLED:        "User isolation not enabled",
  CONVERSATION_ISOLATION_DISABLED:"Conversation isolation not enabled",
  STUDENT_CONTEXT_FILTER_MISSING: "Student context isolation missing",
  TENANT_ISOLATION_MISSING:       "Tenant boundary enforcement missing",
  AUTHORIZATION_CHECK_MISSING:    "Authorization checks not enabled",
  RETRIEVAL_FILTER_MISSING:       "Retrieval filtering not enabled",
  SECURITY_TEST_REQUIRED:         "Data leakage security test required",
  EVIDENCE_REQUIRED:              "Test evidence reference required",
};

function ferpa28RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function ferpa28TestBadge(status: string) {
  const c =
    status === "Passed"     ? "bg-green-100 text-green-700" :
    status === "Failed"     ? "bg-red-100 text-red-700" :
    status === "InProgress" ? "bg-blue-100 text-blue-700" :
                              "bg-gray-100 text-gray-500";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function ferpa28StatusBadge(status: string) {
  const c =
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

// Isolation checklist row
function IsolationCheckRow({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {enabled
        ? <span className="text-green-600 font-bold">✓</span>
        : <span className="text-red-500 font-bold">✗</span>}
      <span className={enabled ? "text-gray-700" : "text-red-600"}>{label}</span>
    </div>
  );
}

function useAuth28() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function Ferpa28AddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaAiIsolationControl | null; onClose: () => void }) {
  const { header } = useAuth28();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/ai-isolation";

  const [form, setForm] = useState({
    aiApplicationName: "", modelProvider: "OpenAI", environment: "Production",
    userIsolationEnabled: false, conversationIsolationEnabled: false,
    studentContextIsolationEnabled: false, tenantIsolationEnabled: false,
    retrievalFilteringEnabled: false, authorizationCheckEnabled: false,
    dataLeakageTestingStatus: "NotTested", testEvidenceReference: "",
    testNotes: "", owner: "", reviewer: "",
    riskLevel: "High", status: "Draft", nextReviewDate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  // Hydrate on edit open
  useState(() => {
    if (initial) setForm({
      aiApplicationName: initial.aiApplicationName,
      modelProvider: initial.modelProvider ?? "OpenAI",
      environment: initial.environment,
      userIsolationEnabled: initial.userIsolationEnabled,
      conversationIsolationEnabled: initial.conversationIsolationEnabled,
      studentContextIsolationEnabled: initial.studentContextIsolationEnabled,
      tenantIsolationEnabled: initial.tenantIsolationEnabled,
      retrievalFilteringEnabled: initial.retrievalFilteringEnabled,
      authorizationCheckEnabled: initial.authorizationCheckEnabled,
      dataLeakageTestingStatus: initial.dataLeakageTestingStatus,
      testEvidenceReference: initial.testEvidenceReference ?? "",
      testNotes: initial.testNotes ?? "",
      owner: initial.owner ?? "",
      reviewer: initial.reviewer ?? "",
      riskLevel: initial.riskLevel,
      status: initial.status,
      nextReviewDate: initial.nextReviewDate ? initial.nextReviewDate.split("T")[0] : "",
    });
  });

  async function handleSubmit() {
    const errs: Record<string, string> = {};
    required(errs, "aiApplicationName", form.aiApplicationName, "Application Name");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const body = {
      ...form,
      testEvidenceReference: form.testEvidenceReference || null,
      testNotes: form.testNotes || null,
      owner: form.owner || null,
      reviewer: form.reviewer || null,
      nextReviewDate: form.nextReviewDate || null,
      lastSecurityTestDate: null,
    };
    const url    = initial ? `${BASE}/${initial.id}` : BASE;
    const method = initial ? "PUT" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Unable to save", variant: "destructive" }); return; }
    toast({ title: initial ? "Control updated." : "AI control added." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;
  const toggleField = (k: string) => set(k, !form[k as keyof typeof form]);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Edit AI Isolation Control" : "Add AI Isolation Control"}</DialogTitle></DialogHeader>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
          ⚠ Track isolation settings only. No conversation content is stored here.
        </p>
        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium">AI Application Name *</label>
              <Input className={fieldCls("", errors, "aiApplicationName")} aria-invalid={!!errors.aiApplicationName} value={form.aiApplicationName} onChange={e => { set("aiApplicationName", e.target.value); clearErr(setErrors, "aiApplicationName"); }} placeholder="Student Advising Bot" />{fErr(errors, "aiApplicationName")}</div>
            <div><label className="text-xs font-medium">Model / Provider</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.modelProvider} onChange={e => set("modelProvider", e.target.value)}>
                {FERPA28_PROVIDERS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Environment</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.environment} onChange={e => set("environment", e.target.value)}>
                {FERPA28_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>
          {/* Isolation toggles */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Isolation Controls</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["userIsolationEnabled",           "User Isolation"],
                ["conversationIsolationEnabled",   "Conversation Isolation"],
                ["studentContextIsolationEnabled", "Student Context Isolation"],
                ["tenantIsolationEnabled",         "Tenant Isolation"],
                ["retrievalFilteringEnabled",      "Retrieval Filtering"],
                ["authorizationCheckEnabled",      "Authorization Checks"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!form[key as keyof typeof form]} onChange={() => toggleField(key)} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          {/* Testing */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Testing Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.dataLeakageTestingStatus} onChange={e => set("dataLeakageTestingStatus", e.target.value)}>
                {FERPA28_TEST_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Test Evidence Reference</label>
              <Input value={form.testEvidenceReference} onChange={e => set("testEvidenceReference", e.target.value)} placeholder="evidence://pentest-2026.pdf" /></div>
            <div className="col-span-2"><label className="text-xs font-medium">Test Notes</label>
              <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.testNotes} onChange={e => set("testNotes", e.target.value)} placeholder="Optional test notes" /></div>
          </div>
          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">Owner</label><Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="IT Security Team" /></div>
            <div><label className="text-xs font-medium">Reviewer</label><Input value={form.reviewer} onChange={e => set("reviewer", e.target.value)} placeholder="Compliance Officer" /></div>
            <div><label className="text-xs font-medium">Risk Level</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                {FERPA28_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Status</label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA28_STATUSES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} /></div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>{initial ? "Save Changes" : "Add Control"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate Isolation dialog ─────────────────────────────────────────────────
function Ferpa28ValidateDialog({ control, onClose }: { control: FerpaAiIsolationControl | null; onClose: () => void }) {
  const { header } = useAuth28();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/ai-isolation";
  const [result, setResult] = useState<{ isCompliant: boolean; codes: string[]; validatedAt: string } | null>(null);

  async function handleValidate() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate Isolation — {control.aiApplicationName}</DialogTitle></DialogHeader>
        <div className="mb-3 text-sm text-gray-600 space-y-1">
          <p><strong>Provider:</strong> {control.modelProvider ?? "—"}</p>
          <p><strong>Environment:</strong> {control.environment}</p>
        </div>
        <div className="border rounded-lg p-3 space-y-1.5 bg-gray-50">
          <p className="text-xs font-semibold text-gray-600 mb-2">Isolation Checklist</p>
          <IsolationCheckRow label="User Isolation"            enabled={control.userIsolationEnabled} />
          <IsolationCheckRow label="Conversation Isolation"   enabled={control.conversationIsolationEnabled} />
          <IsolationCheckRow label="Student Context Isolation" enabled={control.studentContextIsolationEnabled} />
          <IsolationCheckRow label="Tenant Isolation"          enabled={control.tenantIsolationEnabled} />
          <IsolationCheckRow label="Retrieval Filtering"       enabled={control.retrievalFilteringEnabled} />
          <IsolationCheckRow label="Authorization Checks"      enabled={control.authorizationCheckEnabled} />
        </div>
        {result && (
          <div className={`rounded p-3 mt-3 text-sm ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isCompliant ? "✓ Fully isolated — no leakage risks detected." : "✗ Isolation issues detected:"}</p>
            {result.codes.map(c => <p key={c} className="text-xs text-red-700">• {FERPA28_CODE_LABELS[c] ?? c}</p>)}
            <p className="text-xs text-gray-500 mt-1">Validated at {new Date(result.validatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Record Security Test dialog ────────────────────────────────────────────────
function Ferpa28TestDialog({ control, onClose }: { control: FerpaAiIsolationControl | null; onClose: () => void }) {
  const { header } = useAuth28();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/ai-isolation";
  const [testResult,  setTestResult]  = useState("Passed");
  const [testDate,    setTestDate]    = useState("");
  const [evidence,    setEvidence]    = useState(control?.testEvidenceReference ?? "");
  const [notes,       setNotes]       = useState("");

  async function handleSubmit() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}/test`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ testResult, testDate: testDate || null, evidenceReference: evidence || null, notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Failed to record test", variant: "destructive" }); return; }
    toast({ title: "Security test recorded." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Record Security Test — {control.aiApplicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Test Result *</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={testResult} onChange={e => setTestResult(e.target.value)}>
              {FERPA28_TEST_STATUSES.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-medium">Test Date</label><Input type="date" value={testDate} onChange={e => setTestDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Evidence Reference</label>
            <Input value={evidence} onChange={e => setEvidence(e.target.value)} placeholder="evidence://pentest-2026.pdf" /></div>
          <div><label className="text-xs font-medium">Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe test methodology and findings" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Record Test</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function Ferpa28ReviewDialog({ control, onClose }: { control: FerpaAiIsolationControl | null; onClose: () => void }) {
  const { header } = useAuth28();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/ai-isolation";
  const [reviewer,      setReviewer]      = useState(control?.reviewer ?? "");
  const [reviewNotes,   setReviewNotes]   = useState("");
  const [nextReviewDate,setNextReviewDate]= useState("");

  async function handleSubmit() {
    if (!control) return;
    if (!reviewer.trim()) { toast({ title: "Reviewer name is required.", variant: "destructive" }); return; }
    const res = await fetch(`${BASE}/${control.id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewer, reviewNotes: reviewNotes || null, nextReviewDate: nextReviewDate || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review", variant: "destructive" }); return; }
    toast({ title: "Review completed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Review — {control.aiApplicationName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer Name *</label><Input value={reviewer} onChange={e => setReviewer(e.target.value)} placeholder="Compliance Officer" /></div>
          <div><label className="text-xs font-medium">Next Review Date</label><Input type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Optional review notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Complete Review</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function Ferpa28DeleteDialog({ control, onClose }: { control: FerpaAiIsolationControl | null; onClose: () => void }) {
  const { header } = useAuth28();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/ai-isolation";

  async function handleDelete() {
    if (!control) return;
    const res = await fetch(`${BASE}/${control.id}`, { method: "DELETE", headers: header });
    if (!res.ok) { toast({ title: "Unable to delete", variant: "destructive" }); return; }
    toast({ title: "Control removed." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!control) return null;
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Remove AI Isolation Control</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Remove <strong>{control.aiApplicationName}</strong>? This cannot be undone.</p>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={handleDelete}>Remove</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa28DetailDialog({ control, canManage, onClose, onEdit, onValidate, onTest, onReview, onDelete }: {
  control: FerpaAiIsolationControl | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: (c: FerpaAiIsolationControl) => void;
  onValidate: (c: FerpaAiIsolationControl) => void;
  onTest: (c: FerpaAiIsolationControl) => void;
  onReview: (c: FerpaAiIsolationControl) => void;
  onDelete: (c: FerpaAiIsolationControl) => void;
}) {
  if (!control) return null;
  const isPendingReview = control.nextReviewDate && new Date(control.nextReviewDate) < new Date() && control.reviewStatus !== "Reviewed";
  return (
    <Dialog open={!!control} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-600" />
            {control.aiApplicationName}
          </DialogTitle>
        </DialogHeader>
        {control.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Isolation Issues</p>
            {control.validationCodes.map(c => <p key={c} className="text-xs text-red-600">• {FERPA28_CODE_LABELS[c] ?? c}</p>)}
          </div>
        )}
        {isPendingReview && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Review overdue — next review was due {new Date(control.nextReviewDate!).toLocaleDateString()}.</p>
          </div>
        )}
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Isolation Controls</p>
            <div className="border rounded-lg p-3 bg-gray-50 space-y-1.5">
              <IsolationCheckRow label="User Isolation"            enabled={control.userIsolationEnabled} />
              <IsolationCheckRow label="Conversation Isolation"   enabled={control.conversationIsolationEnabled} />
              <IsolationCheckRow label="Student Context Isolation" enabled={control.studentContextIsolationEnabled} />
              <IsolationCheckRow label="Tenant Isolation"          enabled={control.tenantIsolationEnabled} />
              <IsolationCheckRow label="Retrieval Filtering"       enabled={control.retrievalFilteringEnabled} />
              <IsolationCheckRow label="Authorization Checks"      enabled={control.authorizationCheckEnabled} />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Security Testing</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Status</span><span>{ferpa28TestBadge(control.dataLeakageTestingStatus)}</span>
              <span className="text-gray-500">Last Test</span><span>{control.lastSecurityTestDate ? new Date(control.lastSecurityTestDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Evidence</span><span className="truncate">{control.testEvidenceReference ?? <em className="text-red-500">Not provided</em>}</span>
            </div>
            {control.testNotes && <p className="text-xs text-gray-600 bg-gray-50 rounded p-2 mt-1">{control.testNotes}</p>}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Ownership &amp; Review</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Owner</span><span>{control.owner ?? "—"}</span>
              <span className="text-gray-500">Reviewer</span><span>{control.reviewer ?? "—"}</span>
              <span className="text-gray-500">Review Status</span><span>{control.reviewStatus}</span>
              <span className="text-gray-500">Last Review</span><span>{control.lastReviewDate ? new Date(control.lastReviewDate).toLocaleDateString() : "—"}</span>
              <span className="text-gray-500">Next Review</span><span>{control.nextReviewDate ? new Date(control.nextReviewDate).toLocaleDateString() : "—"}</span>
            </div>
            {control.reviewNotes && <p className="text-xs text-gray-600 bg-gray-50 rounded p-2 mt-1">{control.reviewNotes}</p>}
          </div>
        </div>
        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => onEdit(control)}>Edit</Button>
            <Button variant="outline" size="sm" onClick={() => onValidate(control)}>Validate</Button>
            <Button variant="outline" size="sm" onClick={() => onTest(control)}>Record Test</Button>
            <Button variant="outline" size="sm" onClick={() => onReview(control)}>Review</Button>
            <Button variant="destructive" size="sm" onClick={() => onDelete(control)}>Remove</Button>
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && <DialogFooter className="mt-4"><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaAiIsolationSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth28();
  const BASE       = "/api/organization/compliance/ferpa/ai-isolation";

  const [providerFilter,  setProviderFilter]  = useState("");
  const [envFilter,       setEnvFilter]       = useState("");
  const [statusFilter,    setStatusFilter]    = useState("");
  const [riskFilter,      setRiskFilter]      = useState("");
  const [testFilter,      setTestFilter]      = useState("");

  const [showAdd,       setShowAdd]       = useState(false);
  const [editItem,      setEditItem]      = useState<FerpaAiIsolationControl | null>(null);
  const [validateItem,  setValidateItem]  = useState<FerpaAiIsolationControl | null>(null);
  const [testItem,      setTestItem]      = useState<FerpaAiIsolationControl | null>(null);
  const [reviewItem,    setReviewItem]    = useState<FerpaAiIsolationControl | null>(null);
  const [deleteItem,    setDeleteItem]    = useState<FerpaAiIsolationControl | null>(null);
  const [detailItem,    setDetailItem]    = useState<FerpaAiIsolationControl | null>(null);

  const summaryQ = useQuery<Ferpa28Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ control: FerpaAiIsolationControl; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (providerFilter) params.set("provider",      providerFilter);
  if (envFilter)      params.set("environment",   envFilter);
  if (statusFilter)   params.set("status",        statusFilter);
  if (riskFilter)     params.set("riskLevel",     riskFilter);
  if (testFilter)     params.set("testingStatus", testFilter);

  const listQ = useQuery<{ total: number; items: FerpaAiIsolationControl[] }>({
    queryKey: [BASE, providerFilter, envFilter, statusFilter, riskFilter, testFilter],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total AI Systems",      value: s.totalAiSystems,     icon: <Bot className="w-4 h-4 text-blue-500" />,    highlight: false },
    { label: "Compliant",             value: s.compliant,          icon: <CheckCircle2 className="w-4 h-4 text-green-500" />, highlight: false },
    { label: "Non-Compliant",         value: s.nonCompliant,       icon: <XCircle className="w-4 h-4 text-red-500" />, highlight: s.nonCompliant > 0 },
    { label: "Tests Completed",       value: s.isolationTestsDone, icon: <ShieldCheck className="w-4 h-4 text-blue-500" />, highlight: false },
    { label: "Leakage Risks",         value: s.leakageRisks,       icon: <AlertTriangle className="w-4 h-4 text-red-500" />, highlight: s.leakageRisks > 0 },
    { label: "Pending Reviews",       value: s.pendingReviews,     icon: <Clock className="w-4 h-4 text-orange-500" />, highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              Cross-User AI Conversation Isolation
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Governs isolation settings that prevent AI chatbots from leaking student education records across users, sessions, and tenants.
              No conversation content is stored here.
            </p>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add AI Control
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-red-50 border-red-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-red-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-red-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Isolation Issues ({issuesQ.data!.length} systems)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { control: FerpaAiIsolationControl; codes: string[] }) => (
                <div key={item.control.id} className="flex items-start gap-2 text-xs">
                  <span className="text-red-700 font-medium shrink-0">{item.control.aiApplicationName}</span>
                  <span className="text-red-600">{item.codes.map(c => FERPA28_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <Input placeholder="Filter by provider…" value={providerFilter} onChange={e => setProviderFilter(e.target.value)} className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={envFilter} onChange={e => setEnvFilter(e.target.value)}>
            <option value="">All environments</option>
            {FERPA28_ENVIRONMENTS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA28_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA28_RISK_LEVELS.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={testFilter} onChange={e => setTestFilter(e.target.value)}>
            <option value="">All test statuses</option>
            {FERPA28_TEST_STATUSES.map(v => <option key={v}>{v}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">
            No AI isolation controls registered. Add a control to start tracking cross-user data separation.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(ctrl => {
              const isPendingReview = ctrl.nextReviewDate && new Date(ctrl.nextReviewDate) < new Date() && ctrl.reviewStatus !== "Reviewed";
              const isolationScore  = [ctrl.userIsolationEnabled, ctrl.conversationIsolationEnabled, ctrl.studentContextIsolationEnabled, ctrl.tenantIsolationEnabled, ctrl.retrievalFilteringEnabled, ctrl.authorizationCheckEnabled].filter(Boolean).length;
              return (
                <div
                  key={ctrl.id}
                  className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(ctrl)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-800 truncate">{ctrl.aiApplicationName}</span>
                      {ferpa28StatusBadge(ctrl.status)}
                      {ferpa28RiskBadge(ctrl.riskLevel)}
                      {ferpa28TestBadge(ctrl.dataLeakageTestingStatus)}
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{ctrl.modelProvider ?? "—"} · {ctrl.environment}</span>
                  </div>
                  {/* Isolation score bar */}
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex gap-0.5">
                      {[ctrl.userIsolationEnabled, ctrl.conversationIsolationEnabled, ctrl.studentContextIsolationEnabled, ctrl.tenantIsolationEnabled, ctrl.retrievalFilteringEnabled, ctrl.authorizationCheckEnabled].map((enabled, i) => (
                        <div key={i} className={`w-5 h-2 rounded-sm ${enabled ? "bg-green-500" : "bg-red-300"}`} title={["User","Conv","Student","Tenant","Retrieval","AuthZ"][i]} />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">{isolationScore}/6 controls enabled</span>
                    {ctrl.testEvidenceReference && <span className="text-xs text-green-600 ml-1">✓ Evidence</span>}
                    {isPendingReview && <span className="text-xs text-orange-600">⚠ Review overdue</span>}
                  </div>
                  {ctrl.validationCodes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {ctrl.validationCodes.map(c => (
                        <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                          {FERPA28_CODE_LABELS[c] ?? c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa28AddDialog    open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <Ferpa28ValidateDialog control={validateItem} onClose={() => setValidateItem(null)} />
      <Ferpa28TestDialog     control={testItem}     onClose={() => setTestItem(null)} />
      <Ferpa28ReviewDialog   control={reviewItem}   onClose={() => setReviewItem(null)} />
      <Ferpa28DeleteDialog   control={deleteItem}   onClose={() => setDeleteItem(null)} />
      <Ferpa28DetailDialog
        control={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={c => { setDetailItem(null); setEditItem(c); }}
        onValidate={c => { setDetailItem(null); setValidateItem(c); }}
        onTest={c => { setDetailItem(null); setTestItem(c); }}
        onReview={c => { setDetailItem(null); setReviewItem(c); }}
        onDelete={c => { setDetailItem(null); setDeleteItem(c); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FERPA-27 — Chatbot & Student Record Access Audit Logging
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa27Summary = {
  totalAccessEvents: number;
  successfulAccess: number;
  deniedAccess: number;
  unattributedEvents: number;
  chatbotSessions: number;
  pendingReviews: number;
};

type FerpaChatbotAuditEvent = {
  id: string;
  organizationId: string;
  eventTimestamp: string;
  userId: string | null;
  userName: string | null;
  userRole: string | null;
  sessionId: string | null;
  conversationId: string | null;
  chatbotName: string | null;
  sourceApplication: string | null;
  actionType: string | null;
  accessType: string | null;
  educationRecordCategory: string | null;
  dataClassification: string | null;
  purpose: string | null;
  authorizationResult: string | null;
  isSuccess: boolean;
  failureReason: string | null;
  ipAddress: string | null;
  deviceInfo: string | null;
  retentionDate: string | null;
  retentionReviewed: boolean;
  retentionReviewedAt: string | null;
  retentionReviewedBy: string | null;
  notes: string | null;
  createdAt: string;
  validationCodes: string[];
};

const FERPA27_ACTION_TYPES      = ["RecordAccess","RecordQuery","RecordModification","BulkExport"];
const FERPA27_ACCESS_TYPES      = ["Read","Write","Export","Query"];
const FERPA27_CLASSIFICATIONS   = ["FERPA-Protected","Directory-Information","Non-FERPA"];
const FERPA27_AUTH_RESULTS      = ["Approved","Denied","Conditional"];

const FERPA27_CODE_LABELS: Record<string, string> = {
  USER_IDENTITY_MISSING:       "User identity missing",
  ROLE_INFORMATION_MISSING:    "Role information missing",
  SESSION_REFERENCE_MISSING:   "Session reference missing",
  PURPOSE_MISSING:             "Access purpose not recorded",
  ACCESS_DECISION_MISSING:     "Authorization decision not recorded",
  AUDIT_RETENTION_MISSING:     "Retention date not set",
  UNATTRIBUTED_ACCESS_DETECTED:"Approved access without user attribution",
};

function ferpa27ResultBadge(result: string | null) {
  const c =
    result === "Approved"    ? "bg-green-100 text-green-700" :
    result === "Denied"      ? "bg-red-100 text-red-700" :
    result === "Conditional" ? "bg-yellow-100 text-yellow-700" :
                               "bg-gray-100 text-gray-500";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{result ?? "Unknown"}</span>;
}

function ferpa27ActionBadge(action: string | null) {
  const c =
    action === "BulkExport"         ? "bg-red-100 text-red-700" :
    action === "RecordModification" ? "bg-orange-100 text-orange-700" :
    action === "RecordAccess"       ? "bg-blue-100 text-blue-700" :
                                      "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{action ?? "—"}</span>;
}

function useAuth27() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Log Event dialog ──────────────────────────────────────────────────────────
function Ferpa27LogDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { header }  = useAuth27();
  const { toast }   = useToast();
  const qc          = useQueryClient();
  const BASE        = "/api/organization/compliance/ferpa/chatbot-audit";

  const [form, setForm] = useState({
    userId: "", userName: "", userRole: "", sessionId: "", conversationId: "",
    chatbotName: "", sourceApplication: "", actionType: "RecordAccess", accessType: "Read",
    educationRecordCategory: "", dataClassification: "FERPA-Protected",
    purpose: "", authorizationResult: "Approved", isSuccess: true,
    failureReason: "", ipAddress: "", deviceInfo: "", retentionDate: "", notes: "",
  });

  const set = (k: string, v: unknown) => setForm(p => ({ ...p, [k]: v }));

  async function handleSubmit() {
    if (!form.chatbotName.trim()) { toast({ title: "Chatbot name is required.", variant: "destructive" }); return; }
    const body = {
      ...form,
      isSuccess: form.authorizationResult === "Approved",
      retentionDate: form.retentionDate || null,
      conversationId: form.conversationId || null,
      sourceApplication: form.sourceApplication || null,
      ipAddress: form.ipAddress || null,
      deviceInfo: form.deviceInfo || null,
      failureReason: form.failureReason || null,
      notes: form.notes || null,
    };
    const res = await fetch(BASE, { method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
    if (!res.ok) { toast({ title: "Failed to log event", variant: "destructive" }); return; }
    toast({ title: "Access event logged." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Log Chatbot Access Event</DialogTitle></DialogHeader>
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-2">
          ⚠ Do not enter any student record content. Log access metadata only.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="text-xs font-medium">Chatbot Name *</label><Input value={form.chatbotName} onChange={e => set("chatbotName", e.target.value)} placeholder="Student Support Bot" /></div>
          <div><label className="text-xs font-medium">User ID</label><Input value={form.userId} onChange={e => set("userId", e.target.value)} placeholder="usr-123 or IdP sub" /></div>
          <div><label className="text-xs font-medium">User Name</label><Input value={form.userName} onChange={e => set("userName", e.target.value)} placeholder="Jane Smith" /></div>
          <div><label className="text-xs font-medium">User Role</label><Input value={form.userRole} onChange={e => set("userRole", e.target.value)} placeholder="Academic Advisor" /></div>
          <div><label className="text-xs font-medium">Source Application</label><Input value={form.sourceApplication} onChange={e => set("sourceApplication", e.target.value)} placeholder="StudentPortal" /></div>
          <div><label className="text-xs font-medium">Session ID</label><Input value={form.sessionId} onChange={e => set("sessionId", e.target.value)} placeholder="sess-abc-001" /></div>
          <div><label className="text-xs font-medium">Conversation ID</label><Input value={form.conversationId} onChange={e => set("conversationId", e.target.value)} placeholder="conv-xyz-001" /></div>
          <div><label className="text-xs font-medium">Action Type</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.actionType} onChange={e => set("actionType", e.target.value)}>
              {FERPA27_ACTION_TYPES.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-medium">Access Type</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.accessType} onChange={e => set("accessType", e.target.value)}>
              {FERPA27_ACCESS_TYPES.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div className="col-span-2"><label className="text-xs font-medium">Education Record Category (field names only — no content)</label>
            <Input value={form.educationRecordCategory} onChange={e => set("educationRecordCategory", e.target.value)} placeholder="Enrollment Records, GPA" /></div>
          <div><label className="text-xs font-medium">Data Classification</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.dataClassification} onChange={e => set("dataClassification", e.target.value)}>
              {FERPA27_CLASSIFICATIONS.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-medium">Authorization Result</label>
            <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.authorizationResult} onChange={e => set("authorizationResult", e.target.value)}>
              {FERPA27_AUTH_RESULTS.map(v => <option key={v}>{v}</option>)}
            </select>
          </div>
          <div className="col-span-2"><label className="text-xs font-medium">Purpose *</label>
            <Input value={form.purpose} onChange={e => set("purpose", e.target.value)} placeholder="Academic advising support" /></div>
          {form.authorizationResult === "Denied" && (
            <div className="col-span-2"><label className="text-xs font-medium">Failure Reason</label>
              <Input value={form.failureReason} onChange={e => set("failureReason", e.target.value)} placeholder="Describe denial reason" /></div>
          )}
          <div><label className="text-xs font-medium">IP Address</label><Input value={form.ipAddress} onChange={e => set("ipAddress", e.target.value)} placeholder="10.0.0.1" /></div>
          <div><label className="text-xs font-medium">Device Info</label><Input value={form.deviceInfo} onChange={e => set("deviceInfo", e.target.value)} placeholder="Chrome/Windows" /></div>
          <div><label className="text-xs font-medium">Retention Date</label><Input type="date" value={form.retentionDate} onChange={e => set("retentionDate", e.target.value)} /></div>
          <div className="col-span-2"><label className="text-xs font-medium">Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional notes" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Log Event</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate Event dialog ────────────────────────────────────────────────────
function Ferpa27ValidateDialog({ event, onClose }: { event: FerpaChatbotAuditEvent | null; onClose: () => void }) {
  const { header } = useAuth27();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/chatbot-audit";
  const [result, setResult] = useState<{ isValid: boolean; codes: string[]; checkedAt: string } | null>(null);

  async function handleValidate() {
    if (!event) return;
    const res = await fetch(`${BASE}/${event.id}/validate`, { method: "POST", headers: header });
    if (!res.ok) { toast({ title: "Validation error", variant: "destructive" }); return; }
    const data = await res.json();
    setResult(data);
    qc.invalidateQueries({ queryKey: [BASE + "/issues"] });
    qc.invalidateQueries({ queryKey: [BASE] });
  }

  if (!event) return null;
  return (
    <Dialog open={!!event} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Validate Attribution — {event.chatbotName}</DialogTitle></DialogHeader>
        <div className="text-sm text-gray-600 mb-3">
          <p><strong>User:</strong> {event.userName ?? "—"} ({event.userRole ?? "—"})</p>
          <p><strong>Session:</strong> {event.sessionId ?? "—"}</p>
          <p><strong>Result:</strong> {event.authorizationResult ?? "—"}</p>
        </div>
        {result ? (
          <div className={`rounded p-3 text-sm ${result.isValid ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            <p className="font-medium mb-1">{result.isValid ? "✓ Attribution valid — no issues found." : "✗ Attribution issues detected:"}</p>
            {result.codes.map(c => (
              <p key={c} className="text-xs text-red-700">• {FERPA27_CODE_LABELS[c] ?? c}</p>
            ))}
            <p className="text-xs text-gray-500 mt-1">Checked at {new Date(result.checkedAt).toLocaleString()}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Click "Run Validation" to check attribution completeness.</p>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleValidate}>Run Validation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review Retention dialog ───────────────────────────────────────────────────
function Ferpa27RetentionDialog({ event, onClose }: { event: FerpaChatbotAuditEvent | null; onClose: () => void }) {
  const { header } = useAuth27();
  const { toast }  = useToast();
  const qc         = useQueryClient();
  const BASE       = "/api/organization/compliance/ferpa/chatbot-audit";
  const [reviewedBy, setReviewedBy]         = useState("");
  const [newRetentionDate, setNewRetentionDate] = useState("");
  const [notes, setNotes]                   = useState("");

  async function handleSubmit() {
    if (!event) return;
    if (!reviewedBy.trim()) { toast({ title: "Reviewer name is required.", variant: "destructive" }); return; }
    const res = await fetch(`${BASE}/${event.id}/retention-review`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify({ reviewedBy, newRetentionDate: newRetentionDate || null, notes: notes || null }),
    });
    if (!res.ok) { toast({ title: "Unable to save review", variant: "destructive" }); return; }
    toast({ title: "Retention review recorded." });
    qc.invalidateQueries({ queryKey: [BASE + "/summary"] });
    qc.invalidateQueries({ queryKey: [BASE] });
    onClose();
  }

  if (!event) return null;
  return (
    <Dialog open={!!event} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Review Retention — {event.chatbotName}</DialogTitle></DialogHeader>
        <div className="text-sm text-gray-600 mb-3 space-y-1">
          <p><strong>Event timestamp:</strong> {new Date(event.eventTimestamp).toLocaleString()}</p>
          <p><strong>Current retention date:</strong> {event.retentionDate ? new Date(event.retentionDate).toLocaleDateString() : "Not set"}</p>
          {event.retentionReviewed && <p className="text-green-700"><strong>Previously reviewed by:</strong> {event.retentionReviewedBy}</p>}
        </div>
        <div className="space-y-3">
          <div><label className="text-xs font-medium">Reviewer Name *</label><Input value={reviewedBy} onChange={e => setReviewedBy(e.target.value)} placeholder="Compliance Officer" /></div>
          <div><label className="text-xs font-medium">New Retention Date (optional)</label><Input type="date" value={newRetentionDate} onChange={e => setNewRetentionDate(e.target.value)} /></div>
          <div><label className="text-xs font-medium">Review Notes</label>
            <textarea className="w-full border rounded px-2 py-1.5 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Retention confirmed, extended, or flagged for deletion" /></div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit}>Mark Reviewed</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Export Report dialog ──────────────────────────────────────────────────────
function Ferpa27ReportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { header } = useAuth27();
  const { toast }  = useToast();
  const BASE       = "/api/organization/compliance/ferpa/chatbot-audit";
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const [chatbot,  setChatbot]  = useState("");
  const [report,   setReport]   = useState<{ totalEvents: number; successful: number; denied: number; unattributed: number; pendingReviews: number; generatedAt: string } | null>(null);

  async function handleGenerate() {
    const body: Record<string, unknown> = {};
    if (dateFrom) body.dateFrom = dateFrom;
    if (dateTo)   body.dateTo   = dateTo;
    if (chatbot)  body.chatbot  = chatbot;
    const res = await fetch(`${BASE}/report`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...header },
      body: JSON.stringify(body),
    });
    if (!res.ok) { toast({ title: "Report generation failed", variant: "destructive" }); return; }
    const data = await res.json();
    setReport(data);
    toast({ title: "Report generated." });
  }

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Export Chatbot Audit Report</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium">From Date</label><Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
            <div><label className="text-xs font-medium">To Date</label><Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
          </div>
          <div><label className="text-xs font-medium">Chatbot Name (optional)</label><Input value={chatbot} onChange={e => setChatbot(e.target.value)} placeholder="Filter by chatbot" /></div>
        </div>
        {report && (
          <div className="bg-gray-50 border rounded p-3 mt-3 text-sm space-y-1">
            <p className="font-medium text-gray-700">Report Summary</p>
            <p>Total Events: <strong>{report.totalEvents}</strong></p>
            <p>Successful: <strong className="text-green-700">{report.successful}</strong></p>
            <p>Denied: <strong className="text-red-700">{report.denied}</strong></p>
            <p>Unattributed: <strong className="text-amber-700">{report.unattributed}</strong></p>
            <p>Pending Reviews: <strong className="text-orange-700">{report.pendingReviews}</strong></p>
            <p className="text-xs text-gray-500">Generated: {new Date(report.generatedAt).toLocaleString()}</p>
          </div>
        )}
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handleGenerate}>Generate Report</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function Ferpa27DetailDialog({ event, canManage, onClose, onValidate, onRetention }: {
  event: FerpaChatbotAuditEvent | null;
  canManage: boolean;
  onClose: () => void;
  onValidate: (ev: FerpaChatbotAuditEvent) => void;
  onRetention: (ev: FerpaChatbotAuditEvent) => void;
}) {
  if (!event) return null;
  const isPendingRetention = event.retentionDate && new Date(event.retentionDate) < new Date() && !event.retentionReviewed;
  return (
    <Dialog open={!!event} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            Audit Event — {event.chatbotName ?? "Unknown Bot"}
          </DialogTitle>
        </DialogHeader>

        {event.validationCodes.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
            <p className="text-xs font-medium text-red-700 mb-1">Attribution Issues</p>
            {event.validationCodes.map(c => (
              <p key={c} className="text-xs text-red-600">• {FERPA27_CODE_LABELS[c] ?? c}</p>
            ))}
          </div>
        )}

        {isPendingRetention && (
          <div className="bg-orange-50 border border-orange-200 rounded p-2 mb-3">
            <p className="text-xs text-orange-700">⚠ Retention date has passed — review required.</p>
          </div>
        )}

        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Event Details</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Timestamp</span><span>{new Date(event.eventTimestamp).toLocaleString()}</span>
              <span className="text-gray-500">Action Type</span><span>{ferpa27ActionBadge(event.actionType)}</span>
              <span className="text-gray-500">Access Type</span><span>{event.accessType ?? "—"}</span>
              <span className="text-gray-500">Result</span><span>{ferpa27ResultBadge(event.authorizationResult)}</span>
              <span className="text-gray-500">Success</span><span>{event.isSuccess ? "✓ Yes" : "✗ No"}</span>
              {event.failureReason && <><span className="text-gray-500">Failure Reason</span><span className="text-red-700">{event.failureReason}</span></>}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">User Identity</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">User ID</span><span className="font-mono">{event.userId ?? <em className="text-red-500">Missing</em>}</span>
              <span className="text-gray-500">User Name</span><span>{event.userName ?? <em className="text-amber-600">Not recorded</em>}</span>
              <span className="text-gray-500">User Role</span><span>{event.userRole ?? <em className="text-red-500">Missing</em>}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Session & Application</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Session ID</span><span className="font-mono truncate">{event.sessionId ?? "—"}</span>
              <span className="text-gray-500">Conversation ID</span><span className="font-mono truncate">{event.conversationId ?? "—"}</span>
              <span className="text-gray-500">Chatbot</span><span>{event.chatbotName ?? "—"}</span>
              <span className="text-gray-500">Source App</span><span>{event.sourceApplication ?? "—"}</span>
              <span className="text-gray-500">IP Address</span><span className="font-mono">{event.ipAddress ?? "—"}</span>
              <span className="text-gray-500">Device</span><span>{event.deviceInfo ?? "—"}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Education Record</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Category</span><span>{event.educationRecordCategory ?? "—"}</span>
              <span className="text-gray-500">Classification</span><span>{event.dataClassification ?? "—"}</span>
              <span className="text-gray-500 col-span-1">Purpose</span><span className="col-span-1">{event.purpose ?? <em className="text-red-500">Missing</em>}</span>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Retention</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-500">Retention Date</span><span>{event.retentionDate ? new Date(event.retentionDate).toLocaleDateString() : <em className="text-red-500">Not set</em>}</span>
              <span className="text-gray-500">Reviewed</span><span>{event.retentionReviewed ? `✓ ${event.retentionReviewedBy ?? ""}` : "Pending"}</span>
              {event.retentionReviewedAt && <><span className="text-gray-500">Reviewed At</span><span>{new Date(event.retentionReviewedAt).toLocaleString()}</span></>}
            </div>
          </div>
          {event.notes && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-xs text-gray-700 bg-gray-50 rounded p-2">{event.notes}</p>
            </div>
          )}
        </div>

        {canManage && (
          <DialogFooter className="mt-4 gap-2 flex-wrap">
            <Button variant="outline" onClick={() => onValidate(event)}>Validate Attribution</Button>
            <Button variant="outline" onClick={() => onRetention(event)}>Review Retention</Button>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
        {!canManage && (
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main section ──────────────────────────────────────────────────────────────
function FerpaChatbotAuditSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth27();
  const BASE       = "/api/organization/compliance/ferpa/chatbot-audit";

  const [userFilter,      setUserFilter]      = useState("");
  const [roleFilter,      setRoleFilter]      = useState("");
  const [chatbotFilter,   setChatbotFilter]   = useState("");
  const [actionFilter,    setActionFilter]    = useState("");
  const [resultFilter,    setResultFilter]    = useState("");
  const [dateFrom,        setDateFrom]        = useState("");
  const [dateTo,          setDateTo]          = useState("");

  const [showLog,         setShowLog]         = useState(false);
  const [showReport,      setShowReport]      = useState(false);
  const [validateEvent,   setValidateEvent]   = useState<FerpaChatbotAuditEvent | null>(null);
  const [retentionEvent,  setRetentionEvent]  = useState<FerpaChatbotAuditEvent | null>(null);
  const [detailEvent,     setDetailEvent]     = useState<FerpaChatbotAuditEvent | null>(null);
  const [lastOpenKey,     setLastOpenKey]     = useState<string | null>(null);

  // Hydrate detail from key
  const eventsQ_all = useQuery<{ total: number; items: FerpaChatbotAuditEvent[] }>({
    queryKey: [BASE],
    queryFn: async () => {
      const res = await fetch(`${BASE}?pageSize=200`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  // When lastOpenKey set and we have data, find the event for the detail dialog
  const lastOpen = lastOpenKey
    ? (eventsQ_all.data?.items ?? []).find(e => e.id === lastOpenKey) ?? null
    : null;

  const summaryQ = useQuery<Ferpa27Summary>({
    queryKey: [BASE + "/summary"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/summary`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ event: FerpaChatbotAuditEvent; codes: string[] }[]>({
    queryKey: [BASE + "/issues"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/issues`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (userFilter)    params.set("user",       userFilter);
  if (roleFilter)    params.set("role",       roleFilter);
  if (chatbotFilter) params.set("chatbot",    chatbotFilter);
  if (actionFilter)  params.set("actionType", actionFilter);
  if (resultFilter)  params.set("result",     resultFilter);
  if (dateFrom)      params.set("dateFrom",   dateFrom);
  if (dateTo)        params.set("dateTo",     dateTo);

  const listQ = useQuery<{ total: number; items: FerpaChatbotAuditEvent[] }>({
    queryKey: [BASE, userFilter, roleFilter, chatbotFilter, actionFilter, resultFilter, dateFrom, dateTo],
    queryFn: async () => {
      const res = await fetch(`${BASE}?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Access Events",  value: s.totalAccessEvents,  icon: <Activity className="w-4 h-4 text-blue-500" />,  highlight: false },
    { label: "Successful Access",    value: s.successfulAccess,   icon: <CheckCircle2 className="w-4 h-4 text-green-500" />, highlight: false },
    { label: "Denied Access",        value: s.deniedAccess,       icon: <XCircle className="w-4 h-4 text-red-500" />,    highlight: s.deniedAccess > 0 },
    { label: "Unattributed Events",  value: s.unattributedEvents, icon: <AlertTriangle className="w-4 h-4 text-amber-500" />, highlight: s.unattributedEvents > 0 },
    { label: "Chatbot Sessions",     value: s.chatbotSessions,    icon: <MessageSquare className="w-4 h-4 text-purple-500" />, highlight: false },
    { label: "Pending Reviews",      value: s.pendingReviews,     icon: <Clock className="w-4 h-4 text-orange-500" />,   highlight: s.pendingReviews > 0 },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-4 h-4 text-blue-600" />
              Chatbot &amp; Student Record Access Audit Log
            </CardTitle>
            <p className="text-xs text-gray-500 mt-0.5">
              Tracks every chatbot / AI access to FERPA-protected education records with attributable user identity.
              No student record content is stored — metadata and attribution only.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setShowReport(true)}>
              <FileText className="w-3 h-3 mr-1" /> Export Report
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => setShowLog(true)}>
                <Plus className="w-3 h-3 mr-1" /> Log Event
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Summary metrics */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded-lg border p-3 flex flex-col gap-1 ${m.highlight ? "bg-amber-50 border-amber-200" : "bg-gray-50"}`}>
                <div className="flex items-center gap-1">{m.icon}<span className="text-xs text-gray-500 truncate">{m.label}</span></div>
                <span className={`text-xl font-bold ${m.highlight ? "text-amber-700" : "text-gray-800"}`}>{m.value ?? 0}</span>
              </div>
            ))}
          </div>
        )}

        {/* Issues panel */}
        {(issuesQ.data?.length ?? 0) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Attribution Issues ({issuesQ.data!.length} events)
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {issuesQ.data!.slice(0, 8).map((item: { event: FerpaChatbotAuditEvent; codes: string[] }) => (
                <div key={item.event.id} className="flex items-start gap-2 text-xs">
                  <span className="text-amber-700 font-medium shrink-0">{item.event.chatbotName ?? "Unknown"}</span>
                  <span className="text-gray-500 shrink-0">{item.event.userName ?? item.event.userId ?? "—"}</span>
                  <span className="text-amber-600">{item.codes.map(c => FERPA27_CODE_LABELS[c] ?? c).join(", ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          <Input placeholder="Filter by user…" value={userFilter}    onChange={e => setUserFilter(e.target.value)}    className="text-xs h-8" />
          <Input placeholder="Filter by role…" value={roleFilter}    onChange={e => setRoleFilter(e.target.value)}    className="text-xs h-8" />
          <Input placeholder="Filter by chatbot…" value={chatbotFilter} onChange={e => setChatbotFilter(e.target.value)} className="text-xs h-8" />
          <select className="border rounded px-2 text-xs h-8" value={actionFilter} onChange={e => setActionFilter(e.target.value)}>
            <option value="">All action types</option>
            {FERPA27_ACTION_TYPES.map(v => <option key={v}>{v}</option>)}
          </select>
          <select className="border rounded px-2 text-xs h-8" value={resultFilter} onChange={e => setResultFilter(e.target.value)}>
            <option value="">All results</option>
            {FERPA27_AUTH_RESULTS.map(v => <option key={v}>{v}</option>)}
          </select>
          <div className="flex items-center gap-1 col-span-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="text-xs h-8" />
            <span className="text-xs text-gray-400">–</span>
            <Input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   className="text-xs h-8" />
          </div>
        </div>

        {/* Event table */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-gray-400 py-8">No access events found. Log the first event to start tracking.</div>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {items.map(ev => {
              const isPendingRetention = ev.retentionDate && new Date(ev.retentionDate) < new Date() && !ev.retentionReviewed;
              return (
                <div
                  key={ev.id}
                  className="border rounded-lg p-2.5 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => { setLastOpenKey(ev.id); setDetailEvent(ev); }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {ferpa27ResultBadge(ev.authorizationResult)}
                      {ferpa27ActionBadge(ev.actionType)}
                      <span className="text-xs font-medium text-gray-800 truncate">{ev.chatbotName ?? "Unknown"}</span>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">{new Date(ev.eventTimestamp).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{ev.userName ?? ev.userId ?? <em className="text-red-500">No identity</em>}</span>
                    {ev.userRole && <span className="text-gray-400">· {ev.userRole}</span>}
                    {ev.educationRecordCategory && <span className="text-gray-400 truncate">· {ev.educationRecordCategory}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {ev.validationCodes.map(c => (
                      <span key={c} className="inline-flex px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 border border-red-200">
                        {FERPA27_CODE_LABELS[c] ?? c}
                      </span>
                    ))}
                    {isPendingRetention && (
                      <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-orange-50 text-orange-700 border border-orange-200">Retention overdue</span>
                    )}
                    {ev.retentionReviewed && (
                      <span className="inline-flex px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-700">✓ Retention reviewed</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {listQ.data && listQ.data.total > items.length && (
          <p className="text-xs text-gray-400 text-center">Showing {items.length} of {listQ.data.total} events — use filters to narrow results.</p>
        )}
      </CardContent>

      {/* Dialogs */}
      <Ferpa27LogDialog open={showLog} onClose={() => setShowLog(false)} />
      <Ferpa27ReportDialog open={showReport} onClose={() => setShowReport(false)} />
      <Ferpa27ValidateDialog
        event={validateEvent}
        onClose={() => setValidateEvent(null)}
      />
      <Ferpa27RetentionDialog
        event={retentionEvent}
        onClose={() => setRetentionEvent(null)}
      />
      <Ferpa27DetailDialog
        event={detailEvent ?? lastOpen}
        canManage={canManage}
        onClose={() => { setDetailEvent(null); setLastOpenKey(null); }}
        onValidate={ev => { setDetailEvent(null); setValidateEvent(ev); }}
        onRetention={ev => { setDetailEvent(null); setRetentionEvent(ev); }}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Approved Data Sharing Governance Records
// ─────────────────────────────────────────────────────────────────────────────

type Ferpa26Summary = {
  total: number;
  approved: number;
  pendingApproval: number;
  consentRequired: number;
  missingDocumentation: number;
  overdueReviews: number;
};

const FERPA26_STATUSES         = ["Draft","Active","Suspended","Terminated","Archived"];
const FERPA26_APPROVAL_STATUSES= ["Pending","Approved","Rejected","NotRequired"];
const FERPA26_RISK_LEVELS      = ["Low","Medium","High","Critical"];
const FERPA26_RECIPIENT_TYPES  = ["Federal Agency","State Agency","Research Institution","Contractor","School Official","Parent/Guardian","Other"];
const FERPA26_REVIEW_STATUSES  = ["Draft","UnderReview","Reviewed","Archived"];

const FERPA26_CODE_LABELS: Record<string, string> = {
  RECIPIENT_NOT_DEFINED:       "Recipient not defined",
  PURPOSE_NOT_DEFINED:         "Sharing purpose not defined",
  DATA_CATEGORY_MISSING:       "Education record categories missing",
  APPROVAL_MISSING:            "Approval required but not obtained",
  AGREEMENT_REFERENCE_MISSING: "Data sharing agreement reference missing",
  CONSENT_REFERENCE_REQUIRED:  "Consent required but reference not provided",
  REVIEW_OVERDUE:              "Review overdue",
};

function ferpa26ApprovalBadge(status: string) {
  const c =
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "Pending"     ? "bg-yellow-100 text-yellow-700" :
    status === "Rejected"    ? "bg-red-100 text-red-700" :
                               "bg-gray-100 text-gray-500";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function ferpa26StatusBadge(status: string) {
  const c =
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "Suspended"   ? "bg-orange-100 text-orange-700" :
    status === "Terminated"  ? "bg-red-100 text-red-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function ferpa26RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function useAuth26() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ───────────────────────────────────────────────────────────
function DisclosureAddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaDisclosureGovernance | null; onClose: () => void }) {
  const { header } = useAuth26();
  const { toast }  = useToast();
  const isEdit     = !!initial;

  const blank = {
    disclosureName: "", sharingPurpose: "", dataRecipient: "", recipientOrganization: "",
    recipientType: "Federal Agency", educationRecordCategories: "", piiCategories: "",
    dataFieldsShared: "", legalBasis: "FERPA § 99.31", ferpaException: "School Official",
    consentRequired: false, consentReference: "", approvalRequired: true,
    dataSharingAgreementRef: "", contractReference: "", dataOwner: "", department: "",
    riskLevel: "Low", status: "Draft", lastReviewDate: "", nextReviewDate: "",
  };
  const [form, setForm] = useState({ ...blank });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastKey, setLastKey] = useState("");
  const openKey = `${open}-${initial?.id ?? "new"}`;
  if (openKey !== lastKey) {
    setLastKey(openKey);
    if (open) {
      setForm(initial ? {
        disclosureName:            initial.disclosureName,
        sharingPurpose:            initial.sharingPurpose ?? "",
        dataRecipient:             initial.dataRecipient ?? "",
        recipientOrganization:     initial.recipientOrganization ?? "",
        recipientType:             initial.recipientType ?? "Federal Agency",
        educationRecordCategories: initial.educationRecordCategories ?? "",
        piiCategories:             initial.piiCategories ?? "",
        dataFieldsShared:          initial.dataFieldsShared ?? "",
        legalBasis:                initial.legalBasis ?? "",
        ferpaException:            initial.ferpaException ?? "",
        consentRequired:           initial.consentRequired,
        consentReference:          initial.consentReference ?? "",
        approvalRequired:          initial.approvalRequired,
        dataSharingAgreementRef:   initial.dataSharingAgreementRef ?? "",
        contractReference:         initial.contractReference ?? "",
        dataOwner:                 initial.dataOwner ?? "",
        department:                initial.department ?? "",
        riskLevel:                 initial.riskLevel,
        status:                    initial.status,
        lastReviewDate:            initial.lastReviewDate?.slice(0, 10) ?? "",
        nextReviewDate:            initial.nextReviewDate?.slice(0, 10) ?? "",
      } : { ...blank });
    }
  }

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        disclosureName:            form.disclosureName,
        sharingPurpose:            form.sharingPurpose || null,
        dataRecipient:             form.dataRecipient || null,
        recipientOrganization:     form.recipientOrganization || null,
        recipientType:             form.recipientType || null,
        educationRecordCategories: form.educationRecordCategories || null,
        piiCategories:             form.piiCategories || null,
        dataFieldsShared:          form.dataFieldsShared || null,
        legalBasis:                form.legalBasis || null,
        ferpaException:            form.ferpaException || null,
        consentRequired:           form.consentRequired,
        consentReference:          form.consentReference || null,
        approvalRequired:          form.approvalRequired,
        dataSharingAgreementRef:   form.dataSharingAgreementRef || null,
        contractReference:         form.contractReference || null,
        dataOwner:                 form.dataOwner || null,
        department:                form.department || null,
        riskLevel:                 form.riskLevel,
        status:                    form.status,
        lastReviewDate:            form.lastReviewDate || null,
        nextReviewDate:            form.nextReviewDate || null,
      };
      const url    = isEdit ? `/api/organization/compliance/ferpa/disclosure-governance/${initial!.id}` : "/api/organization/compliance/ferpa/disclosure-governance";
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/issues"] });
      toast({ title: isEdit ? "Disclosure record updated" : "Disclosure record created" });
      setErrors({});
      onClose();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Disclosure Record" : "Add Data Sharing Disclosure"}</DialogTitle>
          <DialogDescription>Document an approved education record data sharing activity for FERPA governance. Do not enter actual student data.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Basic info */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Disclosure Identity</p>
            <div className="space-y-1">
              <Label>Disclosure Name <span className="text-red-500">*</span></Label>
              <Input className={fieldCls("", errors, "disclosureName")} aria-invalid={!!errors.disclosureName} value={form.disclosureName} onChange={e => { set("disclosureName", e.target.value); clearErr(setErrors, "disclosureName"); }} placeholder="State Annual Enrollment Report 2026" />
              {fErr(errors, "disclosureName")}
            </div>
            <div className="space-y-1">
              <Label>Sharing Purpose</Label>
              <Input value={form.sharingPurpose} onChange={e => set("sharingPurpose", e.target.value)} placeholder="Regulatory compliance reporting" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Risk Level</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                  {FERPA26_RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                  {FERPA26_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Recipient Type</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.recipientType} onChange={e => set("recipientType", e.target.value)}>
                  {FERPA26_RECIPIENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Recipient */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Recipient</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data Recipient</Label>
                <Input value={form.dataRecipient} onChange={e => set("dataRecipient", e.target.value)} placeholder="State Education Agency" />
              </div>
              <div className="space-y-1">
                <Label>Recipient Organization</Label>
                <Input value={form.recipientOrganization} onChange={e => set("recipientOrganization", e.target.value)} placeholder="Department of Education" />
              </div>
              <div className="space-y-1">
                <Label>Data Owner</Label>
                <Input value={form.dataOwner} onChange={e => set("dataOwner", e.target.value)} placeholder="Registrar's Office" />
              </div>
              <div className="space-y-1">
                <Label>Department</Label>
                <Input value={form.department} onChange={e => set("department", e.target.value)} placeholder="Academic Affairs" />
              </div>
            </div>
          </div>

          {/* Data scope */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Data Scope (no actual student data)</p>
            <div className="space-y-1">
              <Label>Education Record Categories</Label>
              <Input value={form.educationRecordCategories} onChange={e => set("educationRecordCategories", e.target.value)} placeholder="Enrollment records, Graduation records" />
            </div>
            <div className="space-y-1">
              <Label>PII Categories</Label>
              <Input value={form.piiCategories} onChange={e => set("piiCategories", e.target.value)} placeholder="Name, Student ID, Enrollment status" />
            </div>
            <div className="space-y-1">
              <Label>Data Fields Shared (field names only)</Label>
              <Textarea value={form.dataFieldsShared} onChange={e => set("dataFieldsShared", e.target.value)} rows={2} placeholder="GPA, enrollment_status, graduation_date" />
            </div>
          </div>

          {/* Legal basis */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Legal Basis</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Legal Basis</Label>
                <Input value={form.legalBasis} onChange={e => set("legalBasis", e.target.value)} placeholder="FERPA § 99.31" />
              </div>
              <div className="space-y-1">
                <Label>FERPA Exception</Label>
                <Input value={form.ferpaException} onChange={e => set("ferpaException", e.target.value)} placeholder="School Official, Health/Safety Emergency" />
              </div>
            </div>
          </div>

          {/* Consent & Approval */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Consent &amp; Approval</p>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.consentRequired} onChange={e => set("consentRequired", e.target.checked)} className="rounded" />
                Consent Required
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.approvalRequired} onChange={e => set("approvalRequired", e.target.checked)} className="rounded" />
                Approval Required
              </label>
            </div>
            {form.consentRequired && (
              <div className="space-y-1">
                <Label>Consent Reference</Label>
                <Input value={form.consentReference} onChange={e => set("consentReference", e.target.value)} placeholder="consent://form-2026-001" />
              </div>
            )}
          </div>

          {/* Agreement & Review */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Agreement &amp; Review</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data Sharing Agreement Reference</Label>
                <Input value={form.dataSharingAgreementRef} onChange={e => set("dataSharingAgreementRef", e.target.value)} placeholder="DSA-2026-001" />
              </div>
              <div className="space-y-1">
                <Label>Contract / Document Reference</Label>
                <Input value={form.contractReference} onChange={e => set("contractReference", e.target.value)} placeholder="contract://dsa-2026.pdf" />
              </div>
              <div className="space-y-1">
                <Label>Last Review Date</Label>
                <Input type="date" value={form.lastReviewDate} onChange={e => set("lastReviewDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            const errs: Record<string, string> = {};
            required(errs, "disclosureName", form.disclosureName, "Disclosure Name");
            if (Object.keys(errs).length > 0) {
              setErrors(errs);
              toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
              return;
            }
            setErrors({});
            mutation.mutate();
          }} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Create Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Submit Approval dialog ───────────────────────────────────────────────────
function DisclosureSubmitApprovalDialog({ open, item, onClose }: { open: boolean; item: FerpaDisclosureGovernance | null; onClose: () => void }) {
  const { header } = useAuth26();
  const { toast }  = useToast();
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/disclosure-governance/${item.id}/submit-approval`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ notes: notes || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/summary"] });
      toast({ title: "Submitted for approval" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Submit for Approval</DialogTitle>
          <DialogDescription>Submit <strong>{item?.disclosureName}</strong> for governance approval.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Readiness notes or context for approver" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Approve dialog ───────────────────────────────────────────────────────────
function DisclosureApproveDialog({ open, item, onClose }: { open: boolean; item: FerpaDisclosureGovernance | null; onClose: () => void }) {
  const { header } = useAuth26();
  const { toast }  = useToast();
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/disclosure-governance/${item.id}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ notes: notes || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/summary"] });
      toast({ title: "Disclosure approved" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Approve Disclosure</DialogTitle>
          <DialogDescription>Approve the data sharing governance record for <strong>{item?.disclosureName}</strong>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Approval Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Any conditions or observations" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Approving…" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Reject dialog ────────────────────────────────────────────────────────────
function DisclosureRejectDialog({ open, item, onClose }: { open: boolean; item: FerpaDisclosureGovernance | null; onClose: () => void }) {
  const { header } = useAuth26();
  const { toast }  = useToast();
  const [reason, setReason] = useState("");
  const [reasonErr, setReasonErr] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      if (!reason.trim()) { setReasonErr("Rejection reason is required."); throw new Error("Please provide a rejection reason."); }
      setReasonErr("");
      const res = await fetch(`/api/organization/compliance/ferpa/disclosure-governance/${item.id}/reject`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reason: reason || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/summary"] });
      toast({ title: "Disclosure rejected" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Reject Disclosure</DialogTitle>
          <DialogDescription>Reject the approval request for <strong>{item?.disclosureName}</strong>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Reason for Rejection <span className="text-red-500">*</span></Label>
          <Textarea value={reason} onChange={e => { setReason(e.target.value); if (e.target.value.trim()) setReasonErr(""); }} rows={3} placeholder="Missing documentation, insufficient justification, etc."
            className={reasonErr ? "border-red-500 focus-visible:ring-red-500" : ""}
            aria-invalid={!!reasonErr} />
          {reasonErr && <p role="alert" className="text-xs text-red-500 mt-0.5">⚠ {reasonErr}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function DisclosureReviewDialog({ open, item, onClose }: { open: boolean; item: FerpaDisclosureGovernance | null; onClose: () => void }) {
  const { header } = useAuth26();
  const { toast }  = useToast();
  const [reviewerName, setReviewerName] = useState("");
  const [notes, setNotes]               = useState("");
  const [nextDate, setNextDate]         = useState("");
  const [reviewerErr, setReviewerErr]   = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      if (!reviewerName.trim()) { setReviewerErr("Reviewer name is required."); throw new Error("Please fix the highlighted fields."); }
      setReviewerErr("");
      const res = await fetch(`/api/organization/compliance/ferpa/disclosure-governance/${item.id}/review`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reviewerName, reviewNotes: notes || null, nextReviewDate: nextDate || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/summary"] });
      toast({ title: "Review recorded" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Review</DialogTitle>
          <DialogDescription>Review <strong>{item?.disclosureName}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1"><Label>Reviewer Name <span className="text-red-500">*</span></Label>
            <Input value={reviewerName} onChange={e => { setReviewerName(e.target.value); if (e.target.value.trim()) setReviewerErr(""); }} placeholder="Privacy Officer"
              className={reviewerErr ? "border-red-500 focus-visible:ring-red-500" : ""}
              aria-invalid={!!reviewerErr} />
            {reviewerErr && <p role="alert" className="text-xs text-red-500 mt-0.5">⚠ {reviewerErr}</p>}</div>
          <div className="space-y-1"><Label>Next Review Date</Label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} /></div>
          <div className="space-y-1"><Label>Review Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Findings or required changes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function DisclosureDeleteDialog({ open, item, onClose }: { open: boolean; item: FerpaDisclosureGovernance | null; onClose: () => void }) {
  const { header } = useAuth26();
  const { toast }  = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/disclosure-governance/${item.id}`, { method: "DELETE", headers: header });
      await throwIfResNotOk(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/summary"] });
      toast({ title: "Disclosure record deleted" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Disclosure Record</DialogTitle>
          <DialogDescription>Permanently delete <strong>{item?.disclosureName}</strong>?</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function DisclosureDetailDialog({
  open, item, canManage, onClose, onEdit, onSubmitApproval, onApprove, onReject, onReview, onDelete,
}: {
  open: boolean; item: FerpaDisclosureGovernance | null; canManage: boolean;
  onClose: () => void; onEdit: () => void; onSubmitApproval: () => void;
  onApprove: () => void; onReject: () => void; onReview: () => void; onDelete: () => void;
}) {
  if (!item) return null;
  const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
  const row = (label: string, value: string | null | undefined) => value ? (
    <div className="flex flex-col gap-0.5 text-sm">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-blue-600" /> {item.disclosureName}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-1">
            {ferpa26ApprovalBadge(item.approvalStatus)}
            {ferpa26StatusBadge(item.status)}
            {ferpa26RiskBadge(item.riskLevel)}
            {item.reviewStatus && <span className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{item.reviewStatus}</span>}
          </div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Validation issues */}
          {item.validationCodes.length > 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700">⚠ Compliance Issues</p>
              {item.validationCodes.map(c => <p key={c} className="text-xs text-amber-700">• {FERPA26_CODE_LABELS[c] ?? c}</p>)}
            </div>
          )}
          {item.approvalStatus === "Rejected" && item.rejectionReason && (
            <div className="rounded bg-red-50 border border-red-200 p-3">
              <p className="text-xs font-semibold text-red-700">Rejection reason</p>
              <p className="text-sm text-red-700">{item.rejectionReason}</p>
            </div>
          )}

          {/* Recipient */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Recipient</p>
            <div className="grid grid-cols-2 gap-3">
              {row("Recipient", item.dataRecipient)}
              {row("Organization", item.recipientOrganization)}
              {row("Type", item.recipientType)}
              {row("Department", item.department)}
              {row("Data Owner", item.dataOwner)}
            </div>
          </div>

          {/* Data scope */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Data Scope</p>
            {row("Education Record Categories", item.educationRecordCategories)}
            {row("PII Categories", item.piiCategories)}
            {row("Data Fields", item.dataFieldsShared)}
          </div>

          {/* Legal */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Legal Basis</p>
            <div className="grid grid-cols-2 gap-3">
              {row("Legal Basis", item.legalBasis)}
              {row("FERPA Exception", item.ferpaException)}
              <div className="text-sm"><span className="text-xs text-muted-foreground">Consent Required:</span> {item.consentRequired ? <span className="text-amber-700">Yes</span> : "No"}</div>
              {item.consentRequired && row("Consent Reference", item.consentReference)}
            </div>
          </div>

          {/* Approval */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Approval &amp; Agreement</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm"><span className="text-xs text-muted-foreground">Approval Required:</span> {item.approvalRequired ? "Yes" : "No"}</div>
              <div className="text-sm">{ferpa26ApprovalBadge(item.approvalStatus)}</div>
              {item.approvalDate && row("Approval Date", new Date(item.approvalDate).toLocaleDateString())}
              {row("Agreement Ref", item.dataSharingAgreementRef)}
              {row("Contract Ref", item.contractReference)}
            </div>
            {item.approvalNotes && <p className="text-xs text-muted-foreground italic">{item.approvalNotes}</p>}
          </div>

          {/* Review */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Review History</p>
            <div className="grid grid-cols-2 gap-3">
              <div><span className="text-xs text-muted-foreground">Last Review:</span> {item.lastReviewDate ? new Date(item.lastReviewDate).toLocaleDateString() : "Never"}</div>
              <div className={overdue ? "text-red-600 font-medium" : ""}>
                <span className="text-xs text-muted-foreground">Next Review:</span>{" "}
                {item.nextReviewDate ? new Date(item.nextReviewDate).toLocaleDateString() : "Not set"}{overdue && " ⚠"}
              </div>
              {item.reviewerName && row("Reviewer", item.reviewerName)}
            </div>
            {item.reviewNotes && <p className="text-xs text-muted-foreground italic">{item.reviewNotes}</p>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {canManage && (
            <>
              {item.approvalStatus === "Pending" && (
                <>
                  <Button size="sm" onClick={onApprove}><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</Button>
                  <Button size="sm" variant="outline" onClick={onReject} className="text-red-600"><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
                </>
              )}
              {item.approvalStatus === "NotRequired" && (
                <Button size="sm" variant="outline" onClick={onSubmitApproval}><FileCheck className="w-3 h-3 mr-1" /> Submit for Approval</Button>
              )}
              <Button size="sm" variant="outline" onClick={onReview}><RefreshCcw className="w-3 h-3 mr-1" /> Review</Button>
              <Button size="sm" variant="outline" onClick={onDelete} className="text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
              <Button size="sm" onClick={onEdit}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main FERPA-26 Section ─────────────────────────────────────────────────────
function FerpaDisclosureGovernanceSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth26();

  const [recipientFilter,      setRecipientFilter]      = useState("");
  const [purposeFilter,        setPurposeFilter]        = useState("");
  const [categoryFilter,       setCategoryFilter]       = useState("");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("");
  const [riskFilter,           setRiskFilter]           = useState("");
  const [statusFilter,         setStatusFilter]         = useState("");

  const [showAdd,           setShowAdd]           = useState(false);
  const [editItem,          setEditItem]          = useState<FerpaDisclosureGovernance | null>(null);
  const [submitApprovalItem,setSubmitApprovalItem]= useState<FerpaDisclosureGovernance | null>(null);
  const [approveItem,       setApproveItem]       = useState<FerpaDisclosureGovernance | null>(null);
  const [rejectItem,        setRejectItem]        = useState<FerpaDisclosureGovernance | null>(null);
  const [reviewItem,        setReviewItem]        = useState<FerpaDisclosureGovernance | null>(null);
  const [deleteItem,        setDeleteItem]        = useState<FerpaDisclosureGovernance | null>(null);
  const [detailItem,        setDetailItem]        = useState<FerpaDisclosureGovernance | null>(null);

  const summaryQ = useQuery<Ferpa26Summary>({
    queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/summary"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/disclosure-governance/summary", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ record: FerpaDisclosureGovernance; codes: string[] }[]>({
    queryKey: ["/api/organization/compliance/ferpa/disclosure-governance/issues"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/disclosure-governance/issues", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (recipientFilter)      params.set("recipient",      recipientFilter);
  if (purposeFilter)        params.set("purpose",        purposeFilter);
  if (categoryFilter)       params.set("dataCategory",   categoryFilter);
  if (approvalStatusFilter) params.set("approvalStatus", approvalStatusFilter);
  if (riskFilter)           params.set("riskLevel",      riskFilter);
  if (statusFilter)         params.set("status",         statusFilter);

  const listQ = useQuery<{ total: number; items: FerpaDisclosureGovernance[] }>({
    queryKey: ["/api/organization/compliance/ferpa/disclosure-governance", recipientFilter, purposeFilter, categoryFilter, approvalStatusFilter, riskFilter, statusFilter],
    queryFn: async () => {
      const res = await fetch(`/api/organization/compliance/ferpa/disclosure-governance?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Disclosures",       value: s.total,                icon: <FileCheck className="w-4 h-4 text-blue-500" /> },
    { label: "Approved",                value: s.approved,             icon: <CheckCircle2 className="w-4 h-4 text-green-500" /> },
    { label: "Pending Approval",        value: s.pendingApproval,      icon: <Clock className="w-4 h-4 text-yellow-500" /> },
    { label: "Consent Required",        value: s.consentRequired,      icon: <ShieldCheck className="w-4 h-4 text-purple-500" /> },
    { label: "Missing Documentation",   value: s.missingDocumentation, icon: <AlertTriangle className="w-4 h-4 text-amber-500" /> },
    { label: "Overdue Reviews",         value: s.overdueReviews,       icon: <XCircle className="w-4 h-4 text-red-500" /> },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileCheck className="w-4 h-4 text-blue-600" />
              Approved Data Sharing Governance Records
            </CardTitle>
            <CardDescription>
              Maintain documentation of who receives education records, why data is shared, approvals, and governance evidence — 34 CFR § 99
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Disclosure
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : s ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className={`rounded border p-2 text-center space-y-1 ${m.label === "Missing Documentation" && s.missingDocumentation > 0 ? "border-amber-300 bg-amber-50" : m.label === "Overdue Reviews" && s.overdueReviews > 0 ? "border-red-200 bg-red-50" : ""}`}>
                <div className="flex justify-center">{m.icon}</div>
                <p className="text-xl font-bold">{m.value}</p>
                <p className="text-xs text-muted-foreground leading-tight">{m.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Issues */}
        {issuesQ.data && issuesQ.data.length > 0 && (
          <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {issuesQ.data.length} disclosure{issuesQ.data.length !== 1 ? "s" : ""} with compliance issues
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {issuesQ.data.slice(0, 8).map((it: { record: FerpaDisclosureGovernance; codes: string[] }) => (
                <div key={it.record.id} className="flex items-start gap-2 text-xs text-amber-700">
                  <span className="font-medium whitespace-nowrap">{it.record.disclosureName}</span>
                  {it.record.dataRecipient && <span className="text-amber-500">({it.record.dataRecipient})</span>}
                  <span className="text-amber-400">—</span>
                  <span>{it.codes.slice(0, 2).map(c => FERPA26_CODE_LABELS[c] ?? c).join(" · ")}</span>
                </div>
              ))}
              {issuesQ.data.length > 8 && <p className="text-xs text-amber-500">…and {issuesQ.data.length - 8} more</p>}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <Input
            className="h-8 w-44 text-sm"
            placeholder="Filter by recipient…"
            value={recipientFilter}
            onChange={e => setRecipientFilter(e.target.value)}
          />
          <Input
            className="h-8 w-44 text-sm"
            placeholder="Filter by purpose…"
            value={purposeFilter}
            onChange={e => setPurposeFilter(e.target.value)}
          />
          <select className="border rounded px-2 py-1 text-sm h-8" value={approvalStatusFilter} onChange={e => setApprovalStatusFilter(e.target.value)}>
            <option value="">All approval statuses</option>
            {FERPA26_APPROVAL_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA26_RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA26_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {listQ.data?.total === 0
              ? "No data sharing governance records yet. Add your first disclosure."
              : "No records match the current filters."}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(item => {
              const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
              return (
                <div key={item.id} className="rounded border p-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setDetailItem(item)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{item.disclosureName}</span>
                        {ferpa26ApprovalBadge(item.approvalStatus)}
                        {ferpa26StatusBadge(item.status)}
                        {ferpa26RiskBadge(item.riskLevel)}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        {item.dataRecipient && <span>→ {item.dataRecipient}</span>}
                        {item.recipientType && <span className="text-gray-400">({item.recipientType})</span>}
                        {item.sharingPurpose && <span>{item.sharingPurpose}</span>}
                        {item.consentRequired && <span className="text-purple-600 font-medium">Consent req.</span>}
                        {overdue && <span className="text-red-600 font-medium">⚠ Review overdue</span>}
                      </div>
                      {item.validationCodes.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {item.validationCodes.slice(0, 3).map(c => (
                            <span key={c} className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">⚠ {FERPA26_CODE_LABELS[c] ?? c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canManage && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setEditItem(item); }}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={e => { e.stopPropagation(); setDeleteItem(item); }}><Trash2 className="w-3 h-3" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                  {(item.dataSharingAgreementRef || item.department || item.dataOwner) && (
                    <p className="text-xs text-muted-foreground mt-1 space-x-2">
                      {item.dataSharingAgreementRef && <span>DSA: {item.dataSharingAgreementRef}</span>}
                      {item.department && <span>· {item.department}</span>}
                      {item.dataOwner && <span>· Owner: {item.dataOwner}</span>}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <DisclosureAddDialog open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <DisclosureSubmitApprovalDialog open={!!submitApprovalItem} item={submitApprovalItem} onClose={() => setSubmitApprovalItem(null)} />
      <DisclosureApproveDialog open={!!approveItem} item={approveItem} onClose={() => setApproveItem(null)} />
      <DisclosureRejectDialog open={!!rejectItem} item={rejectItem} onClose={() => setRejectItem(null)} />
      <DisclosureReviewDialog open={!!reviewItem} item={reviewItem} onClose={() => setReviewItem(null)} />
      <DisclosureDeleteDialog open={!!deleteItem} item={deleteItem} onClose={() => setDeleteItem(null)} />
      <DisclosureDetailDialog
        open={!!detailItem} item={detailItem} canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={() => { setEditItem(detailItem); setDetailItem(null); }}
        onSubmitApproval={() => { setSubmitApprovalItem(detailItem); setDetailItem(null); }}
        onApprove={() => { setApproveItem(detailItem); setDetailItem(null); }}
        onReject={() => { setRejectItem(detailItem); setDetailItem(null); }}
        onReview={() => { setReviewItem(detailItem); setDetailItem(null); }}
        onDelete={() => { setDeleteItem(detailItem); setDetailItem(null); }}
      />
    </Card>
  );
}

// ── University SSO Integration Compliance Control ─────────────────

type FerpaSsoIntegration = {
  id: string;
  organizationId: string;
  universityName: string;
  integrationName: string;
  environment: string;
  identityProvider: string | null;
  authenticationProtocol: string | null;
  ssoEnabled: boolean;
  metadataUrl: string | null;
  entityId: string | null;
  certificateReference: string | null;
  certificateExpiry: string | null;
  mfaSupported: boolean;
  mfaEnforced: boolean;
  userProvisioningMethod: string | null;
  attributeMappingConfigured: boolean;
  roleMappingConfigured: boolean;
  sessionTimeoutConfigured: boolean;
  logoutUrlConfigured: boolean;
  technicalOwner: string | null;
  securityOwner: string | null;
  evidenceReference: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  reviewerName: string | null;
  reviewNotes: string | null;
  riskLevel: string;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
  certExpiringSoon: boolean;
};

type Ferpa25Summary = {
  total: number;
  activeIntegrations: number;
  entraId: number;
  okta: number;
  shibboleth: number;
  cas: number;
  mfaEnabled: number;
  complianceIssues: number;
  pendingReviews: number;
  certificatesExpiringSoon: number;
};

const FERPA25_PROVIDERS   = ["Microsoft Entra ID","Shibboleth","Okta","CAS","Other"];
const FERPA25_PROTOCOLS   = ["SAML","OAuth2","OpenID Connect","CAS Protocol"];
const FERPA25_ENVS        = ["Production","Staging","Development","Testing"];
const FERPA25_STATUSES    = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA25_RISK_LEVELS = ["Low","Medium","High","Critical"];
const FERPA25_PROVISIONING= ["JIT","SCIM","Manual","None"];

const FERPA25_CODE_LABELS: Record<string, string> = {
  SSO_NOT_ENABLED:                "SSO not enabled",
  IDENTITY_PROVIDER_NOT_DEFINED:  "Identity provider not defined",
  AUTHENTICATION_PROTOCOL_MISSING:"Authentication protocol missing",
  CERTIFICATE_REFERENCE_REQUIRED: "Certificate reference required",
  CERTIFICATE_EXPIRED:            "Certificate expired",
  MFA_NOT_CONFIGURED:             "MFA not configured",
  ATTRIBUTE_MAPPING_MISSING:      "Attribute mapping not configured",
  ROLE_MAPPING_MISSING:           "Role mapping not configured",
  SECURITY_EVIDENCE_REQUIRED:     "Security evidence required",
  REVIEW_OVERDUE:                 "Review overdue",
};

const FERPA25_PROVIDER_COLORS: Record<string, string> = {
  "Microsoft Entra ID": "bg-blue-100 text-blue-700",
  "Shibboleth":         "bg-purple-100 text-purple-700",
  "Okta":               "bg-sky-100 text-sky-700",
  "CAS":                "bg-green-100 text-green-700",
  "Other":              "bg-gray-100 text-gray-600",
};

function ferpa25ProviderBadge(provider: string | null) {
  const cls = provider ? (FERPA25_PROVIDER_COLORS[provider] ?? "bg-gray-100 text-gray-600") : "bg-gray-100 text-gray-400";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{provider ?? "Unknown"}</span>;
}

function ferpa25StatusBadge(status: string) {
  const c =
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{status}</span>;
}

function ferpa25RiskBadge(risk: string) {
  const c =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${c}`}>{risk}</span>;
}

function useAuth25() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ──────────────────────────────────────────────────────────
function SsoAddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaSsoIntegration | null; onClose: () => void }) {
  const { header } = useAuth25();
  const { toast }  = useToast();
  const isEdit     = !!initial;

  const blank = {
    universityName: "", integrationName: "", environment: "Production",
    identityProvider: "Microsoft Entra ID", authenticationProtocol: "SAML",
    ssoEnabled: true, metadataUrl: "", entityId: "", certificateReference: "",
    certificateExpiry: "", mfaSupported: true, mfaEnforced: true,
    userProvisioningMethod: "SCIM",
    attributeMappingConfigured: false, roleMappingConfigured: false,
    sessionTimeoutConfigured: false, logoutUrlConfigured: false,
    technicalOwner: "", securityOwner: "", evidenceReference: "",
    lastReviewDate: "", nextReviewDate: "", reviewerName: "", reviewNotes: "",
    riskLevel: "Medium", status: "Draft",
  };

  const [form, setForm] = useState({ ...blank });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastKey, setLastKey] = useState("");
  const openKey = `${open}-${initial?.id ?? "new"}`;
  if (openKey !== lastKey) {
    setLastKey(openKey);
    if (open) {
      setForm(initial ? {
        universityName:             initial.universityName,
        integrationName:            initial.integrationName,
        environment:                initial.environment,
        identityProvider:           initial.identityProvider ?? "Microsoft Entra ID",
        authenticationProtocol:     initial.authenticationProtocol ?? "SAML",
        ssoEnabled:                 initial.ssoEnabled,
        metadataUrl:                initial.metadataUrl ?? "",
        entityId:                   initial.entityId ?? "",
        certificateReference:       initial.certificateReference ?? "",
        certificateExpiry:          initial.certificateExpiry?.slice(0, 10) ?? "",
        mfaSupported:               initial.mfaSupported,
        mfaEnforced:                initial.mfaEnforced,
        userProvisioningMethod:     initial.userProvisioningMethod ?? "SCIM",
        attributeMappingConfigured: initial.attributeMappingConfigured,
        roleMappingConfigured:      initial.roleMappingConfigured,
        sessionTimeoutConfigured:   initial.sessionTimeoutConfigured,
        logoutUrlConfigured:        initial.logoutUrlConfigured,
        technicalOwner:             initial.technicalOwner ?? "",
        securityOwner:              initial.securityOwner ?? "",
        evidenceReference:          initial.evidenceReference ?? "",
        lastReviewDate:             initial.lastReviewDate?.slice(0, 10) ?? "",
        nextReviewDate:             initial.nextReviewDate?.slice(0, 10) ?? "",
        reviewerName:               initial.reviewerName ?? "",
        reviewNotes:                initial.reviewNotes ?? "",
        riskLevel:                  initial.riskLevel,
        status:                     initial.status,
      } : { ...blank });
    }
  }

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        universityName: form.universityName || null,
        integrationName: form.integrationName,
        environment: form.environment,
        identityProvider: form.identityProvider || null,
        authenticationProtocol: form.authenticationProtocol || null,
        ssoEnabled: form.ssoEnabled,
        metadataUrl: form.metadataUrl || null,
        entityId: form.entityId || null,
        certificateReference: form.certificateReference || null,
        certificateExpiry: form.certificateExpiry || null,
        mfaSupported: form.mfaSupported,
        mfaEnforced: form.mfaEnforced,
        userProvisioningMethod: form.userProvisioningMethod || null,
        attributeMappingConfigured: form.attributeMappingConfigured,
        roleMappingConfigured: form.roleMappingConfigured,
        sessionTimeoutConfigured: form.sessionTimeoutConfigured,
        logoutUrlConfigured: form.logoutUrlConfigured,
        technicalOwner: form.technicalOwner || null,
        securityOwner: form.securityOwner || null,
        evidenceReference: form.evidenceReference || null,
        lastReviewDate: form.lastReviewDate || null,
        nextReviewDate: form.nextReviewDate || null,
        reviewerName: form.reviewerName || null,
        reviewNotes: form.reviewNotes || null,
        riskLevel: form.riskLevel,
        status: form.status,
      };
      const url    = isEdit ? `/api/organization/compliance/ferpa/sso-integrations/${initial!.id}` : "/api/organization/compliance/ferpa/sso-integrations";
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations/issues"] });
      toast({ title: isEdit ? "SSO configuration updated" : "SSO integration created" });
      setErrors({});
      onClose();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit SSO Configuration" : "Add SSO Integration"}</DialogTitle>
          <DialogDescription>Document university identity provider integration for FERPA compliance tracking. No credentials or secrets should be entered here.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Identity */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Integration Identity</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>University Name</Label>
                <Input value={form.universityName} onChange={e => set("universityName", e.target.value)} placeholder="State University" />
              </div>
              <div className="space-y-1">
                <Label>Integration Name <span className="text-red-500">*</span></Label>
                <Input className={fieldCls("", errors, "integrationName")} aria-invalid={!!errors.integrationName} value={form.integrationName} onChange={e => { set("integrationName", e.target.value); clearErr(setErrors, "integrationName"); }} placeholder="Entra ID SSO — Production" />
                {fErr(errors, "integrationName")}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Environment</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.environment} onChange={e => set("environment", e.target.value)}>
                  {FERPA25_ENVS.map(e => <option key={e}>{e}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Identity Provider</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.identityProvider} onChange={e => set("identityProvider", e.target.value)}>
                  {FERPA25_PROVIDERS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Protocol</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.authenticationProtocol} onChange={e => set("authenticationProtocol", e.target.value)}>
                  {FERPA25_PROTOCOLS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ferpa25-sso" checked={form.ssoEnabled} onChange={e => set("ssoEnabled", e.target.checked)} className="rounded" />
              <Label htmlFor="ferpa25-sso" className="cursor-pointer">SSO Enabled</Label>
            </div>
          </div>

          {/* SSO config — no secrets */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">SSO Configuration (no secrets)</p>
            <div className="space-y-1">
              <Label>Metadata URL / Reference</Label>
              <Input value={form.metadataUrl} onChange={e => set("metadataUrl", e.target.value)} placeholder="https://login.microsoftonline.com/tenant/federationmetadata.xml" />
            </div>
            <div className="space-y-1">
              <Label>Entity ID / Issuer</Label>
              <Input value={form.entityId} onChange={e => set("entityId", e.target.value)} placeholder="https://sso.university.edu/saml" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Certificate Reference (thumbprint / ID only)</Label>
                <Input value={form.certificateReference} onChange={e => set("certificateReference", e.target.value)} placeholder="cert-thumbprint-abc123" />
              </div>
              <div className="space-y-1">
                <Label>Certificate Expiry Date</Label>
                <Input type="date" value={form.certificateExpiry} onChange={e => set("certificateExpiry", e.target.value)} />
              </div>
            </div>
          </div>

          {/* MFA & provisioning */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">MFA &amp; Provisioning</p>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.mfaSupported} onChange={e => set("mfaSupported", e.target.checked)} className="rounded" />
                MFA Supported
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.mfaEnforced} onChange={e => set("mfaEnforced", e.target.checked)} className="rounded" />
                MFA Enforced
              </label>
            </div>
            <div className="space-y-1">
              <Label>User Provisioning Method</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.userProvisioningMethod} onChange={e => set("userProvisioningMethod", e.target.value)}>
                {FERPA25_PROVISIONING.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "attributeMappingConfigured", label: "Attribute mapping configured" },
                { k: "roleMappingConfigured",      label: "Role mapping configured" },
                { k: "sessionTimeoutConfigured",   label: "Session timeout configured" },
                { k: "logoutUrlConfigured",        label: "Logout URL configured" },
              ].map(({ k, label }) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={(form as unknown as Record<string, boolean>)[k]} onChange={e => set(k, e.target.checked)} className="rounded" />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Ownership & review */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ownership &amp; Review</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Technical Owner</Label>
                <Input value={form.technicalOwner} onChange={e => set("technicalOwner", e.target.value)} placeholder="IT Security Team" />
              </div>
              <div className="space-y-1">
                <Label>Security Owner</Label>
                <Input value={form.securityOwner} onChange={e => set("securityOwner", e.target.value)} placeholder="Chief Privacy Officer" />
              </div>
              <div className="space-y-1">
                <Label>Risk Level</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                  {FERPA25_RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                  {FERPA25_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Evidence Reference</Label>
              <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://sso-policy-2026.pdf" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Last Review Date</Label>
                <Input type="date" value={form.lastReviewDate} onChange={e => set("lastReviewDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Review Notes</Label>
              <Textarea value={form.reviewNotes} onChange={e => set("reviewNotes", e.target.value)} rows={2} placeholder="Notes from last review" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            const errs: Record<string, string> = {};
            required(errs, "integrationName", form.integrationName, "Integration Name");
            if (Object.keys(errs).length > 0) {
              setErrors(errs);
              toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
              return;
            }
            setErrors({});
            mutation.mutate();
          }} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Create Integration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ───────────────────────────────────────────────────────────
function SsoValidateDialog({ open, item, onClose }: { open: boolean; item: FerpaSsoIntegration | null; onClose: () => void }) {
  const { header } = useAuth25();
  const { toast }  = useToast();
  const [result, setResult] = useState<{ codes: string[]; isCompliant: boolean } | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/sso-integrations/${item.id}/validate`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header }, body: "{}",
      });
      await throwIfResNotOk(res);
      return res.json() as Promise<{ codes: string[]; isCompliant: boolean }>;
    },
    onSuccess: (data) => {
      setResult(data ?? null);
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations"] });
      toast({ title: data?.isCompliant ? "Integration is compliant" : "Compliance issues detected" });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  const handleClose = () => { setResult(null); onClose(); };
  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Validate SSO Integration</DialogTitle>
          <DialogDescription>Run compliance checks on <strong>{item?.integrationName}</strong></DialogDescription>
        </DialogHeader>
        {result ? (
          <div className={`rounded p-3 ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
            {result.isCompliant
              ? <p className="text-sm text-green-700 font-medium">✓ Compliant — all SSO configuration checks passed.</p>
              : <div className="space-y-1"><p className="text-sm font-semibold text-amber-700">Issues detected:</p>
                  {result.codes.map(c => <p key={c} className="text-xs text-amber-700">• {FERPA25_CODE_LABELS[c] ?? c}</p>)}
                </div>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-2">Click Validate to run all 10 compliance checks against this SSO configuration.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Close</Button>
          {!result && <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>{mutation.isPending ? "Validating…" : "Validate"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Connection test dialog ────────────────────────────────────────────────────
function SsoTestDialog({ open, item, onClose }: { open: boolean; item: FerpaSsoIntegration | null; onClose: () => void }) {
  const { header } = useAuth25();
  const { toast }  = useToast();
  const [result, setResult] = useState<{ passed: boolean; message: string; steps: string[] } | null>(null);
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/sso-integrations/${item.id}/test`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header }, body: "{}",
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: (data) => {
      setResult(data ?? null);
      toast({ title: data?.passed ? "Configuration check passed" : "Configuration gaps detected" });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  const handleClose = () => { setResult(null); onClose(); };
  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Run Connection Test</DialogTitle>
          <DialogDescription>Simulate a configuration pre-flight check for <strong>{item?.integrationName}</strong></DialogDescription>
        </DialogHeader>
        {result ? (
          <div className={`rounded p-3 space-y-2 ${result.passed ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
            <p className={`text-sm font-semibold ${result.passed ? "text-green-700" : "text-amber-700"}`}>{result.message}</p>
            <ul className="space-y-1">
              {result.steps.map((s, i) => <li key={i} className="text-xs text-muted-foreground">{s}</li>)}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-2">Runs a simulated pre-flight configuration check. Does not initiate a live authentication attempt against the identity provider.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Close</Button>
          {!result && <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>{mutation.isPending ? "Running…" : "Run Test"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function SsoReviewDialog({ open, item, onClose }: { open: boolean; item: FerpaSsoIntegration | null; onClose: () => void }) {
  const { header } = useAuth25();
  const { toast }  = useToast();
  const [reviewerName, setReviewerName] = useState("");
  const [notes, setNotes]               = useState("");
  const [evidenceRef, setEvidenceRef]   = useState("");
  const [nextDate, setNextDate]         = useState("");
  const [reviewerErr, setReviewerErr]   = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      if (!reviewerName.trim()) { setReviewerErr("Reviewer name is required."); throw new Error("Please fix the highlighted fields."); }
      setReviewerErr("");
      const res = await fetch(`/api/organization/compliance/ferpa/sso-integrations/${item.id}/review`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reviewerName, reviewNotes: notes || null, evidenceReference: evidenceRef || null, nextReviewDate: nextDate || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations/issues"] });
      toast({ title: "Review recorded — status set to Under Review" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Review</DialogTitle>
          <DialogDescription>Review <strong>{item?.integrationName}</strong></DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1"><Label>Reviewer Name <span className="text-red-500">*</span></Label>
            <Input value={reviewerName} onChange={e => { setReviewerName(e.target.value); if (e.target.value.trim()) setReviewerErr(""); }} placeholder="Security Auditor"
              className={reviewerErr ? "border-red-500 focus-visible:ring-red-500" : ""}
              aria-invalid={!!reviewerErr} />
            {reviewerErr && <p role="alert" className="text-xs text-red-500 mt-0.5">⚠ {reviewerErr}</p>}</div>
          <div className="space-y-1"><Label>Evidence Reference</Label>
            <Input value={evidenceRef} onChange={e => setEvidenceRef(e.target.value)} placeholder="evidence://review-2026.pdf" /></div>
          <div className="space-y-1"><Label>Next Review Date</Label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} /></div>
          <div className="space-y-1"><Label>Review Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Findings or required changes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function SsoDeleteDialog({ open, item, onClose }: { open: boolean; item: FerpaSsoIntegration | null; onClose: () => void }) {
  const { header } = useAuth25();
  const { toast }  = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/sso-integrations/${item.id}`, { method: "DELETE", headers: header });
      await throwIfResNotOk(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/sso-integrations/summary"] });
      toast({ title: "SSO integration deleted" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete SSO Integration</DialogTitle>
          <DialogDescription>Permanently delete <strong>{item?.integrationName}</strong>?</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function SsoDetailDialog({
  open, item, canManage, onClose, onEdit, onValidate, onTest, onReview, onDelete,
}: {
  open: boolean; item: FerpaSsoIntegration | null; canManage: boolean;
  onClose: () => void; onEdit: () => void; onValidate: () => void;
  onTest: () => void; onReview: () => void; onDelete: () => void;
}) {
  if (!item) return null;
  const certExpired  = item.certificateExpiry && new Date(item.certificateExpiry) < new Date();
  const overdue      = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();

  const boolRow = (label: string, val: boolean) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={val ? "text-green-700 font-medium" : "text-red-600"}>{val ? "✓ Yes" : "✗ No"}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> {item.integrationName}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-1">
            {ferpa25StatusBadge(item.status)}
            {ferpa25ProviderBadge(item.identityProvider)}
            {ferpa25RiskBadge(item.riskLevel)}
            {item.authenticationProtocol && <span className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{item.authenticationProtocol}</span>}
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${item.environment === "Production" ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"}`}>{item.environment}</span>
          </div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {item.validationCodes.length > 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700">⚠ Compliance Issues</p>
              {item.validationCodes.map(c => <p key={c} className="text-xs text-amber-700">• {FERPA25_CODE_LABELS[c] ?? c}</p>)}
            </div>
          )}

          {/* SSO config */}
          <div className="rounded border p-3 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">SSO Configuration</p>
            {item.universityName && <p><span className="text-muted-foreground">University:</span> {item.universityName}</p>}
            {item.metadataUrl && <p className="break-all text-blue-700 text-xs">{item.metadataUrl}</p>}
            {item.entityId && <p><span className="text-muted-foreground">Entity ID:</span> {item.entityId}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-muted-foreground">Certificate ref:</span>{" "}
                {item.certificateReference ?? <span className="text-red-600">Not provided</span>}
              </div>
              <div className={certExpired ? "text-red-600 font-medium" : item.certExpiringSoon ? "text-orange-600 font-medium" : ""}>
                <span className="text-muted-foreground">Expiry:</span>{" "}
                {item.certificateExpiry ? new Date(item.certificateExpiry).toLocaleDateString() : "Not set"}
                {certExpired && " ⚠ EXPIRED"}{item.certExpiringSoon && !certExpired && " ⚠ Expiring soon"}
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Security Configuration</p>
            {boolRow("MFA Supported",              item.mfaSupported)}
            {boolRow("MFA Enforced",               item.mfaEnforced)}
            {boolRow("Attribute Mapping",          item.attributeMappingConfigured)}
            {boolRow("Role Mapping",               item.roleMappingConfigured)}
            {boolRow("Session Timeout",            item.sessionTimeoutConfigured)}
            {boolRow("Logout URL",                 item.logoutUrlConfigured)}
            {item.userProvisioningMethod && <p className="text-sm"><span className="text-muted-foreground">Provisioning:</span> {item.userProvisioningMethod}</p>}
          </div>

          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Technical owner:</span> {item.technicalOwner ?? "—"}</div>
            <div><span className="text-muted-foreground">Security owner:</span> {item.securityOwner ?? "—"}</div>
          </div>

          {/* Evidence */}
          <div className="rounded border p-3 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Evidence &amp; Review History</p>
            {item.evidenceReference && <p className="text-blue-700 break-all">{item.evidenceReference}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Last Review:</span> {item.lastReviewDate ? new Date(item.lastReviewDate).toLocaleDateString() : "Never"}</div>
              <div className={overdue ? "text-red-600 font-medium" : ""}>
                <span className="text-muted-foreground">Next Review:</span>{" "}
                {item.nextReviewDate ? new Date(item.nextReviewDate).toLocaleDateString() : "Not set"}{overdue && " ⚠"}
              </div>
              {item.reviewerName && <div><span className="text-muted-foreground">Reviewer:</span> {item.reviewerName}</div>}
            </div>
            {item.reviewNotes && <p className="text-muted-foreground italic">{item.reviewNotes}</p>}
            {item.approvedAt && <p className="text-green-700">✓ Approved {new Date(item.approvedAt).toLocaleDateString()}</p>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onValidate}><ShieldCheck className="w-3 h-3 mr-1" /> Validate</Button>
          <Button variant="outline" size="sm" onClick={onTest}><RefreshCcw className="w-3 h-3 mr-1" /> Test</Button>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
              <Button variant="outline" size="sm" onClick={onReview}><RefreshCcw className="w-3 h-3 mr-1" /> Review</Button>
              <Button size="sm" onClick={onEdit}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main FERPA-25 Section ─────────────────────────────────────────────────────
function FerpaSsoIntegrationSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth25();

  const [providerFilter, setProviderFilter] = useState("");
  const [protocolFilter, setProtocolFilter] = useState("");
  const [envFilter,      setEnvFilter]      = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");
  const [mfaFilter,      setMfaFilter]      = useState("");
  const [riskFilter,     setRiskFilter]     = useState("");

  const [showAdd,      setShowAdd]      = useState(false);
  const [editItem,     setEditItem]     = useState<FerpaSsoIntegration | null>(null);
  const [validateItem, setValidateItem] = useState<FerpaSsoIntegration | null>(null);
  const [testItem,     setTestItem]     = useState<FerpaSsoIntegration | null>(null);
  const [reviewItem,   setReviewItem]   = useState<FerpaSsoIntegration | null>(null);
  const [deleteItem,   setDeleteItem]   = useState<FerpaSsoIntegration | null>(null);
  const [detailItem,   setDetailItem]   = useState<FerpaSsoIntegration | null>(null);

  const summaryQ = useQuery<Ferpa25Summary>({
    queryKey: ["/api/organization/compliance/ferpa/sso-integrations/summary"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/sso-integrations/summary", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ integration: FerpaSsoIntegration; codes: string[] }[]>({
    queryKey: ["/api/organization/compliance/ferpa/sso-integrations/issues"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/sso-integrations/issues", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (providerFilter) params.set("provider",    providerFilter);
  if (protocolFilter) params.set("protocol",    protocolFilter);
  if (envFilter)      params.set("environment", envFilter);
  if (statusFilter)   params.set("status",      statusFilter);
  if (mfaFilter)      params.set("mfaEnabled",  mfaFilter === "yes" ? "true" : "false");
  if (riskFilter)     params.set("riskLevel",   riskFilter);

  const listQ = useQuery<{ total: number; items: FerpaSsoIntegration[] }>({
    queryKey: ["/api/organization/compliance/ferpa/sso-integrations", providerFilter, protocolFilter, envFilter, statusFilter, mfaFilter, riskFilter],
    queryFn: async () => {
      const res = await fetch(`/api/organization/compliance/ferpa/sso-integrations?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Integrations", value: s.total,              icon: <ShieldCheck className="w-4 h-4 text-blue-500" /> },
    { label: "Active",             value: s.activeIntegrations, icon: <CheckCircle2 className="w-4 h-4 text-green-500" /> },
    { label: "Entra ID",           value: s.entraId,            icon: <ShieldCheck className="w-4 h-4 text-blue-400" /> },
    { label: "Okta",               value: s.okta,               icon: <ShieldCheck className="w-4 h-4 text-sky-400" /> },
    { label: "Shibboleth",         value: s.shibboleth,         icon: <ShieldCheck className="w-4 h-4 text-purple-400" /> },
    { label: "CAS",                value: s.cas,                icon: <ShieldCheck className="w-4 h-4 text-green-400" /> },
    { label: "MFA Enabled",        value: s.mfaEnabled,         icon: <Lock className="w-4 h-4 text-green-500" /> },
    { label: "Compliance Issues",  value: s.complianceIssues,   icon: <AlertTriangle className="w-4 h-4 text-amber-500" /> },
    { label: "Pending Reviews",    value: s.pendingReviews,     icon: <Clock className="w-4 h-4 text-orange-500" /> },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="w-4 h-4 text-blue-600" />
              University SSO Integration Compliance
            </CardTitle>
            <CardDescription>
              Document and validate university identity provider (Entra ID, Shibboleth, Okta, CAS) SSO configurations for FERPA-compliant education record access — 34 CFR § 99
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Integration
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-5 gap-3">{[...Array(9)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : s ? (
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
            {metrics.map(m => (
              <div key={m.label} className="rounded border p-2 text-center space-y-1">
                <div className="flex justify-center">{m.icon}</div>
                <p className="text-xl font-bold">{m.value}</p>
                <p className="text-xs text-muted-foreground leading-tight">{m.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Issues */}
        {issuesQ.data && issuesQ.data.length > 0 && (
          <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {issuesQ.data.length} integration{issuesQ.data.length !== 1 ? "s" : ""} with compliance issues
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {issuesQ.data.slice(0, 8).map((it: { integration: FerpaSsoIntegration; codes: string[] }) => (
                <div key={it.integration.id} className="flex items-start gap-2 text-xs text-amber-700">
                  <span className="font-medium whitespace-nowrap">{it.integration.integrationName}</span>
                  {it.integration.identityProvider && <span className="text-amber-500">({it.integration.identityProvider})</span>}
                  <span className="text-amber-400">—</span>
                  <span>{it.codes.slice(0, 2).map(c => FERPA25_CODE_LABELS[c] ?? c).join(" · ")}</span>
                </div>
              ))}
              {issuesQ.data.length > 8 && <p className="text-xs text-amber-500">…and {issuesQ.data.length - 8} more</p>}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <select className="border rounded px-2 py-1 text-sm h-8" value={providerFilter} onChange={e => setProviderFilter(e.target.value)}>
            <option value="">All providers</option>
            {FERPA25_PROVIDERS.map(p => <option key={p}>{p}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={protocolFilter} onChange={e => setProtocolFilter(e.target.value)}>
            <option value="">All protocols</option>
            {FERPA25_PROTOCOLS.map(p => <option key={p}>{p}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={envFilter} onChange={e => setEnvFilter(e.target.value)}>
            <option value="">All environments</option>
            {FERPA25_ENVS.map(e => <option key={e}>{e}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA25_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={mfaFilter} onChange={e => setMfaFilter(e.target.value)}>
            <option value="">MFA (all)</option>
            <option value="yes">MFA Enabled</option>
            <option value="no">MFA Disabled</option>
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={riskFilter} onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA25_RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {listQ.data?.total === 0
              ? "No SSO integrations documented yet. Add your first identity provider configuration."
              : "No integrations match the current filters."}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(item => {
              const certExpired  = item.certificateExpiry && new Date(item.certificateExpiry) < new Date();
              const overdue      = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
              return (
                <div key={item.id} className="rounded border p-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setDetailItem(item)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{item.integrationName}</span>
                        {ferpa25StatusBadge(item.status)}
                        {ferpa25ProviderBadge(item.identityProvider)}
                        {item.authenticationProtocol && <span className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{item.authenticationProtocol}</span>}
                        {item.environment !== "Production" && <span className="inline-flex px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-xs">{item.environment}</span>}
                        {ferpa25RiskBadge(item.riskLevel)}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span>{item.mfaEnforced ? "🔒 MFA enforced" : item.mfaSupported ? "MFA supported" : <span className="text-red-600">No MFA</span>}</span>
                        {item.certificateExpiry && (
                          <span className={certExpired ? "text-red-600 font-medium" : item.certExpiringSoon ? "text-orange-600 font-medium" : ""}>
                            Cert: {new Date(item.certificateExpiry).toLocaleDateString()}{certExpired && " ⚠ EXPIRED"}{item.certExpiringSoon && !certExpired && " ⚠ expiring"}
                          </span>
                        )}
                        {overdue && <span className="text-red-600 font-medium">⚠ Review overdue</span>}
                      </div>
                      {item.validationCodes.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {item.validationCodes.slice(0, 3).map(c => (
                            <span key={c} className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">⚠ {FERPA25_CODE_LABELS[c] ?? c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canManage && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setEditItem(item); }}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={e => { e.stopPropagation(); setDeleteItem(item); }}><Trash2 className="w-3 h-3" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                  {(item.technicalOwner || item.universityName) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {item.universityName && <span>{item.universityName} · </span>}
                      {item.technicalOwner && <span>Owner: {item.technicalOwner}</span>}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <SsoAddDialog open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <SsoValidateDialog open={!!validateItem} item={validateItem} onClose={() => setValidateItem(null)} />
      <SsoTestDialog open={!!testItem} item={testItem} onClose={() => setTestItem(null)} />
      <SsoReviewDialog open={!!reviewItem} item={reviewItem} onClose={() => setReviewItem(null)} />
      <SsoDeleteDialog open={!!deleteItem} item={deleteItem} onClose={() => setDeleteItem(null)} />
      <SsoDetailDialog
        open={!!detailItem} item={detailItem} canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={() => { setEditItem(detailItem); setDetailItem(null); }}
        onValidate={() => { setValidateItem(detailItem); setDetailItem(null); }}
        onTest={() => { setTestItem(detailItem); setDetailItem(null); }}
        onReview={() => { setReviewItem(detailItem); setDetailItem(null); }}
        onDelete={() => { setDeleteItem(detailItem); setDetailItem(null); }}
      />
    </Card>
  );
}

// ── FERPA-24 — University User Roles and Least-Privilege RBAC Mapping ────────

type FerpaRoleMapping = {
  id: string;
  organizationId: string;
  roleName: string;
  roleType: string | null;
  department: string | null;
  userCategory: string | null;
  permissionName: string | null;
  permissionDescription: string | null;
  allowedActions: string | null;
  educationRecordCategories: string | null;
  piiAccessLevel: string;
  approvalRequired: boolean;
  approvalDetails: string | null;
  accessJustification: string | null;
  dataPurpose: string | null;
  assignedOwner: string | null;
  riskLevel: string;
  evidenceReference: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  reviewerName: string | null;
  reviewNotes: string | null;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

type Ferpa24Summary = {
  total: number;
  activeRoles: number;
  highRiskAccess: number;
  piiAccessRoles: number;
  leastPrivilegeViolations: number;
  pendingReviews: number;
  withApprovalRequired: number;
};

const FERPA24_ROLE_TYPES   = ["Administrative","Academic","Student","Staff","External","System"];
const FERPA24_USER_CATS    = ["FacultyMember","Registrar","Advisor","ITAdmin","Student","Auditor","Other"];
const FERPA24_STATUSES     = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA24_PII_LEVELS   = ["None","Low","Medium","High","Full"];
const FERPA24_RISK_LEVELS  = ["Low","Medium","High","Critical"];
const FERPA24_ACTIONS      = ["View","Create","Update","Delete","Export"];
const FERPA24_CATEGORIES   = ["Academic","Financial","Health","Disciplinary","Enrollment","Employment"];

const FERPA24_CODE_LABELS: Record<string, string> = {
  ROLE_PERMISSION_NOT_DEFINED: "Permission not defined",
  EXCESSIVE_PII_ACCESS:        "Excessive PII access",
  MISSING_ACCESS_JUSTIFICATION:"Missing access justification",
  MISSING_APPROVAL:            "Approval workflow not documented",
  MISSING_EVIDENCE:            "No evidence reference",
  REVIEW_OVERDUE:              "Review overdue",
  LEAST_PRIVILEGE_VIOLATION:   "Least-privilege violation",
};

function ferpa24StatusBadge(status: string) {
  const color =
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>{status}</span>;
}

function ferpa24RiskBadge(risk: string) {
  const color =
    risk === "Critical" ? "bg-red-100 text-red-700" :
    risk === "High"     ? "bg-orange-100 text-orange-700" :
    risk === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                          "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>{risk}</span>;
}

function ferpa24PiiBadge(level: string) {
  const color =
    level === "Full"   ? "bg-red-100 text-red-700" :
    level === "High"   ? "bg-orange-100 text-orange-700" :
    level === "Medium" ? "bg-yellow-100 text-yellow-700" :
    level === "Low"    ? "bg-blue-100 text-blue-700" :
                         "bg-gray-100 text-gray-500";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>PII: {level}</span>;
}

function useAuth24() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Action checkboxes ─────────────────────────────────────────────────────────
function ActionsCheckboxGroup({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const selected = value ? value.split(",").map(s => s.trim()).filter(Boolean) : [];
  const toggle = (action: string) => {
    const next = selected.includes(action)
      ? selected.filter(a => a !== action)
      : [...selected, action];
    onChange(next.join(","));
  };
  return (
    <div className="flex flex-wrap gap-3">
      {FERPA24_ACTIONS.map(a => (
        <label key={a} className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={selected.includes(a)} onChange={() => toggle(a)} className="rounded" />
          {a}
        </label>
      ))}
    </div>
  );
}

// ── Add/Edit dialog ─────────────────────────────────────────────────────────
function RoleMappingAddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaRoleMapping | null; onClose: () => void }) {
  const { header } = useAuth24();
  const { toast }  = useToast();
  const isEdit     = !!initial;

  const blank = {
    roleName: "", roleType: "Administrative", department: "", userCategory: "Registrar",
    permissionName: "", permissionDescription: "",
    allowedActions: "View", educationRecordCategories: "",
    piiAccessLevel: "None", approvalRequired: false, approvalDetails: "",
    accessJustification: "", dataPurpose: "",
    assignedOwner: "", riskLevel: "Low",
    evidenceReference: "", lastReviewDate: "", nextReviewDate: "",
    reviewerName: "", reviewNotes: "", status: "Draft",
  };

  const [form, setForm] = useState({ ...blank });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastKey, setLastKey] = useState("");
  const openKey = `${open}-${initial?.id ?? "new"}`;
  if (openKey !== lastKey) {
    setLastKey(openKey);
    if (open) {
      setForm(initial ? {
        roleName:                  initial.roleName,
        roleType:                  initial.roleType ?? "Administrative",
        department:                initial.department ?? "",
        userCategory:              initial.userCategory ?? "Registrar",
        permissionName:            initial.permissionName ?? "",
        permissionDescription:     initial.permissionDescription ?? "",
        allowedActions:            initial.allowedActions ?? "View",
        educationRecordCategories: initial.educationRecordCategories ?? "",
        piiAccessLevel:            initial.piiAccessLevel,
        approvalRequired:          initial.approvalRequired,
        approvalDetails:           initial.approvalDetails ?? "",
        accessJustification:       initial.accessJustification ?? "",
        dataPurpose:               initial.dataPurpose ?? "",
        assignedOwner:             initial.assignedOwner ?? "",
        riskLevel:                 initial.riskLevel,
        evidenceReference:         initial.evidenceReference ?? "",
        lastReviewDate:            initial.lastReviewDate?.slice(0, 10) ?? "",
        nextReviewDate:            initial.nextReviewDate?.slice(0, 10) ?? "",
        reviewerName:              initial.reviewerName ?? "",
        reviewNotes:               initial.reviewNotes ?? "",
        status:                    initial.status,
      } : { ...blank });
    }
  }

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        roleName:                  form.roleName,
        roleType:                  form.roleType || null,
        department:                form.department || null,
        userCategory:              form.userCategory || null,
        permissionName:            form.permissionName || null,
        permissionDescription:     form.permissionDescription || null,
        allowedActions:            form.allowedActions || null,
        educationRecordCategories: form.educationRecordCategories || null,
        piiAccessLevel:            form.piiAccessLevel,
        approvalRequired:          form.approvalRequired,
        approvalDetails:           form.approvalDetails || null,
        accessJustification:       form.accessJustification || null,
        dataPurpose:               form.dataPurpose || null,
        assignedOwner:             form.assignedOwner || null,
        riskLevel:                 form.riskLevel,
        evidenceReference:         form.evidenceReference || null,
        lastReviewDate:            form.lastReviewDate || null,
        nextReviewDate:            form.nextReviewDate || null,
        reviewerName:              form.reviewerName || null,
        reviewNotes:               form.reviewNotes || null,
        status:                    form.status,
      };
      const url    = isEdit ? `/api/organization/compliance/ferpa/role-mappings/${initial!.id}` : "/api/organization/compliance/ferpa/role-mappings";
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings/issues"] });
      toast({ title: isEdit ? "Role mapping updated" : "Role mapping created" });
      setErrors({});
      onClose();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Role Mapping" : "Add Role Mapping"}</DialogTitle>
          <DialogDescription>Document a university role's permissions and validate least-privilege access.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Role identity */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Role Identity</p>
            <div className="space-y-1">
              <Label>Role Name <span className="text-red-500">*</span></Label>
              <Input className={fieldCls("", errors, "roleName")} aria-invalid={!!errors.roleName} value={form.roleName} onChange={e => { set("roleName", e.target.value); clearErr(setErrors, "roleName"); }} placeholder="Registrar" />
              {fErr(errors, "roleName")}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Role Type</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.roleType} onChange={e => set("roleType", e.target.value)}>
                  {FERPA24_ROLE_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Department</Label>
                <Input value={form.department} onChange={e => set("department", e.target.value)} placeholder="Records Office" />
              </div>
              <div className="space-y-1">
                <Label>User Category</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.userCategory} onChange={e => set("userCategory", e.target.value)}>
                  {FERPA24_USER_CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA24_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Permission definition */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Permission Definition</p>
            <div className="space-y-1">
              <Label>Permission Name</Label>
              <Input value={form.permissionName} onChange={e => set("permissionName", e.target.value)} placeholder="STUDENT_RECORD_VIEW" />
            </div>
            <div className="space-y-1">
              <Label>Permission Description</Label>
              <Textarea value={form.permissionDescription} onChange={e => set("permissionDescription", e.target.value)} rows={2} placeholder="View student academic records" />
            </div>
            <div className="space-y-2">
              <Label>Allowed Actions</Label>
              <ActionsCheckboxGroup value={form.allowedActions} onChange={v => set("allowedActions", v)} />
            </div>
            <div className="space-y-1">
              <Label>Education Record Categories</Label>
              <Input value={form.educationRecordCategories} onChange={e => set("educationRecordCategories", e.target.value)} placeholder="Academic,Enrollment" />
              <p className="text-xs text-muted-foreground">Comma-separated: {FERPA24_CATEGORIES.join(", ")}</p>
            </div>
          </div>

          {/* Access control */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Access Control</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>PII Access Level</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.piiAccessLevel} onChange={e => set("piiAccessLevel", e.target.value)}>
                  {FERPA24_PII_LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Risk Level</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)}>
                  {FERPA24_RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Access Justification</Label>
              <Textarea value={form.accessJustification} onChange={e => set("accessJustification", e.target.value)} rows={2} placeholder="Business reason for this level of access" />
            </div>
            <div className="space-y-1">
              <Label>Data Purpose</Label>
              <Input value={form.dataPurpose} onChange={e => set("dataPurpose", e.target.value)} placeholder="Official transcript processing" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="ferpa24-approval" checked={form.approvalRequired} onChange={e => set("approvalRequired", e.target.checked)} className="rounded" />
                <Label htmlFor="ferpa24-approval" className="cursor-pointer">Approval required</Label>
              </div>
              {form.approvalRequired && (
                <div className="pl-5 space-y-1">
                  <Label>Approval Details <span className="text-red-500">*</span></Label>
                  <Textarea value={form.approvalDetails} onChange={e => set("approvalDetails", e.target.value)} rows={2} placeholder="VP Academic Affairs must approve annually" />
                </div>
              )}
            </div>
          </div>

          {/* Ownership & review */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ownership &amp; Review</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Assigned Owner</Label>
                <Input value={form.assignedOwner} onChange={e => set("assignedOwner", e.target.value)} placeholder="Chief Privacy Officer" />
              </div>
              <div className="space-y-1">
                <Label>Evidence Reference</Label>
                <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://rbac-policy.pdf" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Last Review Date</Label>
                <Input type="date" value={form.lastReviewDate} onChange={e => set("lastReviewDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Reviewer Name</Label>
              <Input value={form.reviewerName} onChange={e => set("reviewerName", e.target.value)} placeholder="Privacy Officer" />
            </div>
            <div className="space-y-1">
              <Label>Review Notes</Label>
              <Textarea value={form.reviewNotes} onChange={e => set("reviewNotes", e.target.value)} rows={2} placeholder="Notes from last review" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "roleName", form.roleName, "Role Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); mutation.mutate(); }} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Create Mapping"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Validate dialog ──────────────────────────────────────────────────────────
function RoleMappingValidateDialog({ open, item, onClose }: { open: boolean; item: FerpaRoleMapping | null; onClose: () => void }) {
  const { header } = useAuth24();
  const { toast }  = useToast();
  const [result, setResult] = useState<{ codes: string[]; isCompliant: boolean } | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/role-mappings/${item.id}/validate`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header }, body: "{}",
      });
      await throwIfResNotOk(res);
      return res.json() as Promise<{ codes: string[]; isCompliant: boolean }>;
    },
    onSuccess: (data) => {
      setResult(data ?? null);
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings"] });
      toast({ title: data?.isCompliant ? "Role mapping is compliant" : "Compliance issues detected" });
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  const handleClose = () => { setResult(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={o => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Validate Least Privilege</DialogTitle>
          <DialogDescription>Run compliance checks on <strong>{item?.roleName}</strong> — <em>{item?.permissionName}</em></DialogDescription>
        </DialogHeader>
        {result ? (
          <div className={`rounded p-3 ${result.isCompliant ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
            {result.isCompliant
              ? <p className="text-sm text-green-700 font-medium">✓ Compliant — no least-privilege violations detected.</p>
              : (
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-amber-700">Issues detected:</p>
                  {result.codes.map(c => <p key={c} className="text-xs text-amber-700">• {FERPA24_CODE_LABELS[c] ?? c}</p>)}
                </div>
              )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-2">Click Validate to run least-privilege analysis and detect access risk issues.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Close</Button>
          {!result && (
            <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
              {mutation.isPending ? "Validating…" : "Validate"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ─────────────────────────────────────────────────────────────
function RoleMappingReviewDialog({ open, item, onClose }: { open: boolean; item: FerpaRoleMapping | null; onClose: () => void }) {
  const { header } = useAuth24();
  const { toast }  = useToast();
  const [reviewerName, setReviewerName] = useState("");
  const [notes, setNotes]               = useState("");
  const [evidenceRef, setEvidenceRef]   = useState("");
  const [nextDate, setNextDate]         = useState("");

  const [reviewerErr, setReviewerErr]   = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      if (!reviewerName.trim()) { setReviewerErr("Reviewer name is required."); throw new Error("Please fix the highlighted fields."); }
      setReviewerErr("");
      const res = await fetch(`/api/organization/compliance/ferpa/role-mappings/${item.id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reviewerName, reviewNotes: notes || null, evidenceReference: evidenceRef || null, nextReviewDate: nextDate || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings/issues"] });
      toast({ title: "Review recorded — status set to Under Review" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Review Role Mapping</DialogTitle>
          <DialogDescription>Review <strong>{item?.roleName}</strong> — <em>{item?.permissionName}</em></DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Reviewer Name <span className="text-red-500">*</span></Label>
            <Input value={reviewerName} onChange={e => { setReviewerName(e.target.value); if (e.target.value.trim()) setReviewerErr(""); }} placeholder="Privacy Officer"
              className={reviewerErr ? "border-red-500 focus-visible:ring-red-500" : ""}
              aria-invalid={!!reviewerErr} />
            {reviewerErr && <p role="alert" className="text-xs text-red-500 mt-0.5">⚠ {reviewerErr}</p>}
          </div>
          <div className="space-y-1">
            <Label>Evidence Reference</Label>
            <Input value={evidenceRef} onChange={e => setEvidenceRef(e.target.value)} placeholder="evidence://review-2026.pdf" />
          </div>
          <div className="space-y-1">
            <Label>Next Review Date</Label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Review Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Findings or required changes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Approve dialog ────────────────────────────────────────────────────────────
function RoleMappingApproveDialog({ open, item, onClose }: { open: boolean; item: FerpaRoleMapping | null; onClose: () => void }) {
  const { header } = useAuth24();
  const { toast }  = useToast();
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/role-mappings/${item.id}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify({ notes: notes || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings/summary"] });
      toast({ title: "Role mapping approved" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Approve Role Mapping</DialogTitle>
          <DialogDescription>Approve <strong>{item?.roleName}</strong> — <em>{item?.permissionName}</em> as compliant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Approval Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Approving…" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function RoleMappingDeleteDialog({ open, item, onClose }: { open: boolean; item: FerpaRoleMapping | null; onClose: () => void }) {
  const { header } = useAuth24();
  const { toast }  = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/role-mappings/${item.id}`, { method: "DELETE", headers: header });
      await throwIfResNotOk(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/role-mappings/summary"] });
      toast({ title: "Role mapping deleted" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Role Mapping</DialogTitle>
          <DialogDescription>Permanently delete <strong>{item?.roleName}</strong> — <em>{item?.permissionName}</em>?</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function RoleMappingDetailDialog({
  open, item, canManage, onClose, onEdit, onValidate, onReview, onApprove, onDelete,
}: {
  open: boolean; item: FerpaRoleMapping | null; canManage: boolean;
  onClose: () => void; onEdit: () => void; onValidate: () => void;
  onReview: () => void; onApprove: () => void; onDelete: () => void;
}) {
  if (!item) return null;
  const overdue  = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
  const actions  = item.allowedActions ? item.allowedActions.split(",").map(s => s.trim()).filter(Boolean) : [];
  const cats     = item.educationRecordCategories ? item.educationRecordCategories.split(",").map(s => s.trim()).filter(Boolean) : [];

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" /> {item.roleName}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-1">
            {ferpa24StatusBadge(item.status)}
            {ferpa24RiskBadge(item.riskLevel)}
            {ferpa24PiiBadge(item.piiAccessLevel)}
            {item.roleType && <span className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{item.roleType}</span>}
          </div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {item.validationCodes.length > 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700">⚠ Compliance Issues</p>
              {item.validationCodes.map(c => <p key={c} className="text-xs text-amber-700">• {FERPA24_CODE_LABELS[c] ?? c}</p>)}
            </div>
          )}

          {/* Role info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Department:</span> {item.department ?? "—"}</div>
            <div><span className="text-muted-foreground">User category:</span> {item.userCategory ?? "—"}</div>
            <div><span className="text-muted-foreground">Assigned owner:</span> {item.assignedOwner ?? "—"}</div>
          </div>

          {/* Permission matrix */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Permission Matrix</p>
            <p className="text-sm font-medium">{item.permissionName ?? "Not defined"}</p>
            {item.permissionDescription && <p className="text-sm text-muted-foreground">{item.permissionDescription}</p>}
            {actions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {actions.map(a => {
                  const isDangerous = a === "Delete" || a === "Export";
                  return (
                    <span key={a} className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${isDangerous ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>{a}</span>
                  );
                })}
              </div>
            )}
            {cats.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">Education record categories</p>
                <div className="flex flex-wrap gap-1.5">
                  {cats.map(c => <span key={c} className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">{c}</span>)}
                </div>
              </div>
            )}
          </div>

          {/* Justification & purpose */}
          {(item.accessJustification || item.dataPurpose) && (
            <div className="rounded border p-3 space-y-2 text-sm">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Access Justification</p>
              {item.accessJustification && <p>{item.accessJustification}</p>}
              {item.dataPurpose && <p className="text-muted-foreground"><em>Purpose:</em> {item.dataPurpose}</p>}
            </div>
          )}

          {/* Approval info */}
          {item.approvalRequired && (
            <div className="rounded border p-3 space-y-1 text-sm">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Approval Information</p>
              <p>{item.approvalDetails ?? "No approval details provided."}</p>
              {item.approvedAt && <p className="text-green-700">✓ Approved {new Date(item.approvedAt).toLocaleDateString()}</p>}
            </div>
          )}

          {/* Evidence & review */}
          <div className="rounded border p-3 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Evidence &amp; Review History</p>
            {item.evidenceReference && <p className="text-blue-700 break-all">{item.evidenceReference}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Last Review:</span> {item.lastReviewDate ? new Date(item.lastReviewDate).toLocaleDateString() : "Never"}</div>
              <div className={overdue ? "text-red-600 font-medium" : ""}>
                <span className="text-muted-foreground">Next Review:</span>{" "}
                {item.nextReviewDate ? new Date(item.nextReviewDate).toLocaleDateString() : "Not set"}{overdue && " ⚠"}
              </div>
              {item.reviewerName && <div><span className="text-muted-foreground">Reviewer:</span> {item.reviewerName}</div>}
            </div>
            {item.reviewNotes && <p className="text-sm text-muted-foreground italic">{item.reviewNotes}</p>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onValidate}><ShieldCheck className="w-3 h-3 mr-1" /> Validate</Button>
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
              <Button variant="outline" size="sm" onClick={onReview}><RefreshCcw className="w-3 h-3 mr-1" /> Review</Button>
              {item.status === "UnderReview" && (
                <Button variant="outline" size="sm" onClick={onApprove} className="text-green-700 hover:text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</Button>
              )}
              <Button size="sm" onClick={onEdit}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main FERPA-24 Section ─────────────────────────────────────────────────────
function FerpaRoleAccessControlSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth24();

  const [roleFilter,       setRoleFilter]       = useState("");
  const [deptFilter,       setDeptFilter]        = useState("");
  const [permFilter,       setPermFilter]        = useState("");
  const [piiFilter,        setPiiFilter]         = useState("");
  const [statusFilter,     setStatusFilter]      = useState("");
  const [riskFilter,       setRiskFilter]        = useState("");

  const [showAdd,      setShowAdd]      = useState(false);
  const [editItem,     setEditItem]     = useState<FerpaRoleMapping | null>(null);
  const [validateItem, setValidateItem] = useState<FerpaRoleMapping | null>(null);
  const [reviewItem,   setReviewItem]   = useState<FerpaRoleMapping | null>(null);
  const [approveItem,  setApproveItem]  = useState<FerpaRoleMapping | null>(null);
  const [deleteItem,   setDeleteItem]   = useState<FerpaRoleMapping | null>(null);
  const [detailItem,   setDetailItem]   = useState<FerpaRoleMapping | null>(null);

  const summaryQ = useQuery<Ferpa24Summary>({
    queryKey: ["/api/organization/compliance/ferpa/role-mappings/summary"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/role-mappings/summary", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ mapping: FerpaRoleMapping; codes: string[] }[]>({
    queryKey: ["/api/organization/compliance/ferpa/role-mappings/issues"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/role-mappings/issues", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (roleFilter)   params.set("role",       roleFilter);
  if (deptFilter)   params.set("department", deptFilter);
  if (permFilter)   params.set("permission", permFilter);
  if (piiFilter)    params.set("piiLevel",   piiFilter);
  if (statusFilter) params.set("status",     statusFilter);
  if (riskFilter)   params.set("riskLevel",  riskFilter);

  const listQ = useQuery<{ total: number; items: FerpaRoleMapping[] }>({
    queryKey: ["/api/organization/compliance/ferpa/role-mappings", roleFilter, deptFilter, permFilter, piiFilter, statusFilter, riskFilter],
    queryFn: async () => {
      const res = await fetch(`/api/organization/compliance/ferpa/role-mappings?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Role Mappings",       value: s.total,                    icon: <Users className="w-4 h-4 text-blue-500" /> },
    { label: "Active Roles",              value: s.activeRoles,              icon: <ShieldCheck className="w-4 h-4 text-green-500" /> },
    { label: "High Risk Access",          value: s.highRiskAccess,           icon: <AlertTriangle className="w-4 h-4 text-orange-500" /> },
    { label: "PII Access Roles",          value: s.piiAccessRoles,           icon: <Lock className="w-4 h-4 text-red-500" /> },
    { label: "Least-Priv Violations",     value: s.leastPrivilegeViolations, icon: <ShieldAlert className="w-4 h-4 text-red-500" /> },
    { label: "Pending Reviews",           value: s.pendingReviews,           icon: <Clock className="w-4 h-4 text-amber-500" /> },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4 text-blue-600" />
              University User Roles &amp; Least-Privilege RBAC Mapping
            </CardTitle>
            <CardDescription>
              Document university role permissions and validate least-privilege access to education records — 34 CFR § 99
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Role Mapping
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : s ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className="rounded border p-3 text-center space-y-1">
                <div className="flex justify-center">{m.icon}</div>
                <p className="text-xl font-bold">{m.value}</p>
                <p className="text-xs text-muted-foreground leading-tight">{m.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Issues */}
        {issuesQ.data && issuesQ.data.length > 0 && (
          <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {issuesQ.data.length} role mapping{issuesQ.data.length !== 1 ? "s" : ""} with compliance issues
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {issuesQ.data.slice(0, 8).map((it: { mapping: FerpaRoleMapping; codes: string[] }) => (
                <div key={it.mapping.id} className="flex items-start gap-2 text-xs text-amber-700">
                  <span className="font-medium whitespace-nowrap">{it.mapping.roleName}</span>
                  {it.mapping.department && <span className="text-amber-500">({it.mapping.department})</span>}
                  <span className="text-amber-400">—</span>
                  <span>{it.codes.slice(0, 2).map(c => FERPA24_CODE_LABELS[c] ?? c).join(" · ")}</span>
                </div>
              ))}
              {issuesQ.data.length > 8 && <p className="text-xs text-amber-500">…and {issuesQ.data.length - 8} more</p>}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <Input className="h-8 text-sm w-36" placeholder="Role…"       value={roleFilter}   onChange={e => setRoleFilter(e.target.value)} />
          <Input className="h-8 text-sm w-36" placeholder="Department…" value={deptFilter}   onChange={e => setDeptFilter(e.target.value)} />
          <Input className="h-8 text-sm w-36" placeholder="Permission…" value={permFilter}   onChange={e => setPermFilter(e.target.value)} />
          <select className="border rounded px-2 py-1 text-sm h-8" value={piiFilter}    onChange={e => setPiiFilter(e.target.value)}>
            <option value="">All PII levels</option>
            {FERPA24_PII_LEVELS.map(l => <option key={l}>{l}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter}  onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA24_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={riskFilter}   onChange={e => setRiskFilter(e.target.value)}>
            <option value="">All risk levels</option>
            {FERPA24_RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {listQ.data?.total === 0
              ? "No role mappings yet. Add your first university role to begin documenting your RBAC matrix."
              : "No mappings match the current filters."}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(item => {
              const overdue   = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
              const actions   = item.allowedActions ? item.allowedActions.split(",").map(s => s.trim()).filter(Boolean) : [];
              const hasDanger = actions.some(a => a === "Delete" || a === "Export");
              return (
                <div key={item.id} className="rounded border p-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setDetailItem(item)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{item.roleName}</span>
                        {item.department && <span className="text-xs text-muted-foreground">{item.department}</span>}
                        {ferpa24StatusBadge(item.status)}
                        {ferpa24RiskBadge(item.riskLevel)}
                        {ferpa24PiiBadge(item.piiAccessLevel)}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        {item.permissionName && <span className="font-mono bg-gray-100 px-1 rounded">{item.permissionName}</span>}
                        {actions.length > 0 && (
                          <span className={hasDanger ? "text-orange-600 font-medium" : ""}>
                            {actions.join(", ")}
                          </span>
                        )}
                        {overdue && <span className="text-red-600 font-medium">⚠ Review overdue</span>}
                        {item.nextReviewDate && !overdue && new Date(item.nextReviewDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                          <span className="text-orange-600 font-medium">⚠ Review expiring soon</span>
                        )}
                      </div>
                      {item.validationCodes.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {item.validationCodes.slice(0, 3).map(c => (
                            <span key={c} className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">⚠ {FERPA24_CODE_LABELS[c] ?? c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canManage && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setEditItem(item); }}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={e => { e.stopPropagation(); setDeleteItem(item); }}><Trash2 className="w-3 h-3" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                  {item.assignedOwner && <p className="text-xs text-muted-foreground mt-1">Owner: {item.assignedOwner}</p>}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <RoleMappingAddDialog open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <RoleMappingValidateDialog open={!!validateItem} item={validateItem} onClose={() => setValidateItem(null)} />
      <RoleMappingReviewDialog open={!!reviewItem} item={reviewItem} onClose={() => setReviewItem(null)} />
      <RoleMappingApproveDialog open={!!approveItem} item={approveItem} onClose={() => setApproveItem(null)} />
      <RoleMappingDeleteDialog open={!!deleteItem} item={deleteItem} onClose={() => setDeleteItem(null)} />
      <RoleMappingDetailDialog
        open={!!detailItem} item={detailItem} canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={() => { setEditItem(detailItem); setDetailItem(null); }}
        onValidate={() => { setValidateItem(detailItem); setDetailItem(null); }}
        onReview={() => { setReviewItem(detailItem); setDetailItem(null); }}
        onApprove={() => { setApproveItem(detailItem); setDetailItem(null); }}
        onDelete={() => { setDeleteItem(detailItem); setDetailItem(null); }}
      />
    </Card>
  );
}

// ── FERPA-23 — Education Record Retention and Deletion Policies ──────────────

type FerpaRetentionPolicy = {
  id: string;
  organizationId: string;
  policyName: string;
  recordCategory: string | null;
  educationRecordType: string | null;
  policyVersion: string | null;
  retentionPeriod: number;
  retentionUnit: string;
  retentionStartEvent: string | null;
  deletionMethod: string | null;
  deletionApprovalRequired: boolean;
  approvalWorkflowDetails: string | null;
  legalHoldSupported: boolean;
  exceptionHandlingProcedure: string | null;
  responsibleOwner: string | null;
  department: string | null;
  reviewFrequency: number;
  reviewFrequencyUnit: string;
  evidenceReference: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  reviewerName: string | null;
  reviewNotes: string | null;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

type FerpaRetentionSummary = {
  total: number;
  activePolicies: number;
  expiringPolicies: number;
  missingDocumentation: number;
  pendingReviews: number;
  archivedPolicies: number;
  withLegalHold: number;
};

const FERPA23_CATEGORIES     = ["Academic","Financial","Health","Disciplinary","Enrollment","Employment","Other"];
const FERPA23_STATUSES       = ["Draft","Active","UnderReview","Approved","Archived"];
const FERPA23_UNITS          = ["Days","Months","Years"];
const FERPA23_DELETION_METHODS = ["Shredding","SecureDeletion","Anonymisation","Archival","Other"];
const FERPA23_START_EVENTS   = ["Graduation","Withdrawal","LastEnrollment","Termination","RequestDate","Other"];

const FERPA23_CODE_LABELS: Record<string, string> = {
  MISSING_RETENTION_PERIOD:  "Retention period not set",
  MISSING_OWNER:             "Responsible owner missing",
  MISSING_DELETION_METHOD:   "Deletion method not specified",
  MISSING_APPROVAL_WORKFLOW: "Approval workflow not documented",
  MISSING_EVIDENCE:          "No evidence reference",
  REVIEW_OVERDUE:            "Review overdue",
};

function ferpa23StatusBadge(status: string) {
  const color =
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Archived"    ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>{status}</span>;
}

function retentionLabel(p: FerpaRetentionPolicy) {
  if (!p.retentionPeriod) return "No period set";
  return `${p.retentionPeriod} ${p.retentionUnit}${p.retentionStartEvent ? ` from ${p.retentionStartEvent}` : ""}`;
}

function useAuth23() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Add/Edit dialog ─────────────────────────────────────────────────────────
function RetentionPolicyAddDialog({ open, initial, onClose }: { open: boolean; initial: FerpaRetentionPolicy | null; onClose: () => void }) {
  const { header } = useAuth23();
  const { toast }  = useToast();
  const isEdit     = !!initial;

  const blank = {
    policyName: "", recordCategory: "Academic", educationRecordType: "", policyVersion: "v1.0",
    retentionPeriod: 7, retentionUnit: "Years", retentionStartEvent: "Graduation",
    deletionMethod: "SecureDeletion", deletionApprovalRequired: true,
    approvalWorkflowDetails: "", legalHoldSupported: false,
    exceptionHandlingProcedure: "", responsibleOwner: "", department: "",
    reviewFrequency: 1, reviewFrequencyUnit: "Years",
    evidenceReference: "", lastReviewDate: "", nextReviewDate: "",
    reviewerName: "", reviewNotes: "", status: "Draft",
  };

  const [form, setForm] = useState({ ...blank });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastKey, setLastKey] = useState("");
  const openKey = `${open}-${initial?.id ?? "new"}`;
  if (openKey !== lastKey) {
    setLastKey(openKey);
    if (open) {
      setForm(initial ? {
        policyName:                 initial.policyName,
        recordCategory:             initial.recordCategory ?? "Academic",
        educationRecordType:        initial.educationRecordType ?? "",
        policyVersion:              initial.policyVersion ?? "v1.0",
        retentionPeriod:            initial.retentionPeriod,
        retentionUnit:              initial.retentionUnit,
        retentionStartEvent:        initial.retentionStartEvent ?? "Graduation",
        deletionMethod:             initial.deletionMethod ?? "SecureDeletion",
        deletionApprovalRequired:   initial.deletionApprovalRequired,
        approvalWorkflowDetails:    initial.approvalWorkflowDetails ?? "",
        legalHoldSupported:         initial.legalHoldSupported,
        exceptionHandlingProcedure: initial.exceptionHandlingProcedure ?? "",
        responsibleOwner:           initial.responsibleOwner ?? "",
        department:                 initial.department ?? "",
        reviewFrequency:            initial.reviewFrequency,
        reviewFrequencyUnit:        initial.reviewFrequencyUnit,
        evidenceReference:          initial.evidenceReference ?? "",
        lastReviewDate:             initial.lastReviewDate?.slice(0, 10) ?? "",
        nextReviewDate:             initial.nextReviewDate?.slice(0, 10) ?? "",
        reviewerName:               initial.reviewerName ?? "",
        reviewNotes:                initial.reviewNotes ?? "",
        status:                     initial.status,
      } : { ...blank });
    }
  }

  const set = (k: string, v: string | boolean | number) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        policyName:                 form.policyName,
        recordCategory:             form.recordCategory || null,
        educationRecordType:        form.educationRecordType || null,
        policyVersion:              form.policyVersion || null,
        retentionPeriod:            form.retentionPeriod,
        retentionUnit:              form.retentionUnit,
        retentionStartEvent:        form.retentionStartEvent || null,
        deletionMethod:             form.deletionMethod || null,
        deletionApprovalRequired:   form.deletionApprovalRequired,
        approvalWorkflowDetails:    form.approvalWorkflowDetails || null,
        legalHoldSupported:         form.legalHoldSupported,
        exceptionHandlingProcedure: form.exceptionHandlingProcedure || null,
        responsibleOwner:           form.responsibleOwner || null,
        department:                 form.department || null,
        reviewFrequency:            form.reviewFrequency,
        reviewFrequencyUnit:        form.reviewFrequencyUnit,
        evidenceReference:          form.evidenceReference || null,
        lastReviewDate:             form.lastReviewDate || null,
        nextReviewDate:             form.nextReviewDate || null,
        reviewerName:               form.reviewerName || null,
        reviewNotes:                form.reviewNotes || null,
        status:                     form.status,
      };
      const url    = isEdit ? `/api/organization/compliance/ferpa/retention-policies/${initial!.id}` : "/api/organization/compliance/ferpa/retention-policies";
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies/issues"] });
      toast({ title: isEdit ? "Policy updated" : "Retention policy created" });
      setErrors({});
      onClose();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length > 0) {
        setErrors(serverErrs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Retention Policy" : "Add Retention Policy"}</DialogTitle>
          <DialogDescription>Define a retention schedule and deletion lifecycle for a category of education records.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Identity */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Policy Identity</p>
            <div className="space-y-1">
              <Label>Policy Name <span className="text-red-500">*</span></Label>
              <Input value={form.policyName} onChange={e => { set("policyName", e.target.value); clearErr(setErrors, "policyName"); }} placeholder="Academic Transcript Retention"
                className={fieldCls("", errors, "policyName")} aria-invalid={!!errors.policyName} />
              {fErr(errors, "policyName")}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Record Category</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.recordCategory} onChange={e => set("recordCategory", e.target.value)}>
                  {FERPA23_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Education Record Type</Label>
                <Input value={form.educationRecordType} onChange={e => set("educationRecordType", e.target.value)} placeholder="Official Transcript" />
              </div>
              <div className="space-y-1">
                <Label>Policy Version</Label>
                <Input value={form.policyVersion} onChange={e => set("policyVersion", e.target.value)} placeholder="v1.0" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA23_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Retention schedule */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Retention Schedule</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>Retention Period <span className="text-red-500">*</span></Label>
                <Input type="number" min={0} value={form.retentionPeriod} onChange={e => set("retentionPeriod", parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1">
                <Label>Unit</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.retentionUnit} onChange={e => set("retentionUnit", e.target.value)}>
                  {FERPA23_UNITS.map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Start Event</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.retentionStartEvent} onChange={e => set("retentionStartEvent", e.target.value)}>
                  {FERPA23_START_EVENTS.map(e => <option key={e}>{e}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Deletion lifecycle */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Deletion Lifecycle</p>
            <div className="space-y-1">
              <Label>Deletion Method</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.deletionMethod} onChange={e => set("deletionMethod", e.target.value)}>
                <option value="">— Select —</option>
                {FERPA23_DELETION_METHODS.map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="ferpa23-approval" checked={form.deletionApprovalRequired} onChange={e => set("deletionApprovalRequired", e.target.checked)} className="rounded" />
                <Label htmlFor="ferpa23-approval" className="cursor-pointer">Deletion approval required</Label>
              </div>
              {form.deletionApprovalRequired && (
                <div className="pl-5 space-y-1">
                  <Label>Approval Workflow Details <span className="text-red-500">*</span></Label>
                  <Textarea value={form.approvalWorkflowDetails} onChange={e => set("approvalWorkflowDetails", e.target.value)} rows={2} placeholder="Registrar approves; Privacy Officer countersigns" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ferpa23-legal" checked={form.legalHoldSupported} onChange={e => set("legalHoldSupported", e.target.checked)} className="rounded" />
              <Label htmlFor="ferpa23-legal" className="cursor-pointer">Legal hold supported</Label>
            </div>
            <div className="space-y-1">
              <Label>Exception Handling Procedure</Label>
              <Textarea value={form.exceptionHandlingProcedure} onChange={e => set("exceptionHandlingProcedure", e.target.value)} rows={2} placeholder="Submit exception request to Records Management" />
            </div>
          </div>

          {/* Ownership & review */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ownership &amp; Review</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Responsible Owner</Label>
                <Input value={form.responsibleOwner} onChange={e => set("responsibleOwner", e.target.value)} placeholder="Registrar Office" />
              </div>
              <div className="space-y-1">
                <Label>Department</Label>
                <Input value={form.department} onChange={e => set("department", e.target.value)} placeholder="Academic Affairs" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Review Frequency</Label>
                <div className="flex gap-2">
                  <Input type="number" min={0} className="w-20" value={form.reviewFrequency} onChange={e => set("reviewFrequency", parseInt(e.target.value) || 0)} />
                  <select className="border rounded px-2 py-1.5 text-sm flex-1" value={form.reviewFrequencyUnit} onChange={e => set("reviewFrequencyUnit", e.target.value)}>
                    {FERPA23_UNITS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Evidence Reference</Label>
                <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://retention-policy-2026.pdf" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Last Review Date</Label>
                <Input type="date" value={form.lastReviewDate} onChange={e => set("lastReviewDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Reviewer Name</Label>
              <Input value={form.reviewerName} onChange={e => set("reviewerName", e.target.value)} placeholder="Privacy Officer" />
            </div>
            <div className="space-y-1">
              <Label>Review Notes</Label>
              <Textarea value={form.reviewNotes} onChange={e => set("reviewNotes", e.target.value)} rows={2} placeholder="Notes from last review" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            const errs: Record<string, string> = {};
            required(errs, "policyName", form.policyName, "Policy Name");
            if (Object.keys(errs).length > 0) {
              setErrors(errs);
              toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
              return;
            }
            setErrors({});
            mutation.mutate();
          }} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Create Policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ────────────────────────────────────────────────────────────
function RetentionPolicyReviewDialog({ open, item, onClose }: { open: boolean; item: FerpaRetentionPolicy | null; onClose: () => void }) {
  const { header } = useAuth23();
  const { toast }  = useToast();
  const [reviewerName, setReviewerName] = useState("");
  const [notes, setNotes]               = useState("");
  const [evidenceRef, setEvidenceRef]   = useState("");
  const [nextDate, setNextDate]         = useState("");

  const [reviewerErr, setReviewerErr]   = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      if (!reviewerName.trim()) { setReviewerErr("Reviewer name is required."); throw new Error("Please fix the highlighted fields."); }
      setReviewerErr("");
      const res = await fetch(`/api/organization/compliance/ferpa/retention-policies/${item.id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reviewerName, reviewNotes: notes || null, evidenceReference: evidenceRef || null, nextReviewDate: nextDate || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies/issues"] });
      toast({ title: "Review recorded — status set to Under Review" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Policy Review</DialogTitle>
          <DialogDescription>Review <strong>{item?.policyName}</strong>.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Reviewer Name <span className="text-red-500">*</span></Label>
            <Input value={reviewerName} onChange={e => { setReviewerName(e.target.value); if (e.target.value.trim()) setReviewerErr(""); }} placeholder="Privacy Officer"
              className={reviewerErr ? "border-red-500 focus-visible:ring-red-500" : ""}
              aria-invalid={!!reviewerErr} />
            {reviewerErr && <p role="alert" className="text-xs text-red-500 mt-0.5">⚠ {reviewerErr}</p>}
          </div>
          <div className="space-y-1">
            <Label>Evidence Reference</Label>
            <Input value={evidenceRef} onChange={e => setEvidenceRef(e.target.value)} placeholder="evidence://review-2026.pdf" />
          </div>
          <div className="space-y-1">
            <Label>Next Review Date</Label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Review Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Findings or required actions" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Approve dialog ────────────────────────────────────────────────────────────
function RetentionPolicyApproveDialog({ open, item, onClose }: { open: boolean; item: FerpaRetentionPolicy | null; onClose: () => void }) {
  const { header } = useAuth23();
  const { toast }  = useToast();
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/retention-policies/${item.id}/approve`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify({ notes: notes || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies/summary"] });
      toast({ title: "Policy approved" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Approve Retention Policy</DialogTitle>
          <DialogDescription>Approve <strong>{item?.policyName}</strong> as the active retention policy.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Approval Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Approving…" : "Approve Policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Archive dialog ────────────────────────────────────────────────────────────
function RetentionPolicyArchiveDialog({ open, item, onClose }: { open: boolean; item: FerpaRetentionPolicy | null; onClose: () => void }) {
  const { header } = useAuth23();
  const { toast }  = useToast();
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/retention-policies/${item.id}/archive`, {
        method: "POST", headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify({ notes: notes || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies/summary"] });
      toast({ title: "Policy archived" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Archive Policy</DialogTitle>
          <DialogDescription>Archive <strong>{item?.policyName}</strong>? The policy will be preserved but marked inactive.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Reason (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="e.g. Superseded by v2.0" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Archiving…" : "Archive"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ─────────────────────────────────────────────────────────────
function RetentionPolicyDeleteDialog({ open, item, onClose }: { open: boolean; item: FerpaRetentionPolicy | null; onClose: () => void }) {
  const { header } = useAuth23();
  const { toast }  = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/retention-policies/${item.id}`, { method: "DELETE", headers: header });
      await throwIfResNotOk(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/retention-policies/summary"] });
      toast({ title: "Policy deleted" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Policy</DialogTitle>
          <DialogDescription>Permanently delete <strong>{item?.policyName}</strong>? Consider archiving instead.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ─────────────────────────────────────────────────────────────
function RetentionPolicyDetailDialog({
  open, item, canManage, onClose, onEdit, onReview, onApprove, onArchive, onDelete,
}: {
  open: boolean; item: FerpaRetentionPolicy | null; canManage: boolean;
  onClose: () => void; onEdit: () => void; onReview: () => void;
  onApprove: () => void; onArchive: () => void; onDelete: () => void;
}) {
  if (!item) return null;
  const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" /> {item.policyName}
            {item.policyVersion && <span className="text-xs text-muted-foreground font-normal">{item.policyVersion}</span>}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-1">
            {ferpa23StatusBadge(item.status)}
            {item.recordCategory && <span className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{item.recordCategory}</span>}
            {item.legalHoldSupported && <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs"><Lock className="w-2.5 h-2.5" /> Legal Hold</span>}
          </div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {item.validationCodes.length > 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700">⚠ Compliance Issues</p>
              {item.validationCodes.map(c => <p key={c} className="text-xs text-amber-700">• {FERPA23_CODE_LABELS[c] ?? c}</p>)}
            </div>
          )}

          {/* Retention schedule */}
          <div className="rounded border p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Retention Schedule</p>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold">{item.retentionPeriod}</span>
              <span className="text-lg text-muted-foreground">{item.retentionUnit}</span>
              {item.retentionStartEvent && <span className="text-sm text-muted-foreground">from <strong>{item.retentionStartEvent}</strong></span>}
            </div>
            {item.educationRecordType && <p className="text-sm"><span className="text-muted-foreground">Record type:</span> {item.educationRecordType}</p>}
          </div>

          {/* Deletion */}
          <div className="rounded border p-3 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Deletion Lifecycle</p>
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Method:</span> {item.deletionMethod ?? "—"}</div>
              <div><span className="text-muted-foreground">Approval required:</span> {item.deletionApprovalRequired ? "Yes" : "No"}</div>
            </div>
            {item.deletionApprovalRequired && item.approvalWorkflowDetails && (
              <p className="text-sm text-muted-foreground">{item.approvalWorkflowDetails}</p>
            )}
            {item.exceptionHandlingProcedure && (
              <div><p className="text-xs font-medium text-muted-foreground mb-0.5">Exception procedure</p><p className="text-sm">{item.exceptionHandlingProcedure}</p></div>
            )}
          </div>

          {/* Ownership */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Owner:</span> <strong>{item.responsibleOwner ?? "—"}</strong></div>
            <div><span className="text-muted-foreground">Department:</span> {item.department ?? "—"}</div>
            <div><span className="text-muted-foreground">Review frequency:</span> {item.reviewFrequency > 0 ? `${item.reviewFrequency} ${item.reviewFrequencyUnit}` : "Not set"}</div>
          </div>

          {/* Evidence & review */}
          <div className="rounded border p-3 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Evidence &amp; Review</p>
            {item.evidenceReference && <p className="text-blue-700 break-all">{item.evidenceReference}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Last Review:</span> {item.lastReviewDate ? new Date(item.lastReviewDate).toLocaleDateString() : "Never"}</div>
              <div className={overdue ? "text-red-600 font-medium" : ""}>
                <span className="text-muted-foreground">Next Review:</span>{" "}
                {item.nextReviewDate ? new Date(item.nextReviewDate).toLocaleDateString() : "Not set"}{overdue && " ⚠"}
              </div>
              {item.reviewerName && <div><span className="text-muted-foreground">Reviewer:</span> {item.reviewerName}</div>}
            </div>
            {item.reviewNotes && <p className="text-sm text-muted-foreground italic">{item.reviewNotes}</p>}
            {item.approvedAt && <p className="text-sm text-green-700">✓ Approved {new Date(item.approvedAt).toLocaleDateString()}</p>}
            {item.archivedAt && <p className="text-sm text-gray-500">Archived {new Date(item.archivedAt).toLocaleDateString()}</p>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {canManage && item.status !== "Archived" && (
            <>
              <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700"><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
              <Button variant="outline" size="sm" onClick={onArchive}><BookOpen className="w-3 h-3 mr-1" /> Archive</Button>
              <Button variant="outline" size="sm" onClick={onReview}><RefreshCcw className="w-3 h-3 mr-1" /> Review</Button>
              {item.status === "UnderReview" && (
                <Button variant="outline" size="sm" onClick={onApprove} className="text-green-700 hover:text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</Button>
              )}
              <Button size="sm" onClick={onEdit}><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main FERPA-23 Section ─────────────────────────────────────────────────────
function RetentionPoliciesSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth23();

  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");
  const [ownerFilter,    setOwnerFilter]     = useState("");

  const [showAdd,     setShowAdd]     = useState(false);
  const [editItem,    setEditItem]    = useState<FerpaRetentionPolicy | null>(null);
  const [reviewItem,  setReviewItem]  = useState<FerpaRetentionPolicy | null>(null);
  const [approveItem, setApproveItem] = useState<FerpaRetentionPolicy | null>(null);
  const [archiveItem, setArchiveItem] = useState<FerpaRetentionPolicy | null>(null);
  const [deleteItem,  setDeleteItem]  = useState<FerpaRetentionPolicy | null>(null);
  const [detailItem,  setDetailItem]  = useState<FerpaRetentionPolicy | null>(null);

  const summaryQ = useQuery<FerpaRetentionSummary>({
    queryKey: ["/api/organization/compliance/ferpa/retention-policies/summary"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/retention-policies/summary", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ policy: FerpaRetentionPolicy; codes: string[] }[]>({
    queryKey: ["/api/organization/compliance/ferpa/retention-policies/issues"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/retention-policies/issues", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (categoryFilter) params.set("category", categoryFilter);
  if (statusFilter)   params.set("status",   statusFilter);
  if (ownerFilter)    params.set("owner",     ownerFilter);

  const listQ = useQuery<{ total: number; items: FerpaRetentionPolicy[] }>({
    queryKey: ["/api/organization/compliance/ferpa/retention-policies", categoryFilter, statusFilter, ownerFilter],
    queryFn: async () => {
      const res = await fetch(`/api/organization/compliance/ferpa/retention-policies?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Policies",      value: s.total,               icon: <ClipboardList className="w-4 h-4 text-blue-500" /> },
    { label: "Active Policies",     value: s.activePolicies,      icon: <ShieldCheck className="w-4 h-4 text-green-500" /> },
    { label: "Expiring Soon",       value: s.expiringPolicies,    icon: <Clock className="w-4 h-4 text-orange-500" /> },
    { label: "Missing Docs",        value: s.missingDocumentation,icon: <AlertTriangle className="w-4 h-4 text-amber-500" /> },
    { label: "Pending Reviews",     value: s.pendingReviews,      icon: <RefreshCcw className="w-4 h-4 text-red-500" /> },
    { label: "Archived",            value: s.archivedPolicies,    icon: <BookOpen className="w-4 h-4 text-gray-400" /> },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="w-4 h-4 text-blue-600" />
              Education Record Retention &amp; Deletion Policies
            </CardTitle>
            <CardDescription>
              Track retention schedules and deletion lifecycle management for student education records — 34 CFR § 99
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Policy
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : s ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className="rounded border p-3 text-center space-y-1">
                <div className="flex justify-center">{m.icon}</div>
                <p className="text-xl font-bold">{m.value}</p>
                <p className="text-xs text-muted-foreground leading-tight">{m.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Issues */}
        {issuesQ.data && issuesQ.data.length > 0 && (
          <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {issuesQ.data.length} polic{issuesQ.data.length !== 1 ? "ies" : "y"} with compliance issues
            </p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {issuesQ.data.slice(0, 8).map((it: { policy: FerpaRetentionPolicy; codes: string[] }) => (
                <div key={it.policy.id} className="flex items-start gap-2 text-xs text-amber-700">
                  <span className="font-medium whitespace-nowrap">{it.policy.policyName}</span>
                  <span className="text-amber-500">—</span>
                  <span>{it.codes.slice(0, 2).map(c => FERPA23_CODE_LABELS[c] ?? c).join(" · ")}</span>
                </div>
              ))}
              {issuesQ.data.length > 8 && <p className="text-xs text-amber-500">…and {issuesQ.data.length - 8} more</p>}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <select className="border rounded px-2 py-1 text-sm h-8" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            {FERPA23_CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA23_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <Input className="h-8 text-sm w-40" placeholder="Owner…" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} />
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {listQ.data?.total === 0
              ? "No retention policies yet. Create one to begin documenting your education record retention schedule."
              : "No policies match the current filters."}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(item => {
              const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
              const expiring = item.nextReviewDate && !overdue && new Date(item.nextReviewDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
              return (
                <div key={item.id} className="rounded border p-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setDetailItem(item)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{item.policyName}</span>
                        {item.policyVersion && <span className="text-xs text-muted-foreground">{item.policyVersion}</span>}
                        {ferpa23StatusBadge(item.status)}
                        {item.recordCategory && <span className="inline-flex px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">{item.recordCategory}</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span className="font-medium text-foreground">{retentionLabel(item)}</span>
                        {item.deletionMethod && <span>· {item.deletionMethod}</span>}
                        {item.legalHoldSupported && <span className="text-blue-600">· Legal hold</span>}
                        {overdue && <span className="text-red-600 font-medium">⚠ Review overdue</span>}
                        {expiring && <span className="text-orange-600 font-medium">⚠ Review expiring soon</span>}
                      </div>
                      {item.validationCodes.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {item.validationCodes.slice(0, 3).map(c => (
                            <span key={c} className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">⚠ {FERPA23_CODE_LABELS[c] ?? c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canManage && item.status !== "Archived" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setEditItem(item); }}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={e => { e.stopPropagation(); setDeleteItem(item); }}><Trash2 className="w-3 h-3" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                  {item.responsibleOwner && <p className="text-xs text-muted-foreground mt-1">Owner: {item.responsibleOwner}{item.department ? ` · ${item.department}` : ""}</p>}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <RetentionPolicyAddDialog open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <RetentionPolicyReviewDialog open={!!reviewItem} item={reviewItem} onClose={() => setReviewItem(null)} />
      <RetentionPolicyApproveDialog open={!!approveItem} item={approveItem} onClose={() => setApproveItem(null)} />
      <RetentionPolicyArchiveDialog open={!!archiveItem} item={archiveItem} onClose={() => setArchiveItem(null)} />
      <RetentionPolicyDeleteDialog open={!!deleteItem} item={deleteItem} onClose={() => setDeleteItem(null)} />
      <RetentionPolicyDetailDialog
        open={!!detailItem} item={detailItem} canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={() => { setEditItem(detailItem); setDetailItem(null); }}
        onReview={() => { setReviewItem(detailItem); setDetailItem(null); }}
        onApprove={() => { setApproveItem(detailItem); setDetailItem(null); }}
        onArchive={() => { setArchiveItem(detailItem); setDetailItem(null); }}
        onDelete={() => { setDeleteItem(detailItem); setDetailItem(null); }}
      />
    </Card>
  );
}

// ── FERPA-22 — Education Record Data Lineage and AI Data Flow ────────────────

type FerpaDataLineageFlow = {
  id: string;
  organizationId: string;
  flowName: string;
  sourceSystem: string;
  destinationSystem: string;
  dataCategoriesTransferred: string | null;
  educationRecordFieldsInvolved: string | null;
  processingPurpose: string | null;
  dataTransformationSteps: string | null;
  aiModelInvolved: boolean;
  aiModelName: string | null;
  aiUsagePurpose: string | null;
  externalVendorInvolved: boolean;
  externalVendorName: string | null;
  encryptionStatus: string | null;
  dataTransferMethod: string | null;
  dataOwner: string | null;
  reviewerName: string | null;
  documentationReference: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  reviewNotes: string | null;
  status: string;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

type FerpaDataLineageSummary = {
  total: number;
  aiDataFlows: number;
  externalIntegrations: number;
  missingDocumentation: number;
  pendingReviews: number;
  approvedFlows: number;
  encryptedFlows: number;
};

const FERPA22_STATUSES        = ["Draft","Active","UnderReview","Approved","Deprecated"];
const FERPA22_ENCRYPTION      = ["Encrypted","Unencrypted","PartiallyEncrypted"];
const FERPA22_TRANSFER_METHODS= ["API","SFTP","Database","MessageQueue","Manual","Other"];
const FERPA22_CATEGORIES      = ["Academic","Enrollment","Financial","Health","Disciplinary","Employment","Other"];

const FERPA22_CODE_LABELS: Record<string, string> = {
  MISSING_SOURCE:             "Source system missing",
  MISSING_DESTINATION:        "Destination system missing",
  MISSING_DATA_CLASSIFICATION:"Data categories not classified",
  MISSING_AI_DOCUMENTATION:   "AI usage not documented",
  MISSING_EVIDENCE:           "No documentation reference",
  REVIEW_OVERDUE:             "Review overdue",
};

function ferpa22StatusBadge(status: string) {
  const color =
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "Active"      ? "bg-blue-100 text-blue-700" :
    status === "UnderReview" ? "bg-yellow-100 text-yellow-700" :
    status === "Deprecated"  ? "bg-gray-200 text-gray-500" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>{status}</span>;
}

function ferpa22EncBadge(enc: string | null) {
  if (!enc) return null;
  const color = enc === "Encrypted" ? "bg-green-100 text-green-700" :
                enc === "Unencrypted" ? "bg-red-100 text-red-700" :
                "bg-yellow-100 text-yellow-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>{enc}</span>;
}

function useAuth22() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

// ── Flow node/arrow visualisation (pure CSS, no canvas) ───────────────────────
function FlowArrow({ flow }: { flow: FerpaDataLineageFlow }) {
  return (
    <div className="flex items-center gap-2 text-sm flex-wrap">
      <span className="px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 font-medium text-xs">{flow.sourceSystem}</span>
      <span className="flex items-center gap-0.5 text-muted-foreground">
        <span className="text-xs">{flow.dataTransferMethod ?? "→"}</span>
        <span>→</span>
      </span>
      <span className="px-2 py-0.5 rounded bg-purple-50 border border-purple-200 text-purple-700 font-medium text-xs">{flow.destinationSystem}</span>
      {flow.aiModelInvolved && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs font-medium">
          <Activity className="w-2.5 h-2.5" /> AI
        </span>
      )}
      {flow.externalVendorInvolved && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-xs font-medium">
          <Share2 className="w-2.5 h-2.5" /> Ext
        </span>
      )}
    </div>
  );
}

// ── Add/Edit dialog ────────────────────────────────────────────────────────────
function DataLineageAddDialog({
  open, initial, onClose,
}: { open: boolean; initial: FerpaDataLineageFlow | null; onClose: () => void }) {
  const { header } = useAuth22();
  const { toast }  = useToast();
  const isEdit     = !!initial;

  const blank = {
    flowName: "", sourceSystem: "", destinationSystem: "",
    dataCategoriesTransferred: "", educationRecordFieldsInvolved: "",
    processingPurpose: "", dataTransformationSteps: "",
    aiModelInvolved: false, aiModelName: "", aiUsagePurpose: "",
    externalVendorInvolved: false, externalVendorName: "",
    encryptionStatus: "Encrypted", dataTransferMethod: "API",
    dataOwner: "", reviewerName: "", documentationReference: "",
    lastReviewDate: "", nextReviewDate: "",
    reviewNotes: "", status: "Draft",
  };

  const [form, setForm] = useState({ ...blank });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastKey, setLastKey] = useState("");
  const openKey = `${open}-${initial?.id ?? "new"}`;
  if (openKey !== lastKey) {
    setLastKey(openKey);
    if (open) {
      setForm(initial ? {
        flowName:                      initial.flowName,
        sourceSystem:                  initial.sourceSystem,
        destinationSystem:             initial.destinationSystem,
        dataCategoriesTransferred:     initial.dataCategoriesTransferred ?? "",
        educationRecordFieldsInvolved: initial.educationRecordFieldsInvolved ?? "",
        processingPurpose:             initial.processingPurpose ?? "",
        dataTransformationSteps:       initial.dataTransformationSteps ?? "",
        aiModelInvolved:               initial.aiModelInvolved,
        aiModelName:                   initial.aiModelName ?? "",
        aiUsagePurpose:                initial.aiUsagePurpose ?? "",
        externalVendorInvolved:        initial.externalVendorInvolved,
        externalVendorName:            initial.externalVendorName ?? "",
        encryptionStatus:              initial.encryptionStatus ?? "Encrypted",
        dataTransferMethod:            initial.dataTransferMethod ?? "API",
        dataOwner:                     initial.dataOwner ?? "",
        reviewerName:                  initial.reviewerName ?? "",
        documentationReference:        initial.documentationReference ?? "",
        lastReviewDate:                initial.lastReviewDate?.slice(0,10) ?? "",
        nextReviewDate:                initial.nextReviewDate?.slice(0,10) ?? "",
        reviewNotes:                   initial.reviewNotes ?? "",
        status:                        initial.status,
      } : { ...blank });
    }
  }

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        flowName:                      form.flowName,
        sourceSystem:                  form.sourceSystem,
        destinationSystem:             form.destinationSystem,
        dataCategoriesTransferred:     form.dataCategoriesTransferred || null,
        educationRecordFieldsInvolved: form.educationRecordFieldsInvolved || null,
        processingPurpose:             form.processingPurpose || null,
        dataTransformationSteps:       form.dataTransformationSteps || null,
        aiModelInvolved:               form.aiModelInvolved,
        aiModelName:                   form.aiModelName || null,
        aiUsagePurpose:                form.aiUsagePurpose || null,
        externalVendorInvolved:        form.externalVendorInvolved,
        externalVendorName:            form.externalVendorName || null,
        encryptionStatus:              form.encryptionStatus || null,
        dataTransferMethod:            form.dataTransferMethod || null,
        dataOwner:                     form.dataOwner || null,
        reviewerName:                  form.reviewerName || null,
        documentationReference:        form.documentationReference || null,
        lastReviewDate:                form.lastReviewDate || null,
        nextReviewDate:                form.nextReviewDate || null,
        reviewNotes:                   form.reviewNotes || null,
        status:                        form.status,
      };
      const url    = isEdit ? `/api/organization/compliance/ferpa/data-lineage/${initial!.id}` : "/api/organization/compliance/ferpa/data-lineage";
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, { method, headers: { "Content-Type": "application/json", ...header }, body: JSON.stringify(body) });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/issues"] });
      toast({ title: isEdit ? "Data flow updated" : "Data flow added" });
      setErrors({});
      onClose();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Data Flow" : "Document Data Flow"}</DialogTitle>
          <DialogDescription>Document how student education records move between systems.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Flow identity */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Flow Identity</p>
            <div className="space-y-1">
              <Label>Flow Name <span className="text-red-500">*</span></Label>
              <Input className={fieldCls("", errors, "flowName")} aria-invalid={!!errors.flowName} value={form.flowName} onChange={e => { set("flowName", e.target.value); clearErr(setErrors, "flowName"); }} placeholder="SIS → LMS Grade Sync" />
              {fErr(errors, "flowName")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Source System <span className="text-red-500">*</span></Label>
                <Input className={fieldCls("", errors, "sourceSystem")} aria-invalid={!!errors.sourceSystem} value={form.sourceSystem} onChange={e => { set("sourceSystem", e.target.value); clearErr(setErrors, "sourceSystem"); }} placeholder="Student Information System" />
                {fErr(errors, "sourceSystem")}
              </div>
              <div className="space-y-1">
                <Label>Destination System <span className="text-red-500">*</span></Label>
                <Input className={fieldCls("", errors, "destinationSystem")} aria-invalid={!!errors.destinationSystem} value={form.destinationSystem} onChange={e => { set("destinationSystem", e.target.value); clearErr(setErrors, "destinationSystem"); }} placeholder="Learning Management System" />
                {fErr(errors, "destinationSystem")}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Transfer Method</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.dataTransferMethod} onChange={e => set("dataTransferMethod", e.target.value)}>
                  {FERPA22_TRANSFER_METHODS.map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Encryption Status</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.encryptionStatus} onChange={e => set("encryptionStatus", e.target.value)}>
                  {FERPA22_ENCRYPTION.map(e => <option key={e}>{e}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.status} onChange={e => set("status", e.target.value)}>
                {FERPA22_STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Data details */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Data Details</p>
            <div className="space-y-1">
              <Label>Data Categories Transferred</Label>
              <Input value={form.dataCategoriesTransferred} onChange={e => set("dataCategoriesTransferred", e.target.value)} placeholder="Academic, Enrollment, Financial (comma-separated)" />
            </div>
            <div className="space-y-1">
              <Label>Education Record Fields Involved</Label>
              <Input value={form.educationRecordFieldsInvolved} onChange={e => set("educationRecordFieldsInvolved", e.target.value)} placeholder="student_gpa, course_grades, enrollment_status" />
            </div>
            <div className="space-y-1">
              <Label>Processing Purpose</Label>
              <Textarea value={form.processingPurpose} onChange={e => set("processingPurpose", e.target.value)} rows={2} placeholder="Why this data flow exists" />
            </div>
            <div className="space-y-1">
              <Label>Data Transformation Steps</Label>
              <Textarea value={form.dataTransformationSteps} onChange={e => set("dataTransformationSteps", e.target.value)} rows={3} placeholder="1. Extract grades nightly&#10;2. Map student IDs to LMS format&#10;3. Load via API" />
            </div>
          </div>

          {/* AI involvement */}
          <div className="rounded border p-3 space-y-3">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ferpa22-ai" checked={form.aiModelInvolved} onChange={e => set("aiModelInvolved", e.target.checked)} className="rounded" />
              <Label htmlFor="ferpa22-ai" className="cursor-pointer font-medium">AI Model Involvement</Label>
            </div>
            {form.aiModelInvolved && (
              <div className="pl-5 space-y-3">
                <div className="space-y-1">
                  <Label>AI Model Name</Label>
                  <Input value={form.aiModelName} onChange={e => set("aiModelName", e.target.value)} placeholder="GPT-4o, Claude 3, Custom Model" />
                </div>
                <div className="space-y-1">
                  <Label>AI Usage Purpose <span className="text-red-500">*</span></Label>
                  <Textarea value={form.aiUsagePurpose} onChange={e => set("aiUsagePurpose", e.target.value)} rows={2} placeholder="How AI processes or uses student education records" />
                </div>
              </div>
            )}
          </div>

          {/* External vendor */}
          <div className="rounded border p-3 space-y-3">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="ferpa22-ext" checked={form.externalVendorInvolved} onChange={e => set("externalVendorInvolved", e.target.checked)} className="rounded" />
              <Label htmlFor="ferpa22-ext" className="cursor-pointer font-medium">External Vendor / Third-Party System</Label>
            </div>
            {form.externalVendorInvolved && (
              <div className="pl-5 space-y-1">
                <Label>Vendor / System Name</Label>
                <Input value={form.externalVendorName} onChange={e => set("externalVendorName", e.target.value)} placeholder="Canvas LMS, Salesforce, AWS S3" />
              </div>
            )}
          </div>

          {/* Evidence & ownership */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ownership &amp; Evidence</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data Owner</Label>
                <Input value={form.dataOwner} onChange={e => set("dataOwner", e.target.value)} placeholder="Registrar Office" />
              </div>
              <div className="space-y-1">
                <Label>Reviewer</Label>
                <Input value={form.reviewerName} onChange={e => set("reviewerName", e.target.value)} placeholder="Privacy Officer" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Documentation Reference</Label>
              <Input value={form.documentationReference} onChange={e => set("documentationReference", e.target.value)} placeholder="evidence://lineage-2026.pdf or Confluence link" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Last Review Date</Label>
                <Input type="date" value={form.lastReviewDate} onChange={e => set("lastReviewDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Review Notes</Label>
              <Textarea value={form.reviewNotes} onChange={e => set("reviewNotes", e.target.value)} rows={2} placeholder="Notes from last review" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "flowName", form.flowName, "Flow Name"); required(errs, "sourceSystem", form.sourceSystem, "Source System"); required(errs, "destinationSystem", form.destinationSystem, "Destination System"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); mutation.mutate(); }} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Add Flow"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Review dialog ──────────────────────────────────────────────────────────────
function DataLineageReviewDialog({ open, item, onClose }: { open: boolean; item: FerpaDataLineageFlow | null; onClose: () => void }) {
  const { header } = useAuth22();
  const { toast }  = useToast();
  const [reviewerName, setReviewerName] = useState("");
  const [notes, setNotes]               = useState("");
  const [docRef, setDocRef]             = useState("");
  const [nextDate, setNextDate]         = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/data-lineage/${item.id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ reviewerName, reviewNotes: notes || null, documentationReference: docRef || null, nextReviewDate: nextDate || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/issues"] });
      toast({ title: "Review recorded — status set to Under Review" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Flow Review</DialogTitle>
          <DialogDescription>Review <strong>{item?.flowName}</strong> — status moves to Under Review.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Reviewer Name <span className="text-red-500">*</span></Label>
            <Input value={reviewerName} onChange={e => setReviewerName(e.target.value)} placeholder="Privacy Officer" />
          </div>
          <div className="space-y-1">
            <Label>Documentation Reference</Label>
            <Input value={docRef} onChange={e => setDocRef(e.target.value)} placeholder="evidence://review-2026.pdf" />
          </div>
          <div className="space-y-1">
            <Label>Next Review Date</Label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Review Notes</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Findings or required actions" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Approve dialog ─────────────────────────────────────────────────────────────
function DataLineageApproveDialog({ open, item, onClose }: { open: boolean; item: FerpaDataLineageFlow | null; onClose: () => void }) {
  const { header } = useAuth22();
  const { toast }  = useToast();
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/data-lineage/${item.id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify({ notes: notes || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/issues"] });
      toast({ title: "Data flow approved" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Approve Data Flow</DialogTitle>
          <DialogDescription>Approve <strong>{item?.flowName}</strong> as documented and compliant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Approval notes or conditions" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Approving…" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete dialog ──────────────────────────────────────────────────────────────
function DataLineageDeleteDialog({ open, item, onClose }: { open: boolean; item: FerpaDataLineageFlow | null; onClose: () => void }) {
  const { header } = useAuth22();
  const { toast }  = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(`/api/organization/compliance/ferpa/data-lineage/${item.id}`, { method: "DELETE", headers: header });
      await throwIfResNotOk(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-lineage/issues"] });
      toast({ title: "Data flow deleted" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Data Flow</DialogTitle>
          <DialogDescription>Remove <strong>{item?.flowName}</strong>? This cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail dialog ──────────────────────────────────────────────────────────────
function DataLineageDetailDialog({
  open, item, canManage, onClose, onEdit, onReview, onApprove, onDelete,
}: {
  open: boolean; item: FerpaDataLineageFlow | null; canManage: boolean;
  onClose: () => void; onEdit: () => void; onReview: () => void; onApprove: () => void; onDelete: () => void;
}) {
  if (!item) return null;
  const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="w-4 h-4" /> {item.flowName}
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-1">
            {ferpa22StatusBadge(item.status)}
            {ferpa22EncBadge(item.encryptionStatus)}
            {item.aiModelInvolved && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 text-xs font-medium">
                <Activity className="w-3 h-3" /> AI Involved
              </span>
            )}
            {item.externalVendorInvolved && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 text-orange-700 text-xs font-medium">
                <Share2 className="w-3 h-3" /> External Vendor
              </span>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {item.validationCodes.length > 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700">⚠ Compliance Issues</p>
              {item.validationCodes.map(c => <p key={c} className="text-xs text-amber-700">• {FERPA22_CODE_LABELS[c] ?? c}</p>)}
            </div>
          )}

          {/* Source → Destination visualisation */}
          <div className="rounded border p-3 bg-muted/20">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Data Flow</p>
            <FlowArrow flow={item} />
            {item.dataTransferMethod && <p className="text-xs text-muted-foreground mt-1">Transfer method: {item.dataTransferMethod}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Data Owner:</span> {item.dataOwner ?? "—"}</div>
            <div><span className="text-muted-foreground">Reviewer:</span> {item.reviewerName ?? "—"}</div>
            {item.dataCategoriesTransferred && <div className="col-span-2"><span className="text-muted-foreground">Categories:</span> {item.dataCategoriesTransferred}</div>}
            {item.educationRecordFieldsInvolved && <div className="col-span-2"><span className="text-muted-foreground">Fields:</span> <code className="text-xs">{item.educationRecordFieldsInvolved}</code></div>}
          </div>

          {item.processingPurpose && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Processing Purpose</p>
              <p className="text-sm">{item.processingPurpose}</p>
            </div>
          )}
          {item.dataTransformationSteps && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Transformation Steps</p>
              <pre className="text-xs bg-muted rounded p-2 whitespace-pre-wrap">{item.dataTransformationSteps}</pre>
            </div>
          )}

          {item.aiModelInvolved && (
            <div className="rounded border border-indigo-200 bg-indigo-50 p-3 space-y-1">
              <p className="text-xs font-semibold text-indigo-700">AI Model Details</p>
              {item.aiModelName && <p className="text-sm"><span className="text-muted-foreground">Model:</span> {item.aiModelName}</p>}
              {item.aiUsagePurpose && <p className="text-sm"><span className="text-muted-foreground">Usage:</span> {item.aiUsagePurpose}</p>}
            </div>
          )}

          {item.externalVendorInvolved && item.externalVendorName && (
            <div className="rounded border border-orange-200 bg-orange-50 p-3">
              <p className="text-xs font-semibold text-orange-700">External Vendor</p>
              <p className="text-sm">{item.externalVendorName}</p>
            </div>
          )}

          <div className="rounded border p-3 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Documentation &amp; Review</p>
            {item.documentationReference && <p className="text-blue-700 break-all">{item.documentationReference}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Last Review:</span> {item.lastReviewDate ? new Date(item.lastReviewDate).toLocaleDateString() : "Never"}</div>
              <div className={overdue ? "text-red-600 font-medium" : ""}>
                <span className="text-muted-foreground">Next Review:</span>{" "}
                {item.nextReviewDate ? new Date(item.nextReviewDate).toLocaleDateString() : "Not set"}
                {overdue && " ⚠"}
              </div>
            </div>
            {item.reviewNotes && <p className="text-sm text-muted-foreground italic">{item.reviewNotes}</p>}
            {item.approvedAt && <p className="text-sm text-green-700">✓ Approved {new Date(item.approvedAt).toLocaleDateString()}</p>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700">
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
              <Button variant="outline" size="sm" onClick={onReview}>
                <RefreshCcw className="w-3 h-3 mr-1" /> Review
              </Button>
              {item.status === "UnderReview" && (
                <Button variant="outline" size="sm" onClick={onApprove} className="text-green-700 hover:text-green-800">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                </Button>
              )}
              <Button size="sm" onClick={onEdit}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main FERPA-22 Section ──────────────────────────────────────────────────────
function DataLineageSection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth22();

  const [sourceFilter, setSourceFilter] = useState("");
  const [destFilter,   setDestFilter]   = useState("");
  const [aiOnly,       setAiOnly]       = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [showAdd,     setShowAdd]     = useState(false);
  const [editItem,    setEditItem]    = useState<FerpaDataLineageFlow | null>(null);
  const [reviewItem,  setReviewItem]  = useState<FerpaDataLineageFlow | null>(null);
  const [approveItem, setApproveItem] = useState<FerpaDataLineageFlow | null>(null);
  const [deleteItem,  setDeleteItem]  = useState<FerpaDataLineageFlow | null>(null);
  const [detailItem,  setDetailItem]  = useState<FerpaDataLineageFlow | null>(null);

  const summaryQ = useQuery<FerpaDataLineageSummary>({
    queryKey: ["/api/organization/compliance/ferpa/data-lineage/summary"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/data-lineage/summary", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ flow: FerpaDataLineageFlow; codes: string[] }[]>({
    queryKey: ["/api/organization/compliance/ferpa/data-lineage/issues"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/data-lineage/issues", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (sourceFilter) params.set("source", sourceFilter);
  if (destFilter)   params.set("destination", destFilter);
  if (aiOnly)       params.set("aiOnly", "true");
  if (statusFilter) params.set("status", statusFilter);

  const listQ = useQuery<{ total: number; items: FerpaDataLineageFlow[] }>({
    queryKey: ["/api/organization/compliance/ferpa/data-lineage", sourceFilter, destFilter, aiOnly, statusFilter],
    queryFn: async () => {
      const res = await fetch(`/api/organization/compliance/ferpa/data-lineage?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Data Flows",      value: s.total,                icon: <Activity className="w-4 h-4 text-blue-500" /> },
    { label: "AI Data Flows",         value: s.aiDataFlows,          icon: <Activity className="w-4 h-4 text-indigo-500" /> },
    { label: "External Integrations", value: s.externalIntegrations, icon: <Share2 className="w-4 h-4 text-orange-500" /> },
    { label: "Missing Documentation", value: s.missingDocumentation, icon: <AlertTriangle className="w-4 h-4 text-amber-500" /> },
    { label: "Pending Reviews",       value: s.pendingReviews,       icon: <Clock className="w-4 h-4 text-red-500" /> },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-4 h-4 text-blue-600" />
              Education Record Data Lineage &amp; AI Data Flow
            </CardTitle>
            <CardDescription>
              Document how student education records move through systems, including AI and vendor involvement — 34 CFR § 99.3
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Flow
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Summary */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : s ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {metrics.map(m => (
              <div key={m.label} className="rounded border p-3 text-center space-y-1">
                <div className="flex justify-center">{m.icon}</div>
                <p className="text-xl font-bold">{m.value}</p>
                <p className="text-xs text-muted-foreground leading-tight">{m.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Issues */}
        {issuesQ.data && issuesQ.data.length > 0 && (
          <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {issuesQ.data.length} flow{issuesQ.data.length !== 1 ? "s" : ""} with compliance issues
            </p>
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {issuesQ.data.slice(0, 8).map((it: { flow: FerpaDataLineageFlow; codes: string[] }) => (
                <div key={it.flow.id} className="flex items-start gap-2 text-xs text-amber-700">
                  <span className="font-medium whitespace-nowrap">{it.flow.sourceSystem} → {it.flow.destinationSystem}</span>
                  <span className="text-amber-500">—</span>
                  <span>{it.codes.slice(0, 2).map(c => FERPA22_CODE_LABELS[c] ?? c).join(" · ")}</span>
                </div>
              ))}
              {issuesQ.data.length > 8 && <p className="text-xs text-amber-500">…and {issuesQ.data.length - 8} more</p>}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[120px]">
            <Search className="w-3 h-3 absolute left-2 top-2.5 text-muted-foreground" />
            <Input className="pl-7 h-8 text-sm" placeholder="Source system…" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} />
          </div>
          <div className="relative flex-1 min-w-[120px]">
            <Search className="w-3 h-3 absolute left-2 top-2.5 text-muted-foreground" />
            <Input className="pl-7 h-8 text-sm" placeholder="Destination…" value={destFilter} onChange={e => setDestFilter(e.target.value)} />
          </div>
          <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA22_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-sm h-8 px-2 border rounded cursor-pointer select-none">
            <input type="checkbox" checked={aiOnly} onChange={e => setAiOnly(e.target.checked)} className="rounded" />
            AI flows only
          </label>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {listQ.data?.total === 0
              ? "No data flows documented yet. Add one to start building your education record data lineage map."
              : "No flows match the current filters."}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(item => {
              const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
              return (
                <div key={item.id} className="rounded border p-3 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setDetailItem(item)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{item.flowName}</span>
                        {ferpa22StatusBadge(item.status)}
                        {ferpa22EncBadge(item.encryptionStatus)}
                      </div>
                      <FlowArrow flow={item} />
                      {item.validationCodes.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {item.validationCodes.slice(0, 3).map(c => (
                            <span key={c} className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">
                              ⚠ {FERPA22_CODE_LABELS[c] ?? c}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {overdue && <span className="text-xs text-red-600 font-medium">Overdue</span>}
                      {canManage && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={e => { e.stopPropagation(); setEditItem(item); }}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={e => { e.stopPropagation(); setDeleteItem(item); }}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {item.dataOwner && <p className="text-xs text-muted-foreground mt-1">Owner: {item.dataOwner}</p>}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <DataLineageAddDialog open={showAdd || !!editItem} initial={editItem} onClose={() => { setShowAdd(false); setEditItem(null); }} />
      <DataLineageReviewDialog open={!!reviewItem} item={reviewItem} onClose={() => setReviewItem(null)} />
      <DataLineageApproveDialog open={!!approveItem} item={approveItem} onClose={() => setApproveItem(null)} />
      <DataLineageDeleteDialog open={!!deleteItem} item={deleteItem} onClose={() => setDeleteItem(null)} />
      <DataLineageDetailDialog
        open={!!detailItem} item={detailItem} canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={() => { setEditItem(detailItem); setDetailItem(null); }}
        onReview={() => { setReviewItem(detailItem); setDetailItem(null); }}
        onApprove={() => { setApproveItem(detailItem); setDetailItem(null); }}
        onDelete={() => { setDeleteItem(detailItem); setDetailItem(null); }}
      />
    </Card>
  );
}

// ── Field-Level Education Record Data Inventory ────────────────────

type FerpaFieldInventoryItem = {
  id: string;
  organizationId: string;
  systemName: string;
  dataSource: string | null;
  fieldName: string;
  fieldDescription: string | null;
  educationRecordCategory: string | null;
  piiClassification: string | null;
  sensitivityLevel: string;
  isEducationRecord: boolean;
  dataOwner: string | null;
  department: string | null;
  storageLocation: string | null;
  processingPurpose: string | null;
  accessRoles: string | null;
  reviewStatus: string;
  evidenceReference: string | null;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  reviewerName: string | null;
  reviewNotes: string | null;
  createdAt: string;
  updatedAt: string;
  validationCodes: string[];
};

type FerpaFieldInventorySummary = {
  total: number;
  educationRecordFields: number;
  piiFields: number;
  highSensitivityFields: number;
  missingClassification: number;
  missingOwner: number;
  missingEvidence: number;
  pendingReviews: number;
  systems: number;
};

const FERPA21_CATEGORIES  = ["Academic","Enrollment","Financial","Health","Disciplinary","Employment","Other"];
const FERPA21_PII_TYPES   = ["None","PII","SPII","EducationRecord","HealthRecord"];
const FERPA21_SENSITIVITY = ["Low","Medium","High","Critical"];
const FERPA21_STATUSES    = ["Pending","Reviewed","NeedsUpdate","Approved"];

const FERPA21_CODE_LABELS: Record<string, string> = {
  MISSING_DATA_OWNER:         "Missing data owner",
  MISSING_PII_CLASSIFICATION: "PII classification not set",
  MISSING_EVIDENCE:           "No evidence reference",
  MISSING_STORAGE_LOCATION:   "Storage location missing",
  REVIEW_OVERDUE:             "Review overdue",
};

function ferpa21SensitivityBadge(level: string) {
  const color =
    level === "Critical" ? "bg-red-100 text-red-700" :
    level === "High"     ? "bg-orange-100 text-orange-700" :
    level === "Medium"   ? "bg-yellow-100 text-yellow-700" :
                           "bg-green-100 text-green-700";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>{level}</span>;
}

function ferpa21ReviewStatusBadge(status: string) {
  const color =
    status === "Approved"    ? "bg-green-100 text-green-700" :
    status === "Reviewed"    ? "bg-blue-100 text-blue-700" :
    status === "NeedsUpdate" ? "bg-orange-100 text-orange-700" :
                               "bg-gray-100 text-gray-600";
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${color}`}>{status}</span>;
}

function useAuth21() {
  const token =
    (typeof window !== "undefined" && (localStorage.getItem("dotnetbot.jwt") || sessionStorage.getItem("dotnetbot.jwt.session"))) || "";
  const header: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  return { header };
}

function FieldInventoryAddDialog({
  open, initial, onClose,
}: {
  open: boolean;
  initial: FerpaFieldInventoryItem | null;
  onClose: () => void;
}) {
  const { header } = useAuth21();
  const { toast }  = useToast();
  const isEdit     = !!initial;

  const blank = {
    systemName: "", dataSource: "", fieldName: "", fieldDescription: "",
    educationRecordCategory: "Academic", piiClassification: "EducationRecord",
    sensitivityLevel: "Medium", isEducationRecord: true,
    dataOwner: "", department: "", storageLocation: "", processingPurpose: "",
    accessRoles: "", reviewStatus: "Pending", evidenceReference: "",
    reviewerName: "", reviewNotes: "",
    lastReviewDate: "", nextReviewDate: "",
  };

  const [form, setForm] = useState({ ...blank });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Hydrate when dialog opens
  const [lastOpenKey, setLastOpenKey] = useState("");
  const openKey = `${open}-${initial?.id ?? "new"}`;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (open) {
      if (initial) {
        setForm({
          systemName:              initial.systemName ?? "",
          dataSource:              initial.dataSource ?? "",
          fieldName:               initial.fieldName ?? "",
          fieldDescription:        initial.fieldDescription ?? "",
          educationRecordCategory: initial.educationRecordCategory ?? "Academic",
          piiClassification:       initial.piiClassification ?? "",
          sensitivityLevel:        initial.sensitivityLevel ?? "Medium",
          isEducationRecord:       initial.isEducationRecord ?? false,
          dataOwner:               initial.dataOwner ?? "",
          department:              initial.department ?? "",
          storageLocation:         initial.storageLocation ?? "",
          processingPurpose:       initial.processingPurpose ?? "",
          accessRoles:             initial.accessRoles ?? "",
          reviewStatus:            initial.reviewStatus ?? "Pending",
          evidenceReference:       initial.evidenceReference ?? "",
          reviewerName:            initial.reviewerName ?? "",
          reviewNotes:             initial.reviewNotes ?? "",
          lastReviewDate:          initial.lastReviewDate?.slice(0, 10) ?? "",
          nextReviewDate:          initial.nextReviewDate?.slice(0, 10) ?? "",
        });
      } else {
        setForm({ ...blank });
      }
    }
  }

  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        systemName:              form.systemName,
        dataSource:              form.dataSource || null,
        fieldName:               form.fieldName,
        fieldDescription:        form.fieldDescription || null,
        educationRecordCategory: form.educationRecordCategory || null,
        piiClassification:       form.piiClassification || null,
        sensitivityLevel:        form.sensitivityLevel,
        isEducationRecord:       form.isEducationRecord,
        dataOwner:               form.dataOwner || null,
        department:              form.department || null,
        storageLocation:         form.storageLocation || null,
        processingPurpose:       form.processingPurpose || null,
        accessRoles:             form.accessRoles || null,
        reviewStatus:            form.reviewStatus,
        evidenceReference:       form.evidenceReference || null,
        lastReviewDate:          form.lastReviewDate || null,
        nextReviewDate:          form.nextReviewDate || null,
        reviewerName:            form.reviewerName || null,
        reviewNotes:             form.reviewNotes || null,
      };
      const url    = isEdit
        ? `/api/organization/compliance/ferpa/field-inventory/${initial!.id}`
        : "/api/organization/compliance/ferpa/field-inventory";
      const method = isEdit ? "PUT" : "POST";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", ...header },
        body: JSON.stringify(body),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory/issues"] });
      toast({ title: isEdit ? "Field record updated" : "Field record added" });
      setErrors({});
      onClose();
    },
    onError: (e: any) => {
      const serverErrs = extractServerErrors(e);
      if (serverErrs && Object.keys(serverErrs).length) { setErrors(serverErrs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else toast({ title: "Unable to save", description: e?.message ?? "An unexpected error occurred.", variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) { setErrors({}); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Field Record" : "Add Field to Inventory"}</DialogTitle>
          <DialogDescription>Track a data field containing education records or PII.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* System info */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">System Information</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>System Name <span className="text-red-500">*</span></Label>
                <Input className={fieldCls("", errors, "systemName")} aria-invalid={!!errors.systemName} value={form.systemName} onChange={e => { set("systemName", e.target.value); clearErr(setErrors, "systemName"); }} placeholder="Student Information System" />
                {fErr(errors, "systemName")}
              </div>
              <div className="space-y-1">
                <Label>Data Source</Label>
                <Input value={form.dataSource} onChange={e => set("dataSource", e.target.value)} placeholder="Banner ERP, MySQL students_db" />
              </div>
            </div>
          </div>

          {/* Field metadata */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Field Metadata</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Field Name <span className="text-red-500">*</span></Label>
                <Input className={fieldCls("", errors, "fieldName")} aria-invalid={!!errors.fieldName} value={form.fieldName} onChange={e => { set("fieldName", e.target.value); clearErr(setErrors, "fieldName"); }} placeholder="student_gpa" />
                {fErr(errors, "fieldName")}
              </div>
              <div className="space-y-1">
                <Label>Education Record Category</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.educationRecordCategory} onChange={e => set("educationRecordCategory", e.target.value)}>
                  {FERPA21_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Field Description</Label>
              <Textarea value={form.fieldDescription} onChange={e => set("fieldDescription", e.target.value)} rows={2} placeholder="Describe what this field stores" />
            </div>
          </div>

          {/* Classification */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Classification</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label>PII Classification</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.piiClassification} onChange={e => set("piiClassification", e.target.value)}>
                  <option value="">— Select —</option>
                  {FERPA21_PII_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Sensitivity Level</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.sensitivityLevel} onChange={e => set("sensitivityLevel", e.target.value)}>
                  {FERPA21_SENSITIVITY.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Review Status</Label>
                <select className="w-full border rounded px-2 py-1.5 text-sm" value={form.reviewStatus} onChange={e => set("reviewStatus", e.target.value)}>
                  {FERPA21_STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="ferpa21-isEdRecord"
                checked={form.isEducationRecord}
                onChange={e => set("isEducationRecord", e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="ferpa21-isEdRecord" className="cursor-pointer">
                This field constitutes an <strong>Education Record</strong> under 34 CFR § 99.3
              </Label>
            </div>
          </div>

          {/* Ownership */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ownership &amp; Storage</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Data Owner</Label>
                <Input value={form.dataOwner} onChange={e => set("dataOwner", e.target.value)} placeholder="Registrar Office" />
              </div>
              <div className="space-y-1">
                <Label>Department</Label>
                <Input value={form.department} onChange={e => set("department", e.target.value)} placeholder="Academic Affairs" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Storage Location</Label>
              <Input value={form.storageLocation} onChange={e => set("storageLocation", e.target.value)} placeholder="Primary DB / students table / column gpa" />
            </div>
            <div className="space-y-1">
              <Label>Processing Purpose</Label>
              <Textarea value={form.processingPurpose} onChange={e => set("processingPurpose", e.target.value)} rows={2} placeholder="Describe why this field is collected and how it is used" />
            </div>
            <div className="space-y-1">
              <Label>Access Roles</Label>
              <Input value={form.accessRoles} onChange={e => set("accessRoles", e.target.value)} placeholder="Registrar, Advisor, Faculty" />
            </div>
          </div>

          {/* Evidence & review */}
          <div className="rounded border p-3 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Evidence &amp; Review</p>
            <div className="space-y-1">
              <Label>Evidence Reference</Label>
              <Input value={form.evidenceReference} onChange={e => set("evidenceReference", e.target.value)} placeholder="evidence://data-inventory-2026.pdf or doc link" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Last Review Date</Label>
                <Input type="date" value={form.lastReviewDate} onChange={e => set("lastReviewDate", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Next Review Date</Label>
                <Input type="date" value={form.nextReviewDate} onChange={e => set("nextReviewDate", e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Reviewer Name</Label>
              <Input value={form.reviewerName} onChange={e => set("reviewerName", e.target.value)} placeholder="Data Steward / Privacy Officer" />
            </div>
            <div className="space-y-1">
              <Label>Review Notes</Label>
              <Textarea value={form.reviewNotes} onChange={e => set("reviewNotes", e.target.value)} rows={2} placeholder="Notes from the last review" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { const errs: Record<string, string> = {}; required(errs, "systemName", form.systemName, "System Name"); required(errs, "fieldName", form.fieldName, "Field Name"); if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } setErrors({}); mutation.mutate(); }} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Add Field"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInventoryReviewDialog({
  open, item, onClose,
}: {
  open: boolean;
  item: FerpaFieldInventoryItem | null;
  onClose: () => void;
}) {
  const { header } = useAuth21();
  const { toast }  = useToast();
  const [reviewerName, setReviewerName] = useState("");
  const [reviewNotes, setReviewNotes]   = useState("");
  const [evidenceRef, setEvidenceRef]   = useState("");
  const [nextDate,    setNextDate]      = useState("");

  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(
        `/api/organization/compliance/ferpa/field-inventory/${item.id}/review`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...header },
          body: JSON.stringify({
            reviewerName,
            reviewNotes:       reviewNotes || null,
            evidenceReference: evidenceRef || null,
            nextReviewDate:    nextDate    || null,
          }),
        }
      );
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory/issues"] });
      toast({ title: "Review recorded" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Field Review</DialogTitle>
          <DialogDescription>
            Record the outcome of a privacy review for <strong>{item?.fieldName}</strong>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Reviewer Name <span className="text-red-500">*</span></Label>
            <Input value={reviewerName} onChange={e => setReviewerName(e.target.value)} placeholder="Privacy Officer" />
          </div>
          <div className="space-y-1">
            <Label>Evidence Reference</Label>
            <Input value={evidenceRef} onChange={e => setEvidenceRef(e.target.value)} placeholder="evidence://review-2026.pdf" />
          </div>
          <div className="space-y-1">
            <Label>Next Review Date</Label>
            <Input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Review Notes</Label>
            <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} rows={3} placeholder="Findings, required actions, or notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : "Record Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInventoryDeleteDialog({
  open, item, onClose,
}: {
  open: boolean;
  item: FerpaFieldInventoryItem | null;
  onClose: () => void;
}) {
  const { header } = useAuth21();
  const { toast }  = useToast();
  const mutation = useMutation({
    mutationFn: async () => {
      if (!item) return;
      const res = await fetch(
        `/api/organization/compliance/ferpa/field-inventory/${item.id}`,
        { method: "DELETE", headers: header }
      );
      await throwIfResNotOk(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/field-inventory/issues"] });
      toast({ title: "Field record deleted" });
      onClose();
    },
    onError: (e: Error) => toast({ title: "Unable to complete action", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Field Record</DialogTitle>
          <DialogDescription>
            Remove <strong>{item?.fieldName}</strong> from the data inventory? This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInventoryDetailDialog({
  open, item, canManage, onClose, onEdit, onReview, onDelete,
}: {
  open: boolean;
  item: FerpaFieldInventoryItem | null;
  canManage: boolean;
  onClose: () => void;
  onEdit: () => void;
  onReview: () => void;
  onDelete: () => void;
}) {
  if (!item) return null;
  const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <code className="text-sm">{item.fieldName}</code>
            <span className="text-muted-foreground font-normal">in {item.systemName}</span>
          </DialogTitle>
          <div className="flex flex-wrap gap-2 pt-1">
            {ferpa21ReviewStatusBadge(item.reviewStatus)}
            {ferpa21SensitivityBadge(item.sensitivityLevel)}
            {item.isEducationRecord && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                <GraduationCap className="w-3 h-3" /> Education Record
              </span>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Issues */}
          {item.validationCodes.length > 0 && (
            <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700">⚠ Compliance Issues</p>
              {item.validationCodes.map(c => (
                <p key={c} className="text-xs text-amber-700">• {FERPA21_CODE_LABELS[c] ?? c}</p>
              ))}
            </div>
          )}

          {/* Identity */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">System:</span> <strong>{item.systemName}</strong></div>
            <div><span className="text-muted-foreground">Data Source:</span> {item.dataSource ?? "—"}</div>
            <div><span className="text-muted-foreground">Field Name:</span> <code>{item.fieldName}</code></div>
            <div><span className="text-muted-foreground">Category:</span> {item.educationRecordCategory ?? "—"}</div>
            <div><span className="text-muted-foreground">PII Classification:</span> {item.piiClassification ?? "—"}</div>
            <div><span className="text-muted-foreground">Data Owner:</span> {item.dataOwner ?? "—"}</div>
            <div><span className="text-muted-foreground">Department:</span> {item.department ?? "—"}</div>
            <div><span className="text-muted-foreground">Storage Location:</span> {item.storageLocation ?? "—"}</div>
          </div>
          {item.fieldDescription && (
            <p className="text-sm text-muted-foreground">{item.fieldDescription}</p>
          )}
          {item.processingPurpose && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Processing Purpose</p>
              <p className="text-sm">{item.processingPurpose}</p>
            </div>
          )}
          {item.accessRoles && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Access Roles</p>
              <div className="flex flex-wrap gap-1">
                {item.accessRoles.split(",").map(r => (
                  <span key={r.trim()} className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">{r.trim()}</span>
                ))}
              </div>
            </div>
          )}

          {/* Evidence & review */}
          <div className="rounded border p-3 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Evidence &amp; Review</p>
            {item.evidenceReference && <p className="text-blue-700 break-all">{item.evidenceReference}</p>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Last Review:</span> {item.lastReviewDate ? new Date(item.lastReviewDate).toLocaleDateString() : "Never"}</div>
              <div className={overdue ? "text-red-600 font-medium" : ""}>
                <span className="text-muted-foreground">Next Review:</span>{" "}
                {item.nextReviewDate ? new Date(item.nextReviewDate).toLocaleDateString() : "Not set"}
                {overdue && " ⚠"}
              </div>
              {item.reviewerName && <div><span className="text-muted-foreground">Reviewer:</span> {item.reviewerName}</div>}
            </div>
            {item.reviewNotes && <p className="text-sm text-muted-foreground italic">{item.reviewNotes}</p>}
          </div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {canManage && (
            <>
              <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700">
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
              <Button variant="outline" size="sm" onClick={onReview}>
                <RefreshCcw className="w-3 h-3 mr-1" /> Record Review
              </Button>
              <Button size="sm" onClick={onEdit}>
                <Pencil className="w-3 h-3 mr-1" /> Edit
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInventorySection({ canManage }: { canManage: boolean }) {
  const { header } = useAuth21();

  const [systemFilter,   setSystemFilter]   = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sensitFilter,   setSensitFilter]   = useState("");
  const [ownerFilter,    setOwnerFilter]    = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");

  const [showAdd,    setShowAdd]    = useState(false);
  const [editItem,   setEditItem]   = useState<FerpaFieldInventoryItem | null>(null);
  const [reviewItem, setReviewItem] = useState<FerpaFieldInventoryItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<FerpaFieldInventoryItem | null>(null);
  const [detailItem, setDetailItem] = useState<FerpaFieldInventoryItem | null>(null);

  const summaryQ = useQuery<FerpaFieldInventorySummary>({
    queryKey: ["/api/organization/compliance/ferpa/field-inventory/summary"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/field-inventory/summary", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const issuesQ = useQuery<{ field: FerpaFieldInventoryItem; codes: string[] }[]>({
    queryKey: ["/api/organization/compliance/ferpa/field-inventory/issues"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/field-inventory/issues", { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const params = new URLSearchParams({ pageSize: "200" });
  if (systemFilter)   params.set("system",      systemFilter);
  if (categoryFilter) params.set("category",    categoryFilter);
  if (sensitFilter)   params.set("sensitivity", sensitFilter);
  if (ownerFilter)    params.set("owner",        ownerFilter);
  if (statusFilter)   params.set("status",       statusFilter);

  const listQ = useQuery<{ total: number; items: FerpaFieldInventoryItem[] }>({
    queryKey: ["/api/organization/compliance/ferpa/field-inventory", systemFilter, categoryFilter, sensitFilter, ownerFilter, statusFilter],
    queryFn: async () => {
      const res = await fetch(`/api/organization/compliance/ferpa/field-inventory?${params}`, { headers: header });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const s = summaryQ.data;
  const metrics = s ? [
    { label: "Total Fields",            value: s.total,                 icon: <ClipboardList className="w-4 h-4 text-blue-500" /> },
    { label: "Education Record Fields", value: s.educationRecordFields,  icon: <GraduationCap className="w-4 h-4 text-indigo-500" /> },
    { label: "PII Fields",              value: s.piiFields,              icon: <Lock className="w-4 h-4 text-purple-500" /> },
    { label: "High Sensitivity",        value: s.highSensitivityFields,  icon: <ShieldAlert className="w-4 h-4 text-orange-500" /> },
    { label: "Missing Classification",  value: s.missingClassification,  icon: <AlertTriangle className="w-4 h-4 text-amber-500" /> },
    { label: "Pending Reviews",         value: s.pendingReviews,         icon: <Clock className="w-4 h-4 text-red-500" /> },
  ] : [];

  const items = listQ.data?.items ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList className="w-4 h-4 text-blue-600" />
              Field-Level Education Record Data Inventory
            </CardTitle>
            <CardDescription>
              Track individual data fields containing student education records or PII — 34 CFR § 99.3
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="w-3 h-3 mr-1" /> Add Field
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Summary metrics */}
        {summaryQ.isLoading ? (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : s ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map(m => (
              <div key={m.label} className="rounded border p-3 text-center space-y-1">
                <div className="flex justify-center">{m.icon}</div>
                <p className="text-xl font-bold">{m.value}</p>
                <p className="text-xs text-muted-foreground leading-tight">{m.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Issues panel */}
        {issuesQ.data && issuesQ.data.length > 0 && (
          <div className="rounded bg-amber-50 border border-amber-200 p-3 space-y-2">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {issuesQ.data.length} field{issuesQ.data.length !== 1 ? "s" : ""} with compliance issues
            </p>
            <div className="space-y-1 max-h-36 overflow-y-auto">
              {issuesQ.data.slice(0, 8).map((it: { field: FerpaFieldInventoryItem; codes: string[] }) => (
                <div key={it.field.id} className="flex items-start gap-2 text-xs text-amber-700">
                  <span className="font-mono font-medium whitespace-nowrap">{it.field.systemName} / {it.field.fieldName}</span>
                  <span className="text-amber-500">—</span>
                  <span>{it.codes.slice(0, 2).map(c => FERPA21_CODE_LABELS[c] ?? c).join(" · ")}</span>
                </div>
              ))}
              {issuesQ.data.length > 8 && (
                <p className="text-xs text-amber-500">…and {issuesQ.data.length - 8} more</p>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[140px]">
            <Search className="w-3 h-3 absolute left-2 top-2.5 text-muted-foreground" />
            <Input
              className="pl-7 h-8 text-sm"
              placeholder="System or source…"
              value={systemFilter}
              onChange={e => setSystemFilter(e.target.value)}
            />
          </div>
          <select className="border rounded px-2 py-1 text-sm h-8" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
            <option value="">All categories</option>
            {FERPA21_CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select className="border rounded px-2 py-1 text-sm h-8" value={sensitFilter} onChange={e => setSensitFilter(e.target.value)}>
            <option value="">All sensitivity</option>
            {FERPA21_SENSITIVITY.map(s => <option key={s}>{s}</option>)}
          </select>
          <Input
            className="h-8 text-sm w-36"
            placeholder="Owner…"
            value={ownerFilter}
            onChange={e => setOwnerFilter(e.target.value)}
          />
          <select className="border rounded px-2 py-1 text-sm h-8" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {FERPA21_STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>

        {/* List */}
        {listQ.isLoading ? (
          <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            {listQ.data?.total === 0
              ? "No field records yet. Add one to begin building your education record data inventory."
              : "No results match the current filters."}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(item => {
              const overdue = item.nextReviewDate && new Date(item.nextReviewDate) < new Date();
              return (
                <div
                  key={item.id}
                  className="rounded border p-3 hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => setDetailItem(item)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="text-sm font-semibold">{item.fieldName}</code>
                        <span className="text-muted-foreground text-xs">in {item.systemName}</span>
                        {item.dataSource && <span className="text-muted-foreground text-xs">({item.dataSource})</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {ferpa21ReviewStatusBadge(item.reviewStatus)}
                        {ferpa21SensitivityBadge(item.sensitivityLevel)}
                        {item.isEducationRecord && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">
                            <GraduationCap className="w-2.5 h-2.5" /> Ed Record
                          </span>
                        )}
                        {item.piiClassification && item.piiClassification !== "None" && (
                          <span className="inline-flex px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs">{item.piiClassification}</span>
                        )}
                        {item.educationRecordCategory && (
                          <span className="text-xs text-muted-foreground">{item.educationRecordCategory}</span>
                        )}
                      </div>
                      {item.validationCodes.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {item.validationCodes.slice(0, 3).map(c => (
                            <span key={c} className="inline-flex px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs">
                              ⚠ {FERPA21_CODE_LABELS[c] ?? c}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {overdue && <span className="text-xs text-red-600 font-medium">Review overdue</span>}
                      {canManage && (
                        <>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={e => { e.stopPropagation(); setEditItem(item); }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            onClick={e => { e.stopPropagation(); setDeleteItem(item); }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {(item.dataOwner || item.department) && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {[item.dataOwner, item.department].filter(Boolean).join(" · ")}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <FieldInventoryAddDialog
        open={showAdd || !!editItem}
        initial={editItem}
        onClose={() => { setShowAdd(false); setEditItem(null); }}
      />
      <FieldInventoryReviewDialog
        open={!!reviewItem}
        item={reviewItem}
        onClose={() => setReviewItem(null)}
      />
      <FieldInventoryDeleteDialog
        open={!!deleteItem}
        item={deleteItem}
        onClose={() => setDeleteItem(null)}
      />
      <FieldInventoryDetailDialog
        open={!!detailItem}
        item={detailItem}
        canManage={canManage}
        onClose={() => setDetailItem(null)}
        onEdit={() => { setEditItem(detailItem); setDetailItem(null); }}
        onReview={() => { setReviewItem(detailItem); setDetailItem(null); }}
        onDelete={() => { setDeleteItem(detailItem); setDetailItem(null); }}
      />
    </Card>
  );
}

// ── FERPA-16 — Data Sharing Governance Section ────────────────────────────────

type DataSharingRecord = {
  id: string; organizationId: string; recipientName: string; recipientType: string;
  recipientOrganization: string | null; approvedPurpose: string; dataElementsShared: string | null;
  studentDataCategories: string | null; legalBasis: string; ferpaExceptionType: string | null;
  consentRequired: boolean; consentReference: string | null; contractReference: string | null;
  dpaReference: string | null; dataSharingAgreementReference: string | null;
  effectiveDate: string; expirationDate: string | null; approvalStatus: string;
  approvedBy: string | null; approvedAt: string | null; rejectionReason: string | null;
  createdAt: string; updatedAt: string;
};

type DataSharingCheckLog = {
  id: string; organizationId: string; sharingRecordId: string | null;
  recipientName: string; purpose: string | null;
  outcome: string; blockCode: string | null; blockReason: string | null; timestamp: string;
};

const DS_RECIPIENT_TYPES = ["Vendor","Institution","Government","ResearchPartner","Other"];
const DS_SHARING_STATUSES = ["Pending","Approved","Rejected","Expired"];
const STATUS_BADGE_DS: Record<string,string> = {
  Approved: "bg-green-100 text-green-800",
  Pending:  "bg-yellow-100 text-yellow-800",
  Rejected: "bg-red-100 text-red-800",
  Expired:  "bg-gray-100 text-gray-600",
};

const EMPTY_SHARING: Omit<DataSharingRecord,"id"|"organizationId"|"approvedBy"|"approvedAt"|"rejectionReason"|"createdAt"|"updatedAt"> = {
  recipientName: "", recipientType: "Vendor", recipientOrganization: null,
  approvedPurpose: "", dataElementsShared: null, studentDataCategories: null,
  legalBasis: "", ferpaExceptionType: null, consentRequired: false, consentReference: null,
  contractReference: null, dpaReference: null, dataSharingAgreementReference: null,
  effectiveDate: new Date().toISOString().slice(0,10), expirationDate: null, approvalStatus: "Pending",
};

function DataSharingGovernanceSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab]           = useState<"registry"|"check"|"logs">("registry");
  const [open, setOpen]         = useState(false);
  const [editing, setEditing]   = useState<DataSharingRecord | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string,string>>({});
  const [form, setForm]         = useState({ ...EMPTY_SHARING });
  const [statusTarget, setStatusTarget] = useState<DataSharingRecord | null>(null);
  const [statusForm, setStatusForm]     = useState({ status: "Approved", rejectionReason: "" });

  // Governance check
  const [chk, setChk]     = useState({ recipientName: "", purpose: "", sharingRecordId: "" });
  const [chkRes, setChkRes] = useState<any>(null);
  const [chkLoading, setChkLoading] = useState(false);

  const RQ = ["/api/organization/compliance/ferpa/data-sharing"];
  const LQ = ["/api/organization/compliance/ferpa/data-sharing/check-logs"];

  const { data: regData, isLoading: regLoading } = useQuery<PagedResult<DataSharingRecord>>({
    queryKey: RQ,
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/data-sharing?pageSize=100",
        { headers: bearerHeader() });
      if (!res.ok) throw new Error("Failed to load sharing registry");
      return res.json();
    },
  });

  const { data: logData } = useQuery<PagedResult<DataSharingCheckLog>>({
    queryKey: LQ,
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/data-sharing/check-logs?pageSize=50",
        { headers: bearerHeader() });
      if (!res.ok) throw new Error("Failed to load check logs");
      return res.json();
    },
    enabled: tab === "logs",
  });

  const save = useMutation({
    mutationFn: async () => {
      const url = editing
        ? `/api/organization/compliance/ferpa/data-sharing/${editing.id}`
        : "/api/organization/compliance/ferpa/data-sharing";
      const res = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          ...form,
          recipientOrganization:         form.recipientOrganization       || null,
          dataElementsShared:            form.dataElementsShared           || null,
          studentDataCategories:         form.studentDataCategories        || null,
          ferpaExceptionType:            form.ferpaExceptionType           || null,
          consentReference:              form.consentReference             || null,
          contractReference:             form.contractReference            || null,
          dpaReference:                  form.dpaReference                 || null,
          dataSharingAgreementReference: form.dataSharingAgreementReference|| null,
          expirationDate:                form.expirationDate
            ? new Date(form.expirationDate).toISOString() : null,
          effectiveDate: new Date(form.effectiveDate).toISOString(),
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RQ });
      toast({ title: editing ? "Record updated" : "Sharing record registered" });
      setOpen(false); setEditing(null); setFieldErrors({});
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) setFieldErrors(errs);
      else toast({ title: "Unable to save", description: e.message, variant: "destructive" });
    },
  });

  const patchStatus = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `/api/organization/compliance/ferpa/data-sharing/${statusTarget!.id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...bearerHeader() },
          body: JSON.stringify({
            status: statusForm.status,
            rejectionReason: statusForm.rejectionReason || null,
          }),
        });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RQ });
      toast({ title: `Record ${statusForm.status.toLowerCase()}` });
      setStatusTarget(null);
    },
    onError: (e: any) => toast({ title: "Unable to update status", description: e.message, variant: "destructive" }),
  });

  const runCheck = async () => {
    if (!chk.recipientName.trim()) {
      toast({ title: "Enter a recipient name", variant: "destructive" });
      return;
    }
    setChkLoading(true); setChkRes(null);
    try {
      const res = await fetch("/api/organization/compliance/ferpa/data-sharing/governance-check", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          recipientName:   chk.recipientName,
          purpose:         chk.purpose   || null,
          sharingRecordId: chk.sharingRecordId || null,
        }),
      });
      const body = await res.json();
      setChkRes(body);
      queryClient.invalidateQueries({ queryKey: LQ });
    } catch (e: any) {
      toast({ title: "Check failed", description: e.message, variant: "destructive" });
    } finally { setChkLoading(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_SHARING });
    setFieldErrors({});
    setOpen(true);
  };

  const openEdit = (r: DataSharingRecord) => {
    setEditing(r);
    setForm({
      recipientName: r.recipientName, recipientType: r.recipientType,
      recipientOrganization: r.recipientOrganization ?? null,
      approvedPurpose: r.approvedPurpose, dataElementsShared: r.dataElementsShared ?? null,
      studentDataCategories: r.studentDataCategories ?? null,
      legalBasis: r.legalBasis, ferpaExceptionType: r.ferpaExceptionType ?? null,
      consentRequired: r.consentRequired, consentReference: r.consentReference ?? null,
      contractReference: r.contractReference ?? null, dpaReference: r.dpaReference ?? null,
      dataSharingAgreementReference: r.dataSharingAgreementReference ?? null,
      effectiveDate: r.effectiveDate.slice(0,10),
      expirationDate: r.expirationDate?.slice(0,10) ?? null,
      approvalStatus: r.approvalStatus,
    });
    setFieldErrors({});
    setOpen(true);
  };

  const handleSave = () => {
    const errs: Record<string,string> = {};
    if (!form.recipientName.trim())   errs.recipientName   = "Recipient name is required.";
    if (!form.approvedPurpose.trim()) errs.approvedPurpose = "Approved purpose is required.";
    if (!form.legalBasis.trim())      errs.legalBasis      = "FERPA legal basis is required.";
    if (!form.effectiveDate)          errs.effectiveDate   = "Effective date is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    save.mutate();
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Share2 className="w-4 h-4" /> Data Sharing Governance
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99 — approved external data-sharing registry, legal basis, and governance gate
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={openNew}>
              <Plus className="w-4 h-4 mr-1" /> Register Sharing
            </Button>
          )}
        </div>
        <div className="flex gap-1 pt-2 border-b">
          {(["registry","check","logs"] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs rounded-t font-medium transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {t === "registry" ? "Registry" : t === "check" ? "Governance Check" : "Check Log"}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="pt-4">

        {/* ── TAB: Registry ────────────────────────────────── */}
        {tab === "registry" && (
          <>
            {regLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_,i)=><Skeleton key={i} className="h-14 w-full"/>)}</div>
            ) : !regData?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No data-sharing agreements registered. Click "Register Sharing" to add one.
              </p>
            ) : (
              <div className="divide-y text-sm">
                {regData.items.map(r => (
                  <div key={r.id} className="py-3 flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{r.recipientName}</span>
                        <span className="text-xs text-muted-foreground bg-gray-50 border rounded px-1.5 py-0.5">{r.recipientType}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${STATUS_BADGE_DS[r.approvalStatus] ?? "bg-gray-100 text-gray-600"}`}>
                          {r.approvalStatus}
                        </span>
                        {r.expirationDate && new Date(r.expirationDate) < new Date() && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded">Expired</span>
                        )}
                        {r.consentRequired && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">Consent req.</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.approvedPurpose}</p>
                      <div className="text-xs text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                        <span>Legal: {r.legalBasis.slice(0,40)}{r.legalBasis.length>40?"…":""}</span>
                        {r.ferpaExceptionType && <span>Exception: {r.ferpaExceptionType}</span>}
                        {r.expirationDate && <span>Expires: {r.expirationDate.slice(0,10)}</span>}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1 shrink-0">
                        {(r.approvalStatus === "Pending" || r.approvalStatus === "Approved") && (
                          <Button variant="ghost" size="sm" className="h-6 text-xs px-2"
                            onClick={() => { setStatusTarget(r); setStatusForm({ status: "Approved", rejectionReason: "" }); }}>
                            Approve/Reject
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          aria-label="Edit" onClick={() => openEdit(r)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB: Governance Check ────────────────────────── */}
        {tab === "check" && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Validate whether a data-sharing arrangement may proceed. All checks are logged.
            </p>
            <div>
              <Label htmlFor="ds-chk-rec">Recipient Name *</Label>
              <Input id="ds-chk-rec" placeholder="Exact recipient name from the registry"
                value={chk.recipientName}
                onChange={e => setChk(c => ({ ...c, recipientName: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="ds-chk-purpose">Purpose <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input id="ds-chk-purpose" placeholder="Describe the specific sharing purpose"
                value={chk.purpose}
                onChange={e => setChk(c => ({ ...c, purpose: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="ds-chk-id">Sharing Record ID <span className="text-muted-foreground text-xs">(optional — look up by UUID instead of name)</span></Label>
              <Input id="ds-chk-id" placeholder="UUID of the sharing record"
                className="font-mono text-sm"
                value={chk.sharingRecordId}
                onChange={e => setChk(c => ({ ...c, sharingRecordId: e.target.value }))} />
            </div>
            <Button onClick={runCheck} disabled={chkLoading} className="w-full">
              {chkLoading ? "Checking…" : "Run Governance Check"}
            </Button>

            {chkRes && (
              <div className={`rounded-lg border p-4 ${chkRes.outcome === "Approved" ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                <div className="flex items-center gap-2 font-medium text-sm mb-1">
                  {chkRes.outcome === "Approved"
                    ? <><CheckCircle2 className="w-4 h-4 text-green-600" /> Sharing Authorized</>
                    : <><XCircle className="w-4 h-4 text-red-600" /> Sharing Blocked</>}
                </div>
                {chkRes.blockReason && <p className="text-xs text-muted-foreground">{chkRes.blockReason}</p>}
                {chkRes.blockCode   && <p className="text-xs mt-1 font-mono font-medium text-red-700">{chkRes.blockCode}</p>}
                {chkRes.outcome === "Approved" && (
                  <div className="mt-2 space-y-1 text-xs">
                    <p><span className="text-muted-foreground">Recipient:</span> {chkRes.recipientName} ({chkRes.recipientType})</p>
                    <p><span className="text-muted-foreground">Purpose:</span> {chkRes.approvedPurpose}</p>
                    {chkRes.ferpaExceptionType && <p><span className="text-muted-foreground">Exception:</span> {chkRes.ferpaExceptionType}</p>}
                    {chkRes.consentRequired && <p className="text-blue-700">⚠ Consent required before sharing</p>}
                    {chkRes.expirationDate && <p><span className="text-muted-foreground">Expires:</span> {chkRes.expirationDate?.slice(0,10)}</p>}
                  </div>
                )}
                <p className="text-xs mt-2 text-muted-foreground">Log ID: <span className="font-mono">{chkRes.logId}</span></p>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Check Log ───────────────────────────────── */}
        {tab === "logs" && (
          <>
            {!logData?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No governance checks logged yet.
              </p>
            ) : (
              <div className="divide-y text-sm">
                {logData.items.map(l => (
                  <div key={l.id} className="py-2 flex items-start gap-3">
                    {l.outcome === "Approved"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0"/>
                      : <XCircle     className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{l.recipientName}</span>
                        {l.blockCode && (
                          <span className="font-mono text-xs px-1.5 py-0.5 bg-red-50 text-red-700 rounded">{l.blockCode}</span>
                        )}
                      </div>
                      {l.purpose    && <p className="text-xs text-muted-foreground mt-0.5">Purpose: {l.purpose}</p>}
                      {l.blockReason&& <p className="text-xs text-red-600 mt-0.5">{l.blockReason}</p>}
                      <p className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(l.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* ── Register / Edit Dialog ──────────────────────────── */}
      <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) { setEditing(null); setFieldErrors({}); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Sharing Record" : "Register Data-Sharing Agreement"}</DialogTitle>
            <DialogDescription>
              Document the approved purpose, recipient, legal basis, and data elements for this external sharing arrangement.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ds-rec-name">Recipient Name *</Label>
                <Input id="ds-rec-name" placeholder="e.g. State Dept of Education"
                  value={form.recipientName}
                  onChange={e => { setForm(f=>({...f,recipientName:e.target.value})); setFieldErrors(fe=>({...fe,recipientName:""})); }}
                  aria-invalid={!!fieldErrors.recipientName||undefined}/>
                <FieldErr id="ds-rec-name-err" msg={fieldErrors.recipientName}/>
              </div>
              <div>
                <Label htmlFor="ds-rec-type">Recipient Type</Label>
                <select id="ds-rec-type"
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={form.recipientType}
                  onChange={e => setForm(f=>({...f,recipientType:e.target.value}))}>
                  {DS_RECIPIENT_TYPES.map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="ds-org">Recipient Organization</Label>
              <Input id="ds-org" placeholder="Full legal name of recipient organization"
                value={form.recipientOrganization??""} onChange={e=>setForm(f=>({...f,recipientOrganization:e.target.value||null}))}/>
            </div>

            <div>
              <Label htmlFor="ds-purpose">Approved Purpose *</Label>
              <Textarea id="ds-purpose" rows={2} placeholder="Describe the specific, documented purpose for this data sharing"
                value={form.approvedPurpose}
                onChange={e=>{setForm(f=>({...f,approvedPurpose:e.target.value}));setFieldErrors(fe=>({...fe,approvedPurpose:""}));}}
                aria-invalid={!!fieldErrors.approvedPurpose||undefined}/>
              <FieldErr id="ds-purpose-err" msg={fieldErrors.approvedPurpose}/>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ds-elements">Data Elements Shared <span className="text-muted-foreground text-xs">(JSON array)</span></Label>
                <Input id="ds-elements" placeholder='["GPA","Enrollment","StudentId"]'
                  value={form.dataElementsShared??""} onChange={e=>setForm(f=>({...f,dataElementsShared:e.target.value||null}))}/>
              </div>
              <div>
                <Label htmlFor="ds-categories">Student Data Categories</Label>
                <Input id="ds-categories" placeholder='["Academic","Financial"]'
                  value={form.studentDataCategories??""} onChange={e=>setForm(f=>({...f,studentDataCategories:e.target.value||null}))}/>
              </div>
            </div>

            <div>
              <Label htmlFor="ds-legal">FERPA Legal Basis *</Label>
              <Input id="ds-legal" placeholder="e.g. § 99.31(a)(3) Authorized representative of the Secretary"
                value={form.legalBasis}
                onChange={e=>{setForm(f=>({...f,legalBasis:e.target.value}));setFieldErrors(fe=>({...fe,legalBasis:""}));}}
                aria-invalid={!!fieldErrors.legalBasis||undefined}/>
              <FieldErr id="ds-legal-err" msg={fieldErrors.legalBasis}/>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ds-exc">FERPA Exception Type</Label>
                <Input id="ds-exc" placeholder="e.g. § 99.31(a)(1) School Official"
                  value={form.ferpaExceptionType??""} onChange={e=>setForm(f=>({...f,ferpaExceptionType:e.target.value||null}))}/>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="ds-consent">Consent</Label>
                <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
                  <input type="checkbox" checked={form.consentRequired}
                    onChange={e=>setForm(f=>({...f,consentRequired:e.target.checked}))}/>
                  Student consent required
                </label>
              </div>
            </div>

            {form.consentRequired && (
              <div>
                <Label htmlFor="ds-consent-ref">Consent Reference</Label>
                <Input id="ds-consent-ref" placeholder="Consent form ID or reference"
                  value={form.consentReference??""} onChange={e=>setForm(f=>({...f,consentReference:e.target.value||null}))}/>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ds-contract">Contract Reference</Label>
                <Input id="ds-contract" placeholder="CONTRACT-001"
                  value={form.contractReference??""} onChange={e=>setForm(f=>({...f,contractReference:e.target.value||null}))}/>
              </div>
              <div>
                <Label htmlFor="ds-dpa">DPA Reference</Label>
                <Input id="ds-dpa" placeholder="DPA-001"
                  value={form.dpaReference??""} onChange={e=>setForm(f=>({...f,dpaReference:e.target.value||null}))}/>
              </div>
              <div>
                <Label htmlFor="ds-dsa">DSA Reference</Label>
                <Input id="ds-dsa" placeholder="DSA-001"
                  value={form.dataSharingAgreementReference??""} onChange={e=>setForm(f=>({...f,dataSharingAgreementReference:e.target.value||null}))}/>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ds-eff">Effective Date *</Label>
                <Input id="ds-eff" type="date"
                  value={form.effectiveDate}
                  onChange={e=>{setForm(f=>({...f,effectiveDate:e.target.value}));setFieldErrors(fe=>({...fe,effectiveDate:""}));}}
                  aria-invalid={!!fieldErrors.effectiveDate||undefined}/>
                <FieldErr id="ds-eff-err" msg={fieldErrors.effectiveDate}/>
              </div>
              <div>
                <Label htmlFor="ds-exp">Expiration Date</Label>
                <Input id="ds-exp" type="date"
                  value={form.expirationDate??""} onChange={e=>setForm(f=>({...f,expirationDate:e.target.value||null}))}/>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={()=>{setOpen(false);setEditing(null);}}>Cancel</Button>
            <Button onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? "Saving…" : editing ? "Save Changes" : "Register"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Status Dialog ───────────────────────────────────── */}
      {statusTarget && (
        <Dialog open={!!statusTarget} onOpenChange={v=>{if(!v)setStatusTarget(null);}}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Update Approval Status</DialogTitle>
              <DialogDescription>{statusTarget.recipientName}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="ds-status-sel">New Status</Label>
                <select id="ds-status-sel"
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={statusForm.status}
                  onChange={e=>setStatusForm(f=>({...f,status:e.target.value}))}>
                  {DS_SHARING_STATUSES.filter(s => s !== "Pending").map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              {statusForm.status === "Rejected" && (
                <div>
                  <Label htmlFor="ds-reject-reason">Rejection Reason *</Label>
                  <Textarea id="ds-reject-reason" rows={2}
                    value={statusForm.rejectionReason}
                    onChange={e=>setStatusForm(f=>({...f,rejectionReason:e.target.value}))}/>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setStatusTarget(null)}>Cancel</Button>
              <Button onClick={()=>patchStatus.mutate()} disabled={patchStatus.isPending}>
                {patchStatus.isPending?"Saving…":"Update"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

// ── FERPA-15 — Audit Event Store Section ──────────────────────────────────────

type FerpaAuditEventDto = {
  id: string;
  organizationId: string;
  eventType: string;
  entityType: string | null;
  entityId: string | null;
  userId: string | null;
  userRole: string | null;
  studentInvolved: boolean;
  requestPurpose: string | null;
  correlationId: string | null;
  decision: string;
  reasonCode: string | null;
  details: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  retentionPolicy: string;
  expiresAt: string | null;
  hasIntegrityHash: boolean;
  source: string;
  timestamp: string;
};

const FERPA15_EVENT_TYPES = [
  "RecordAccess","DisclosureCreated","DisclosureBlocked",
  "ConsentUsed","ExceptionUsed","SchoolOfficialAccess",
  "LeiCheck","RedisclosureCheck","DataMinimization",
  "AccessControl","VendorAccess","DirectoryCheck",
  "AmendmentFiled","AmendmentDecision","AnnualNotice",
];

const FERPA15_DECISIONS = ["Allowed","Denied","Recorded"];

const DECISION_BADGE: Record<string, string> = {
  Allowed:  "bg-green-100 text-green-800",
  Denied:   "bg-red-100 text-red-800",
  Recorded: "bg-gray-100 text-gray-700",
};

function FerpaAuditSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"events" | "capture" | "verify">("events");

  // Filters
  const [fEventType, setFEventType] = useState("");
  const [fDecision,  setFDecision]  = useState("");
  const [fFrom,      setFFrom]      = useState("");
  const [fTo,        setFTo]        = useState("");
  const [fEntityType,setFEntityType]= useState("");

  // Capture form
  const [cap, setCap] = useState({
    eventType: "RecordAccess", decision: "Recorded", entityType: "", entityId: "",
    userRole: "", studentId: "", requestPurpose: "", correlationId: "", retentionPolicy: "Standard",
    details: "", reasonCode: "",
  });
  const [capErr, setCapErr] = useState<Record<string,string>>({});

  // Verify
  const [verifyId,  setVerifyId]  = useState("");
  const [verifyRes, setVerifyRes] = useState<any>(null);
  const [verifying, setVerifying] = useState(false);

  const AQ = ["/api/organization/compliance/ferpa/audit-events",
              fEventType, fDecision, fFrom, fTo, fEntityType];

  const { data, isLoading, refetch } = useQuery<{ total: number; items: FerpaAuditEventDto[] }>({
    queryKey: AQ,
    queryFn: async () => {
      const params = new URLSearchParams({ pageSize: "100" });
      if (fEventType)  params.set("eventType",  fEventType);
      if (fDecision)   params.set("decision",   fDecision);
      if (fFrom)       params.set("from",        fFrom);
      if (fTo)         params.set("to",          fTo);
      if (fEntityType) params.set("entityType",  fEntityType);
      const res = await fetch(
        `/api/organization/compliance/ferpa/audit-events?${params}`,
        { headers: bearerHeader() });
      if (!res.ok) throw new Error("Failed to load audit events");
      return res.json();
    },
    enabled: tab === "events",
  });

  const capture = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/audit-events", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          eventType:       cap.eventType,
          entityType:      cap.entityType || null,
          entityId:        cap.entityId   || null,
          userRole:        cap.userRole   || null,
          studentId:       cap.studentId  || null,
          requestPurpose:  cap.requestPurpose || null,
          correlationId:   cap.correlationId  || null,
          decision:        cap.decision,
          reasonCode:      cap.reasonCode || null,
          details:         cap.details    || null,
          retentionPolicy: cap.retentionPolicy || "Standard",
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/audit-events"] });
      toast({ title: "Audit event captured" });
      setCap(c => ({ ...c, entityId: "", studentId: "", requestPurpose: "", correlationId: "", details: "", reasonCode: "" }));
      setCapErr({});
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) setCapErr(errs);
      else toast({ title: "Capture failed", description: e.message, variant: "destructive" });
    },
  });

  const handleCapture = () => {
    const errs: Record<string,string> = {};
    if (!cap.eventType) errs.eventType = "Event type is required.";
    if (Object.keys(errs).length) { setCapErr(errs); return; }
    setCapErr({});
    capture.mutate();
  };

  const runVerify = async () => {
    if (!verifyId.trim()) { toast({ title: "Enter an audit event ID", variant: "destructive" }); return; }
    setVerifying(true); setVerifyRes(null);
    try {
      const res = await fetch(
        `/api/organization/compliance/ferpa/audit-events/${verifyId.trim()}/verify`,
        { method: "POST", headers: bearerHeader() });
      const body = await res.json();
      setVerifyRes(body);
    } catch (e: any) {
      toast({ title: "Verify failed", description: e.message, variant: "destructive" });
    } finally { setVerifying(false); }
  };

  const sourceLabel = (s: string) =>
    s === "audit_log" ? "Legacy" : "Audit Trail";

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ClipboardList className="w-4 h-4" /> Audit Trail
            </CardTitle>
            <CardDescription className="text-xs">
              FERPA § 99 auditability — immutable, tamper-evident log of all FERPA compliance events
            </CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
        <div className="flex gap-1 pt-2 border-b">
          {(["events","capture","verify"] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs rounded-t font-medium transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {t === "events" ? "Audit Events" : t === "capture" ? "Capture Event" : "Integrity Check"}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {/* ── TAB: Audit Events ─────────────────────────────── */}
        {tab === "events" && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
              <div>
                <Label className="text-xs">Event Type</Label>
                <select className="w-full border rounded px-2 py-1.5 text-xs mt-1"
                  value={fEventType} onChange={e => setFEventType(e.target.value)}>
                  <option value="">All types</option>
                  {FERPA15_EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Decision</Label>
                <select className="w-full border rounded px-2 py-1.5 text-xs mt-1"
                  value={fDecision} onChange={e => setFDecision(e.target.value)}>
                  <option value="">All decisions</option>
                  {FERPA15_DECISIONS.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" className="text-xs mt-1"
                  value={fFrom} onChange={e => setFFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" className="text-xs mt-1"
                  value={fTo} onChange={e => setFTo(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Entity Type</Label>
                <Input placeholder="e.g. Transcript" className="text-xs mt-1"
                  value={fEntityType} onChange={e => setFEntityType(e.target.value)} />
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-2">{[...Array(5)].map((_,i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : !data?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No audit events found. Adjust filters or capture an event using the "Capture Event" tab.
              </p>
            ) : (
              <div className="text-xs">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground">{data.total} event{data.total !== 1 ? "s" : ""}</span>
                </div>
                <div className="rounded border divide-y">
                  {data.items.map(ev => (
                    <div key={ev.id} className="px-3 py-2.5 flex items-start gap-3">
                      {/* Decision icon */}
                      <div className="pt-0.5 shrink-0">
                        {ev.decision === "Allowed"
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                          : ev.decision === "Denied"
                          ? <XCircle className="w-3.5 h-3.5 text-red-500" />
                          : <Info className="w-3.5 h-3.5 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{ev.eventType}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${DECISION_BADGE[ev.decision] ?? "bg-gray-100 text-gray-600"}`}>
                            {ev.decision}
                          </span>
                          {ev.reasonCode && (
                            <span className="px-1.5 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-mono">{ev.reasonCode}</span>
                          )}
                          {ev.entityType && (
                            <span className="text-muted-foreground">{ev.entityType}{ev.entityId ? ` #${ev.entityId.slice(0,8)}` : ""}</span>
                          )}
                          <span className="text-muted-foreground text-[10px] px-1.5 py-0.5 bg-gray-50 rounded border">{sourceLabel(ev.source)}</span>
                          {ev.studentInvolved && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">Student ref</span>
                          )}
                          {!ev.hasIntegrityHash && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-yellow-50 text-yellow-700 rounded">No hash</span>
                          )}
                        </div>
                        <div className="text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
                          {ev.userRole && <span>Role: {ev.userRole}</span>}
                          {ev.requestPurpose && <span className="truncate max-w-xs">Purpose: {ev.requestPurpose}</span>}
                          <span>{fmtDateTime(ev.timestamp)}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{ev.id}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Capture Event ───────────────────────────── */}
        {tab === "capture" && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Manually capture a FERPA audit event. Student PII will be encrypted before storage.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="f15-et">Event Type *</Label>
                <select id="f15-et"
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={cap.eventType} onChange={e => setCap(c => ({ ...c, eventType: e.target.value }))}>
                  {FERPA15_EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
                <FieldErr id="f15-et-err" msg={capErr.eventType} />
              </div>
              <div>
                <Label htmlFor="f15-dec">Decision</Label>
                <select id="f15-dec"
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={cap.decision} onChange={e => setCap(c => ({ ...c, decision: e.target.value }))}>
                  {FERPA15_DECISIONS.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="f15-enttype">Entity Type</Label>
                <Input id="f15-enttype" placeholder="e.g. FerpaDisclosureRecord"
                  value={cap.entityType}
                  onChange={e => setCap(c => ({ ...c, entityType: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="f15-entid">Entity ID</Label>
                <Input id="f15-entid" placeholder="UUID or identifier"
                  value={cap.entityId}
                  onChange={e => setCap(c => ({ ...c, entityId: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="f15-role">User Role</Label>
                <Input id="f15-role" placeholder="e.g. Registrar"
                  value={cap.userRole}
                  onChange={e => setCap(c => ({ ...c, userRole: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="f15-rc">Reason Code</Label>
                <Input id="f15-rc" placeholder="e.g. LEI_REQUIRED"
                  value={cap.reasonCode}
                  onChange={e => setCap(c => ({ ...c, reasonCode: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label htmlFor="f15-sid">Student ID <span className="text-muted-foreground text-xs">(will be encrypted — never stored as plaintext)</span></Label>
              <Input id="f15-sid" placeholder="Student identifier (PII — encrypted at rest)"
                value={cap.studentId}
                onChange={e => setCap(c => ({ ...c, studentId: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="f15-purpose">Request Purpose</Label>
              <Input id="f15-purpose" placeholder="e.g. Academic advising review"
                value={cap.requestPurpose}
                onChange={e => setCap(c => ({ ...c, requestPurpose: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="f15-details">Details <span className="text-muted-foreground text-xs">(no PII)</span></Label>
              <Textarea id="f15-details" rows={2} placeholder="Optional structured JSON details"
                value={cap.details}
                onChange={e => setCap(c => ({ ...c, details: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="f15-retention">Retention Policy</Label>
              <select id="f15-retention"
                className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={cap.retentionPolicy}
                onChange={e => setCap(c => ({ ...c, retentionPolicy: e.target.value }))}>
                <option value="Standard">Standard (7 years)</option>
                <option value="Extended">Extended (10 years)</option>
                <option value="Permanent">Permanent</option>
              </select>
            </div>
            <Button onClick={handleCapture} disabled={capture.isPending} className="w-full">
              {capture.isPending ? "Capturing…" : "Capture Audit Event"}
            </Button>
          </div>
        )}

        {/* ── TAB: Integrity Check ─────────────────────────── */}
        {tab === "verify" && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Verify the tamper-evident HMAC-SHA256 integrity hash of a specific audit event.
              Copy the event UUID from the Audit Events tab.
            </p>
            <div className="flex gap-2">
              <Input placeholder="Audit event UUID"
                value={verifyId}
                onChange={e => setVerifyId(e.target.value)}
                className="font-mono text-sm" />
              <Button onClick={runVerify} disabled={verifying}>
                {verifying ? "Verifying…" : "Verify"}
              </Button>
            </div>

            {verifyRes && (
              <div className={`rounded-lg border p-4 ${
                verifyRes.integrityValid === true  ? "border-green-300 bg-green-50"  :
                verifyRes.integrityValid === false ? "border-red-300 bg-red-50"      :
                                                    "border-yellow-300 bg-yellow-50"}`}>
                <div className="flex items-center gap-2 font-medium text-sm mb-1">
                  {verifyRes.integrityValid === true
                    ? <><ShieldCheck className="w-4 h-4 text-green-600" /> Integrity Verified</>
                    : verifyRes.integrityValid === false
                    ? <><AlertTriangle className="w-4 h-4 text-red-600" /> Integrity Violation</>
                    : <><Info className="w-4 h-4 text-yellow-600" /> Cannot Verify</>}
                </div>
                <p className="text-xs text-muted-foreground">{verifyRes.message}</p>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Has Hash</span>
                    <p className="font-medium">{verifyRes.hasHash ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Key Available</span>
                    <p className="font-medium">{verifyRes.keyAvailable ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Result</span>
                    <p className={`font-medium ${verifyRes.integrityValid === true ? "text-green-700" : verifyRes.integrityValid === false ? "text-red-700" : "text-yellow-700"}`}>
                      {verifyRes.integrityValid === true ? "PASS" : verifyRes.integrityValid === false ? "FAIL" : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── FERPA-14 — Access Control Matrix Section ──────────────────────────────────

type AcmEntry = {
  id: string; role: string; recordType: string;
  isPermitted: boolean; requiresLei: boolean;
  requiresMinimization: boolean; requiresAudit: boolean;
  scopeConstraints: string | null; policyJustification: string | null;
  isActive: boolean; createdAt: string; updatedAt: string;
};

type AccessDenialLog = {
  id: string; requestingUserId: string | null; requestingRole: string | null;
  requestingStudentId: string | null; recordType: string; purpose: string | null;
  outcome: string; denialCode: string | null; denialReason: string | null;
  timestamp: string;
};

const RECORD_TYPES = [
  "Transcript","Enrollment","Disciplinary","Financial",
  "Medical","Directory","Assessment","Attendance","Other",
];
const FERPA_ROLES = [
  "Student","Faculty","Advisor","Registrar","StudentHealth",
  "Admin","Auditor","Vendor","External","Other",
];

function AccessControlSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [tab, setTab] = useState<"matrix" | "check" | "denials">("matrix");
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    role: "Faculty",
    recordType: "Transcript",
    isPermitted: true,
    requiresLei: false,
    requiresMinimization: false,
    requiresAudit: true,
    scopeConstraints: "",
    policyJustification: "",
  });

  // Protected-record check form
  const [chk, setChk] = useState({
    role: "Faculty",
    recordType: "Transcript",
    studentId: "",
    requestingStudentId: "",
    purpose: "",
    isVendorRequest: false,
  });
  const [chkResult, setChkResult] = useState<any>(null);
  const [chkLoading, setChkLoading] = useState(false);

  const MQ = ["/api/organization/compliance/ferpa/access-control-matrix"];
  const DQ = ["/api/organization/compliance/ferpa/access-denial-logs"];

  const { data: matrixData, isLoading: matrixLoading } = useQuery<PagedResult<AcmEntry>>({
    queryKey: MQ,
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/access-control-matrix?pageSize=200", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load access control matrix");
      return res.json();
    },
  });

  const { data: denialData } = useQuery<PagedResult<AccessDenialLog>>({
    queryKey: DQ,
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/access-denial-logs?pageSize=50", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load denial logs");
      return res.json();
    },
    enabled: tab === "denials",
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/access-control-matrix", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          ...form,
          scopeConstraints:    form.scopeConstraints    || null,
          policyJustification: form.policyJustification || null,
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MQ });
      toast({ title: "Access control entry added" });
      setOpen(false);
      setFieldErrors({});
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Correct highlighted fields.", variant: "destructive" }); }
      else { setFieldErrors({ _: e.message ?? "Unable to save" }); toast({ title: "Failed to save entry", description: e.message, variant: "destructive" }); }
    },
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await fetch(`/api/organization/compliance/ferpa/access-control-matrix/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ isActive }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MQ });
      toast({ title: "Entry updated" });
    },
    onError: () => toast({ title: "Update failed", variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/access-control-matrix/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MQ });
      toast({ title: "Entry removed" });
    },
    onError: () => toast({ title: "Failed to remove entry", variant: "destructive" }),
  });

  const runCheck = async () => {
    setChkLoading(true);
    setChkResult(null);
    try {
      const res = await fetch("/api/organization/compliance/ferpa/protected-record-check", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          role:               chk.role,
          recordType:         chk.recordType,
          studentId:          chk.studentId || null,
          requestingStudentId: chk.requestingStudentId || null,
          purpose:            chk.purpose || null,
          isVendorRequest:    chk.isVendorRequest,
          vendorId:           null,
        }),
      });
      const body = await res.json();
      setChkResult(body);
      queryClient.invalidateQueries({ queryKey: DQ });
    } catch (e: any) {
      toast({ title: "Check failed", description: e.message, variant: "destructive" });
    } finally { setChkLoading(false); }
  };

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.role.trim())       errs.role       = "Role is required.";
    if (!form.recordType.trim()) errs.recordType = "Record type is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); return; }
    setFieldErrors({});
    create.mutate();
  };

  // Group matrix entries by role for compact display
  const byRole = matrixData?.items.reduce((acc, e) => {
    (acc[e.role] ??= []).push(e);
    return acc;
  }, {} as Record<string, AcmEntry[]>) ?? {};

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Access Control
            </CardTitle>
            <CardDescription className="text-xs">34 CFR § 99 — role-based access policy, authorization gate, and denial log</CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Add Entry
            </Button>
          )}
        </div>
        <div className="flex gap-1 pt-2 border-b">
          {(["matrix", "check", "denials"] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-xs rounded-t font-medium transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
              {t === "matrix" ? "Access Matrix" : t === "check" ? "Authorization Check" : "Denial Log"}
            </button>
          ))}
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {/* ── TAB: Access Matrix ─────────────────────────────────── */}
        {tab === "matrix" && (
          <>
            {matrixLoading ? (
              <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : !matrixData?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No access control entries defined. Click "Add Entry" to define the role-to-record-type access policy.
              </p>
            ) : (
              <div className="space-y-4">
                {Object.entries(byRole).map(([role, entries]) => (
                  <div key={role}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{role}</span>
                    </div>
                    <div className="rounded border divide-y text-sm">
                      {entries.map(e => (
                        <div key={e.id} className={`px-3 py-2 flex items-center gap-2 ${!e.isActive ? "opacity-50" : ""}`}>
                          <div className="flex-1 flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-xs">{e.recordType}</span>
                            {e.isPermitted
                              ? <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 rounded">Permitted</span>
                              : <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-800 rounded">Denied</span>}
                            {e.requiresLei          && <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">LEI required</span>}
                            {e.requiresMinimization && <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Minimization</span>}
                            {e.requiresAudit        && <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">Audited</span>}
                            {!e.isActive            && <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">Inactive</span>}
                          </div>
                          {canManage && (
                            <div className="flex items-center gap-1 shrink-0">
                              <Button variant="ghost" size="sm" className="h-6 text-xs px-2"
                                onClick={() => toggleActive.mutate({ id: e.id, isActive: !e.isActive })}>
                                {e.isActive ? "Disable" : "Enable"}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7"
                                aria-label="Remove entry" onClick={() => remove.mutate(e.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB: Authorization Check ──────────────────────────── */}
        {tab === "check" && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Evaluate whether a role is authorized to access a record type based on the access control matrix.
              Every check is logged in the Denial Log.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa14-chk-role">Role</Label>
                <select id="ferpa14-chk-role"
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={chk.role} onChange={e => setChk(f => ({ ...f, role: e.target.value }))}>
                  {FERPA_ROLES.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="ferpa14-chk-rt">Record Type</Label>
                <select id="ferpa14-chk-rt"
                  className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={chk.recordType} onChange={e => setChk(f => ({ ...f, recordType: e.target.value }))}>
                  {RECORD_TYPES.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="ferpa14-chk-purpose">Purpose (required if LEI applies)</Label>
              <Input id="ferpa14-chk-purpose" placeholder="e.g. Academic performance review for enrolled students"
                value={chk.purpose}
                onChange={e => setChk(f => ({ ...f, purpose: e.target.value }))} />
            </div>
            {chk.role === "Student" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ferpa14-chk-sid">Record Student ID</Label>
                  <Input id="ferpa14-chk-sid" placeholder="Student whose record is requested"
                    value={chk.studentId}
                    onChange={e => setChk(f => ({ ...f, studentId: e.target.value }))} />
                </div>
                <div>
                  <Label htmlFor="ferpa14-chk-rsid">Requesting Student ID</Label>
                  <Input id="ferpa14-chk-rsid" placeholder="Authenticated student"
                    value={chk.requestingStudentId}
                    onChange={e => setChk(f => ({ ...f, requestingStudentId: e.target.value }))} />
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={chk.isVendorRequest}
                onChange={e => setChk(f => ({ ...f, isVendorRequest: e.target.checked }))} />
              This is a vendor / third-party access request
            </label>
            <Button onClick={runCheck} disabled={chkLoading} className="w-full">
              {chkLoading ? "Checking…" : "Run Authorization Check"}
            </Button>

            {chkResult && (
              <div className={`rounded-lg border p-4 ${chkResult.outcome === "Allowed" ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50"}`}>
                <div className="flex items-center gap-2 font-medium text-sm mb-1">
                  {chkResult.outcome === "Allowed"
                    ? <><CheckCircle2 className="w-4 h-4 text-green-600" /> Access Authorized</>
                    : <><XCircle className="w-4 h-4 text-red-600" /> Access Denied</>}
                </div>
                <p className="text-xs text-muted-foreground">{chkResult.message || chkResult.denialReason}</p>
                {chkResult.denialCode && (
                  <p className="text-xs mt-1 font-mono font-medium text-red-700">{chkResult.denialCode}</p>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {chkResult.requiresLei          && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">LEI required</span>}
                  {chkResult.requiresMinimization && <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Minimization required</span>}
                  {chkResult.requiresAudit        && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Audited</span>}
                </div>
                <p className="text-xs mt-1 text-muted-foreground">Log ID: <span className="font-mono">{chkResult.logId}</span></p>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: Denial Log ───────────────────────────────────── */}
        {tab === "denials" && (
          <>
            {!denialData?.items.length ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                No authorization decisions logged yet. Use the "Authorization Check" tab to run a check.
              </p>
            ) : (
              <div className="divide-y text-sm">
                {denialData.items.map(l => (
                  <div key={l.id} className="py-2 flex items-start gap-3">
                    {l.outcome === "Allowed"
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{l.requestingRole ?? "Unknown"}</span>
                        <span className="text-muted-foreground">→</span>
                        <span>{l.recordType}</span>
                        {l.denialCode && <Badge variant="outline" className="text-xs text-red-700">{l.denialCode}</Badge>}
                      </div>
                      {l.purpose && <p className="text-xs text-muted-foreground mt-0.5">Purpose: {l.purpose}</p>}
                      {l.denialReason && <p className="text-xs text-red-600 mt-0.5">{l.denialReason}</p>}
                      <p className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(l.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* ── Add Entry Dialog ─────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={v => { setOpen(v); if (!v) setFieldErrors({}); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Access Control Entry</DialogTitle>
            <DialogDescription>Define whether a role may access a record type and what controls apply.</DialogDescription>
          </DialogHeader>

          {fieldErrors._ && <p role="alert" className="text-xs text-destructive -mt-1">{fieldErrors._}</p>}

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ferpa14-role">Role *</Label>
                <select id="ferpa14-role" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={form.role}
                  onChange={e => { setForm(f => ({ ...f, role: e.target.value })); if (fieldErrors.role) setFieldErrors(fe => ({ ...fe, role: "" })); }}
                  aria-invalid={!!fieldErrors.role || undefined}>
                  {FERPA_ROLES.map(r => <option key={r}>{r}</option>)}
                </select>
                <FieldErr id="ferpa14-role-err" msg={fieldErrors.role} />
              </div>
              <div>
                <Label htmlFor="ferpa14-rt">Record Type *</Label>
                <select id="ferpa14-rt" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                  value={form.recordType}
                  onChange={e => { setForm(f => ({ ...f, recordType: e.target.value })); if (fieldErrors.recordType) setFieldErrors(fe => ({ ...fe, recordType: "" })); }}
                  aria-invalid={!!fieldErrors.recordType || undefined}>
                  {RECORD_TYPES.map(r => <option key={r}>{r}</option>)}
                </select>
                <FieldErr id="ferpa14-rt-err" msg={fieldErrors.recordType} />
              </div>
            </div>

            <div className="space-y-2">
              {([
                ["isPermitted",          "Permitted to access this record type"],
                ["requiresLei",          "Requires Legitimate Educational Interest check"],
                ["requiresMinimization", "Requires data minimization"],
                ["requiresAudit",        "Every access must be audited"],
              ] as [keyof typeof form, string][]).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox"
                    checked={form[key] as boolean}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))} />
                  {label}
                </label>
              ))}
            </div>

            <div>
              <Label htmlFor="ferpa14-scope">Scope Constraints <span className="text-muted-foreground text-xs">(optional JSON array)</span></Label>
              <Input id="ferpa14-scope" placeholder='e.g. ["ownRecord"] or ["assignedAdvisees"]'
                value={form.scopeConstraints}
                onChange={e => setForm(f => ({ ...f, scopeConstraints: e.target.value }))} />
            </div>

            <div>
              <Label htmlFor="ferpa14-just">Policy Justification</Label>
              <Textarea id="ferpa14-just" rows={2}
                placeholder="Brief justification for this access control policy"
                value={form.policyJustification}
                onChange={e => setForm(f => ({ ...f, policyJustification: e.target.value }))} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Add Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── FERPA-12 — Data Minimization Section ─────────────────────────────────────

type DataClassification = {
  id: string; entityName: string; fieldName: string;
  classification: string; purpose: string;
  allowedRoles: string | null; allowedUsages: string | null;
  isActive: boolean; notes: string | null;
  createdAt: string; updatedAt: string;
};

type MinimizationLog = {
  id: string; requestingUserRole: string; purpose: string; entityName: string;
  fieldsRequested: string; fieldsReturned: string; fieldsRedacted: string;
  redactionReasons: string | null; timestamp: string;
};

const CLASSIFICATION_LEVELS = ["Required", "Sensitive", "Optional", "Restricted"] as const;

const classificationBadge = (c: string) => {
  const map: Record<string, string> = {
    Required:   "bg-green-100 text-green-800",
    Optional:   "bg-blue-100 text-blue-800",
    Sensitive:  "bg-yellow-100 text-yellow-800",
    Restricted: "bg-red-100 text-red-800",
  };
  return map[c] ?? "bg-gray-100 text-gray-800";
};

function DataClassificationsTab({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [filterEntity, setFilterEntity] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [form, setForm] = useState({
    entityName: "", fieldName: "", classification: "Optional",
    purpose: "", allowedRoles: "", allowedUsages: "", isActive: true, notes: "",
  });
  const [fe, setFe] = useState<Record<string, string>>({});
  const sf = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (fe[k]) setFe(p => ({ ...p, [k]: "" }));
  };

  const qk = ["/api/organization/compliance/ferpa/data-classifications", filterEntity, filterClass];
  const { data, isLoading } = useQuery<PagedResult<DataClassification>>({
    queryKey: qk,
    queryFn: async () => {
      const params = new URLSearchParams({ pageSize: "100" });
      if (filterEntity) params.set("entityName", filterEntity);
      if (filterClass)  params.set("classification", filterClass);
      const r = await fetch(`/api/organization/compliance/ferpa/data-classifications?${params}`, { headers: bearerHeader() });
      if (!r.ok) throw new Error("Failed to load classifications");
      return r.json();
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const url = editId
        ? `/api/organization/compliance/ferpa/data-classifications/${editId}`
        : "/api/organization/compliance/ferpa/data-classifications";
      const body: Record<string, unknown> = {
        classification: form.classification, purpose: form.purpose,
        allowedRoles: form.allowedRoles.trim() || null,
        allowedUsages: form.allowedUsages.trim() || null,
        isActive: form.isActive, notes: form.notes || null,
      };
      if (!editId) { body.entityName = form.entityName; body.fieldName = form.fieldName; }
      const r = await fetch(url, {
        method: editId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify(body),
      });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-classifications"] });
      toast({ title: editId ? "Classification updated" : "Classification created" });
      setOpen(false); setEditId(null); setFe({});
      setForm({ entityName: "", fieldName: "", classification: "Optional", purpose: "", allowedRoles: "", allowedUsages: "", isActive: true, notes: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) { setFe(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else { setFe({ _: e.message }); toast({ title: "Failed to save", description: e.message, variant: "destructive" }); }
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/organization/compliance/ferpa/data-classifications/${id}`, { method: "DELETE", headers: bearerHeader() });
      if (!r.ok) throw new Error("Unable to delete");
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/data-classifications"] }); toast({ title: "Classification removed" }); },
    onError: () => toast({ title: "Failed to remove classification", variant: "destructive" }),
  });

  const openEdit = (c: DataClassification) => {
    setEditId(c.id);
    setForm({
      entityName: c.entityName, fieldName: c.fieldName, classification: c.classification,
      purpose: c.purpose, allowedRoles: c.allowedRoles ?? "", allowedUsages: c.allowedUsages ?? "",
      isActive: c.isActive, notes: c.notes ?? "",
    });
    setOpen(true);
  };

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!editId && !form.entityName.trim()) errs.entityName = "Required.";
    if (!editId && !form.fieldName.trim()) errs.fieldName = "Required.";
    if (!form.purpose.trim()) errs.purpose = "Required.";
    if (Object.keys(errs).length) { setFe(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    save.mutate();
  };

  // Unique entities for filter dropdown
  const entities = [...new Set(data?.items.map(x => x.entityName) ?? [])];

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select className="border rounded px-2 py-1 text-xs" value={filterEntity} onChange={e => setFilterEntity(e.target.value)}>
          <option value="">All entities</option>
          {entities.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select className="border rounded px-2 py-1 text-xs" value={filterClass} onChange={e => setFilterClass(e.target.value)}>
          <option value="">All levels</option>
          {CLASSIFICATION_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {(filterEntity || filterClass) && (
          <button className="text-xs text-muted-foreground underline" onClick={() => { setFilterEntity(""); setFilterClass(""); }}>Clear</button>
        )}
        {canManage && (
          <Button size="sm" className="ml-auto" onClick={() => { setEditId(null); setForm({ entityName: "", fieldName: "", classification: "Optional", purpose: "", allowedRoles: "", allowedUsages: "", isActive: true, notes: "" }); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Add Classification
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      ) : !data?.items.length ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          <p className="font-medium mb-1">No field classifications defined</p>
          <p className="text-xs max-w-sm mx-auto">
            Add classifications to map each student data field to its sensitivity level (Required, Sensitive, Optional, Restricted).
            The minimization check gate uses this catalog to decide what each role may access.
          </p>
        </div>
      ) : (
        <div className="divide-y text-sm">
          {data.items.map(c => (
            <div key={c.id} className="py-2.5 flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{c.entityName}.</span>
                  <span className="font-mono font-medium text-xs -ml-2">{c.fieldName}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${classificationBadge(c.classification)}`}>{c.classification}</span>
                  {!c.isActive && <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500">Inactive</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{c.purpose}</p>
                {c.allowedRoles && (
                  <p className="text-xs text-muted-foreground">Roles: <span className="font-mono">{c.allowedRoles}</span></p>
                )}
              </div>
              {canManage && (
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Edit" onClick={() => openEdit(c)}>
                    <Pencil className="w-3.5 h-3.5" aria-hidden />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Delete" onClick={() => del.mutate(c.id)}>
                    <Trash2 className="w-3.5 h-3.5" aria-hidden />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFe({}); setEditId(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Classification" : "Add Data Classification"}</DialogTitle>
            <DialogDescription>
              Map a student data field to its sensitivity level. The minimization check gate uses this catalog
              to enforce role-based and purpose-based access decisions.
            </DialogDescription>
          </DialogHeader>
          {fe._ && <p role="alert" className="text-xs text-destructive">{fe._}</p>}
          <div className="space-y-3">
            {!editId && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="dc-entity">Entity *</Label>
                  <Input id="dc-entity" value={form.entityName} onChange={sf("entityName")} placeholder="StudentRecord" className={fieldCls("", fe, "entityName")} aria-invalid={!!fe.entityName || undefined} />
                  <FieldErr id="dc-entity-err" msg={fe.entityName} />
                </div>
                <div>
                  <Label htmlFor="dc-field">Field *</Label>
                  <Input id="dc-field" value={form.fieldName} onChange={sf("fieldName")} placeholder="GPA" className={fieldCls("", fe, "fieldName")} aria-invalid={!!fe.fieldName || undefined} />
                  <FieldErr id="dc-field-err" msg={fe.fieldName} />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="dc-class">Classification *</Label>
              <select id="dc-class" className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.classification} onChange={sf("classification")}>
                <option value="Required">Required — minimum necessary, all authorized roles</option>
                <option value="Optional">Optional — contextual; included in full, omitted in minimal</option>
                <option value="Sensitive">Sensitive — student PII, specific role + purpose required</option>
                <option value="Restricted">Restricted — never general API (SSN, medical, financial)</option>
              </select>
            </div>
            <div>
              <Label htmlFor="dc-purpose">Purpose *</Label>
              <Input id="dc-purpose" value={form.purpose} onChange={sf("purpose")} placeholder="Academic advising, Enrollment verification…" className={fieldCls("", fe, "purpose")} aria-invalid={!!fe.purpose || undefined} />
              <FieldErr id="dc-purpose-err" msg={fe.purpose} />
            </div>
            <div>
              <Label htmlFor="dc-roles">Allowed Roles <span className="text-muted-foreground text-xs">(JSON array; leave blank = all roles)</span></Label>
              <Input id="dc-roles" value={form.allowedRoles} onChange={sf("allowedRoles")} placeholder='["Advisor","Registrar"]' className="font-mono text-xs" />
            </div>
            <div>
              <Label htmlFor="dc-usages">Allowed Usages <span className="text-muted-foreground text-xs">(JSON array; leave blank = any purpose)</span></Label>
              <Input id="dc-usages" value={form.allowedUsages} onChange={sf("allowedUsages")} placeholder='["AcademicAdvising","EnrollmentVerification"]' className="font-mono text-xs" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
              Active — included in minimization checks
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : editId ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MinimizationCheckTab() {
  const { toast } = useToast();
  const [form, setForm] = useState({ entityName: "", fields: "", requestingRole: "", purpose: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{
    permitted: string[]; redacted: string[];
    redactionReasons: Record<string, string>; logId: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const errs: Record<string, string> = {};
    required(errs, "entityName", form.entityName, "Entity Name");
    required(errs, "fields", form.fields, "Fields");
    required(errs, "requestingRole", form.requestingRole, "Requesting Role");
    if (Object.keys(errs).length) { setErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setErrors({});
    const fields = form.fields.split(",").map(f => f.trim()).filter(Boolean);
    setBusy(true); setResult(null);
    try {
      const r = await fetch("/api/organization/compliance/ferpa/minimization-check", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ entityName: form.entityName.trim(), fields, requestingRole: form.requestingRole.trim(), purpose: form.purpose.trim() }),
      });
      const body = await r.json();
      if (r.ok) setResult({ permitted: body.permitted, redacted: body.redacted, redactionReasons: body.redactionReasons, logId: body.logId });
      else toast({ title: body.message ?? "Check failed", variant: "destructive" });
    } catch (e: any) {
      toast({ title: "Check failed", description: e.message, variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
        The minimization check gate evaluates a field list against the data classification catalog for a given
        role and purpose. Fields are split into <strong>permitted</strong> (the minimum necessary) and
        <strong>redacted</strong> (withheld with reasons). Every check is logged.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="mc-entity">Entity Name *</Label>
          <Input id="mc-entity" value={form.entityName} onChange={e => { setForm(f => ({ ...f, entityName: e.target.value })); clearErr(setErrors, "entityName"); }} placeholder="StudentRecord" className={fieldCls("", errors, "entityName")} aria-invalid={!!errors.entityName} />{fErr(errors, "entityName")}
        </div>
        <div>
          <Label htmlFor="mc-fields">Fields * <span className="text-muted-foreground text-xs">(comma-separated)</span></Label>
          <Input id="mc-fields" value={form.fields} onChange={e => { setForm(f => ({ ...f, fields: e.target.value })); clearErr(setErrors, "fields"); }} placeholder="StudentId, GPA, SSN, EnrollStatus" className={fieldCls("", errors, "fields")} aria-invalid={!!errors.fields} />{fErr(errors, "fields")}
        </div>
        <div>
          <Label htmlFor="mc-role">Requesting Role *</Label>
          <Input id="mc-role" value={form.requestingRole} onChange={e => { setForm(f => ({ ...f, requestingRole: e.target.value })); clearErr(setErrors, "requestingRole"); }} placeholder="Faculty" className={fieldCls("", errors, "requestingRole")} aria-invalid={!!errors.requestingRole} />{fErr(errors, "requestingRole")}
        </div>
        <div>
          <Label htmlFor="mc-purpose">Purpose</Label>
          <Input id="mc-purpose" value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} placeholder="AcademicAdvising" />
        </div>
      </div>
      <Button onClick={run} disabled={busy}>{busy ? "Checking…" : "Run Minimization Check"}</Button>

      {result && (
        <div className="space-y-3">
          <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
            <p className="font-semibold text-green-900 mb-1">✓ Permitted fields ({result.permitted.length})</p>
            {result.permitted.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {result.permitted.map(f => (
                  <span key={f} className="font-mono text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">{f}</span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-green-700">No fields permitted for this role/purpose.</p>
            )}
          </div>
          {result.redacted.length > 0 && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm">
              <p className="font-semibold text-red-900 mb-1">✗ Redacted fields ({result.redacted.length})</p>
              <div className="space-y-1">
                {result.redacted.map(f => (
                  <div key={f} className="flex items-start gap-2">
                    <span className="font-mono text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded shrink-0">{f}</span>
                    {result.redactionReasons[f] && (
                      <span className="text-xs text-red-700">{result.redactionReasons[f]}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-muted-foreground font-mono">Log ID: {result.logId}</p>
        </div>
      )}
    </div>
  );
}

function MinimizationLogsTab() {
  const { data, isLoading } = useQuery<PagedResult<MinimizationLog>>({
    queryKey: ["/api/organization/compliance/ferpa/minimization-logs"],
    queryFn: async () => {
      const r = await fetch("/api/organization/compliance/ferpa/minimization-logs?pageSize=50", { headers: bearerHeader() });
      if (!r.ok) throw new Error("Failed to load logs");
      return r.json();
    },
  });

  const parseArr = (s: string): string[] => { try { return JSON.parse(s) ?? []; } catch { return []; } };

  return (
    <div>
      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : !data?.items.length ? (
        <p className="text-sm text-muted-foreground text-center py-6">No minimization checks have been run yet. Use the Check tab to evaluate field access.</p>
      ) : (
        <div className="divide-y text-sm">
          {data.items.map(log => {
            const redacted = parseArr(log.fieldsRedacted);
            const returned = parseArr(log.fieldsReturned);
            return (
              <div key={log.id} className="py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{log.entityName}</span>
                  <span className="text-xs">role: <span className="font-medium">{log.requestingUserRole}</span></span>
                  {log.purpose && <span className="text-xs">purpose: <span className="font-medium">{log.purpose}</span></span>}
                  <span className="ml-auto text-xs text-muted-foreground">{fmtDate(log.timestamp)}</span>
                </div>
                <div className="flex gap-3 mt-1 text-xs flex-wrap">
                  <span className="text-green-700">✓ {returned.length} permitted: {returned.join(", ") || "none"}</span>
                  {redacted.length > 0 && (
                    <span className="text-red-700">✗ {redacted.length} redacted: {redacted.join(", ")}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DataMinimizationSection({ canManage }: { canManage: boolean }) {
  const [tab, setTab] = useState<"classifications" | "check" | "logs">("classifications");
  const { data: catalog } = useQuery<PagedResult<DataClassification>>({
    queryKey: ["/api/organization/compliance/ferpa/data-classifications"],
    queryFn: async () => {
      const r = await fetch("/api/organization/compliance/ferpa/data-classifications?pageSize=100", { headers: bearerHeader() });
      if (!r.ok) throw new Error("Failed");
      return r.json();
    },
  });
  const classifiedCount = catalog?.total ?? 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="w-4 h-4" /> Data Minimization
          <span className="text-xs font-normal text-muted-foreground ml-1">FERPA minimum-necessary principle</span>
        </CardTitle>
        <CardDescription className="text-xs">
          Classify student data fields by sensitivity level and enforce role-based + purpose-based access decisions.
          {classifiedCount > 0 && <span className="ml-1">· {classifiedCount} field{classifiedCount !== 1 ? "s" : ""} classified</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex gap-1 border-b mb-4">
          {(["classifications", "check", "logs"] as const).map(t => {
            const labels: Record<typeof t, string> = { classifications: "Field Classifications", check: "Minimization Check", logs: "Decision Log" };
            return (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {labels[t]}
              </button>
            );
          })}
        </div>
        {tab === "classifications" && <DataClassificationsTab canManage={canManage} />}
        {tab === "check"           && <MinimizationCheckTab />}
        {tab === "logs"            && <MinimizationLogsTab />}
      </CardContent>
    </Card>
  );
}

// ── FERPA-11 — Directory Information Section (34 CFR § 99.3 / § 99.37) ───────

type DirectoryField = {
  id: string; fieldName: string; description: string; category: string;
  isActive: boolean; isDisclosureAllowed: boolean; sortOrder: number;
  notes: string | null; createdAt: string; updatedAt: string;
};

type DirectoryNotice = {
  id: string; title: string; content: string; version: string;
  publicationDate: string | null; effectiveDate: string | null;
  status: string; notes: string | null; createdAt: string; updatedAt: string;
};

const DIR_CATEGORIES = ["Name", "Contact", "Enrollment", "Academic", "Activity", "Other"] as const;

function DirectoryFieldsTab({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ fieldName: "", description: "", category: "Enrollment", isActive: true, isDisclosureAllowed: true, sortOrder: 0, notes: "" });
  const [fe, setFe] = useState<Record<string, string>>({});
  const sf = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (fe[k]) setFe(p => ({ ...p, [k]: "" }));
  };

  const { data, isLoading } = useQuery<PagedResult<DirectoryField>>({
    queryKey: ["/api/organization/compliance/ferpa/directory-fields"],
    queryFn: async () => {
      const r = await fetch("/api/organization/compliance/ferpa/directory-fields?pageSize=100", { headers: bearerHeader() });
      if (!r.ok) throw new Error("Failed to load directory fields");
      return r.json();
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const url = editId
        ? `/api/organization/compliance/ferpa/directory-fields/${editId}`
        : "/api/organization/compliance/ferpa/directory-fields";
      const r = await fetch(url, {
        method: editId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ ...form, sortOrder: Number(form.sortOrder), notes: form.notes || null }),
      });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/directory-fields"] });
      toast({ title: editId ? "Directory field updated" : "Directory field created" });
      setOpen(false); setEditId(null); setFe({});
      setForm({ fieldName: "", description: "", category: "Enrollment", isActive: true, isDisclosureAllowed: true, sortOrder: 0, notes: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) { setFe(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else { setFe({ _: e.message ?? "Could not save." }); toast({ title: "Failed to save", description: e.message, variant: "destructive" }); }
    },
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/organization/compliance/ferpa/directory-fields/${id}`, { method: "DELETE", headers: bearerHeader() });
      if (!r.ok) throw new Error("Unable to delete");
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/directory-fields"] }); toast({ title: "Directory field removed" }); },
    onError: () => toast({ title: "Failed to remove field", variant: "destructive" }),
  });

  const openEdit = (f: DirectoryField) => {
    setEditId(f.id);
    setForm({ fieldName: f.fieldName, description: f.description, category: f.category, isActive: f.isActive, isDisclosureAllowed: f.isDisclosureAllowed, sortOrder: f.sortOrder, notes: f.notes ?? "" });
    setOpen(true);
  };

  const categoryBadge = (c: string) => {
    const map: Record<string, string> = { Name: "bg-purple-100 text-purple-800", Contact: "bg-blue-100 text-blue-800", Enrollment: "bg-green-100 text-green-800", Academic: "bg-yellow-100 text-yellow-800", Activity: "bg-orange-100 text-orange-800", Other: "bg-gray-100 text-gray-800" };
    return map[c] ?? "bg-gray-100 text-gray-800";
  };

  return (
    <div>
      {canManage && (
        <div className="flex justify-end mb-3">
          <Button size="sm" onClick={() => { setEditId(null); setForm({ fieldName: "", description: "", category: "Enrollment", isActive: true, isDisclosureAllowed: true, sortOrder: 0, notes: "" }); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Add Field
          </Button>
        </div>
      )}
      {isLoading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      ) : !data?.items.length ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          <p className="font-medium mb-1">No directory fields defined</p>
          <p className="text-xs">Add the fields your institution designates as directory information (e.g. Name, Enrollment Status, Major).</p>
        </div>
      ) : (
        <div className="divide-y text-sm">
          {data.items.map(f => (
            <div key={f.id} className="py-2.5 flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-medium text-xs">{f.fieldName}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${categoryBadge(f.category)}`}>{f.category}</span>
                  {!f.isActive && <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500">Inactive</span>}
                  {!f.isDisclosureAllowed && <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">Disclosure Restricted</span>}
                </div>
                <p className="text-muted-foreground text-xs mt-0.5">{f.description}</p>
              </div>
              {canManage && (
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Edit field" onClick={() => openEdit(f)}>
                    <Pencil className="w-3.5 h-3.5" aria-hidden />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Delete field" onClick={() => del.mutate(f.id)}>
                    <Trash2 className="w-3.5 h-3.5" aria-hidden />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFe({}); setEditId(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Directory Field" : "Add Directory Field"}</DialogTitle>
            <DialogDescription>Define a field as directory information (§ 99.3). Only designated, active fields may be disclosed through the directory workflow.</DialogDescription>
          </DialogHeader>
          {fe._ && <p role="alert" className="text-xs text-destructive px-1">{fe._}</p>}
          <div className="space-y-3">
            {!editId && (
              <div>
                <Label htmlFor="df-fieldName">Field Key * <span className="text-muted-foreground text-xs">(machine-readable, e.g. "Email")</span></Label>
                <Input id="df-fieldName" value={form.fieldName} onChange={sf("fieldName")} placeholder="EnrollmentStatus" className={fieldCls("", fe, "fieldName")} aria-invalid={!!fe.fieldName || undefined} />
                <FieldErr id="df-fieldName-err" msg={fe.fieldName} />
              </div>
            )}
            <div>
              <Label htmlFor="df-description">Description *</Label>
              <Input id="df-description" value={form.description} onChange={sf("description")} placeholder="Student's current enrollment status (full-time, part-time, etc.)" className={fieldCls("", fe, "description")} aria-invalid={!!fe.description || undefined} />
              <FieldErr id="df-desc-err" msg={fe.description} />
            </div>
            <div>
              <Label htmlFor="df-category">Category</Label>
              <select id="df-category" className="w-full border rounded px-2 py-1.5 text-sm mt-1" value={form.category} onChange={sf("category")}>
                {DIR_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
                Active designation
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isDisclosureAllowed} onChange={e => setForm(f => ({ ...f, isDisclosureAllowed: e.target.checked }))} />
                Disclosure allowed
              </label>
            </div>
            <div>
              <Label htmlFor="df-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="df-notes" value={form.notes} onChange={sf("notes")} placeholder="e.g. opt-out deadline 10 days after term start" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => { const errs: Record<string,string> = {}; if (!editId && !form.fieldName.trim()) errs.fieldName = "Required."; if (!form.description.trim()) errs.description = "Required."; if (Object.keys(errs).length) { setFe(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; } save.mutate(); }} disabled={save.isPending}>
              {save.isPending ? "Saving…" : editId ? "Update Field" : "Create Field"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DirectoryCheckTab() {
  const { toast } = useToast();
  const [form, setForm] = useState({ studentId: "", fieldName: "" });
  const [result, setResult] = useState<{ outcome: string; message: string; code?: string; fieldName?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!form.studentId.trim() || !form.fieldName.trim()) {
      toast({ title: "Both Student ID and Field Name are required.", variant: "destructive" }); return;
    }
    setBusy(true); setResult(null);
    try {
      const r = await fetch("/api/organization/compliance/ferpa/directory-check", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ studentId: form.studentId.trim(), fieldName: form.fieldName.trim() }),
      });
      const body = await r.json();
      if (r.ok) setResult({ outcome: "Allowed", message: body.message, fieldName: body.fieldName });
      else setResult({ outcome: "Blocked", message: body.message ?? body.detail ?? "Blocked", code: body.code });
    } catch (e: any) {
      toast({ title: "Check failed", description: e.message, variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
        The directory check gate (§ 99.37) verifies whether a specific directory information field may be
        disclosed for a student — based on active field designation and opt-out status.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="dc-student">Student ID</Label>
          <Input id="dc-student" value={form.studentId} onChange={e => setForm(f => ({ ...f, studentId: e.target.value }))} placeholder="e.g. STU-0001" />
        </div>
        <div>
          <Label htmlFor="dc-field">Field Name</Label>
          <Input id="dc-field" value={form.fieldName} onChange={e => setForm(f => ({ ...f, fieldName: e.target.value }))} placeholder="e.g. EnrollmentStatus" />
        </div>
      </div>
      <Button onClick={run} disabled={busy}>{busy ? "Checking…" : "Run Directory Check"}</Button>
      {result && (
        <div className={`rounded-md border p-3 text-sm ${result.outcome === "Allowed" ? "bg-green-50 border-green-200 text-green-900" : "bg-red-50 border-red-200 text-red-900"}`}>
          <div className="font-semibold mb-1">{result.outcome === "Allowed" ? "✓ Disclosure Permitted" : "✗ Disclosure Blocked"}</div>
          <p className="text-xs">{result.message}</p>
          {result.code && <p className="text-xs mt-1 font-mono opacity-70">{result.code}</p>}
        </div>
      )}
    </div>
  );
}

function DirectoryNoticeTab({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", version: "", effectiveDate: "", publish: false, notes: "" });
  const [fe, setFe] = useState<Record<string, string>>({});

  const { data: notices, isLoading } = useQuery<PagedResult<DirectoryNotice>>({
    queryKey: ["/api/organization/compliance/ferpa/directory-notices"],
    queryFn: async () => {
      const r = await fetch("/api/organization/compliance/ferpa/directory-notices?pageSize=20", { headers: bearerHeader() });
      if (!r.ok) throw new Error("Failed to load notices");
      return r.json();
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const r = await fetch("/api/organization/compliance/ferpa/directory-notices", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ ...form, effectiveDate: form.effectiveDate ? new Date(form.effectiveDate).toISOString() : null, notes: form.notes || null }),
      });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/directory-notices"] });
      toast({ title: form.publish ? "Notice published" : "Notice draft saved" });
      setOpen(false); setFe({});
      setForm({ title: "", content: "", version: "", effectiveDate: "", publish: false, notes: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) { setFe(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); }
      else { setFe({ _: e.message }); toast({ title: "Failed to save notice", description: e.message, variant: "destructive" }); }
    },
  });

  const publish = useMutation({
    mutationFn: async (id: string) => {
      const r = await fetch(`/api/organization/compliance/ferpa/directory-notices/${id}/publish`, { method: "PATCH", headers: bearerHeader() });
      await throwIfResNotOk(r);
      return r.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/directory-notices"] }); toast({ title: "Notice published" }); },
    onError: () => toast({ title: "Failed to publish notice", variant: "destructive" }),
  });

  const statusBadge = (s: string) => {
    if (s === "Published") return "bg-green-100 text-green-800";
    if (s === "Archived")  return "bg-gray-100 text-gray-600";
    return "bg-yellow-100 text-yellow-800"; // Draft
  };

  return (
    <div>
      {canManage && (
        <div className="flex justify-end mb-3">
          <Button size="sm" onClick={() => { setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New Notice
          </Button>
        </div>
      )}
      {isLoading ? (
        <div className="space-y-2">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : !notices?.items.length ? (
        <p className="text-sm text-muted-foreground text-center py-6">No directory information notices on file. Create one to track annual notice obligations (§ 99.34).</p>
      ) : (
        <div className="divide-y text-sm">
          {notices.items.map(n => (
            <div key={n.id} className="py-3 flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{n.title}</span>
                  <span className="text-muted-foreground text-xs">v{n.version}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusBadge(n.status)}`}>{n.status}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {n.effectiveDate && <span>Effective {fmtDate(n.effectiveDate)}</span>}
                  {n.publicationDate && <span className="ml-2">Published {fmtDate(n.publicationDate)}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.content}</p>
              </div>
              {canManage && n.status === "Draft" && (
                <Button variant="outline" size="sm" className="shrink-0 text-xs" onClick={() => publish.mutate(n.id)} disabled={publish.isPending}>
                  Publish
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setFe({}); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>New Directory Information Notice</DialogTitle>
            <DialogDescription>Draft or publish the annual notice informing students of directory information designations and their opt-out right (§ 99.34 / § 99.37).</DialogDescription>
          </DialogHeader>
          {fe._ && <p role="alert" className="text-xs text-destructive">{fe._}</p>}
          <div className="space-y-3">
            <div>
              <Label htmlFor="dn-title">Title *</Label>
              <Input id="dn-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="2026–2027 Annual Directory Information Notice" className={fieldCls("", fe, "title")} aria-invalid={!!fe.title || undefined} />
              <FieldErr id="dn-title-err" msg={fe.title} />
            </div>
            <div>
              <Label htmlFor="dn-version">Version Label *</Label>
              <Input id="dn-version" value={form.version} onChange={e => setForm(f => ({ ...f, version: e.target.value }))} placeholder="2026-2027" className={fieldCls("", fe, "version")} aria-invalid={!!fe.version || undefined} />
              <FieldErr id="dn-ver-err" msg={fe.version} />
            </div>
            <div>
              <Label htmlFor="dn-content">Notice Content *</Label>
              <textarea id="dn-content" rows={5} className={`w-full border rounded px-2 py-1.5 text-sm mt-1${fe.content ? " border-red-500" : ""}`}
                value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="The following categories of student information are designated as directory information..." aria-invalid={!!fe.content || undefined} />
              <FieldErr id="dn-content-err" msg={fe.content} />
            </div>
            <div>
              <Label htmlFor="dn-effective">Effective Date <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="dn-effective" type="date" value={form.effectiveDate} onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.publish} onChange={e => setForm(f => ({ ...f, publish: e.target.checked }))} />
              Publish immediately (archives any current published notice)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => {
              const errs: Record<string,string> = {};
              if (!form.title.trim()) errs.title = "Required.";
              if (!form.version.trim()) errs.version = "Required.";
              if (!form.content.trim()) errs.content = "Required.";
              if (Object.keys(errs).length) { setFe(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
              save.mutate();
            }} disabled={save.isPending}>
              {save.isPending ? "Saving…" : form.publish ? "Publish Notice" : "Save Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DirectoryInformationSection({ canManage }: { canManage: boolean }) {
  const [tab, setTab] = useState<"fields" | "optouts" | "notice" | "check">("fields");
  const { data: fields } = useQuery<PagedResult<DirectoryField>>({
    queryKey: ["/api/organization/compliance/ferpa/directory-fields"],
    queryFn: async () => {
      const r = await fetch("/api/organization/compliance/ferpa/directory-fields?pageSize=100", { headers: bearerHeader() });
      if (!r.ok) throw new Error("Failed to load");
      return r.json();
    },
  });
  const activeCount = fields?.items.filter(f => f.isActive).length ?? 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> Directory Information
          <span className="text-xs font-normal text-muted-foreground ml-1">34 CFR § 99.3 / § 99.37</span>
        </CardTitle>
        <CardDescription className="text-xs">
          Manage the institution's directory information designation, student opt-out preferences, and annual notice obligations.
          {activeCount > 0 && <span className="ml-1">· {activeCount} active field{activeCount !== 1 ? "s" : ""} designated</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Tab bar */}
        <div className="flex gap-1 border-b mb-4">
          {(["fields", "optouts", "notice", "check"] as const).map(t => {
            const labels: Record<typeof t, string> = { fields: "Directory Fields", optouts: "Opt-Outs", notice: "Annual Notice", check: "Disclosure Check" };
            return (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${tab === t ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                {labels[t]}
              </button>
            );
          })}
        </div>

        {tab === "fields"  && <DirectoryFieldsTab   canManage={canManage} />}
        {tab === "optouts" && <DirectoryPreferencesSection canManage={canManage} />}
        {tab === "notice"  && <DirectoryNoticeTab   canManage={canManage} />}
        {tab === "check"   && <DirectoryCheckTab />}
      </CardContent>
    </Card>
  );
}

// ── Directory Preferences Section ─────────────────────────────────────────────

type DirectoryPreference = {
  id: string; studentId: string; optOutStatus: string;
  effectiveDate: string; updatedBy: string | null;
  notes: string | null; createdAt: string; updatedAt: string;
};

function DirectoryPreferencesSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    studentId: "",
    optOutStatus: "OptedOut",
    notes: "",
  });

  const { data, isLoading } = useQuery<PagedResult<DirectoryPreference>>({
    queryKey: ["/api/organization/compliance/ferpa/directory-preferences"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/directory-preferences?pageSize=50", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load directory preferences");
      return res.json();
    },
  });

  const upsert = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/directory-preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({ ...form, notes: form.notes || null }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/directory-preferences"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Directory preference saved" });
      setOpen(false);
      setFieldErrors({});
      setForm({ studentId: "", optOutStatus: "OptedOut", notes: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        setFieldErrors({ _: e.message ?? "Could not save. Please check the fields and try again." });
        toast({ title: "Failed to save directory preference", description: e.message, variant: "destructive" });
      }
    },
  });

  const handleSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.studentId.trim()) errs.studentId = "Student ID is required.";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    upsert.mutate();
  };

  const setField = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (fieldErrors[key]) setFieldErrors(fe => ({ ...fe, [key]: "" }));
  };

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/directory-preferences/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/directory-preferences"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Directory preference removed" });
    },
    onError: () => toast({ title: "Failed to remove directory preference", variant: "destructive" }),
  });

  const optedOutCount = data?.items.filter(p => p.optOutStatus === "OptedOut").length ?? 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Directory Information Opt-Outs
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99.37 — students who have restricted disclosure of directory information
              {data && (
                <span className="ml-1">
                  · {optedOutCount} of {data.total} opted out
                </span>
              )}
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Set Preference
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : !data?.items.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">No directory preferences recorded.</p>
        ) : (
          <div className="divide-y text-sm">
            {data.items.map(p => (
              <div key={p.id} className="py-2 flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{p.studentId}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      p.optOutStatus === "OptedOut"
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}>
                      {p.optOutStatus === "OptedOut" ? "Opted Out" : "Opted In"}
                    </span>
                  </div>
                  <div className="text-muted-foreground text-xs mt-0.5">
                    Effective {fmtDate(p.effectiveDate)}
                    {p.notes && ` · ${p.notes}`}
                  </div>
                </div>
                {canManage && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                    aria-label="Remove consent record"
                    onClick={() => remove.mutate(p.id)}>
                    <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setFieldErrors({}); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set Directory Preference</DialogTitle>
            <DialogDescription>
              Record or update a student&apos;s directory information opt-out election (§ 99.37).
              One preference per student — submitting again updates the existing record.
            </DialogDescription>
          </DialogHeader>

          {fieldErrors._ && (
            <p role="alert" className="text-xs text-destructive px-1 -mt-1">{fieldErrors._}</p>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="ferpa-dir-studentId">Student ID *</Label>
              <Input
                id="ferpa-dir-studentId"
                value={form.studentId}
                onChange={setField("studentId")}
                placeholder="Student identifier"
                required
                className={fieldCls("", fieldErrors, "studentId")}
                aria-invalid={!!fieldErrors.studentId || undefined}
                aria-describedby={fieldErrors.studentId ? "ferpa-dir-studentId-err" : undefined}
              />
              <FieldErr id="ferpa-dir-studentId-err" msg={fieldErrors.studentId} />
            </div>

            <div>
              <Label htmlFor="ferpa-dir-optOutStatus">Directory Opt-Out Status</Label>
              <select id="ferpa-dir-optOutStatus" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={form.optOutStatus} onChange={setField("optOutStatus")}>
                <option value="OptedOut">Opted Out — restrict directory information disclosures</option>
                <option value="OptedIn">Opted In — allow directory information disclosures</option>
              </select>
            </div>

            <div>
              <Label htmlFor="ferpa-dir-notes">Notes <span className="text-muted-foreground">(optional)</span></Label>
              <Input id="ferpa-dir-notes" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. submitted via registration portal" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={upsert.isPending}>
              {upsert.isPending ? "Saving…" : "Save Preference"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Annual Rights Notice Section (FERPA-04 — 34 CFR § 99.7) ──────────────────

function AnnualNoticesSection({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();

  // ── List ──
  const { data, isLoading, refetch } = useQuery<PagedResult<AnnualNotice>>({
    queryKey: ["/api/organization/compliance/ferpa/annual-notices"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/annual-notices?pageSize=20", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load annual notices");
      return res.json();
    },
  });

  // ── Template fetch ──
  const [templateContent, setTemplateContent] = useState<string | null>(null);
  const [templateYear, setTemplateYear] = useState<string>("");

  async function loadTemplate() {
    const res = await fetch("/api/organization/compliance/ferpa/annual-notices/template", {
      headers: bearerHeader(),
    });
    if (!res.ok) return;
    const { content, academicYear } = await res.json();
    setTemplateContent(content);
    setTemplateYear(academicYear);
    setForm(f => ({
      ...f,
      noticeContent: content,
      academicYear: academicYear,
      noticeTitle: `Annual FERPA Rights Notice ${academicYear}`,
    }));
    setFieldErrors({});
  }

  // ── Draft dialog ──
  const [draftOpen, setDraftOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    noticeTitle: "",
    noticeContent: "",
    academicYear: "",
    distributionMethod: "",
    notes: "",
  });

  const setField = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    if (fieldErrors[key]) setFieldErrors(fe => ({ ...fe, [key]: "" }));
  };

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/annual-notices", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          noticeTitle: form.noticeTitle,
          noticeContent: form.noticeContent,
          academicYear: form.academicYear,
          distributionMethod: form.distributionMethod || null,
          notes: form.notes || null,
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/annual-notices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Annual notice drafted", description: "Review the content then publish it to generate compliance evidence." });
      setDraftOpen(false);
      setFieldErrors({});
      setForm({ noticeTitle: "", noticeContent: "", academicYear: "", distributionMethod: "", notes: "" });
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setFieldErrors(errs);
        toast({ title: "Please correct the highlighted fields.", variant: "destructive" });
      } else {
        toast({ title: "Failed to save notice", description: e.message, variant: "destructive" });
      }
    },
  });

  const handleDraftSubmit = () => {
    const errs: Record<string, string> = {};
    if (!form.noticeTitle.trim()) errs.noticeTitle = "Notice title is required.";
    if (!form.noticeContent.trim()) errs.noticeContent = "Notice content is required.";
    if (!form.academicYear.trim()) errs.academicYear = "Academic year is required (e.g. 2025-2026).";
    if (form.academicYear.trim() && !/^\d{4}-\d{4}$/.test(form.academicYear.trim()))
      errs.academicYear = "Use the format YYYY-YYYY (e.g. 2025-2026).";
    if (Object.keys(errs).length) { setFieldErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setFieldErrors({});
    create.mutate();
  };

  // ── Publish dialog ──
  const [publishTarget, setPublishTarget] = useState<AnnualNotice | null>(null);
  const [publishForm, setPublishForm] = useState({ distributionMethod: "Email", distributionDate: new Date().toISOString().split("T")[0], notes: "" });
  const [publishErrors, setPublishErrors] = useState<Record<string, string>>({});

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!publishTarget) return;
      const res = await fetch(`/api/organization/compliance/ferpa/annual-notices/${publishTarget.id}/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...bearerHeader() },
        body: JSON.stringify({
          distributionMethod: publishForm.distributionMethod,
          distributionDate: new Date(publishForm.distributionDate).toISOString(),
          notes: publishForm.notes || null,
        }),
      });
      await throwIfResNotOk(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/annual-notices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Annual notice published", description: "Compliance evidence record has been generated." });
      setPublishTarget(null);
      setPublishErrors({});
    },
    onError: (e: any) => {
      const errs = toFlatFieldErrors(e);
      if (Object.keys(errs).length) {
        setPublishErrors(errs);
      } else {
        toast({ title: "Failed to publish notice", description: e.message, variant: "destructive" });
      }
    },
  });

  const handlePublishSubmit = () => {
    const errs: Record<string, string> = {};
    if (!publishForm.distributionMethod) errs.distributionMethod = "Distribution method is required.";
    if (Object.keys(errs).length) { setPublishErrors(errs); toast({ title: "Please correct the highlighted fields.", variant: "destructive" }); return; }
    setPublishErrors({});
    publishMutation.mutate();
  };

  // ── Archive / Delete ──
  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/annual-notices/${id}/archive`, {
        method: "POST", headers: bearerHeader(),
      });
      await throwIfResNotOk(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/annual-notices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Notice archived" });
    },
    onError: (e: any) => toast({ title: "Failed to archive notice", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/organization/compliance/ferpa/annual-notices/${id}`, {
        method: "DELETE", headers: bearerHeader(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = body?.errors?._?.[0] ?? body?.message ?? "Could not delete.";
        throw new Error(msg);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/annual-notices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/ferpa/summary"] });
      toast({ title: "Draft deleted" });
    },
    onError: (e: any) => toast({ title: "Cannot delete notice", description: e.message, variant: "destructive" }),
  });

  // ── Expand/collapse notice content ──
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const noticeBadgeColor = (status: string) =>
    status === "Published" ? "bg-green-100 text-green-800" :
    status === "Archived"  ? "bg-gray-100 text-gray-600" :
    status === "Review"    ? "bg-blue-100 text-blue-800" :
    "bg-yellow-100 text-yellow-800";

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-4 h-4" /> Annual FERPA Rights Notice
            </CardTitle>
            <CardDescription className="text-xs">
              34 CFR § 99.7 — annual notification of student rights under FERPA
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={async () => { setDraftOpen(true); if (!templateContent) await loadTemplate(); }}>
              <Plus className="w-4 h-4 mr-1" /> Draft Notice
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : !data?.items.length ? (
          <div className="text-center py-6 space-y-2">
            <Bell className="w-8 h-8 text-orange-400 mx-auto" />
            <p className="text-sm font-medium text-orange-700">No annual FERPA notice on record</p>
            <p className="text-xs text-muted-foreground">
              34 CFR § 99.7 requires annual notification of student rights. Draft and publish a notice to generate compliance evidence.
            </p>
          </div>
        ) : (
          <div className="divide-y text-sm">
            {data.items.map(n => (
              <div key={n.id} className="py-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{n.noticeTitle}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${noticeBadgeColor(n.status)}`}>{n.status}</span>
                      <Badge variant="outline" className="text-xs">{n.academicYear}</Badge>
                    </div>
                    <div className="text-muted-foreground text-xs mt-0.5">
                      Created {fmtDate(n.createdAt)}
                      {n.publishedDate && ` · Published ${fmtDate(n.publishedDate)}`}
                      {n.distributionMethod && ` via ${n.distributionMethod}`}
                      {n.distributionDate && ` on ${fmtDate(n.distributionDate)}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7"
                      title="Preview notice content"
                      aria-label="Preview notice content"
                      onClick={() => setExpandedId(expandedId === n.id ? null : n.id)}>
                      <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                    </Button>
                    {canManage && (n.status === "Draft" || n.status === "Review") && (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => { setPublishTarget(n); setPublishForm({ distributionMethod: "Email", distributionDate: new Date().toISOString().split("T")[0], notes: "" }); setPublishErrors({}); }}>
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Publish
                      </Button>
                    )}
                    {canManage && n.status === "Published" && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
                        onClick={() => archiveMutation.mutate(n.id)}
                        disabled={archiveMutation.isPending}>
                        Archive
                      </Button>
                    )}
                    {canManage && (n.status === "Draft" || n.status === "Review") && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                        onClick={() => deleteMutation.mutate(n.id)}
                        disabled={deleteMutation.isPending}
                        aria-label="Delete annual notice">
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
                {expandedId === n.id && (
                  <pre className="whitespace-pre-wrap text-xs bg-muted rounded p-3 max-h-60 overflow-y-auto font-sans border">
                    {n.noticeContent}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Draft Dialog */}
      <Dialog open={draftOpen} onOpenChange={(v) => { setDraftOpen(v); if (!v) { setFieldErrors({}); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Draft Annual FERPA Rights Notice</DialogTitle>
            <DialogDescription>
              Create the annual notice required by 34 CFR § 99.7. The content must cover all six required rights.
              Use the template to start with a pre-written § 99.7 compliant notice.
            </DialogDescription>
          </DialogHeader>

          {fieldErrors._ && (
            <p role="alert" className="text-xs text-destructive px-1 -mt-1">{fieldErrors._}</p>
          )}

          <div className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" variant="outline" type="button"
                onClick={loadTemplate}>
                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Fill § 99.7 Template
              </Button>
            </div>

            <div>
              <Label htmlFor="fan-title">Notice Title *</Label>
              <Input id="fan-title" value={form.noticeTitle} onChange={setField("noticeTitle")}
                placeholder="e.g. Annual FERPA Rights Notice 2025-2026" required
                aria-invalid={!!fieldErrors.noticeTitle || undefined}
                aria-describedby={fieldErrors.noticeTitle ? "fan-title-err" : undefined}
              className={fieldCls("", fieldErrors, "noticeTitle")}
              />
              <FieldErr id="fan-title-err" msg={fieldErrors.noticeTitle} />
            </div>

            <div>
              <Label htmlFor="fan-year">Academic Year *</Label>
              <Input id="fan-year" value={form.academicYear} onChange={setField("academicYear")}
                placeholder="e.g. 2025-2026" required
                aria-invalid={!!fieldErrors.academicYear || undefined}
                aria-describedby={fieldErrors.academicYear ? "fan-year-err" : undefined}
              className={fieldCls("", fieldErrors, "academicYear")}
              />
              <FieldErr id="fan-year-err" msg={fieldErrors.academicYear} />
            </div>

            <div>
              <Label htmlFor="fan-content">
                Notice Content * <span className="text-muted-foreground text-xs">(must include all six § 99.7(a) rights)</span>
              </Label>
              <Textarea id="fan-content" value={form.noticeContent} onChange={setField("noticeContent")}
                placeholder="Full text of the annual FERPA rights notice…"
                rows={14} required
                className={fieldCls("", fieldErrors, "noticeContent")}
                aria-invalid={!!fieldErrors.noticeContent || undefined}
                aria-describedby={fieldErrors.noticeContent ? "fan-content-err" : undefined}
              />
              <FieldErr id="fan-content-err" msg={fieldErrors.noticeContent} />
            </div>

            <div>
              <Label htmlFor="fan-method">
                Distribution Method <span className="text-muted-foreground text-xs">(optional — set when publishing)</span>
              </Label>
              <select id="fan-method" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={form.distributionMethod} onChange={setField("distributionMethod")}>
                <option value="">— Select later —</option>
                <option>Email</option>
                <option>StudentPortal</option>
                <option>Website</option>
                <option>Handbook</option>
                <option>Other</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDraftOpen(false)}>Cancel</Button>
            <Button onClick={handleDraftSubmit} disabled={create.isPending}>
              {create.isPending ? "Saving…" : "Save Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish Dialog */}
      <Dialog open={!!publishTarget} onOpenChange={(v) => { if (!v) setPublishTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Publish Annual FERPA Notice</DialogTitle>
            <DialogDescription>
              Publishing records how this notice was distributed to eligible students and generates a
              compliance evidence record for Annual Rights Notice (§ 99.7).
            </DialogDescription>
          </DialogHeader>

          {publishTarget && (
            <div className="rounded bg-muted px-3 py-2 text-sm">
              <span className="font-medium">{publishTarget.noticeTitle}</span>
              <span className="ml-2 text-xs text-muted-foreground">({publishTarget.academicYear})</span>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <Label htmlFor="pub-method">Distribution Method *</Label>
              <select id="pub-method" className="w-full border rounded px-2 py-1.5 text-sm mt-1"
                value={publishForm.distributionMethod}
                onChange={e => { setPublishForm(f => ({ ...f, distributionMethod: e.target.value })); setPublishErrors(pe => ({ ...pe, distributionMethod: "" })); }}>
                <option value="">— Select —</option>
                <option>Email</option>
                <option>StudentPortal</option>
                <option>Website</option>
                <option>Handbook</option>
                <option>Other</option>
              </select>
              <FieldErr id="pub-method-err" msg={publishErrors.distributionMethod} />
            </div>
            <div>
              <Label htmlFor="pub-date">Distribution Date</Label>
              <Input id="pub-date" type="date" value={publishForm.distributionDate}
                onChange={e => setPublishForm(f => ({ ...f, distributionDate: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="pub-notes">Notes <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input id="pub-notes" value={publishForm.notes}
                onChange={e => setPublishForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Emailed to all enrolled students via student portal" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishTarget(null)}>Cancel</Button>
            <Button onClick={handlePublishSubmit} disabled={publishMutation.isPending}>
              {publishMutation.isPending ? "Publishing…" : "Publish & Generate Evidence"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Root FerpaTab ─────────────────────────────────────────────────────────────

export function FerpaTab({ canManage }: { canManage: boolean }) {
  const { data: summary, isLoading: summaryLoading } = useQuery<FerpaSummary>({
    queryKey: ["/api/organization/compliance/ferpa/summary"],
    queryFn: async () => {
      const res = await fetch("/api/organization/compliance/ferpa/summary", {
        headers: bearerHeader(),
      });
      if (!res.ok) throw new Error("Failed to load FERPA summary");
      return res.json();
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <GraduationCap className="w-5 h-5 text-blue-600" />
        <div>
          <h2 className="text-lg font-semibold">FERPA Compliance</h2>
          <p className="text-sm text-muted-foreground">
            Family Educational Rights and Privacy Act — 34 CFR Part 99
          </p>
        </div>
      </div>

      {/* G1 — Deadline alerts banner (above fold) */}
      <DeadlineAlertsBanner />

      {/* Summary */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[...Array(7)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
        </div>
      ) : summary ? (
        <SummarySection summary={summary} />
      ) : null}

      {/* ── FERPA Knowledge Source Verification ───────────────────────────── */}
      <div className="rounded-lg border border-purple-200 bg-purple-50/30 p-4">
        <div className="flex items-center gap-2 mb-3">
          <GraduationCap className="w-4 h-4 text-purple-700" />
          <h3 className="font-semibold text-purple-900">Knowledge Source Verification</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Each knowledge source is automatically evaluated against FERPA controls when added.
          Supply evidence for manual controls to activate each source.
        </p>
        <FerpaSourceVerifications canManage={canManage} />
      </div>

      {/* All FERPA controls are rendered inside the bot-grouped verification cards above.
          Standalone module sections have been removed — see FerpaSourceVerifications / BotGroupCard. */}
    </div>
  );
}
